import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import './App.css';

// ── Auth helpers ──────────────────────────────────────────────────────────────
function getToken() {
  return sessionStorage.getItem('aurelius_token');
}

function setToken(token) {
  sessionStorage.setItem('aurelius_token', token);
}

function clearToken() {
  sessionStorage.removeItem('aurelius_token');
  sessionStorage.removeItem('aurelius_role');
  sessionStorage.removeItem('aurelius_clientId');
  sessionStorage.removeItem('aurelius_email');
  sessionStorage.removeItem('aurelius_displayName');
}

function authFetch(url, options) {
  var token = getToken();
  var headers = Object.assign({}, (options && options.headers) || {});
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return fetch(url, Object.assign({}, options || {}, { headers: headers })).then(function(r) {
    if (r.status === 401) {
      clearToken();
      window.location.reload();
    }
    return r;
  });
}

const API = window.location.hostname === 'localhost' ? 'http://localhost:4000/api' : '/api';

// Unified GET helper — returns { ok, data, error } shape. Replaces the
// 33+ ad-hoc .then(r => r.json()).catch(() => {}) patterns that were
// silently eating errors and leaving tabs stuck on empty skeletons.
async function apiGet(path) {
  try {
    var r = await authFetch(API + path);
    if (!r.ok) {
      var msg;
      try { var body = await r.json(); msg = body.error || ('HTTP ' + r.status); } catch (e) { msg = 'HTTP ' + r.status; }
      return { ok: false, data: null, error: msg, status: r.status };
    }
    var data = await r.json();
    return { ok: true, data: data, error: null, status: r.status };
  } catch (e) {
    return { ok: false, data: null, error: e && e.message ? e.message : 'Network error', status: 0 };
  }
}

// Compact error banner for tabs when a fetch fails — gives users feedback
// and a retry path instead of an empty screen.
function ErrorBanner({ message, onRetry }) {
  return (
    <div className="api-error-banner">
      <span className="api-error-banner-icon">⚠</span>
      <div className="api-error-banner-body">
        <div className="api-error-banner-title">We couldn't load this view</div>
        <div className="api-error-banner-detail">{message || 'Unknown error. Try again in a moment.'}</div>
      </div>
      {onRetry && <button className="api-error-banner-retry" onClick={onRetry}>Retry</button>}
    </div>
  );
}

// ── Login Page ────────────────────────────────────────────────────────────────
function LoginPage({ clientId, clientName, onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);
  const [appVersion, setAppVersion] = useState('');
  const [theme, setLoginTheme] = useState(sessionStorage.getItem('aurelius_theme') || 'dark');
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoProcessing, setSsoProcessing] = useState(false);
  // Per-client URL flow defaults to password-only (legacy shared-account
  // pattern still works). User clicks "Sign in with a different account"
  // to reveal an email field for per-user logins (pre-SSO clients with
  // multiple named accounts).
  const [useEmail, setUseEmail] = useState(false);
  const [email, setEmail] = useState('');
  const canvasRef = useRef(null);

  useEffect(function() {
    document.documentElement.setAttribute('data-theme', theme);
    sessionStorage.setItem('aurelius_theme', theme);
  }, [theme]);

  useEffect(function() {
    fetch(API + '/version').then(function(r) { return r.json(); }).then(function(data) { setAppVersion(data.version); }).catch(function() {});
  }, []);

  // Is SSO enabled on the server? Only show the button on the admin page.
  useEffect(function() {
    if (clientId) return; // client URLs don't get SSO
    fetch(API + '/auth/microsoft/config')
      .then(function(r) { return r.ok ? r.json() : { enabled: false }; })
      .then(function(d) { setSsoEnabled(!!d.enabled); })
      .catch(function() {});
  }, [clientId]);

  // Handle SSO callback — backend has set aurelius_sso_token cookie + redirected with ?sso=success|error
  useEffect(function() {
    var params = new URLSearchParams(window.location.search);
    var ssoParam = params.get('sso');
    if (!ssoParam) return;

    // Strip query params from the URL so refresh doesn't re-trigger
    window.history.replaceState({}, '', window.location.pathname);

    if (ssoParam === 'error') {
      var reasonMap = {
        wrong_tenant: 'Your account is from a different Microsoft tenant.',
        no_matching_user: 'Your Microsoft account is not authorized for Aurelius.',
        state_mismatch: 'Session expired during sign-in — try again.',
        missing_state: 'Session expired during sign-in — try again.',
        exchange_failed: 'Microsoft sign-in failed. Try again or use a password.',
        no_oid: 'Microsoft did not return a stable identifier — contact support.'
      };
      setError(reasonMap[params.get('reason')] || 'Microsoft sign-in failed.');
      setShake(true);
      setTimeout(function() { setShake(false); }, 500);
      return;
    }

    if (ssoParam === 'success') {
      // Token lives in a short-lived cookie — read, extract, clear.
      setSsoProcessing(true);
      var cookieMatch = document.cookie.match(/aurelius_sso_token=([^;]+)/);
      if (!cookieMatch) { setError('Sign-in handoff failed — please try again.'); setSsoProcessing(false); return; }
      try {
        var parsed = JSON.parse(decodeURIComponent(cookieMatch[1]));
        // Clear the handoff cookie aggressively
        document.cookie = 'aurelius_sso_token=; Max-Age=0; path=/';
        setToken(parsed.token);
        sessionStorage.setItem('aurelius_role', parsed.role);
        if (parsed.email)       sessionStorage.setItem('aurelius_email', parsed.email);
        if (parsed.displayName) sessionStorage.setItem('aurelius_displayName', parsed.displayName);
        sessionStorage.removeItem('aurelius_clientId');
        setSuccess(true);
        setTimeout(function() { onLogin(parsed); }, 400);
      } catch (e) {
        setError('Sign-in handoff failed — please try again.');
        setSsoProcessing(false);
      }
    }
  }, [onLogin]);

  var [demoLaunching, setDemoLaunching] = useState(false);
  var launchDemo = function() {
    setDemoLaunching(true);
    setError('');
    sessionStorage.removeItem('aurelius_demo_role');
    fetch(API + '/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId: 'demo', password: 'aurelius-demo-2026' })
    }).then(function(r) { return r.json().then(function(data) { return { ok: r.ok, data: data }; }); })
      .then(function(resp) {
        if (resp.ok) {
          setToken(resp.data.token);
          sessionStorage.setItem('aurelius_role', resp.data.role);
          if (resp.data.clientId)     sessionStorage.setItem('aurelius_clientId', resp.data.clientId);
          if (resp.data.email)        sessionStorage.setItem('aurelius_email', resp.data.email);
          if (resp.data.displayName)  sessionStorage.setItem('aurelius_displayName', resp.data.displayName);
          setSuccess(true);
          setTimeout(function() { onLogin(resp.data); }, 500);
        } else {
          setError('Demo temporarily unavailable.');
          setDemoLaunching(false);
        }
      }).catch(function() {
        setError('Demo temporarily unavailable.');
        setDemoLaunching(false);
      });
  };

  useEffect(function() {
    var canvas = canvasRef.current;
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    var particles = [];
    var count = 40;
    var animId;
    function resize() {
      canvas.width = canvas.offsetWidth * (window.devicePixelRatio || 1);
      canvas.height = canvas.offsetHeight * (window.devicePixelRatio || 1);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
    }
    resize();
    window.addEventListener('resize', resize);
    for (var i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5,
        hue: Math.random() > 0.5 ? 250 : 190,
      });
    }
    function draw() {
      var w = canvas.offsetWidth;
      var h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        for (var j = i + 1; j < particles.length; j++) {
          var q = particles[j];
          var dx = p.x - q.x, dy = p.y - q.y;
          var dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = 'rgba(120,80,255,' + ((1 - dist / 120) * 0.12) + ')';
            ctx.lineWidth = 0.5; ctx.stroke();
          }
        }
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.hue === 250
          ? 'rgba(120,80,255,' + (0.3 + p.r * 0.15) + ')'
          : 'rgba(0,200,255,' + (0.25 + p.r * 0.12) + ')';
        ctx.fill();
      }
      animId = requestAnimationFrame(draw);
    }
    draw();
    return function() { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  var handleSubmit = async function(e) {
    e.preventDefault();
    setError('');
    setShake(false);
    setLoading(true);
    try {
      var body = { password: password };
      if (clientId) body.clientId = clientId;
      // Include email when the operator chose "different account" mode (per-
      // user login on a non-SSO client). Backend prefers email + verifies
      // it matches the URL's clientId.
      if (useEmail && email) body.email = email.trim();
      var resp = await fetch(API + '/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      var data = await resp.json();
      if (resp.ok) {
        setToken(data.token);
        sessionStorage.setItem('aurelius_role', data.role);
        if (data.clientId)    sessionStorage.setItem('aurelius_clientId', data.clientId);
        if (data.email)       sessionStorage.setItem('aurelius_email', data.email);
        if (data.displayName) sessionStorage.setItem('aurelius_displayName', data.displayName);
        setSuccess(true);
        setTimeout(function() { onLogin(data); }, 600);
      } else {
        setError(data.error || 'Invalid password');
        setShake(true);
        setTimeout(function() { setShake(false); }, 500);
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
      setShake(true);
      setTimeout(function() { setShake(false); }, 500);
    }
    setLoading(false);
  };

  // Polished loading screen while Launch Demo is in flight
  if (demoLaunching && !error) {
    return (
      <div className="login-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{`
          @keyframes demoSpin { to { transform: rotate(360deg); } }
          @keyframes demoFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
          .demo-loading-wrap { text-align: center; animation: demoFade 0.4s ease-out both; }
          .demo-loading-brand { font-size: 11px; font-weight: 700; letter-spacing: 6px; color: var(--accent-blue); text-transform: uppercase; margin-bottom: 18px; }
          .demo-loading-title { font-size: 26px; font-weight: 800; color: var(--text-primary); margin-bottom: 10px; letter-spacing: -0.5px; }
          .demo-loading-sub { font-size: 13px; color: var(--text-muted); margin-bottom: 36px; max-width: 380px; line-height: 1.6; }
          .demo-loading-spinner { width: 40px; height: 40px; border: 3px solid rgba(91,158,244,0.15); border-top-color: var(--accent-blue); border-radius: 50%; animation: demoSpin 0.8s linear infinite; margin: 0 auto; }
        `}</style>
        <div className="demo-loading-wrap">
          <div className="demo-loading-brand">Aurelius</div>
          <div className="demo-loading-title">Loading live demo</div>
          <div className="demo-loading-sub">Signing you in as Helios Industries — a 52-person technology services firm using the full Aurelius platform.</div>
          <div className="demo-loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={{ display: 'flex', flexDirection: 'row' }}>
      <style>{`
        @keyframes loginShake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-5px)} 80%{transform:translateX(5px)} }
        @keyframes loginMesh1 { 0%{transform:translate(0,0)} 50%{transform:translate(30px,-20px)} 100%{transform:translate(0,0)} }
        @keyframes loginMesh2 { 0%{transform:translate(0,0)} 50%{transform:translate(-20px,30px)} 100%{transform:translate(0,0)} }
        @keyframes loginSuccess { 0%{transform:scale(1)} 50%{transform:scale(1.03)} 100%{transform:scale(1)} }
        @keyframes loginStatFade { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes loginGridPulse { 0%,100%{opacity:0.03} 50%{opacity:0.06} }
        .login-split-left {
          flex: 0 0 58%; display: flex; flex-direction: column; justify-content: center; align-items: center;
          position: relative; overflow: hidden; padding: 48px;
        }
        .login-split-right {
          flex: 1; display: flex; flex-direction: column; justify-content: center; align-items: center;
          position: relative; padding: 48px; z-index: 2;
        }
        .login-mesh-orb {
          position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none; opacity: 0.12;
        }
        .login-grid-overlay {
          position: absolute; inset: 0; pointer-events: none;
          background-image: repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(120,80,255,0.04) 60px),
                            repeating-linear-gradient(90deg, transparent, transparent 59px, rgba(120,80,255,0.04) 60px);
          background-size: 60px 60px; animation: loginGridPulse 8s ease-in-out infinite;
        }
        .login-left-divider {
          position: absolute; right: 0; top: 10%; bottom: 10%; width: 1px;
          background: linear-gradient(180deg, transparent, var(--border-default, rgba(56,97,168,0.15)) 30%, var(--border-default, rgba(56,97,168,0.15)) 70%, transparent);
        }
        .login-card-v2 {
          width: 100%; max-width: 380px;
          background: var(--bg-card, rgba(15,27,54,0.6)); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--border-default, rgba(56,97,168,0.15)); border-radius: 16px;
          padding: 44px 36px; box-shadow: 0 24px 80px rgba(0,0,0,0.4);
        }
        .login-card-v2.shake { animation: loginShake 0.4s ease; }
        .login-card-v2.success { animation: loginSuccess 0.5s ease; }
        .login-pw-wrap {
          position: relative; display: flex; align-items: center;
        }
        .login-pw-wrap input { flex: 1; padding-right: 44px; }
        .login-pw-toggle {
          position: absolute; right: 14px; background: none; border: none; cursor: pointer; padding: 4px;
          color: var(--text-muted, #546889); font-size: 16px; line-height: 1; transition: color 0.2s;
        }
        .login-pw-toggle:hover { color: var(--text-secondary, #8b9dc3); }
        .login-btn-v2 {
          width: 100%; padding: 14px; border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
          font-family: var(--font-body, 'DM Sans', sans-serif); cursor: pointer; letter-spacing: 0.8px;
          text-transform: uppercase; transition: all 0.3s; position: relative; overflow: hidden;
          background: linear-gradient(135deg, #1e3a7a, var(--accent-blue, #5b9ef4)); color: #fff;
        }
        .login-btn-v2:hover:not(:disabled) { box-shadow: 0 4px 24px rgba(91,158,244,0.3); transform: translateY(-1px); }
        .login-btn-v2:disabled { opacity: 0.35; cursor: not-allowed; transform: none; box-shadow: none; }
        .login-btn-v2.loading { background: linear-gradient(135deg, #1e3a7a, #3b6fc4); }
        .login-btn-v2.success-state { background: linear-gradient(135deg, #0f5132, #22c55e); }
        .login-demo-section { margin-top: 28px; }
        .login-demo-divider {
          display: flex; align-items: center; gap: 12px;
          font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 2px;
          color: var(--text-muted, #546889); margin-bottom: 14px;
        }
        .login-demo-divider::before, .login-demo-divider::after {
          content: ''; flex: 1; height: 1px; background: rgba(120, 80, 255, 0.12);
        }
        .login-demo-landing { display: flex; flex-direction: column; gap: 18px; }
        .login-demo-persona-card {
          padding: 20px 22px;
          background: linear-gradient(135deg, rgba(120, 80, 255, 0.06), rgba(91, 158, 244, 0.04));
          border: 1px solid rgba(120, 80, 255, 0.18);
          border-radius: 12px;
          text-align: center;
        }
        .login-demo-persona-eyebrow {
          font-size: 9px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase;
          background: linear-gradient(135deg, #a78bfa, #5b9ef4);
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
          margin-bottom: 10px;
        }
        .login-demo-persona-name {
          font-size: 20px; font-weight: 700; color: var(--text-primary);
          letter-spacing: -0.3px; margin-bottom: 6px;
        }
        .login-demo-persona-meta {
          font-size: 12px; color: var(--text-muted, #546889);
          letter-spacing: 0.2px; line-height: 1.5;
        }
        .login-demo-note {
          text-align: center; margin-top: 2px;
          font-size: 10px; color: var(--text-muted, #546889); opacity: 0.7; letter-spacing: 0.4px;
        }
        .login-theme-toggle {
          position: absolute; top: 20px; right: 20px; z-index: 10;
          background: var(--bg-card, rgba(15,27,54,0.6)); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
          border: 1px solid var(--border-default, rgba(56,97,168,0.15)); border-radius: 10px;
          width: 36px; height: 36px; cursor: pointer; font-size: 16px; line-height: 1;
          color: var(--text-muted, #546889); transition: all 0.3s;
          display: flex; align-items: center; justify-content: center;
        }
        .login-theme-toggle:hover { color: var(--text-primary, #e8edf5); border-color: var(--accent-blue, #5b9ef4); box-shadow: 0 0 12px rgba(91,158,244,0.15); }
        @media (max-width: 900px) {
          .login-split-left { display: none !important; }
          .login-split-right { flex: 1; }
        }
      `}</style>

      {/* Left panel — brand + animated particle network */}
      <div className="login-split-left">
        <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
        <div className="login-grid-overlay" />
        <div className="login-left-divider" />

        <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img src="/logo.png" alt="CloudByte" style={{ width: 72, height: 72, marginBottom: 20, filter: 'drop-shadow(0 0 30px rgba(120,80,255,0.35))' }} />
          <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: 6, color: 'var(--text-primary, #e8edf5)', marginBottom: 4, textAlign: 'center' }}>AURELIUS</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted, #546889)', letterSpacing: 2, textAlign: 'center' }}>Security & operations dashboard</div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="login-split-right">
        <button className="login-theme-toggle" onClick={function() { setLoginTheme(theme === 'dark' ? 'light' : 'dark'); }} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          {theme === 'dark' ? '☀' : '☾'}
        </button>
        <div className={'login-card-v2' + (shake ? ' shake' : '') + (success ? ' success' : '')}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div className="login-split-mobile-logo" style={{ display: 'none' }}><img src="/logo.png" alt="CloudByte" style={{ width: 48, height: 48 }} /></div>
            <style>{`.login-split-right .login-split-mobile-logo { display: none !important; } @media (max-width: 900px) { .login-split-right .login-split-mobile-logo { display: flex !important; justify-content: center; margin-bottom: 8px; } }`}</style>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 3, color: 'var(--text-primary, #e8edf5)', marginBottom: 4 }}>
              {clientId === 'demo' ? 'Take a tour' : clientId ? 'Welcome back' : 'Sign in'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted, #546889)', letterSpacing: 1.5 }}>
              {clientId === 'demo' ? 'A live look at the platform' : clientId ? 'Client dashboard access' : 'Administrator access'}
            </div>
            {clientName && clientId !== 'demo' && (
              <div style={{ marginTop: 16, padding: '10px 16px', background: 'var(--accent-blue-dim, rgba(91,158,244,0.12))', border: '1px solid rgba(91,158,244,0.2)', borderRadius: 8, fontSize: 14, fontWeight: 600, color: 'var(--accent-blue, #5b9ef4)' }}>
                {clientName}
              </div>
            )}
          </div>

          {clientId === 'demo' ? (
            <div className="login-demo-landing">
              <div className="login-demo-persona-card">
                <div className="login-demo-persona-body">
                  <div className="login-demo-persona-eyebrow">Sample Environment</div>
                  <div className="login-demo-persona-name">Helios Industries</div>
                  <div className="login-demo-persona-meta">52 employees · 3 offices · Full stack deployed</div>
                </div>
              </div>
              <button type="button" className={'login-btn-v2' + (demoLaunching ? ' loading' : '') + (success ? ' success-state' : '')} onClick={function() { launchDemo(); }} disabled={demoLaunching || success}>
                {success ? '✓ Entering' : demoLaunching ? 'Launching...' : 'Enter Demo'}
              </button>
              <div className="login-demo-note">No signup · No credit card · No strings attached</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Microsoft SSO — only for admin login, only if server has SSO configured */}
              {ssoEnabled && !clientId && (
                <>
                  <button
                    type="button"
                    className={'login-sso-btn' + (ssoProcessing ? ' loading' : '') + (success ? ' success-state' : '')}
                    onClick={function() { window.location.href = API + '/auth/microsoft/login'; }}
                    disabled={ssoProcessing || success || loading}
                    title="Sign in with your CloudByte Group Microsoft account"
                  >
                    <svg className="login-sso-logo" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                    </svg>
                    <span>{ssoProcessing ? 'Signing in…' : success ? '✓ Signed in' : 'Sign in with Microsoft'}</span>
                  </button>
                  <div className="login-sso-divider"><span>or use password</span></div>
                </>
              )}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {useEmail && (
                  <input
                    type="email"
                    className="login-input"
                    placeholder="you@company.com"
                    value={email}
                    onChange={function(e) { setEmail(e.target.value); }}
                    autoFocus
                    autoComplete="username"
                    style={{ borderColor: error ? 'var(--accent-red, #f06565)' : undefined, boxShadow: error ? '0 0 0 3px var(--accent-red-dim, rgba(240,101,101,0.1))' : undefined }}
                  />
                )}
                <div className="login-pw-wrap">
                  <input
                    type={showPw ? 'text' : 'password'}
                    className="login-input"
                    placeholder="Enter password"
                    value={password}
                    onChange={function(e) { setPassword(e.target.value); }}
                    autoFocus={!ssoEnabled && !useEmail}
                    autoComplete="current-password"
                    style={{ borderColor: error ? 'var(--accent-red, #f06565)' : undefined, boxShadow: error ? '0 0 0 3px var(--accent-red-dim, rgba(240,101,101,0.1))' : undefined }}
                  />
                  <button type="button" className="login-pw-toggle" onClick={function() { setShowPw(!showPw); }} tabIndex={-1} title={showPw ? 'Hide password' : 'Show password'}>
                    {showPw ? '◉' : '○'}
                  </button>
                </div>
                <button type="submit" className={'login-btn-v2' + (loading ? ' loading' : '') + (success ? ' success-state' : '')} disabled={loading || !password || (useEmail && !email) || success || ssoProcessing}>
                  {success ? '✓ Authenticated' : loading ? 'Verifying...' : 'Enter Aurelius'}
                </button>
                {clientId && clientId !== 'demo' && (
                  <button
                    type="button"
                    onClick={function() { setUseEmail(!useEmail); setError(''); if (useEmail) setEmail(''); }}
                    style={{
                      background: 'transparent', border: 0, padding: 0, marginTop: -4,
                      color: 'var(--text-muted, #546889)', fontSize: 11, cursor: 'pointer',
                      textDecoration: 'underline', textUnderlineOffset: 2, opacity: 0.8,
                      alignSelf: 'flex-start', fontFamily: 'inherit'
                    }}
                    title={useEmail ? 'Use the shared account password' : 'Sign in with your individual account email'}
                  >
                    {useEmail ? 'Use shared account password' : 'Sign in with a different account'}
                  </button>
                )}
              </form>
            </div>
          )}

          {error && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--accent-red-dim, rgba(240,101,101,0.1))', border: '1px solid rgba(240,101,101,0.15)', borderRadius: 8, color: 'var(--accent-red, #f06565)', fontSize: 13, fontWeight: 500, animation: 'loginStatFade 0.3s ease' }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 24, textAlign: 'center', fontSize: 10, color: 'var(--text-muted, #546889)', letterSpacing: 0.5 }}>
            {clientId === 'demo' ? 'Poke around · Break nothing · Leave impressed' : 'Encrypted connection · Secure authentication'}
          </div>
        </div>

        <div style={{ marginTop: 32, maxWidth: 380, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted, #546889)', opacity: 0.45, fontStyle: 'italic', lineHeight: 1.7, letterSpacing: 0.2 }}>
            "The first rule is to keep an untroubled spirit. The second is to look things in the face and know them for what they are."
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted, #546889)', opacity: 0.3, marginTop: 6, letterSpacing: 1 }}>
            MARCUS AURELIUS · MEDITATIONS IV.3
          </div>
        </div>
        <div style={{ marginTop: 16, fontSize: 10, color: 'var(--text-muted, #546889)', opacity: 0.3, letterSpacing: 0.3 }}>
          Aurelius {appVersion ? 'v' + appVersion : ''} · CloudByte Group LLC
        </div>
      </div>
    </div>
  );
}
// ── Detect client URL mode ────────────────────────────────────────
function getClientFromURL() {
  const path = window.location.pathname;
  const match = path.match(/^\/client\/([a-z0-9-]+)/i);
  return match ? match[1].toLowerCase() : null;
}

const URL_CLIENT = getClientFromURL();
const IS_CLIENT_VIEW = !!URL_CLIENT;
const IS_DEMO_VIEW = URL_CLIENT === 'demo';

// Prospect-facing tenant assessment route: /assess/<token>
function getAssessTokenFromURL() {
  var m = window.location.pathname.match(/^\/assess\/([A-Za-z0-9_-]+)/);
  if (!m) return null;
  // Reserved sub-paths (error page, etc.) should not be treated as tokens
  if (m[1] === 'error') return null;
  return m[1];
}
const URL_ASSESS_TOKEN = getAssessTokenFromURL();
const IS_ASSESS_VIEW = !!URL_ASSESS_TOKEN || window.location.pathname === '/assess/error';
// Admin-only features (AI insights, QBR generation) are available to admins
// AND to demo visitors — the demo is a sales tool, prospects need to see these.
const CAN_USE_ADMIN_FEATURES = !IS_CLIENT_VIEW || IS_DEMO_VIEW;

const placeholders = {
  patches: { compliant: 0, pending: 0, failed: 0 },
  credentials: { total: 0, weak: 0, reused: 0, breached: 0, mfaEnabled: 0 },
  siem: { totalEvents: 0, findings: 0, critical: 0, high: 0, medium: 0 },
  tickets: { open: 0, resolved: 0, avgResponse: '—', sla: 0, csat: 0 },
  projects: [],
  cmmc_domains: {
    'Access Control (AC)': 0, 'Awareness & Training (AT)': 0,
    'Audit & Accountability (AU)': 0, 'Config Management (CM)': 0,
    'Identification & Auth (IA)': 0, 'Incident Response (IR)': 0,
    'Maintenance (MA)': 0, 'Media Protection (MP)': 0,
    'Personnel Security (PS)': 0, 'Physical Protection (PE)': 0,
    'Risk Assessment (RA)': 0, 'Security Assessment (CA)': 0,
    'System & Comm Protection (SC)': 0, 'System & Info Integrity (SI)': 0,
  },
};

function scoreVerdict(score) {
  if (score === 0) return { text: 'No data', color: 'var(--text-muted)' };
  if (score >= 90) return { text: 'Strong', color: '#22c55e' };
  if (score >= 70) return { text: 'Good', color: '#f59e0b' };
  if (score >= 50) return { text: 'Needs attention', color: '#f97316' };
  return { text: 'Critical', color: '#ef4444' };
}

function ScoreRing({ score, label, size = 100, hero }) {
  // 240-degree gradient arc (not a full ring)
  var gaugeSize = hero ? 120 : 96;
  var strokeW = hero ? 10 : 8;
  var radius = (gaugeSize - strokeW) / 2;
  var arcDeg = 240;
  var arcRad = (arcDeg * Math.PI) / 180;
  var circumference = arcRad * radius;
  var startAngle = 150; // starts at bottom-left

  // score === null/undefined → render a neutral "Not configured" gauge instead
  // of a red 0% ring. The integration just isn't wired for this client yet.
  var unconfigured = score == null;
  var fillLen = unconfigured ? 0 : (score / 100) * circumference;

  // Gradient color stops based on score
  var colorStart, colorEnd, glowColor;
  if (unconfigured)        { colorStart = '#475569'; colorEnd = '#64748b'; glowColor = 'rgba(100,116,139,0.10)'; }
  else if (score >= 85)    { colorStart = '#22c55e'; colorEnd = '#4ade80'; glowColor = 'rgba(34,197,94,0.25)'; }
  else if (score >= 70)    { colorStart = '#f59e0b'; colorEnd = '#fbbf24'; glowColor = 'rgba(245,158,11,0.25)'; }
  else if (score >= 50)    { colorStart = '#f97316'; colorEnd = '#fb923c'; glowColor = 'rgba(249,115,22,0.25)'; }
  else                     { colorStart = '#ef4444'; colorEnd = '#f87171'; glowColor = 'rgba(239,68,68,0.25)'; }

  var verdict = unconfigured ? { text: 'Not configured', color: '#64748b' } : scoreVerdict(score);
  var gradId = 'sg-' + label.replace(/\s/g, '').toLowerCase();
  var cx = gaugeSize / 2;
  var cy = gaugeSize / 2;

  // Build arc path
  function polarToCart(cx, cy, r, deg) {
    var rad = ((deg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }
  var arcStart = polarToCart(cx, cy, radius, startAngle);
  var arcEnd = polarToCart(cx, cy, radius, startAngle + arcDeg);
  var largeArc = arcDeg > 180 ? 1 : 0;
  var bgPath = 'M ' + arcStart.x + ' ' + arcStart.y + ' A ' + radius + ' ' + radius + ' 0 ' + largeArc + ' 1 ' + arcEnd.x + ' ' + arcEnd.y;

  return (
    <div className={'score-gauge' + (hero ? ' score-gauge-hero' : '')} style={{ '--score-glow': glowColor }}>
      <svg width={gaugeSize} height={gaugeSize} viewBox={'0 0 ' + gaugeSize + ' ' + gaugeSize}>
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={colorStart} />
            <stop offset="100%" stopColor={colorEnd} />
          </linearGradient>
        </defs>
        {/* Background track */}
        <path d={bgPath} fill="none" stroke="var(--bg-elevated, #1e293b)" strokeWidth={strokeW} strokeLinecap="round" />
        {/* Filled arc — skipped when unconfigured or score=0 */}
        {!unconfigured && score > 0 && <path d={bgPath} fill="none" stroke={'url(#' + gradId + ')'} strokeWidth={strokeW}
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference - fillLen}
          style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)' }} />}
        {/* Center label — number for scored, dash for unconfigured */}
        {unconfigured
          ? <text x={cx} y={cy - 2} textAnchor="middle" dominantBaseline="central" className="score-gauge-number" fill="var(--text-muted, #94a3b8)">—</text>
          : <text x={cx} y={cy - 2} textAnchor="middle" dominantBaseline="central" className="score-gauge-number" fill="var(--text-primary, #f1f5f9)">{score}<tspan className="score-gauge-suffix">/100</tspan></text>
        }
      </svg>
      <span className="score-gauge-label">{label}</span>
      <span className="score-gauge-verdict" style={{ background: verdict.color + '15', color: verdict.color }}>{verdict.text}</span>
    </div>
  );
}

function StatCard({ title, value, subtitle, accent, live, onClick }) {
  return (
    <div className={`stat-card ${live ? 'stat-card-live' : ''} ${onClick ? 'stat-card-clickable' : ''}`} onClick={onClick || undefined}>
      <div className="stat-title">
        {title}
        {live && <span className="live-badge">LIVE</span>}
        {onClick && <span className="stat-drill">Details ›</span>}
      </div>
      <div className="stat-value" style={{ color: accent || '#e8edf5' }}>{value}</div>
      {subtitle && <div className="stat-subtitle">{subtitle}</div>}
    </div>
  );
}

function ProgressBar({ label, value, status }) {
  const color = value >= 80 ? '#22c55e' : value >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="progress-item">
      <div className="progress-header">
        <span>{label}</span>
        <span className={`status-badge ${status.toLowerCase().replace(' ', '-')}`}>{status}</span>
      </div>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
      <div className="progress-pct">{value}%</div>
    </div>
  );
}

function CMMCBar({ domain, score }) {
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <div className="cmmc-bar-row">
      <div className="cmmc-domain-name">{domain}</div>
      <div className="cmmc-bar-track">
        <div className="cmmc-bar-fill" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <div className="cmmc-bar-score">{score}%</div>
    </div>
  );
}

function TrendSparkline({ data, dataKey, color, label, height = 80, domain, suffix, invertDelta }) {
  if (!data || data.length === 0) return null;
  var latest = data[data.length - 1] ? data[data.length - 1][dataKey] : null;
  var first = data[0] ? data[0][dataKey] : null;
  var delta = latest != null && first != null ? latest - first : null;
  var yDomain = domain || ['dataMin - 2', 'dataMax + 2'];
  var deltaIsGood = invertDelta ? delta < 0 : delta > 0;

  return (
    <div className="sparkline-container">
      <div className="sparkline-header">
        <div className="sparkline-label">{label}</div>
        <div className="sparkline-current">
          <span className="sparkline-value" style={{ color: color }}>{latest != null ? latest : '—'}{suffix || ''}</span>
          {delta != null && delta !== 0 && data.length > 1 && (
            <span className={'sparkline-delta ' + (deltaIsGood ? 'sparkline-delta-up' : 'sparkline-delta-down')}>
              {delta > 0 ? '▲' : '▼'} {Math.abs(delta)}{suffix || ''}
            </span>
          )}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: -12 }}>
          <XAxis dataKey="date" hide={true} />
          <YAxis
            domain={yDomain}
            width={40}
            tick={{ fontSize: 10, fill: '#546889', fontFamily: 'var(--font-mono, monospace)' }}
            tickLine={false}
            axisLine={false}
            tickCount={3}
          />
          <Tooltip
            contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, fontSize: 12 }}
            labelStyle={{ color: '#94a3b8' }}
            itemStyle={{ color: color }}
            formatter={function(value) { return [value + (suffix || ''), label]; }}
            labelFormatter={function(d) { return d; }}
          />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={data.length < 10} activeDot={{ r: 4, fill: color }} />
        </LineChart>
      </ResponsiveContainer>
      {data.length === 1 && <div className="sparkline-hint">Trends appear as more daily snapshots are recorded</div>}
    </div>
  );
}

// ── Capability Coverage ──────────────────────────────────────────────
// Replaces the legacy vendor-name stack with a capability-first view.
// Clients think in capabilities ("do I have endpoint protection?") not
// in vendor names ("do I have NinjaOne?"). The map below resolves each
// capability against whatever vendor(s) actually back it for the client
// — works seamlessly for mixed stacks (UniFi + Meraki), CMMC clients
// where Atomus Aegis substitutes for Huntress + Blumira, and clients
// who don't deploy every capability.
//
// Each capability returns:
//   state:  'active' | 'inactive'
//   vendors: [{ name, live, role }] — what's backing it (or nothing if inactive)
//   essential: true if a CBG-tier client should always have this; controls
//              whether 'inactive' shows as a gap or a soft skip
var CAPABILITIES = [
  {
    id: 'endpoint',
    label: 'Endpoint Protection',
    description: 'RMM-managed patching, configuration baseline, and AV coverage on managed endpoints.',
    essential: true,
    evaluate: function(c, live) {
      var vendors = [];
      if (live.ninjaone)     vendors.push({ name: 'NinjaOne (Commercial)', live: true, role: 'RMM' });
      if (live.ninjaone_gov) vendors.push({ name: 'NinjaOne Gov',          live: true, role: 'RMM (FedRAMP)' });
      // Defender is the AV baseline on every Microsoft tenant we manage.
      // Surfaced separately rather than implicit so clients see it explicitly.
      if (live.defender)     vendors.push({ name: 'Microsoft Defender',    live: true, role: 'Built-in AV' });
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'threat',
    label: 'Threat Detection (EDR)',
    description: 'Active monitoring for malicious behavior on endpoints, with response capability.',
    essential: true,
    evaluate: function(c, live) {
      // Defender for Endpoint is the EDR floor on every Microsoft-tenant
      // client (CMMC + commercial). Huntress is the layered 24/7 SOC for
      // commercial tier. Atomus Aegis is NOT EDR — it's compliance
      // evidence collection (lives under its own capability below).
      var vendors = [];
      if (live.defender) vendors.push({ name: 'Microsoft Defender for Endpoint', live: true, role: 'Baseline EDR' });
      if (live.huntress) vendors.push({ name: 'Huntress',                         live: true, role: '24/7 managed SOC' });
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'identity',
    label: 'Identity & Access',
    description: 'User accounts, MFA, conditional access, and lifecycle governance.',
    essential: true,
    evaluate: function(c, live) {
      var vendors = [];
      if (live.entra) vendors.push({ name: 'Microsoft Entra', live: true, role: 'Primary IdP' });
      // Future: live.google_workspace for clients with Google IdP
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'network',
    label: 'Network Monitoring',
    description: 'Visibility into gateways, switches, access points, and site-level health.',
    essential: false,
    evaluate: function(c, live) {
      var vendors = [];
      if (live.unifi)  vendors.push({ name: 'Ubiquiti UniFi', live: true, role: 'Network controller' });
      if (live.meraki) vendors.push({ name: 'Cisco Meraki',   live: true, role: 'Cloud-managed network' });
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'siem',
    label: 'SIEM / Audit Logging',
    description: 'Centralized log aggregation and security event correlation.',
    essential: true,
    evaluate: function(c, live) {
      // Tier split: Blumira for commercial, Microsoft Sentinel for CMMC
      // (wired through Atomus approval). Atomus Aegis is NOT a SIEM —
      // it's compliance evidence collection. Don't conflate.
      var vendors = [];
      if (live.blumira)  vendors.push({ name: 'Blumira',            live: true, role: 'Commercial SIEM' });
      if (live.sentinel) vendors.push({ name: 'Microsoft Sentinel', live: true, role: 'CMMC SIEM (via Atomus)' });
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'vault',
    label: 'Password Vault',
    description: 'Encrypted credential storage and secure sharing across teams.',
    essential: false,
    evaluate: function(c, live) {
      var vendors = [];
      if (live.keeper) vendors.push({ name: c.cmmc ? 'KeeperGov' : 'Keeper', live: true, role: 'FIPS-validated vault' });
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'compliance_evidence',
    label: 'Compliance Evidence Collection',
    description: 'Endpoint-level audit log collection and CMMC control evidence — separate from threat detection.',
    essential: false,
    onlyIf: function(c) { return !!c.cmmc; },           // CMMC-only — Aegis is the compliance agent, not an EDR
    evaluate: function(c, live) {
      return { state: live.atomus ? 'active' : 'inactive', vendors: live.atomus ? [{ name: 'Atomus Aegis', live: true, role: 'Endpoint compliance agent' }] : [] };
    }
  },
  {
    id: 'compliance',
    label: 'Compliance Posture',
    description: 'CMMC L1/L2 control evidence, SSP support, and continuous control monitoring.',
    essential: false,
    onlyIf: function(c) { return !!c.cmmc; },           // hidden for non-CMMC clients
    evaluate: function(c, live) {
      return { state: 'active', vendors: [{ name: 'Atomus + Aurelius', live: true, role: 'CMMC partnership' }] };
    }
  },
  {
    id: 'projects',
    label: 'Project Tracking',
    description: 'Active engagement tracking, milestones, and task progress.',
    essential: false,
    evaluate: function(c, live) {
      return { state: live.asana ? 'active' : 'inactive', vendors: live.asana ? [{ name: 'Asana', live: true, role: 'PM platform' }] : [] };
    }
  },
  {
    id: 'devops',
    label: 'DevOps Tracking',
    description: 'Engineering issue tracking and release coordination.',
    essential: false,
    evaluate: function(c, live) {
      return { state: live.jira ? 'active' : 'inactive', vendors: live.jira ? [{ name: 'Jira', live: true, role: 'Issue tracker' }] : [] };
    }
  },
  {
    id: 'spend',
    label: 'Cloud Spend Mgmt',
    description: 'Subscription cost tracking and license utilization across the SaaS stack.',
    essential: false,
    evaluate: function(c, live) {
      return { state: live.pax8 ? 'active' : 'inactive', vendors: live.pax8 ? [{ name: 'Pax8', live: true, role: 'CSP marketplace' }] : [] };
    }
  }
];

// Compute the rolled-up capability state for the current client.
function computeCoverage(c, live) {
  live = live || {};
  return CAPABILITIES.filter(function(cap) {
    return !cap.onlyIf || cap.onlyIf(c);
  }).map(function(cap) {
    var r = cap.evaluate(c, live);
    return Object.assign({}, cap, r);
  });
}

// Compact pill in the client banner. Click → drawer.
function CoverageBadge({ c, live, onClick }) {
  var caps = computeCoverage(c, live);
  var active = caps.filter(function(x) { return x.state === 'active'; }).length;
  var total = caps.length;
  var essentialGaps = caps.filter(function(x) { return x.essential && x.state === 'inactive'; }).length;
  var pct = total > 0 ? Math.round((active / total) * 100) : 0;
  var tone = essentialGaps > 0 ? 'gap' : pct === 100 ? 'full' : 'partial';
  return (
    <button type="button" className={'coverage-badge coverage-badge-' + tone} onClick={onClick} title="Click to see capability coverage">
      <span className="coverage-badge-grid" aria-hidden="true">
        {/* mini 3×3 dot grid that fills proportionally */}
        {[0,1,2,3,4,5,6,7,8].map(function(i) {
          var on = i < Math.round((active / Math.max(total, 1)) * 9);
          return <span key={i} className={'coverage-grid-dot' + (on ? ' on' : '')} />;
        })}
      </span>
      <span className="coverage-badge-text">
        <strong>{active}</strong>/{total} <span className="coverage-badge-sub">capabilities</span>
        {essentialGaps > 0 && <span className="coverage-badge-gap-flag">{essentialGaps} gap{essentialGaps === 1 ? '' : 's'}</span>}
      </span>
    </button>
  );
}

// Drawer overlay — shows the full capability matrix with vendor attribution.
function CoverageDrawer({ c, live, onClose }) {
  var caps = computeCoverage(c, live);
  var active = caps.filter(function(x) { return x.state === 'active'; }).length;
  return (
    <>
      <div className="coverage-drawer-overlay" onClick={onClose} />
      <aside className="coverage-drawer" role="dialog" aria-label="Service Coverage">
        <div className="coverage-drawer-head">
          <div>
            <div className="coverage-drawer-title">Service Coverage</div>
            <div className="coverage-drawer-sub">
              <strong>{active}</strong> of <strong>{caps.length}</strong> capabilities active for {c.name}
            </div>
          </div>
          <button type="button" className="coverage-drawer-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="coverage-drawer-body">
          {caps.map(function(cap) {
            var stateCls = 'coverage-cap-' + cap.state + (cap.essential ? ' coverage-cap-essential' : '');
            return (
              <div key={cap.id} className={'coverage-cap ' + stateCls}>
                <div className="coverage-cap-marker">
                  {cap.state === 'active'
                    ? <span className="coverage-cap-check">✓</span>
                    : cap.essential
                      ? <span className="coverage-cap-warn">⊘</span>
                      : <span className="coverage-cap-dash">—</span>}
                </div>
                <div className="coverage-cap-body">
                  <div className="coverage-cap-row">
                    <div className="coverage-cap-label">{cap.label}</div>
                    {cap.essential && cap.state === 'inactive' && <span className="coverage-cap-tag coverage-cap-tag-gap">Recommended — not deployed</span>}
                    {!cap.essential && cap.state === 'inactive' && <span className="coverage-cap-tag coverage-cap-tag-opt">Optional</span>}
                  </div>
                  <div className="coverage-cap-desc">{cap.description}</div>
                  {cap.vendors.length > 0 && (
                    <div className="coverage-cap-vendors">
                      {cap.vendors.map(function(v, i) {
                        return (
                          <span key={i} className="coverage-cap-vendor">
                            <span className="coverage-cap-vendor-dot" />
                            <span className="coverage-cap-vendor-name">{v.name}</span>
                            {v.role && <span className="coverage-cap-vendor-role">{v.role}</span>}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="coverage-drawer-foot">
          Vendor attribution shown for transparency. Talk to CloudByte Group about expanding coverage.
        </div>
      </aside>
    </>
  );
}

function computeTrend(history, dataKey) {
  if (!history || history.length < 2) return null;
  var latest = null, prev = null;
  for (var i = history.length - 1; i >= 0; i--) {
    if (history[i][dataKey] != null) {
      if (latest === null) latest = history[i][dataKey];
      else if (prev === null) { prev = history[i][dataKey]; break; }
    }
  }
  if (latest === null || prev === null) return null;
  if (latest > prev) return 'up';
  if (latest < prev) return 'down';
  return 'flat';
}

function exportTableCSV(headers, rows, filename) {
  var csv = headers.join(',') + '\n';
  rows.forEach(function(row) {
    csv += row.map(function(cell) {
      var s = String(cell == null ? '' : cell);
      if (s.indexOf(',') !== -1 || s.indexOf('"') !== -1 || s.indexOf('\n') !== -1) s = '"' + s.replace(/"/g, '""') + '"';
      return s;
    }).join(',') + '\n';
  });
  var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a');
  a.href = url; a.download = (filename || 'export') + '.csv';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function KPIStrip({ items }) {
  return (
    <div className="kpi-strip">
      {items.map(function(item, i) {
        var classes = 'kpi-strip-item';
        if (item.onClick) classes += ' kpi-strip-clickable';
        if (item.live) classes += ' kpi-strip-live';
        return (
          <div key={i} className={classes} onClick={item.onClick || undefined}>
            <div className="kpi-strip-label">{item.label}</div>
            <div className="kpi-strip-value" style={{ color: item.accent || 'var(--text-primary)' }}>
              {item.value}
              {item.trend && <span className={'trend-arrow ' + item.trend}>{item.trend === 'up' ? '↑' : item.trend === 'down' ? '↓' : '→'}</span>}
            </div>
            {item.subtitle && <div className="kpi-strip-subtitle">{item.subtitle}</div>}
          </div>
        );
      })}
    </div>
  );
}

function StatCardCompact({ title, value, subtitle, accent, live, onClick }) {
  return (
    <div className={'stat-card-compact' + (live ? ' stat-card-live' : '') + (onClick ? ' stat-card-clickable' : '')} onClick={onClick || undefined}>
      <div className="stat-title">
        {title}
        {live && <span className="live-badge">LIVE</span>}
        {onClick && <span className="stat-drill">Details ›</span>}
      </div>
      <div className="stat-value" style={{ color: accent || '#e8edf5' }}>{value}</div>
      {subtitle && <div className="stat-subtitle">{subtitle}</div>}
    </div>
  );
}

function CollapsibleSection({ title, defaultOpen, children, rightContent }) {
  var _state = useState(defaultOpen !== false);
  var isOpen = _state[0];
  var setIsOpen = _state[1];
  return (
    <>
      <div className="section-title-collapsible" onClick={function() { setIsOpen(!isOpen); }}>
        <span className={'section-chevron' + (isOpen ? '' : ' collapsed')}>&#9662;</span>
        {title}
        {rightContent && <span className="section-title-right">{rightContent}</span>}
      </div>
      <div className={'section-content' + (isOpen ? '' : ' collapsed')}>
        {children}
      </div>
    </>
  );
}

// ── v8 Components — Content Bands, Segment Bars, Metric Lists ───────────────

function SegmentBar({ segments, size, showLegend }) {
  var total = segments.reduce(function(s, seg) { return s + (seg.value || 0); }, 0);
  if (total === 0) return null;
  return (
    <div className="segment-bar-wrap">
      <div className={'segment-bar' + (size === 'lg' ? ' segment-bar-lg' : '')}>
        {segments.map(function(seg, i) {
          if (!seg.value) return null;
          var pct = (seg.value / total) * 100;
          return <div key={i} className="segment-bar-segment" style={{ width: pct + '%', background: seg.color }} title={seg.label + ': ' + seg.value + ' (' + Math.round(pct) + '%)'} />;
        })}
      </div>
      {showLegend !== false && (
        <div className="segment-bar-legend">
          {segments.map(function(seg, i) {
            if (!seg.value && !seg.showZero) return null;
            return (
              <div key={i} className={'segment-bar-legend-item' + (seg.onClick ? ' clickable' : '')} onClick={seg.onClick || undefined}>
                <span className="segment-bar-legend-dot" style={{ background: seg.color }} />
                <span className="segment-bar-legend-value">{seg.value}</span>
                <span className="segment-bar-legend-label">{seg.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ContentBand({ title, subtitle, accent, children, actions, rightContent }) {
  return (
    <div className={'content-band' + (accent ? ' content-band-accent' : '')}>
      {(title || actions || rightContent) && (
        <div className="content-band-header">
          <div>
            {title && <div className="content-band-title">{title}</div>}
            {subtitle && <div className="content-band-subtitle">{subtitle}</div>}
          </div>
          <div className="content-band-actions">
            {rightContent}
            {actions}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

function MetricList({ children }) {
  return <div className="metric-list">{children}</div>;
}

function MetricListItem({ icon, iconColor, label, sublabel, value, valueColor, badge, badgeColor, trail, onClick }) {
  return (
    <div className={'metric-list-item' + (onClick ? ' clickable' : '')} onClick={onClick || undefined}>
      {icon && <div className="metric-list-icon" style={{ background: (iconColor || 'var(--accent-blue)') + '15', color: iconColor || 'var(--accent-blue)' }}>{icon}</div>}
      <div className="metric-list-body">
        <div className="metric-list-label">{label}</div>
        {sublabel && <div className="metric-list-sublabel">{sublabel}</div>}
      </div>
      {badge && <span className="metric-list-badge" style={{ background: (badgeColor || 'var(--accent-blue)') + '18', color: badgeColor || 'var(--accent-blue)' }}>{badge}</span>}
      {value != null && <div className="metric-list-value" style={{ color: valueColor || 'var(--text-primary)' }}>{value}</div>}
      {trail && <div className="metric-list-trail">{trail}</div>}
      {onClick && <span className="metric-list-arrow">›</span>}
    </div>
  );
}

function MicroBar({ value, max, color, label }) {
  var pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="micro-bar">
      <div className="micro-bar-track">
        <div className="micro-bar-fill" style={{ width: pct + '%', background: color || 'var(--accent-blue)' }} />
      </div>
      <span className="micro-bar-label" style={{ color: color || 'var(--text-primary)' }}>{label || pct + '%'}</span>
    </div>
  );
}

function QuickStats({ items }) {
  return (
    <div className="quick-stats">
      {items.map(function(item, i) {
        return (
          <div key={i} className="quick-stat">
            <span className="quick-stat-value" style={{ color: item.color || 'var(--text-primary)' }}>{item.value}</span>
            <span className="quick-stat-label">{item.label}</span>
            {item.sub && <span className="quick-stat-sub">{item.sub}</span>}
          </div>
        );
      })}
    </div>
  );
}

function ChangelogPanel({ isOpen, onClose }) {
  var [releases, setReleases] = useState([]);
  var [loading, setLoading] = useState(true);
  useEffect(function() {
    if (!isOpen) return;
    var handler = function(e) { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return function() { window.removeEventListener('keydown', handler); };
  }, [isOpen, onClose]);
  useEffect(function() {
    if (!isOpen) return;
    authFetch(API + '/changelog').then(function(r) { return r.json(); }).then(function(data) {
      setReleases(data); setLoading(false);
      sessionStorage.setItem('aurelius_changelog_seen', data[0] ? data[0].version : '');
    }).catch(function() { setLoading(false); });
  }, [isOpen]);
  if (!isOpen) return null;
  var typeIcons = { feature: '✦', improvement: '↑', security: '◆', fix: '●' };
  var typeColors = { feature: 'var(--accent-blue, #5b9ef4)', improvement: '#22c55e', security: '#a78bfa', fix: '#f59e0b' };
  var typeLabels = { feature: 'New', improvement: 'Improved', security: 'Security', fix: 'Fix' };
  return (
    <>
      <div className="changelog-overlay" onClick={onClose} />
      <div className="changelog-panel">
        <div className="changelog-header">
          <div>
            <div className="changelog-title">What's New</div>
            <div className="changelog-subtitle">Release notes & updates</div>
          </div>
          <button className="changelog-close" onClick={onClose}>✕</button>
        </div>
        <div className="changelog-body">
          {loading ? <div className="changelog-loading">Loading...</div> :
            releases.map(function(release, ri) {
              return (
                <div key={ri} className="changelog-release">
                  <div className="changelog-release-meta">
                    <span className="changelog-version">v{release.version}</span>
                    <span className="changelog-date">{release.date}</span>
                  </div>
                  <div className="changelog-release-title">{release.title}</div>
                  <div className="changelog-entries">
                    {release.entries.map(function(entry, ei) {
                      return (
                        <div key={ei} className="changelog-entry">
                          <span className="changelog-entry-type" style={{ color: typeColors[entry.type] || '#8b9dc3', background: (typeColors[entry.type] || '#8b9dc3') + '18' }}>{typeLabels[entry.type] || entry.type}</span>
                          <span className="changelog-entry-text">{entry.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          }
        </div>
      </div>
    </>
  );
}

// ── Shared Table Utilities ───────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  var diff = Date.now() - new Date(dateStr).getTime();
  var mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return mins + 'm ago';
  var hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  var days = Math.floor(hrs / 24);
  if (days < 30) return days + 'd ago';
  var months = Math.floor(days / 30);
  return months + 'mo ago';
}

function sortRows(rows, field, dir, accessors) {
  if (!field || !accessors[field]) return rows;
  return rows.slice().sort(function(a, b) {
    var va = accessors[field](a);
    var vb = accessors[field](b);
    if (va == null || va === '') return 1;
    if (vb == null || vb === '') return -1;
    if (typeof va === 'number' && typeof vb === 'number') return dir === 'asc' ? va - vb : vb - va;
    if (typeof va === 'boolean') { va = va ? 1 : 0; vb = vb ? 1 : 0; return dir === 'asc' ? va - vb : vb - va; }
    va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
    return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  });
}

function SortTh({ field, current, dir, onSort, children }) {
  var active = current === field;
  return (
    <th onClick={function() { onSort(field); }}>
      {children}
      <span className={'sort-arrow' + (active ? ' active' : '')}>{active ? (dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}</span>
    </th>
  );
}

function useTableSort(defaultField, defaultDir) {
  var _s = useState(defaultField);
  var _d = useState(defaultDir || 'asc');
  function toggle(field) {
    if (_s[0] === field) _d[1](_d[0] === 'asc' ? 'desc' : 'asc');
    else { _s[1](field); _d[1]('asc'); }
  }
  return { sortField: _s[0], sortDir: _d[0], toggleSort: toggle };
}

function TableSearch({ value, onChange, count, total, noun, onExport }) {
  return (
    <div className="table-toolbar">
      <div className="table-search-wrap">
        <span className="table-search-icon">⌕</span>
        <input className="table-search" placeholder={'Search ' + (noun || 'rows') + '... (Esc to clear)'} value={value} onChange={function(e) { onChange(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Escape') { onChange(''); e.target.blur(); } }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {total > 0 && <span className="table-row-count">{count === total ? total + ' ' + (noun || 'rows') : count + ' of ' + total}</span>}
        {onExport && <button className="export-csv-btn" onClick={onExport} title="Export to CSV">⬇ CSV</button>}
      </div>
    </div>
  );
}

function filterRows(rows, query, fields) {
  if (!query || !query.trim()) return rows;
  var q = query.toLowerCase().trim();
  return rows.filter(function(row) {
    return fields.some(function(fn) {
      var val = fn(row);
      return val != null && String(val).toLowerCase().indexOf(q) !== -1;
    });
  });
}

function applyFilters(rows, active, accessors) {
  var keys = Object.keys(active);
  return rows.filter(function(row) {
    return keys.every(function(k) {
      if (active[k] == null) return true;
      var fn = accessors[k];
      if (!fn) return true;
      return String(fn(row)).toLowerCase() === String(active[k]).toLowerCase();
    });
  });
}

function TableFilters({ filters, active, onChange }) {
  var hasActive = Object.keys(active).some(function(k) { return active[k] != null; });
  return (
    <div className="table-filters">
      {filters.map(function(f) {
        return (
          <div key={f.key} className="table-filter-group">
            <span className="table-filter-label">{f.label}</span>
            {f.options.map(function(opt) {
              var isActive = active[f.key] === opt.value;
              return (
                <button key={opt.value} className={'table-filter-pill' + (isActive ? ' table-filter-active' : '')}
                  style={isActive && opt.color ? { borderColor: opt.color, color: opt.color, background: opt.color + '18' } : undefined}
                  onClick={function() { var next = Object.assign({}, active); next[f.key] = isActive ? null : opt.value; onChange(next); }}>
                  {opt.color && <span className="table-filter-dot" style={{ background: opt.color }} />}
                  {opt.label}
                  {opt.count != null && <span className="table-filter-count">{opt.count}</span>}
                </button>
              );
            })}
          </div>
        );
      })}
      {hasActive && <button className="table-filter-clear" onClick={function() { onChange({}); }}>✕ Clear</button>}
    </div>
  );
}

function Pagination({ page, totalPages, onPageChange, totalItems, startIndex, endIndex }) {
  if (totalPages <= 1) return null;
  return (
    <div className="table-pagination">
      <span className="table-pagination-info">Showing {startIndex + 1}–{endIndex} of {totalItems}</span>
      <div className="table-pagination-controls">
        <button className="table-pagination-btn" disabled={page === 0} onClick={function() { onPageChange(0); }}>«</button>
        <button className="table-pagination-btn" disabled={page === 0} onClick={function() { onPageChange(page - 1); }}>‹</button>
        <span className="table-pagination-current">{page + 1} / {totalPages}</span>
        <button className="table-pagination-btn" disabled={page >= totalPages - 1} onClick={function() { onPageChange(page + 1); }}>›</button>
        <button className="table-pagination-btn" disabled={page >= totalPages - 1} onClick={function() { onPageChange(totalPages - 1); }}>»</button>
      </div>
    </div>
  );
}

function TabDescription({ text }) {
  return <div className="tab-description">{text}</div>;
}

function HealthVerdict({ score, context }) {
  // score === null/undefined means "no data to score against" (e.g. no
  // integrations configured for this domain). Render a neutral
  // "informational" banner instead of a red Critical — alarmism on absence
  // of data is worse than helpful.
  var level;
  if (score == null) level = 'unconfigured';
  else if (score >= 90) level = 'excellent';
  else if (score >= 75) level = 'good';
  else if (score >= 50) level = 'attention';
  else level = 'critical';
  var labels = { excellent: 'Excellent', good: 'Good', attention: 'Needs Attention', critical: 'Critical', unconfigured: 'Not Yet Configured' };
  return (
    <div className={'health-verdict health-verdict-' + level}>
      <span className="health-verdict-dot" />
      <span className="health-verdict-label">{labels[level]}</span>
      <span className="health-verdict-context">{context}</span>
    </div>
  );
}

function DataFreshness({ live }) {
  return (
    <div className="data-freshness">
      {live && <span className="data-freshness-dot" />}
      {live ? 'Live data · Auto-refreshing every 60s' : 'Historical data · Updated daily at 11:00 AM ET'}
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="skeleton-wrap">
      <div className="skeleton-line w60 h24" />
      <div className="skeleton-line w40" />
      <div className="skeleton-kpi-row"><div className="skeleton-kpi" /><div className="skeleton-kpi" /><div className="skeleton-kpi" /><div className="skeleton-kpi" /></div>
      <div className="skeleton-grid"><div className="skeleton-card" /><div className="skeleton-card" /><div className="skeleton-card" /><div className="skeleton-card" /><div className="skeleton-card" /><div className="skeleton-card" /></div>
      <div className="skeleton-line w80" style={{ marginTop: 20 }} />
      <div className="skeleton-line w100 h40" />
    </div>
  );
}

function Breadcrumb({ items }) {
  return (
    <div className="breadcrumb">
      {items.map(function(item, i) {
        var isLast = i === items.length - 1;
        return isLast
          ? <span key={i} className="breadcrumb-current">{item.label}</span>
          : <><button key={i} className="breadcrumb-link" onClick={item.onClick}>{item.label}</button><span className="breadcrumb-sep">›</span></>;
      })}
    </div>
  );
}

function EmptyStateAction({ icon, title, text, cta }) {
  return (
    <div className="empty-state-action">
      {icon && <div className="empty-state-icon">{icon}</div>}
      <div className="empty-state-title">{title}</div>
      <div className="empty-state-text">{text}</div>
      {cta && <div className="empty-state-cta">{cta}</div>}
    </div>
  );
}

function OverviewTab({ c, live, clientId, onNavigate }) {
  const [history, setHistory] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Fetch trend history
  useEffect(function() {
    if (!clientId) return;
    authFetch(API + '/clients/' + clientId + '/history?days=30')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) {
        if (data && data.entries) {
          setHistory(data.entries.map(function(e) {
            return {
              date: e.date,
              overall: e.scores ? e.scores.overall : null,
              endpoint: e.scores ? e.scores.endpoint : null,
              threat: e.scores ? e.scores.threat : null,
              total: e.endpoints ? e.endpoints.total : null,
              healthy: e.endpoints ? e.endpoints.healthy : null,
              critical: e.endpoints ? e.endpoints.critical : null,
              activeThreats: e.threats ? e.threats.active : null,
              agents: e.huntress_agents ? e.huntress_agents.total : null,
            };
          }));
        }
      }).catch(function() {});
  }, [clientId]);

  // Fetch summaries from all areas
  useEffect(function() {
    if (!clientId) return;
    setSummaryLoading(true);
    Promise.all([
      authFetch(API + '/clients/' + clientId + '/network').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/tickets').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/projects').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/subscriptions').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/changes').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) {
      setSummaries({ network: results[0], tickets: results[1], projects: results[2], budget: results[3], changes: results[4] });
      setSummaryLoading(false);
    });
  }, [clientId]);

  var net = summaries.network && summaries.network.enabled ? summaries.network.summary : null;
  var tix = summaries.tickets && summaries.tickets.metrics ? summaries.tickets.metrics : null;
  var proj = summaries.projects && summaries.projects.projects ? summaries.projects.projects : null;
  var budget = summaries.budget && summaries.budget.enabled ? summaries.budget.summary : null;
  var changes = summaries.changes && summaries.changes.summary ? summaries.changes.summary : null;

  var projTotalTasks = proj ? proj.reduce(function(s, p) { return s + p.totalTasks; }, 0) : 0;
  var projCompleted = proj ? proj.reduce(function(s, p) { return s + p.completedTasks; }, 0) : 0;
  var projOverdue = proj ? proj.reduce(function(s, p) { return s + p.overdueTasks; }, 0) : 0;
  var projProgress = projTotalTasks > 0 ? Math.round((projCompleted / projTotalTasks) * 100) : 0;

  var siemSummary = c.live && c.live.blumira ? {
    total: (c.scores.siem === 95 ? 0 : c.scores.siem === 85 ? '< 5' : '5+'),
  } : null;

  return (
    <div className="tab-content">
      <TabDescription text="Executive summary of your security posture, operations, and project status. Scores are calculated from live endpoint health, threat activity, credential hygiene, and SIEM coverage." />
      <DataFreshness live={true} />
      <HealthVerdict score={c.scores.overall} context={
        c.scores.overall >= 90 ? 'All security domains are performing well. No immediate action required.' :
        c.scores.overall >= 75 ? 'Most areas are healthy, but ' + (c.scores.endpoint < 75 ? 'endpoint health' : c.scores.threat < 75 ? 'active threats' : c.scores.credential < 75 ? 'credential hygiene' : 'SIEM coverage') + ' needs attention.' :
        c.scores.overall >= 50 ? 'Multiple security areas need review. Recommend scheduling a remediation call.' :
        'Critical deficiencies detected across multiple domains. Immediate action recommended.'
      } />
      {/* Score Rings */}
      <div className="scores-row">
        <ScoreRing score={c.scores.overall} label="Overall" hero={true} />
        <ScoreRing score={c.scores.endpoint} label="Endpoint" />
        <ScoreRing score={c.scores.threat} label="Threat" />
        <ScoreRing score={c.scores.credential} label="Credential" />
        <ScoreRing score={c.scores.siem} label="SIEM" />
      </div>

      {/* 30-Day Trends */}
      {history.length > 0 && (
        <CollapsibleSection title="30-Day Trends" defaultOpen={true}>
          <div className="sparklines-grid">
            <TrendSparkline data={history} dataKey="overall" color="#60a5fa" label="Overall Score" domain={[0, 100]} suffix="%" />
            <TrendSparkline data={history} dataKey="endpoint" color="#22c55e" label="Endpoint Score" domain={[0, 100]} suffix="%" />
            <TrendSparkline data={history} dataKey="healthy" color="#22c55e" label="Healthy Endpoints" />
            <TrendSparkline data={history} dataKey="critical" color="#ef4444" label="Unreachable" invertDelta={true} />
            <TrendSparkline data={history} dataKey="activeThreats" color="#f59e0b" label="Active Threats" invertDelta={true} />
            <TrendSparkline data={history} dataKey="agents" color="#a78bfa" label="Huntress Agents" />
          </div>
        </CollapsibleSection>
      )}

      {/* Security at a Glance — KPI Strip */}
      <KPIStrip items={[
        { label: 'Total Endpoints', value: c.endpoints.total, trend: computeTrend(history, 'total'), subtitle: 'Devices under management', live: live.ninjaone, onClick: onNavigate ? function() { onNavigate('posture'); } : undefined },
        { label: 'Online Now', value: c.endpoints.online || 0, trend: computeTrend(history, 'healthy'), accent: '#22c55e', subtitle: c.endpoints.total > 0 ? Math.round(((c.endpoints.online || 0) / c.endpoints.total) * 100) + '% of fleet' : '', live: live.ninjaone },
        { label: 'Needs Attention', value: c.endpoints.stale || c.endpoints.critical || 0, trend: computeTrend(history, 'critical'), accent: (c.endpoints.stale || c.endpoints.critical || 0) > 0 ? '#ef4444' : '#22c55e', subtitle: (c.endpoints.stale || c.endpoints.critical || 0) > 0 ? 'Not seen in 7+ days — may need remediation' : 'All devices reporting normally', live: live.ninjaone, onClick: (c.endpoints.stale || c.endpoints.critical || 0) > 0 && onNavigate ? function() { onNavigate('posture'); } : undefined },
        { label: 'Active Threats', value: c.threats.active, trend: computeTrend(history, 'activeThreats'), accent: c.threats.active > 0 ? '#ef4444' : '#22c55e', subtitle: c.threats.active > 0 ? 'Requires immediate investigation' : 'No active threats detected', live: live.huntress, onClick: c.threats.active > 0 && onNavigate ? function() { onNavigate('security'); } : undefined },
      ].concat(c.huntress_agents ? [{ label: 'EDR Agents', value: c.huntress_agents.total, trend: computeTrend(history, 'agents'), subtitle: c.huntress_agents.online + ' active in last 24h (' + (c.huntress_agents.total > 0 ? Math.round((c.huntress_agents.online / c.huntress_agents.total) * 100) : 0) + '% coverage)', live: live.huntress }] : [])} />

      {/* Fleet Status — Segment Bar */}
      <ContentBand title="Fleet Distribution" subtitle={c.endpoints.total + ' managed devices'} rightContent={onNavigate ? <button className="tab-drill-link" onClick={function() { sessionStorage.setItem('aurelius_tab_entry', 'posture:devices'); onNavigate('posture'); }}>View All Devices ›</button> : null}>
        <SegmentBar size="lg" segments={[
          { label: 'Online', value: c.endpoints.online || 0, color: '#22c55e', onClick: onNavigate ? function() { onNavigate('posture'); } : undefined },
          { label: 'Off-Hours', value: c.endpoints.offHours || 0, color: '#64748b' },
          { label: 'Needs Attention', value: c.endpoints.stale || c.endpoints.critical || 0, color: '#ef4444', onClick: (c.endpoints.stale || c.endpoints.critical || 0) > 0 && onNavigate ? function() { onNavigate('posture'); } : undefined },
        ]} />
      </ContentBand>

      {/* Operations — Metric List */}
      <ContentBand title="Operations" subtitle="Infrastructure, support, and change management">
        {(net || tix || changes || summaryLoading) ? (
          <MetricList>
            {net && <MetricListItem icon="◉" iconColor="#22c55e" label="Network Infrastructure" sublabel={net.offline > 0 ? net.offline + ' currently powered off' : 'All devices active'} value={net.online + ' / ' + net.total} badge={net.firmware_outdated > 0 ? net.firmware_outdated + ' outdated firmware' : null} badgeColor={net.firmware_outdated > 0 ? '#f59e0b' : null} onClick={onNavigate ? function() { onNavigate('network'); } : undefined} />}
            {tix && <MetricListItem icon="◎" iconColor={tix.waitingForSupport > 0 ? '#f59e0b' : '#22c55e'} label="Support Tickets" sublabel={tix.waitingForSupport > 0 ? tix.waitingForSupport + ' waiting for support' : 'None waiting'} value={tix.open} trail={tix.open > 0 ? 'open' : null} onClick={onNavigate ? function() { onNavigate('support'); } : undefined} />}
            {changes && (function() { var changeTotal = changes.submitted + changes.under_review + changes.approved + changes.in_progress; return <MetricListItem icon="△" iconColor={changes.submitted > 0 ? '#f59e0b' : '#64748b'} label="Change Requests" sublabel={changes.submitted > 0 ? changes.submitted + ' awaiting review' : 'None pending'} value={changeTotal} onClick={onNavigate ? function() { onNavigate('changes'); } : undefined} />; })()}
            {summaryLoading && !net && !tix && !changes && <MetricListItem label="Loading operations data..." sublabel="Fetching from integrations" />}
          </MetricList>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>No operations data configured yet.</div>
        )}
      </ContentBand>

      {/* Projects & Budget — Metric List */}
      {(proj || budget) && (
        <ContentBand title="Projects & Budget" subtitle="Active work and spend tracking">
          <MetricList>
            {proj && proj.length > 0 && (
              <MetricListItem icon="◆" iconColor="#a78bfa" label={'Active Projects — ' + proj.length} sublabel={projOverdue > 0 ? projOverdue + ' overdue tasks' : 'On track'} value={<MicroBar value={projCompleted} max={projTotalTasks} color={projProgress >= 75 ? '#22c55e' : projProgress >= 50 ? '#f59e0b' : '#ef4444'} label={projProgress + '%'} />} onClick={onNavigate ? function() { onNavigate('projects'); } : undefined} />
            )}
            {budget && (
              <>
                <MetricListItem icon="$" iconColor="#5b9ef4" label="Estimated Monthly Spend" sublabel={budget.totalSubscriptions + ' active subscriptions'} value={'$' + Math.round(budget.totalMonthlyCost).toLocaleString()} valueColor="#5b9ef4" onClick={onNavigate ? function() { onNavigate('budget'); } : undefined} />
              </>
            )}
          </MetricList>
        </ContentBand>
      )}
    </div>
  );
}
// ── Identity Tab (Entra / Graph) ──────────────────────────────────
// ── Identity Components ───────────────────────────────────────────
function IdentityUserTiles({ summary, users }) {
  var u = summary.users;
  var disabledWithLicense = (users || []).filter(function(x) { return !x.enabled && x.licensed; }).length;
  var licensedPct = u.total > 0 ? Math.round((u.licensed / u.total) * 100) : 0;
  var guestPct = u.total > 0 ? Math.round((u.guests / u.total) * 100) : 0;
  return (
    <div className="metric-tiles">
      <div className="metric-tile metric-tile-good">
        <div className="metric-tile-label">Enabled</div>
        <div className="metric-tile-value">{u.enabled || 0}</div>
        <div className="metric-tile-context">Active identities</div>
      </div>
      <div className={'metric-tile' + (disabledWithLicense > 0 ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">Disabled</div>
        <div className="metric-tile-value">{u.disabled || 0}</div>
        <div className="metric-tile-context">{disabledWithLicense > 0 ? disabledWithLicense + ' still consuming licenses' : 'No license waste'}</div>
      </div>
      <div className={'metric-tile' + (guestPct > 20 ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">Guests</div>
        <div className="metric-tile-value">{u.guests || 0}</div>
        <div className="metric-tile-context">{guestPct}% of directory</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Licensed</div>
        <div className="metric-tile-value">{u.licensed || 0}</div>
        <div className="metric-tile-context">{licensedPct}% of directory</div>
      </div>
    </div>
  );
}

function IdentityDeviceTiles({ summary }) {
  var d = summary.devices;
  var compliantPct = d.total > 0 ? Math.round((d.compliant / d.total) * 100) : 0;
  var managedPct = d.total > 0 ? Math.round((d.managed / d.total) * 100) : 0;
  var nonCompliant = Math.max(0, (d.total || 0) - (d.compliant || 0));
  var osParts = [];
  if (d.windows) osParts.push(d.windows + ' Win');
  if (d.mac) osParts.push(d.mac + ' Mac');
  if (d.mobile) osParts.push(d.mobile + ' Mobile');
  return (
    <div className="metric-tiles">
      <div className={'metric-tile ' + (compliantPct >= 90 ? 'metric-tile-good' : compliantPct < 70 ? 'metric-tile-alert' : '')}>
        <div className="metric-tile-label">Compliant</div>
        <div className="metric-tile-value">{compliantPct}<span className="metric-tile-unit">%</span></div>
        <div className="metric-tile-context">{d.compliant || 0} of {d.total || 0} devices</div>
      </div>
      <div className={'metric-tile' + (nonCompliant > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
        <div className="metric-tile-label">Non-Compliant</div>
        <div className="metric-tile-value">{nonCompliant}</div>
        <div className="metric-tile-context">{nonCompliant > 0 ? 'May be blocked by conditional access' : 'All policies met'}</div>
      </div>
      <div className={'metric-tile' + (managedPct < 80 && d.total > 0 ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">Managed</div>
        <div className="metric-tile-value">{managedPct}<span className="metric-tile-unit">%</span></div>
        <div className="metric-tile-context">{d.managed || 0} enrolled in MDM</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Enrolled</div>
        <div className="metric-tile-value">{d.total || 0}</div>
        <div className="metric-tile-context">{osParts.length > 0 ? osParts.join(' · ') : 'No devices'}</div>
      </div>
    </div>
  );
}

function GapAnalysisTiles({ gapAnalysis, onNavigate }) {
  var entraOnly = gapAnalysis.entraOnly.length;
  var ninjaOnly = gapAnalysis.ninjaOnly.length;
  var matched = Math.max(0, Math.min(gapAnalysis.entraTotal, gapAnalysis.ninjaTotal) - Math.max(entraOnly, ninjaOnly));
  var tiles = [
    { key: 'matched', label: 'Matched', sublabel: 'In both Entra + NinjaOne', count: matched, alert: false, clickable: false },
    { key: 'entra', label: 'Entra Only', sublabel: entraOnly > 0 ? 'Missing RMM agent — deploy NinjaOne' : 'Full coverage', count: entraOnly, alert: entraOnly > 0, clickable: entraOnly > 0 },
    { key: 'ninja', label: 'NinjaOne Only', sublabel: ninjaOnly > 0 ? 'Missing Entra enrollment — check AAD join' : 'Full coverage', count: ninjaOnly, alert: ninjaOnly > 0, clickable: ninjaOnly > 0 }
  ];
  return (
    <div className="fleet-tiles">
      {tiles.map(function(t) {
        var isAlertState = t.alert && t.count > 0;
        var classes = 'ticket-flow-stage ticket-flow-stage-gap-' + t.key +
          (t.count === 0 ? ' ticket-flow-stage-empty' : '') +
          (t.clickable && onNavigate ? ' ticket-flow-stage-clickable' : '') +
          (isAlertState ? ' ticket-flow-stage-aging' : '');
        return (
          <div key={t.key} className={classes} onClick={t.clickable && onNavigate ? function() { onNavigate('gaps'); } : undefined}>
            <div className="ticket-flow-stage-label">{t.label}</div>
            <div className="ticket-flow-stage-count">{t.count}</div>
            <div className="ticket-flow-stage-sub">{t.sublabel}</div>
          </div>
        );
      })}
    </div>
  );
}

function IdentityTab({ clientId, live }) {
  // Parse landing-context hint (e.g. from Red Team drawer click on a named user or device)
  // Supported keys:
  //   identity:users:<name>     -> open users view, seed search with name
  //   identity:devices[:<name>] -> open devices view, optional search seed
  //   identity:intune           -> open intune view
  //   identity:gaps             -> open gaps view
  var identityHint = sessionStorage.getItem('aurelius_tab_entry');
  var initialView = 'summary';
  var initialUserSearch = '';
  var initialDeviceSearch = '';
  var hintBanner = null;
  if (identityHint && identityHint.indexOf('identity:') === 0) {
    var parts = identityHint.split(':');
    if (parts[1] === 'users') { initialView = 'users'; if (parts[2]) { initialUserSearch = parts[2]; hintBanner = { kind: 'user', name: parts[2] }; } }
    else if (parts[1] === 'devices') { initialView = 'devices'; if (parts[2]) { initialDeviceSearch = parts[2]; hintBanner = { kind: 'device', name: parts[2] }; } }
    else if (parts[1] === 'intune') { initialView = 'intune'; }
    else if (parts[1] === 'gaps') { initialView = 'gaps'; }
    sessionStorage.removeItem('aurelius_tab_entry');
  }
  const [entra, setEntra] = useState(null);
  const [posture, setPosture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(initialView);
  const [arrivalBanner, setArrivalBanner] = useState(hintBanner);
  var userSort = useTableSort('name', 'asc');
  var deviceSort = useTableSort('name', 'asc');
  var intuneSort = useTableSort('name', 'asc');
  var gapEntraSort = useTableSort('name', 'asc');
  var gapNinjaSort = useTableSort('name', 'asc');
  const [userSearch, setUserSearch] = useState(initialUserSearch);
  const [deviceSearch, setDeviceSearch] = useState(initialDeviceSearch);
  const [intuneSearch, setIntuneSearch] = useState('');
  const [userFilters, setUserFilters] = useState({});
  const [deviceFilters, setDeviceFilters] = useState({});
  const [intuneFilters, setIntuneFilters] = useState({});
  var [userPage, setUserPage] = useState(0);
  var [devicePage, setDevicePage] = useState(0);
  var [intunePage, setIntunePage] = useState(0);
  useEffect(function() {
    setLoading(true);
    setEntra(null);
    setPosture(null);
    Promise.all([
      authFetch(API + '/clients/' + clientId + '/entra').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/posture').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) {
      setEntra(results[0]);
      setPosture(results[1]);
      setLoading(false);
    });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!entra || !entra.summary) return <EmptyStateAction icon="🔐" title="Microsoft Entra Not Connected" text="Identity and device management requires Microsoft Entra (Azure AD) integration. Once connected, you'll see user accounts, device compliance, Intune enrollment, and gap analysis." cta="Contact CloudByte Group to enable" />;

  var s = entra.summary;
  var enabledPct = s.users.total > 0 ? Math.round((s.users.enabled / s.users.total) * 100) : 0;
  var managedPct = s.devices.total > 0 ? Math.round((s.devices.managed / s.devices.total) * 100) : 0;
  var compliantPct = s.devices.total > 0 ? Math.round((s.devices.compliant / s.devices.total) * 100) : 0;
  var encryptedPct = s.intune.total > 0 ? Math.round((s.intune.encrypted / s.intune.total) * 100) : 0;

  var gapAnalysis = null;
  if (posture && posture.devices && entra.devices) {
    var ninjaNames = posture.devices.map(function(d) { return (d.name || '').toLowerCase(); });
    var entraNames = entra.devices.map(function(d) { return (d.name || '').toLowerCase(); });
    var inEntraNotNinja = entra.devices.filter(function(d) {
      var name = (d.name || '').toLowerCase();
      return name && !ninjaNames.some(function(n) { return n === name || name.includes(n) || n.includes(name); });
    });
    var inNinjaNotEntra = posture.devices.filter(function(d) {
      var name = (d.name || '').toLowerCase();
      return name && !entraNames.some(function(n) { return n === name || name.includes(n) || n.includes(name); });
    });
    gapAnalysis = {
      entraOnly: inEntraNotNinja,
      ninjaOnly: inNinjaNotEntra,
      entraTotal: entra.devices.length,
      ninjaTotal: posture.devices.length,
    };
  }

  return (
    <div className="tab-content">
      {arrivalBanner && (
        <div className="arrival-banner">
          <span className="arrival-banner-badge">FROM RED TEAM</span>
          <span className="arrival-banner-text">
            {arrivalBanner.kind === 'user'
              ? <>You're investigating <strong>{arrivalBanner.name}</strong> — surfaced by a Red Team simulation. Filtered below.</>
              : <>You're investigating <strong>{arrivalBanner.name}</strong> — involved in a Red Team simulation. Filtered below.</>}
          </span>
          <button className="arrival-banner-close" onClick={function() { setArrivalBanner(null); }}>&#10005;</button>
        </div>
      )}
      {view === 'summary' && (
        <>
          <TabDescription text="User and device identity management across your Microsoft Entra tenant. Includes compliance status, device enrollment, and gap analysis between Entra and your RMM agent." />
          <DataFreshness live={true} />
          <ContentBand title="User Directory" subtitle={s.users.total + ' identities in Microsoft Entra'} rightContent={<><span className="live-badge">LIVE</span><button className="tab-drill-link" onClick={function() { setView('users'); }}>Users ›</button><button className="tab-drill-link" onClick={function() { setView('devices'); }}>Devices ›</button>{entra.managedDevices && entra.managedDevices.length > 0 && <button className="tab-drill-link" onClick={function() { setView('intune'); }}>Intune ›</button>}{gapAnalysis && (gapAnalysis.entraOnly.length > 0 || gapAnalysis.ninjaOnly.length > 0) && <button className="tab-drill-link" onClick={function() { setView('gaps'); }}>Gaps ›</button>}</>}>
            <IdentityUserTiles summary={s} users={entra.users} />
          </ContentBand>
          <ContentBand title="Device Compliance — Entra ID" subtitle={s.devices.total + ' enrolled devices'}>
            <IdentityDeviceTiles summary={s} />
          </ContentBand>
          <ContentBand title="Intune Management">
            <MetricList>
              <MetricListItem icon="◈" iconColor="#22c55e" label="Intune Managed Devices" sublabel={s.intune.total + ' enrolled'} value={<MicroBar value={encryptedPct} max={100} color={encryptedPct >= 90 ? '#22c55e' : encryptedPct >= 70 ? '#f59e0b' : '#ef4444'} label={encryptedPct + '% encrypted'} />} />
              <MetricListItem icon="◎" iconColor={s.intune.nonCompliant > 0 ? '#ef4444' : '#22c55e'} label="Compliance Status" sublabel={s.intune.compliant + ' compliant · ' + s.intune.nonCompliant + ' non-compliant'} value={s.intune.nonCompliant > 0 ? s.intune.nonCompliant : '✓'} valueColor={s.intune.nonCompliant > 0 ? '#ef4444' : '#22c55e'} />
            </MetricList>
          </ContentBand>
          {gapAnalysis && (
            <ContentBand title="Device Gap Analysis — Entra vs NinjaOne" subtitle="Systems should match — gaps mean missing coverage">
              <GapAnalysisTiles gapAnalysis={gapAnalysis} onNavigate={setView} />
            </ContentBand>
          )}
        </>
      )}
      {view === 'users' && (function() {
        var userAccessors = { name: function(u) { return u.name; }, email: function(u) { return u.email || ''; }, status: function(u) { return u.enabled ? 'Enabled' : 'Disabled'; }, licensed: function(u) { return u.licensed ? 'Yes' : 'No'; }, type: function(u) { return u.type; } };
        var searchedUsers = applyFilters(
          filterRows(entra.users, userSearch, [function(u) { return u.name; }, function(u) { return u.email || ''; }]),
          userFilters,
          { status: function(u){return u.enabled ? 'Enabled' : 'Disabled';}, type: function(u){return u.type;}, licensed: function(u){return u.licensed ? 'Yes' : 'No';} }
        );
        var sortedUsers = sortRows(searchedUsers, userSort.sortField, userSort.sortDir, userAccessors);
        var enabledCount = entra.users.filter(function(u){return u.enabled;}).length;
        var disabledCount = entra.users.length - enabledCount;
        var memberCount = entra.users.filter(function(u){return u.type==='Member';}).length;
        var guestCount = entra.users.filter(function(u){return u.type==='Guest';}).length;
        var uPageSize = 25;
        var uTotalPages = Math.max(1, Math.ceil(sortedUsers.length / uPageSize));
        var uSafePage = Math.min(userPage, uTotalPages - 1);
        var pagedUsers = sortedUsers.slice(uSafePage * uPageSize, (uSafePage + 1) * uPageSize);
        return (
        <>
          <div className="section-title">
            All Users ({entra.users.length})
            <button className="insights-btn" style={{ marginLeft: 12, verticalAlign: 'middle' }} onClick={function() { setView('summary'); }}>Back</button>
          </div>
          <TableSearch value={userSearch} onChange={function(v) { setUserSearch(v); setUserPage(0); }} count={sortedUsers.length} total={entra.users.length} noun="users" onExport={function() { exportTableCSV(['Name','Email','Status','Licensed','Type'], sortedUsers.map(function(u) { return [u.name, u.email, u.enabled ? 'Enabled' : 'Disabled', u.licensed ? 'Yes' : 'No', u.type]; }), 'entra-users'); }} />
          <TableFilters filters={[
            { key: 'status', label: 'Status', options: [
              { value: 'Enabled', label: 'Enabled', color: '#22c55e', count: enabledCount },
              { value: 'Disabled', label: 'Disabled', color: '#64748b', count: disabledCount },
            ]},
            { key: 'type', label: 'Type', options: [
              { value: 'Member', label: 'Member', count: memberCount },
              { value: 'Guest', label: 'Guest', count: guestCount },
            ]},
            { key: 'licensed', label: 'Licensed', options: [
              { value: 'Yes', label: 'Licensed', color: '#22c55e' },
              { value: 'No', label: 'Unlicensed', color: '#64748b' },
            ]},
          ]} active={userFilters} onChange={function(f) { setUserFilters(f); setUserPage(0); }} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="name" current={userSort.sortField} dir={userSort.sortDir} onSort={userSort.toggleSort}>Name</SortTh><SortTh field="email" current={userSort.sortField} dir={userSort.sortDir} onSort={userSort.toggleSort}>Email</SortTh><SortTh field="status" current={userSort.sortField} dir={userSort.sortDir} onSort={userSort.toggleSort}>Status</SortTh><SortTh field="licensed" current={userSort.sortField} dir={userSort.sortDir} onSort={userSort.toggleSort}>Licensed</SortTh><SortTh field="type" current={userSort.sortField} dir={userSort.sortDir} onSort={userSort.toggleSort}>Type</SortTh></tr></thead>
              <tbody>
                {pagedUsers.length === 0 && <tr><td colSpan="5" className="table-no-results">No users match your search</td></tr>}
                {pagedUsers.map(function(u, i) {
                  return (
                    <tr key={i} className={!u.enabled ? 'posture-row-offline' : ''}>
                      <td className="posture-device-name" title={u.name}><span className={'status-dot ' + (u.enabled ? 'status-dot-good' : 'status-dot-muted')} />{u.name}</td>
                      <td title={u.email || ''}>{u.email || '—'}</td>
                      <td><span className={'posture-status ' + (u.enabled ? 'posture-online' : 'posture-offline')}>{u.enabled ? 'Enabled' : 'Disabled'}</span></td>
                      <td>{u.licensed ? 'Yes' : 'No'}</td>
                      <td>{u.type}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={uSafePage} totalPages={uTotalPages} onPageChange={setUserPage} totalItems={sortedUsers.length} startIndex={uSafePage * uPageSize} endIndex={Math.min((uSafePage + 1) * uPageSize, sortedUsers.length)} />
        </>
      );
      })()}
      {view === 'devices' && (function() {
        var da = { name: function(d) { return d.name; }, registeredOwner: function(d) { return d.registeredOwner || ''; }, os: function(d) { return d.os || ''; }, compliant: function(d) { return d.compliant === true ? 'Yes' : 'No'; }, trustType: function(d) { return d.trustType || ''; }, lastSignIn: function(d) { return d.lastSignIn ? new Date(d.lastSignIn).getTime() : 0; } };
        var searched = applyFilters(
          filterRows(entra.devices, deviceSearch, [function(d) { return d.name; }, function(d) { return d.os; }, function(d) { return d.registeredOwner; }]),
          deviceFilters,
          { os: function(d){return (d.os||'').split(' ')[0];}, compliant: function(d){return d.compliant===true?'Yes':d.compliant===false?'No':'Unknown';} }
        );
        var sorted = sortRows(searched, deviceSort.sortField, deviceSort.sortDir, da);
        var osGroups = {}; entra.devices.forEach(function(d){var o=(d.os||'Unknown').split(' ')[0]; osGroups[o]=(osGroups[o]||0)+1;});
        var dPageSize = 25;
        var dTotalPages = Math.max(1, Math.ceil(sorted.length / dPageSize));
        var dSafePage = Math.min(devicePage, dTotalPages - 1);
        var pagedDevices = sorted.slice(dSafePage * dPageSize, (dSafePage + 1) * dPageSize);
        return (
        <>
          <div className="section-title">
            Entra Devices ({entra.devices.length})
            <button className="insights-btn" style={{ marginLeft: 12, verticalAlign: 'middle' }} onClick={function() { setView('summary'); }}>Back</button>
          </div>
          <TableSearch value={deviceSearch} onChange={function(v) { setDeviceSearch(v); setDevicePage(0); }} count={sorted.length} total={entra.devices.length} noun="devices" onExport={function() { exportTableCSV(['Device','User','OS','Version','Compliant','Managed','Trust Type','Last Sign-In'], sorted.map(function(d) { return [d.name, d.registeredOwner || '', d.os || '', d.osVersion || '', d.compliant === true ? 'Yes' : d.compliant === false ? 'No' : '', d.managed === true ? 'Yes' : d.managed === false ? 'No' : '', d.trustType || '', d.lastSignIn ? new Date(d.lastSignIn).toLocaleDateString() : '']; }), 'entra-devices'); }} />
          <TableFilters filters={[
            { key: 'os', label: 'OS', options: Object.entries(osGroups).sort(function(a,b){return b[1]-a[1];}).map(function([o,c]) { return { value: o, label: o, count: c }; }) },
            { key: 'compliant', label: 'Compliance', options: [
              { value: 'Yes', label: 'Compliant', color: '#22c55e' },
              { value: 'No', label: 'Non-Compliant', color: '#ef4444' },
            ]},
          ]} active={deviceFilters} onChange={function(f) { setDeviceFilters(f); setDevicePage(0); }} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="name" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>Device</SortTh><SortTh field="registeredOwner" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>User</SortTh><SortTh field="os" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>OS</SortTh><th>Version</th><SortTh field="compliant" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>Compliant</SortTh><th>Managed</th><SortTh field="trustType" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>Trust Type</SortTh><SortTh field="lastSignIn" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>Last Sign-In</SortTh></tr></thead>
              <tbody>
                {pagedDevices.length === 0 && <tr><td colSpan="8" className="table-no-results">No devices match your search</td></tr>}
                {pagedDevices.map(function(d, i) {
                  var lastSignIn = d.lastSignIn ? new Date(d.lastSignIn).toLocaleDateString() : '—';
                  return (
                    <tr key={i}>
                      <td className="posture-device-name" title={d.name}>{d.name}</td>
                      <td title={d.registeredOwner || ''}>{d.registeredOwner ? d.registeredOwner.split('@')[0] : '—'}</td>
                      <td>{d.os || '—'}</td>
                      <td>{d.osVersion || '—'}</td>
                      <td><span className={'posture-av-state posture-av-' + (d.compliant === true ? 'on' : d.compliant === false ? 'off' : 'unknown')}>{d.compliant === true ? 'Yes' : d.compliant === false ? 'No' : '—'}</span></td>
                      <td>{d.managed === true ? 'Yes' : d.managed === false ? 'No' : '—'}</td>
                      <td>{d.trustType || '—'}</td>
                      <td className="posture-date">{lastSignIn}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={dSafePage} totalPages={dTotalPages} onPageChange={setDevicePage} totalItems={sorted.length} startIndex={dSafePage * dPageSize} endIndex={Math.min((dSafePage + 1) * dPageSize, sorted.length)} />
        </>
      );
      })()}
      {view === 'intune' && entra.managedDevices && (function() {
        var ia = { name: function(d) { return d.name; }, os: function(d) { return d.os || ''; }, compliance: function(d) { return d.compliance || ''; }, encrypted: function(d) { return d.encrypted === true ? 'Yes' : 'No'; }, user: function(d) { return d.user || ''; }, model: function(d) { return d.model || ''; }, lastSync: function(d) { return d.lastSync ? new Date(d.lastSync).getTime() : 0; } };
        var searched = filterRows(entra.managedDevices, intuneSearch, [function(d) { return d.name; }, function(d) { return d.user; }, function(d) { return d.model; }]);
        var sorted = sortRows(searched, intuneSort.sortField, intuneSort.sortDir, ia);
        var iPageSize = 25;
        var iTotalPages = Math.max(1, Math.ceil(sorted.length / iPageSize));
        var iSafePage = Math.min(intunePage, iTotalPages - 1);
        var pagedIntune = sorted.slice(iSafePage * iPageSize, (iSafePage + 1) * iPageSize);
        return (
        <>
          <div className="section-title">
            Intune Managed Devices ({entra.managedDevices.length})
            <button className="insights-btn" style={{ marginLeft: 12, verticalAlign: 'middle' }} onClick={function() { setView('summary'); }}>Back</button>
          </div>
          <TableSearch value={intuneSearch} onChange={function(v) { setIntuneSearch(v); setIntunePage(0); }} count={sorted.length} total={entra.managedDevices.length} noun="devices" onExport={function() { exportTableCSV(['Device','OS','Compliance','Encrypted','User','Model','Last Sync'], sorted.map(function(d) { return [d.name, d.os || '', d.compliance || '', d.encrypted === true ? 'Yes' : d.encrypted === false ? 'No' : '', d.user || '', d.model || '', d.lastSync ? new Date(d.lastSync).toLocaleDateString() : '']; }), 'intune-devices'); }} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="name" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>Device</SortTh><SortTh field="os" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>OS</SortTh><SortTh field="compliance" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>Compliance</SortTh><SortTh field="encrypted" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>Encrypted</SortTh><SortTh field="user" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>User</SortTh><SortTh field="model" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>Model</SortTh><SortTh field="lastSync" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>Last Sync</SortTh></tr></thead>
              <tbody>
                {pagedIntune.length === 0 && <tr><td colSpan="7" className="table-no-results">No devices match your search</td></tr>}
                {pagedIntune.map(function(d, i) {
                  var lastSync = d.lastSync ? new Date(d.lastSync).toLocaleDateString() : '—';
                  return (
                    <tr key={i}>
                      <td className="posture-device-name" title={d.name}>{d.name}</td>
                      <td>{d.os || '—'}</td>
                      <td><span className={'posture-av-state posture-av-' + (d.compliance === 'compliant' ? 'on' : d.compliance === 'noncompliant' ? 'off' : 'unknown')}>{d.compliance || '—'}</span></td>
                      <td><span className={'posture-av-state posture-av-' + (d.encrypted === true ? 'on' : d.encrypted === false ? 'off' : 'unknown')}>{d.encrypted === true ? 'Yes' : d.encrypted === false ? 'No' : '—'}</span></td>
                      <td title={d.user || ''}>{d.user || '—'}</td>
                      <td>{d.model || '—'}</td>
                      <td className="posture-date">{lastSync}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={iSafePage} totalPages={iTotalPages} onPageChange={setIntunePage} totalItems={sorted.length} startIndex={iSafePage * iPageSize} endIndex={Math.min((iSafePage + 1) * iPageSize, sorted.length)} />
        </>
      );
      })()}
      {view === 'gaps' && gapAnalysis && (
        <>
          <div className="section-title">
            Device Gap Analysis
            <button className="insights-btn" style={{ marginLeft: 12, verticalAlign: 'middle' }} onClick={function() { setView('summary'); }}>Back</button>
          </div>
          {gapAnalysis.entraOnly.length > 0 && (
            <>
              <div className="section-title">In Entra but NOT in NinjaOne ({gapAnalysis.entraOnly.length})</div>
              <p className="gap-description">These devices are registered in Microsoft Entra but have no NinjaOne RMM agent.</p>
              <div className="posture-table-wrap">
                <table className="posture-table">
                  <thead><tr><SortTh field="name" current={gapEntraSort.sortField} dir={gapEntraSort.sortDir} onSort={gapEntraSort.toggleSort}>Device</SortTh><th>User</th><SortTh field="os" current={gapEntraSort.sortField} dir={gapEntraSort.sortDir} onSort={gapEntraSort.toggleSort}>OS</SortTh><th>Managed</th><th>Compliant</th><SortTh field="trustType" current={gapEntraSort.sortField} dir={gapEntraSort.sortDir} onSort={gapEntraSort.toggleSort}>Trust Type</SortTh><th>Last Sign-In</th></tr></thead>
                  <tbody>
                    {sortRows(gapAnalysis.entraOnly, gapEntraSort.sortField, gapEntraSort.sortDir, { name: function(d) { return d.name; }, os: function(d) { return d.os || ''; }, trustType: function(d) { return d.trustType || ''; } }).map(function(d, i) {
                      return (
                        <tr key={i}>
                          <td className="posture-device-name">{d.name}</td>
                          <td title={d.registeredOwner || ''}>{d.registeredOwner ? d.registeredOwner.split('@')[0] : '—'}</td>
                          <td>{d.os || '—'}</td>
                          <td>{d.managed === true ? 'Yes' : 'No'}</td>
                          <td>{d.compliant === true ? 'Yes' : 'No'}</td>
                          <td style={{ fontSize: 12 }}>{d.trustType || '—'}</td>
                          <td className="posture-date">{d.lastSignIn ? new Date(d.lastSignIn).toLocaleDateString() : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {gapAnalysis.ninjaOnly.length > 0 && (
            <>
              <div className="section-title">In NinjaOne but NOT in Entra ({gapAnalysis.ninjaOnly.length})</div>
              <p className="gap-description">These devices have the NinjaOne RMM agent but are not registered in Microsoft Entra.</p>
              <div className="posture-table-wrap">
                <table className="posture-table">
                  <thead><tr><SortTh field="name" current={gapNinjaSort.sortField} dir={gapNinjaSort.sortDir} onSort={gapNinjaSort.toggleSort}>Device</SortTh><SortTh field="type" current={gapNinjaSort.sortField} dir={gapNinjaSort.sortDir} onSort={gapNinjaSort.toggleSort}>Type</SortTh><SortTh field="status" current={gapNinjaSort.sortField} dir={gapNinjaSort.sortDir} onSort={gapNinjaSort.toggleSort}>Status</SortTh><th>Antivirus</th><th>Last Contact</th></tr></thead>
                  <tbody>
                    {sortRows(gapAnalysis.ninjaOnly, gapNinjaSort.sortField, gapNinjaSort.sortDir, { name: function(d) { return d.name; }, type: function(d) { return d.nodeClass || ''; }, status: function(d) { return d.offline ? 'Offline' : 'Online'; } }).map(function(d, i) {
                      return (
                        <tr key={i}>
                          <td className="posture-device-name">{d.name}</td>
                          <td>{(d.nodeClass || '').replace('WINDOWS_', '').replace('_', ' ')}</td>
                          <td><span className={'posture-status ' + (d.offline ? 'posture-offline' : 'posture-online')}>{d.offline ? 'Offline' : 'Online'}</span></td>
                          <td>{d.av ? d.av.product : 'None'}</td>
                          <td className="posture-date">{d.lastContact ? new Date(d.lastContact * 1000).toLocaleDateString() : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {gapAnalysis.entraOnly.length === 0 && gapAnalysis.ninjaOnly.length === 0 && (
            <div className="empty-state">No device gaps detected.</div>
          )}
        </>
      )}
    </div>
  );
}

// ── Keeper Tab (dedicated credential management view) ────────────
function KeeperTab({ clientId }) {
  return (
    <div className="tab-content">
      <TabDescription text="Credential and secret management via Keeper. Tracks vault enrollment, password hygiene, shared folder governance, and enforcement policies across every user and team." />
      <DataFreshness live={true} />
      <KeeperVaultSection clientId={clientId} />
    </div>
  );
}

// ── Red Team Tab (continuous adversarial simulation) ──────────────
function RedTeamTab({ clientId, onNavigate }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [detailId, setDetailId] = useState(null);
  useEffect(function() {
    setLoading(true); setData(null);
    authFetch(API + '/clients/' + clientId + '/redteam')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  var openDetail = function(attackId) { setDetailId(attackId); };
  var closeDetail = function() { setDetailId(null); };

  // Convert a Red Team recommendation into a pre-filled Change Request
  var convertToChangeRequest = function(rec, sourceFinding) {
    var prefill = rec.change_request_prefill;
    if (!prefill) return;
    sessionStorage.setItem('aurelius_change_prefill', JSON.stringify({
      title: prefill.title,
      description: prefill.description,
      type: prefill.type,
      category: prefill.category,
      priority: prefill.priority,
      reference: 'Red Team finding ' + (sourceFinding || rec.id),
      impact_summary: prefill.impact_summary,
      source: 'redteam',
      source_id: sourceFinding || rec.id
    }));
    if (onNavigate) onNavigate('changes');
  };

  // Navigate to a user in Identity tab with the user name in search
  var goToUser = function(userName) {
    sessionStorage.setItem('aurelius_tab_entry', 'identity:users:' + userName);
    if (onNavigate) onNavigate('identity');
  };

  // Navigate to a device in Posture tab with the device name in search
  var goToDevice = function(deviceName) {
    sessionStorage.setItem('aurelius_tab_entry', 'posture:devices:' + deviceName);
    if (onNavigate) onNavigate('posture');
  };

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!data || !data.enabled) {
    return <div className="tab-content"><EmptyStateAction icon="⚔" title="Red Team Not Activated" text="Aurelius Red Team runs continuous autonomous adversarial simulations against your environment — phishing, credential attacks, lateral movement, data exfiltration, and supply-chain compromise. Shows you exactly what almost got through, every day." cta="Contact CloudByte Group to activate" /></div>;
  }

  var s = data.summary;
  var br = data.breach_rehearsal;
  var crit = data.critical_event;
  function timeAgoLabel(iso) { var diff = Date.now() - new Date(iso).getTime(); var h = Math.floor(diff / 3600000); if (h < 1) return Math.floor(diff / 60000) + 'm ago'; if (h < 24) return h + 'h ago'; return Math.floor(h / 24) + 'd ago'; }
  function timeUntilLabel(iso) { var diff = new Date(iso).getTime() - Date.now(); var h = Math.floor(diff / 3600000); var m = Math.floor((diff % 3600000) / 60000); if (h < 0) return 'running now'; return h + 'h ' + m + 'm'; }
  var outcomeStyle = { blocked: { color: '#22c55e', label: 'Blocked' }, partial: { color: '#f59e0b', label: 'Partial' }, successful: { color: '#ef4444', label: 'Got Through' } };
  var stageStyle = { succeeded: { color: '#ef4444', label: 'Succeeded', dot: '●' }, detected: { color: '#f59e0b', label: 'Detected', dot: '◐' }, blocked: { color: '#22c55e', label: 'Blocked', dot: '■' }, simulated: { color: '#a78bfa', label: 'Simulated', dot: '◇' } };
  var priorityStyle = { high: { color: '#ef4444', label: 'High Priority' }, medium: { color: '#f59e0b', label: 'Medium' }, low: { color: '#64748b', label: 'Low' } };

  return (
    <div className="tab-content">
      <TabDescription text="Aurelius Red Team runs continuous autonomous adversarial simulations against your environment — phishing, credential attacks, lateral movement, data exfiltration, and supply-chain compromise. Every morning: here's exactly what almost got through." />
      <DataFreshness live={true} />

      {/* Hero — Last Run */}
      <ContentBand title="Last Night's Rehearsal" subtitle={'Completed ' + timeAgoLabel(data.last_run) + ' · Next run in ' + timeUntilLabel(data.next_run)} rightContent={<span className="live-badge red-team-live">● ACTIVE</span>}>
        <div className="metric-tiles">
          <div className="metric-tile metric-tile-good">
            <div className="metric-tile-label">Attacks Blocked</div>
            <div className="metric-tile-value">{s.blocked}<span className="metric-tile-unit"> / {s.total_attacks_24h}</span></div>
            <div className="metric-tile-context">Stopped at earliest kill-chain stage</div>
          </div>
          <div className={'metric-tile' + (s.partial > 0 ? ' metric-tile-alert' : '')}>
            <div className="metric-tile-label">Partial Success</div>
            <div className="metric-tile-value">{s.partial}</div>
            <div className="metric-tile-context">Progressed further than expected</div>
          </div>
          <div className={'metric-tile' + (s.successful > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
            <div className="metric-tile-label">Got Through</div>
            <div className="metric-tile-value">{s.successful}</div>
            <div className="metric-tile-context">{s.successful > 0 ? 'See detailed breakdown below' : 'Full chain defended'}</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">Attack Surface Score</div>
            <div className="metric-tile-value">{s.attack_surface_score}<span className="metric-tile-unit">/100</span></div>
            <div className="metric-tile-context">{s.total_simulations_30d} simulations in last 30 days</div>
          </div>
        </div>
      </ContentBand>

      {/* Breach Rehearsal Score */}
      <ContentBand title="Breach Rehearsal Score" subtitle="Estimated wall-clock time for a sophisticated attacker to reach full compromise">
        <div className="redteam-rehearsal">
          <div className="redteam-rehearsal-hero">
            <div className="redteam-rehearsal-value">{br.estimated_time_to_compromise_hours}<span className="redteam-rehearsal-unit">h</span></div>
            <div className="redteam-rehearsal-label">Time to full compromise</div>
            <div className="redteam-rehearsal-trend">
              <span className="redteam-trend-icon redteam-trend-down">↓</span>
              <span>{Math.abs(br.delta_pct)}% harder than last quarter ({br.previous_quarter_hours}h)</span>
            </div>
          </div>
          <div className="redteam-rehearsal-benchmark">
            <div className="redteam-benchmark-row">
              <span className="redteam-benchmark-label">You</span>
              <div className="redteam-benchmark-bar"><div className="redteam-benchmark-bar-fill redteam-benchmark-you" style={{ width: Math.min(100, (br.estimated_time_to_compromise_hours / 6) * 100) + '%' }} /></div>
              <span className="redteam-benchmark-value">{br.estimated_time_to_compromise_hours}h</span>
            </div>
            <div className="redteam-benchmark-row">
              <span className="redteam-benchmark-label">Peer Median</span>
              <div className="redteam-benchmark-bar"><div className="redteam-benchmark-bar-fill redteam-benchmark-peer" style={{ width: Math.min(100, (br.peer_benchmark_hours / 6) * 100) + '%' }} /></div>
              <span className="redteam-benchmark-value">{br.peer_benchmark_hours}h</span>
            </div>
            <div className="redteam-benchmark-note">You're in the <strong>{br.peer_quartile} quartile</strong> for tech-services firms 50-100 employees · Longer is better (harder to compromise)</div>
          </div>
        </div>
      </ContentBand>

      {/* Critical Event — What Got Through */}
      <ContentBand title="What Almost Got Through" subtitle="Click for the full breakdown — plain English, step-by-step, with named users and remediation scripts">
        <div className="redteam-critical redteam-critical-clickable" onClick={function() { openDetail(crit.id); }}>
          <div className="redteam-critical-header">
            <div>
              <div className="redteam-critical-type">{crit.type_label}</div>
              <div className="redteam-critical-meta">Target: <button className="redteam-entity-link" onClick={function(e) { e.stopPropagation(); goToUser(crit.target_user); }}>{crit.target_user}</button> ({crit.target_role}) · Device: <button className="redteam-entity-link" onClick={function(e) { e.stopPropagation(); goToDevice(crit.target_device); }}>{crit.target_device}</button> · {timeAgoLabel(crit.time)}</div>
            </div>
            <span className="redteam-critical-outcome" style={{ background: outcomeStyle[crit.outcome].color + '18', color: outcomeStyle[crit.outcome].color, borderColor: outcomeStyle[crit.outcome].color + '40' }}>{outcomeStyle[crit.outcome].label}</span>
          </div>
          <div className="redteam-critical-lure">
            <span className="redteam-critical-lure-label">What happened</span>
            <span>{crit.lure}</span>
          </div>
          <div className="redteam-critical-narrative">{crit.narrative}</div>
          <div className="redteam-critical-cta">
            <span>Click for full breakdown, named users, step-by-step, and copy-paste remediation scripts</span>
            <span className="redteam-critical-cta-arrow">→</span>
          </div>

          {crit.training_status && (
            <div className="redteam-training-box">
              <div className="redteam-training-header">
                <span className="redteam-training-badge">Huntress MSAT</span>
                <span className="redteam-training-title">Affected user's training record</span>
              </div>
              <div className="redteam-training-grid">
                <div className="redteam-training-cell">
                  <div className="redteam-training-cell-label">Last training</div>
                  <div className="redteam-training-cell-value">{crit.training_status.last_training}</div>
                  <div className="redteam-training-cell-sub">Completed {Math.round((Date.now() - new Date(crit.training_status.last_training_date).getTime()) / 86400000)}d ago · Score {crit.training_status.last_training_score}%</div>
                </div>
                <div className="redteam-training-cell">
                  <div className="redteam-training-cell-label">Last phishing simulation</div>
                  <div className="redteam-training-cell-value redteam-training-cell-warn">{crit.training_status.last_phishing_sim_result}</div>
                  <div className="redteam-training-cell-sub">{Math.round((Date.now() - new Date(crit.training_status.last_phishing_sim_date).getTime()) / 86400000)}d ago</div>
                </div>
                <div className="redteam-training-cell">
                  <div className="redteam-training-cell-label">Overall awareness score</div>
                  <div className={'redteam-training-cell-value ' + (crit.training_status.overall_score >= 80 ? 'redteam-training-cell-good' : crit.training_status.overall_score >= 60 ? 'redteam-training-cell-warn' : 'redteam-training-cell-alert')}>{crit.training_status.overall_score}<span className="redteam-training-cell-unit">/100</span></div>
                  <div className="redteam-training-cell-sub">{crit.training_status.overall_percentile}th percentile in organization</div>
                </div>
              </div>
            </div>
          )}

          <div className="redteam-killchain-label">Kill Chain</div>
          <div className="redteam-killchain">
            {crit.kill_chain.map(function(stage, i) {
              var style = stageStyle[stage.status] || stageStyle.simulated;
              return (
                <div key={i} className="redteam-killchain-stage">
                  <div className="redteam-killchain-connector" style={i === 0 ? { visibility: 'hidden' } : {}} />
                  <div className="redteam-killchain-node" style={{ background: style.color + '20', color: style.color, borderColor: style.color }}>
                    <span className="redteam-killchain-dot">{style.dot}</span>
                  </div>
                  <div className="redteam-killchain-stage-name">{stage.stage}</div>
                  <div className="redteam-killchain-stage-status" style={{ color: style.color }}>{style.label}</div>
                  <div className="redteam-killchain-stage-detail">{stage.detail}</div>
                </div>
              );
            })}
          </div>

          <div className="redteam-recs-label">Recommended Closures</div>
          <div className="redteam-recs">
            {crit.recommendations.map(function(r, i) {
              var hasPrefill = !!r.change_request_prefill;
              return (
                <div key={i} className={'redteam-rec redteam-rec-' + r.priority}>
                  <div className="redteam-rec-header">
                    <span className="redteam-rec-priority" style={{ color: priorityStyle[r.priority].color }}>{priorityStyle[r.priority].label}</span>
                    <span className="redteam-rec-impact">Blocks {r.impact_pct}% of this vector</span>
                  </div>
                  <div className="redteam-rec-title">{r.title}</div>
                  <div className="redteam-rec-detail">{r.detail}</div>
                  <div className="redteam-rec-meta">
                    <span>Cost: <strong>{r.cost}</strong></span>
                    <span>Timeline: <strong>{r.timeline}</strong></span>
                    {hasPrefill && r.change_request_prefill.impact_summary && (
                      <>
                        <span>Users: <strong>{r.change_request_prefill.impact_summary.users_affected}</strong></span>
                        {r.change_request_prefill.impact_summary.devices_affected > 0 && <span>Devices: <strong>{r.change_request_prefill.impact_summary.devices_affected}</strong></span>}
                      </>
                    )}
                  </div>
                  {hasPrefill && (
                    <div className="redteam-rec-actions">
                      <button className="redteam-convert-btn" onClick={function() { convertToChangeRequest(r, crit.id); }}>
                        <span className="redteam-convert-btn-icon">→</span>
                        <span>Convert to Change Request</span>
                      </button>
                      <span className="redteam-convert-hint">Pre-fills impact analysis, cost, timeline</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </ContentBand>

      {/* Attack Timeline */}
      <ContentBand title="Attack Timeline" subtitle="Every simulation run in the last 24 hours — click any row for the full breakdown">
        <div className="redteam-timeline">
          {data.timeline.map(function(a) {
            var style = outcomeStyle[a.outcome] || outcomeStyle.blocked;
            return (
              <div key={a.id} className={'redteam-timeline-row redteam-timeline-clickable redteam-timeline-' + a.outcome} onClick={function() { openDetail(a.id); }}>
                <div className="redteam-timeline-time">{a.time_label}</div>
                <div className="redteam-timeline-dot" style={{ background: style.color }} />
                <div className="redteam-timeline-body">
                  <div className="redteam-timeline-type">{a.type_label}</div>
                  <div className="redteam-timeline-target">{a.target}</div>
                  <div className="redteam-timeline-result">{a.result}</div>
                </div>
                <div className="redteam-timeline-right">
                  <span className="redteam-timeline-outcome" style={{ background: style.color + '18', color: style.color }}>{style.label}</span>
                  {a.detect_source && <div className="redteam-timeline-detect">{a.detect_source}{a.detect_time_min !== null ? ' · ' + a.detect_time_min + 'm' : ''}</div>}
                  <div className="redteam-timeline-more">Details ›</div>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      {/* Attack Detail Drawer */}
      <AttackDetailPanel isOpen={!!detailId} attackId={detailId} clientId={clientId} onClose={closeDetail} onNavigate={onNavigate} onConvertToCR={function(rec) { convertToChangeRequest(rec, detailId); setDetailId(null); }} goToUser={function(u) { setDetailId(null); goToUser(u); }} goToDevice={function(d) { setDetailId(null); goToDevice(d); }} />

      {/* Attack Categories */}
      <ContentBand title="Attack Categories — 7-Day Performance" subtitle="How many simulations ran per category and how many succeeded">
        <div className="redteam-categories">
          {data.categories.map(function(c) {
            var rate = c.success_rate;
            var color = rate === 0 ? '#22c55e' : rate < 15 ? '#f59e0b' : '#ef4444';
            return (
              <div key={c.id} className="redteam-category">
                <div className="redteam-category-icon" style={{ color: color, background: color + '14' }}>{c.icon}</div>
                <div className="redteam-category-body">
                  <div className="redteam-category-label">{c.label}</div>
                  <div className="redteam-category-stats">
                    <span><strong>{c.runs_7d}</strong> runs</span>
                    <span><strong>{c.successful_7d}</strong> through</span>
                    <span style={{ color: color }}><strong>{rate}%</strong> success rate</span>
                  </div>
                  <div className="redteam-category-bar">
                    <div className="redteam-category-bar-fill" style={{ width: (rate + 2) + '%', background: color }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      {/* Remediation Queue */}
      <ContentBand title="Remediation Queue" subtitle="Prioritized by expected impact against observed attack patterns">
        <div className="redteam-remediations">
          {data.remediation_queue.map(function(r, i) {
            return (
              <div key={i} className={'redteam-remediation redteam-remediation-' + r.priority}>
                <div className="redteam-remediation-priority" style={{ color: priorityStyle[r.priority].color }}>{priorityStyle[r.priority].label}</div>
                <div className="redteam-remediation-body">
                  <div className="redteam-remediation-title">{r.title}</div>
                  <div className="redteam-remediation-category">{r.category}</div>
                </div>
                <div className="redteam-remediation-meta">
                  <div className="redteam-remediation-impact"><strong>{r.blocks_pct}%</strong> blocked</div>
                  <div className="redteam-remediation-cost">{r.cost}</div>
                  <div className="redteam-remediation-timeline">{r.timeline}</div>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      {/* Upcoming Attacks */}
      <ContentBand title="Scheduled Simulations" subtitle="Pre-announced so your team can observe or pause if needed">
        <MetricList>
          {data.upcoming.map(function(u, i) {
            return <MetricListItem key={i} icon="⚔" iconColor="#a78bfa" label={u.type} sublabel={'Target: ' + u.target + ' · Campaign: ' + u.campaign} value={u.when} valueColor="var(--text-primary)" />;
          })}
        </MetricList>
      </ContentBand>

      {/* 6-Week Trend */}
      <ContentBand title="6-Week Trend" subtitle="Time-to-compromise and block-rate trajectory — lower compromise time and higher block rate = stronger posture">
        <div className="redteam-trend">
          {data.trend.map(function(t, i) {
            var heightPct = Math.min(100, (t.time_to_compromise_hours / 6) * 100);
            var blockPct = t.block_rate_pct;
            var isCurrent = i === data.trend.length - 1;
            return (
              <div key={i} className={'redteam-trend-col' + (isCurrent ? ' is-current' : '')}>
                <div className="redteam-trend-bar-wrap">
                  <div className="redteam-trend-bar" style={{ height: heightPct + '%' }} />
                </div>
                <div className="redteam-trend-label">{t.time_to_compromise_hours}h</div>
                <div className="redteam-trend-sublabel">{blockPct}% blocked</div>
                <div className="redteam-trend-period">{t.period}</div>
              </div>
            );
          })}
        </div>
      </ContentBand>
    </div>
  );
}

// ── Attack Detail Panel (slide-out drawer from Red Team) ─────────
function AttackDetailPanel({ isOpen, attackId, clientId, onClose, onConvertToCR, goToUser, goToDevice }) {
  var [detail, setDetail] = useState(null);
  var [loading, setLoading] = useState(false);
  var [showTechnical, setShowTechnical] = useState(false);
  var [expandedStep, setExpandedStep] = useState(null);
  var [copiedStep, setCopiedStep] = useState(null);
  useEffect(function() {
    if (!isOpen) return;
    var handler = function(e) { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return function() { window.removeEventListener('keydown', handler); };
  }, [isOpen, onClose]);

  useEffect(function() {
    if (!isOpen || !attackId) return;
    setLoading(true); setDetail(null); setShowTechnical(false); setExpandedStep(null); setCopiedStep(null);
    authFetch(API + '/clients/' + clientId + '/redteam/' + attackId)
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setDetail(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [isOpen, attackId, clientId]);

  if (!isOpen) return null;

  var outcomeStyle = { blocked: { color: '#22c55e', label: 'Blocked' }, partial: { color: '#f59e0b', label: 'Partial' }, successful: { color: '#ef4444', label: 'Got Through' } };
  var userOutcomeIcon = { clicked: '✕', compromised_credentials: '✕', recognized_phish: '✓', reported_email: '✓', ignored: '○', declined_consent: '✓' };
  var userOutcomeLabel = { clicked: 'Clicked link', compromised_credentials: 'Credentials captured', recognized_phish: 'Spotted the phish', reported_email: 'Reported to security', ignored: 'Did not interact', declined_consent: 'Declined permission grant' };
  var userOutcomeColor = { clicked: '#ef4444', compromised_credentials: '#ef4444', recognized_phish: '#22c55e', reported_email: '#22c55e', ignored: '#64748b', declined_consent: '#22c55e' };

  var copyToClipboard = function(text, stepNum) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function() { setCopiedStep(stepNum); setTimeout(function() { setCopiedStep(null); }, 2000); });
    }
  };

  return (
    <>
      <div className="qbr-overlay" onClick={onClose} />
      <div className="attack-detail-panel">
        <div className="attack-detail-header">
          <div>
            <div className="attack-detail-eyebrow">RED TEAM FINDING</div>
            <div className="attack-detail-title">{detail ? detail.type_label : 'Loading…'}</div>
            {detail && <div className="attack-detail-meta">ID: {detail.id} · {new Date(detail.time).toLocaleString()}</div>}
          </div>
          <button className="qbr-close-btn" onClick={onClose}>&#10005;</button>
        </div>

        <div className="attack-detail-body">
          {loading && <div className="qbr-loading"><div className="qbr-loading-spinner" /><div className="qbr-loading-stage">Loading attack detail…</div></div>}
          {!loading && detail && (
            <>
              {/* Executive Summary — plain English, top billing */}
              <div className="attack-detail-section attack-detail-summary">
                <div className="attack-detail-section-title">In plain English</div>
                <div className="attack-detail-summary-text">{detail.plain_summary}</div>
                <div className="attack-detail-impact">
                  <div className="attack-detail-impact-label">Business impact</div>
                  <div className="attack-detail-impact-text">{detail.business_impact}</div>
                </div>
              </div>

              {/* Who was involved */}
              <div className="attack-detail-section">
                <div className="attack-detail-section-title">Who was involved · click any name to view their security profile</div>
                <div className="attack-detail-users">
                  {detail.targets.users.map(function(u, i) {
                    var color = userOutcomeColor[u.outcome] || '#64748b';
                    return (
                      <button key={i} className="attack-detail-user" onClick={function() { goToUser(u.name); }}>
                        <span className="attack-detail-user-icon" style={{ background: color + '20', color: color }}>{userOutcomeIcon[u.outcome] || '●'}</span>
                        <div className="attack-detail-user-body">
                          <div className="attack-detail-user-name">{u.name}</div>
                          <div className="attack-detail-user-role">{u.role}</div>
                        </div>
                        <div className="attack-detail-user-outcome" style={{ color: color }}>{userOutcomeLabel[u.outcome] || u.outcome}</div>
                      </button>
                    );
                  })}
                </div>
                {detail.targets.devices.length > 0 && (
                  <div className="attack-detail-devices">
                    <div className="attack-detail-section-sub">Devices involved</div>
                    {detail.targets.devices.map(function(d, i) {
                      return (
                        <button key={i} className="attack-detail-device" onClick={function() { goToDevice(d.name); }}>
                          <span className="attack-detail-device-icon">◈</span>
                          <div>
                            <div className="attack-detail-device-name">{d.name}</div>
                            <div className="attack-detail-device-sub">{d.role_in_attack} · Owner: {d.owner}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Step-by-step */}
              <div className="attack-detail-section">
                <div className="attack-detail-section-title">
                  How it unfolded · step by step
                  <button className="attack-detail-toggle" onClick={function() { setShowTechnical(!showTechnical); }}>
                    {showTechnical ? 'Show plain-language only' : 'Also show technical details'}
                  </button>
                </div>
                <div className="attack-detail-steps">
                  {detail.steps.map(function(step, i) {
                    return (
                      <div key={i} className="attack-detail-step">
                        <div className="attack-detail-step-time">{step.time}</div>
                        <div className="attack-detail-step-body">
                          <div className="attack-detail-step-plain">{step.plain}</div>
                          {showTechnical && <div className="attack-detail-step-technical">{step.technical}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Technical Indicators (collapsible) */}
              {showTechnical && (
                <div className="attack-detail-section attack-detail-technical">
                  <div className="attack-detail-section-title">Technical indicators</div>
                  <div className="attack-detail-mitre">
                    <span className="attack-detail-mitre-label">MITRE ATT&amp;CK</span>
                    <a href={detail.indicators.mitre_url} target="_blank" rel="noopener noreferrer" className="attack-detail-mitre-link">{detail.indicators.mitre_technique} ↗</a>
                  </div>
                  <div className="attack-detail-iocs-label">Indicators of Compromise (IOCs)</div>
                  <div className="attack-detail-iocs">
                    {detail.indicators.iocs.map(function(ioc, i) {
                      return (
                        <div key={i} className="attack-detail-ioc">
                          <span className="attack-detail-ioc-type">{ioc.type}</span>
                          <span className="attack-detail-ioc-value">{ioc.value}</span>
                          <span className="attack-detail-ioc-note">{ioc.note}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="attack-detail-iocs-label">Detection sources</div>
                  <div className="attack-detail-detections">
                    {detail.detection_sources.map(function(d, i) {
                      return (
                        <div key={i} className="attack-detail-detection">
                          <span className="attack-detail-detection-source">{d.source}</span>
                          <span className="attack-detail-detection-signal">{d.signal}</span>
                          {d.alert_id && <span className="attack-detail-detection-alert">{d.alert_id}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Remediation Playbook */}
              <div className="attack-detail-section attack-detail-playbook">
                <div className="attack-detail-section-title">Remediation playbook · ordered by priority</div>
                {detail.playbook.map(function(p, i) {
                  var isExpanded = expandedStep === i;
                  var hasScript = p.powershell || p.conditional_access_template || p.huntress_console;
                  return (
                    <div key={i} className={'attack-detail-playbook-step' + (isExpanded ? ' is-expanded' : '')}>
                      <div className="attack-detail-playbook-header" onClick={function() { setExpandedStep(isExpanded ? null : i); }}>
                        <span className="attack-detail-playbook-num">{p.step}</span>
                        <div className="attack-detail-playbook-title-wrap">
                          <div className="attack-detail-playbook-title">{p.title}</div>
                          <div className="attack-detail-playbook-plain">{p.plain}</div>
                        </div>
                        <div className="attack-detail-playbook-time">{p.estimated_time}</div>
                        <span className="attack-detail-playbook-chevron">{isExpanded ? '▾' : '▸'}</span>
                      </div>
                      {isExpanded && (
                        <div className="attack-detail-playbook-body">
                          {p.powershell && (
                            <div className="attack-detail-code-block">
                              <div className="attack-detail-code-header">
                                <span>PowerShell · copy to run in Exchange Online / Microsoft Graph PowerShell</span>
                                <button className="attack-detail-code-copy" onClick={function() { copyToClipboard(p.powershell, 'ps-' + i); }}>{copiedStep === 'ps-' + i ? '✓ Copied' : 'Copy'}</button>
                              </div>
                              <pre className="attack-detail-code">{p.powershell}</pre>
                            </div>
                          )}
                          {p.conditional_access_template && (
                            <div className="attack-detail-code-block">
                              <div className="attack-detail-code-header">
                                <span>Conditional Access policy template · paste into Entra admin portal</span>
                                <button className="attack-detail-code-copy" onClick={function() { copyToClipboard(JSON.stringify(p.conditional_access_template, null, 2), 'ca-' + i); }}>{copiedStep === 'ca-' + i ? '✓ Copied' : 'Copy'}</button>
                              </div>
                              <pre className="attack-detail-code">{JSON.stringify(p.conditional_access_template, null, 2)}</pre>
                            </div>
                          )}
                          {p.huntress_console && (
                            <div className="attack-detail-link-block">
                              <span>Huntress MSAT Console: </span>
                              <a href={p.huntress_console} target="_blank" rel="noopener noreferrer">{p.huntress_console} ↗</a>
                            </div>
                          )}
                          {p.is_change_request && (
                            <div className="attack-detail-playbook-actions">
                              <button className="redteam-convert-btn" onClick={function() { onConvertToCR({ change_request_prefill: {
                                title: 'Deploy FIDO2 hardware keys — Finance & Executive roles',
                                description: 'Source: Aurelius Red Team ' + detail.id + '\n\n' + p.plain,
                                type: 'Security Change', category: 'Hardware Request', priority: 'High',
                                impact_summary: { users_affected: 8, devices_affected: 8, license_impact: '$0 (covered by existing E5)', cost_estimate: '$4,200 one-time', timeline: '2 weeks' }
                              }}); }}>
                                <span className="redteam-convert-btn-icon">→</span>
                                <span>Convert to Change Request</span>
                              </button>
                              <span className="redteam-convert-hint">Sends through normal approval workflow with full impact pre-filled</span>
                            </div>
                          )}
                          {!hasScript && !p.is_change_request && (
                            <div className="attack-detail-no-script">No automation needed — outcome was positive or requires operational follow-up only.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Device Posture Tab ────────────────────────────────────────────
// ── Posture Fleet Components ──────────────────────────────────────
function FleetStatusTiles({ summary, filter, onFilterClick }) {
  var s = summary;
  var tiles = [
    { key: 'online', label: 'Online', sublabel: 'Fleet operational', count: s.online || 0, alert: false },
    { key: 'offline', label: 'Powered Off', sublabel: 'Offline devices', count: s.offline || 0, alert: false },
    { key: 'stale', label: 'Stale', sublabel: 'Not seen 7+ days', count: s.stale || 0, alert: true }
  ];
  return (
    <div className="fleet-tiles">
      {tiles.map(function(t) {
        var count = t.count;
        var isActive = filter === t.key;
        var isClickable = count > 0 && onFilterClick;
        var isAlertState = t.alert && count > 0;
        var classes = 'ticket-flow-stage ticket-flow-stage-fleet-' + t.key +
          (count === 0 ? ' ticket-flow-stage-empty' : '') +
          (isActive ? ' ticket-flow-stage-active' : '') +
          (isClickable ? ' ticket-flow-stage-clickable' : '') +
          (isAlertState ? ' ticket-flow-stage-aging' : '');
        return (
          <div key={t.key} className={classes} onClick={isClickable ? function() { onFilterClick(isActive ? null : t.key); } : undefined}>
            <div className="ticket-flow-stage-label">{t.label}</div>
            <div className="ticket-flow-stage-count">{count}</div>
            <div className="ticket-flow-stage-sub">{t.sublabel}</div>
          </div>
        );
      })}
    </div>
  );
}

function FleetProtectionTiles({ summary, onFilterClick }) {
  var s = summary;
  var avPct = s.total > 0 ? Math.round((s.avProtected / s.total) * 100) : 0;
  var avAlertLevel = avPct < 70 ? 'alert' : (avPct < 90 ? '' : 'good');
  var typeParts = [];
  if (s.windows) typeParts.push(s.windows + ' Windows');
  if (s.mac) typeParts.push(s.mac + ' macOS');
  if (s.linux) typeParts.push(s.linux + ' Linux');
  if (s.servers) typeParts.push(s.servers + ' Servers');
  return (
    <div className="metric-tiles">
      <div className={'metric-tile' + (avAlertLevel ? ' metric-tile-' + avAlertLevel : '')}>
        <div className="metric-tile-label">AV Coverage</div>
        <div className="metric-tile-value">{avPct}<span className="metric-tile-unit">%</span></div>
        <div className="metric-tile-context">{s.avProtected} of {s.total} protected</div>
      </div>
      <div
        className={'metric-tile' + (s.avUnprotected > 0 ? ' metric-tile-alert' : ' metric-tile-good') + (s.avUnprotected > 0 && onFilterClick ? ' metric-tile-clickable' : '')}
        onClick={s.avUnprotected > 0 && onFilterClick ? function() { onFilterClick('av-unprotected'); } : undefined}
      >
        <div className="metric-tile-label">Unprotected</div>
        <div className="metric-tile-value">{s.avUnprotected || 0}</div>
        <div className="metric-tile-context">{s.avUnprotected > 0 ? 'Devices without active AV' : 'All devices protected'}</div>
      </div>
      <div
        className={'metric-tile' + (s.defenderDisabled > 0 ? ' metric-tile-alert' : '') + (s.defenderDisabled > 0 && onFilterClick ? ' metric-tile-clickable' : '')}
        onClick={s.defenderDisabled > 0 && onFilterClick ? function() { onFilterClick('defender-off'); } : undefined}
      >
        <div className="metric-tile-label">Defender Disabled</div>
        <div className="metric-tile-value">{s.defenderDisabled || 0}</div>
        <div className="metric-tile-context">{s.defenderDisabled > 0 ? 'Check policy enforcement' : 'Defender active where supported'}</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Total Managed</div>
        <div className="metric-tile-value">{s.total || 0}</div>
        <div className="metric-tile-context">{typeParts.length > 0 ? typeParts.join(' · ') : 'Device fleet'}</div>
      </div>
    </div>
  );
}

function PostureTab({ clientId, live }) {
  const [posture, setPosture] = useState(null);
  const [loading, setLoading] = useState(true);
  // Parse entry hint (e.g. from Overview "View All Devices" or Red Team device link).
  // Supported keys:
  //   posture:devices             -> open device list
  //   posture:devices:<name>      -> open device list, seed search with device name
  var entryHint = sessionStorage.getItem('aurelius_tab_entry');
  var initialView = 'summary';
  var initialDevSearch = '';
  var postureArrivalBanner = null;
  if (entryHint && entryHint.indexOf('posture:devices') === 0) {
    initialView = 'devices';
    var hintParts = entryHint.split(':');
    if (hintParts[2]) { initialDevSearch = hintParts[2]; postureArrivalBanner = { name: hintParts[2] }; }
    sessionStorage.removeItem('aurelius_tab_entry');
  }
  const [view, setView] = useState(initialView);
  const [arrivalBanner, setArrivalBanner] = useState(postureArrivalBanner);
  const [filter, setFilter] = useState(null);
  const [licensing, setLicensing] = useState(null);
  var devSort = useTableSort('name', 'asc');
  var licSort = useTableSort('name', 'asc');
  var [devSearch, setDevSearch] = useState(initialDevSearch);
  var [devPage, setDevPage] = useState(0);
  var [devFilters, setDevFilters] = useState({});

  useEffect(function() {
    setLoading(true);
    setPosture(null);
    setLicensing(null);
    authFetch(API + '/clients/' + clientId + '/posture')
      .then(function(r) { return r.json(); })
      .then(function(data) { setPosture(data); setLoading(false); })
      .catch(function() { setLoading(false); });
    authFetch(API + '/clients/' + clientId + '/licensing')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setLicensing(data); })
      .catch(function() {});
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!posture || !posture.summary) return <EmptyStateAction icon="🖥" title="Device Posture Unavailable" text="NinjaOne RMM integration is required for device posture data. Once connected, you'll see real-time device health, antivirus coverage, and fleet status." cta="Contact CloudByte Group to enable" />;

  var s = posture.summary;
  var avPct = s.total > 0 ? Math.round((s.avProtected / s.total) * 100) : 0;
  var onlinePct = s.total > 0 ? Math.round((s.online / s.total) * 100) : 0;
  var stalePct = s.total > 0 ? Math.round(((s.stale || 0) / s.total) * 100) : 0;
  var managedPct = 100 - stalePct;
  var filterLabels = {
    'online': 'Online Devices', 'offline': 'Offline Devices',
    'av-protected': 'AV Protected', 'av-unprotected': 'AV Unprotected', 'defender-off': 'Defender Disabled',
  };

  var filteredDevices = posture.devices;
  if (filter === 'online') filteredDevices = posture.devices.filter(function(d) { return !d.offline; });
  else if (filter === 'offline') filteredDevices = posture.devices.filter(function(d) { return d.offline; });
  else if (filter === 'av-protected') filteredDevices = posture.devices.filter(function(d) { return d.av && d.av.state === 'ON'; });
  else if (filter === 'av-unprotected') filteredDevices = posture.devices.filter(function(d) { return !d.av || d.av.state !== 'ON'; });
  else if (filter === 'defender-off') filteredDevices = posture.devices.filter(function(d) { return d.defenderDisabled; });

  return (
    <div className="tab-content">
      {arrivalBanner && (
        <div className="arrival-banner">
          <span className="arrival-banner-badge">FROM RED TEAM</span>
          <span className="arrival-banner-text">You're investigating device <strong>{arrivalBanner.name}</strong> — involved in a Red Team simulation. Filtered below.</span>
          <button className="arrival-banner-close" onClick={function() { setArrivalBanner(null); }}>&#10005;</button>
        </div>
      )}
      {view === 'summary' ? (
        <>
          <TabDescription text="Real-time device health from NinjaOne RMM. Shows antivirus coverage, defender status, and device reachability across your managed fleet." />
          <DataFreshness live={live.ninjaone} />
          <ContentBand title="Device Fleet Status" subtitle={s.total + ' managed devices — click any tile to drill in'} rightContent={<>{live.ninjaone && <span className="live-badge">LIVE</span>}<button className="tab-drill-link" onClick={function() { setFilter(null); setView('devices'); }}>View All Devices ›</button></>}>
            <FleetStatusTiles summary={s} filter={null} onFilterClick={function(key) { if (key) { setFilter(key); setView('devices'); } }} />
            <FleetProtectionTiles summary={s} onFilterClick={function(key) { if (key) { setFilter(key); setView('devices'); } }} />
          </ContentBand>
          <CollapsibleSection title="Device Types" defaultOpen={true}>
            <div className="stats-grid-compact">
              <StatCardCompact title="Windows" value={s.windows} />
              <StatCardCompact title="macOS" value={s.mac} />
              <StatCardCompact title="Linux" value={s.linux} />
              <StatCardCompact title="Servers" value={s.servers} />
            </div>
          </CollapsibleSection>
          <CollapsibleSection title="Antivirus Products" defaultOpen={true}>
            <div className="stats-grid-compact">
              {Object.entries(s.avProducts).map(function([name, count]) {
                return <StatCardCompact key={name} title={name} value={count} />;
              })}
            </div>
            {Object.keys(s.avProducts).length === 0 && <div className="empty-state">No antivirus data reported.</div>}
          </CollapsibleSection>
          {licensing && licensing.enabled && (
            <CollapsibleSection title="Microsoft Licensing" defaultOpen={true} rightContent={<span className="live-badge">LIVE</span>}>
              <div className="stats-grid-compact">
                <StatCardCompact title="License SKUs" value={licensing.summary.total_skus} live={true} />
                <StatCardCompact title="Purchased" value={licensing.summary.total_purchased} live={true} />
                <StatCardCompact title="Assigned" value={licensing.summary.total_assigned} accent="#34d399" />
                <StatCardCompact title="Available" value={licensing.summary.total_available} accent={licensing.summary.total_available > 5 ? '#f5a623' : '#34d399'} />
                <StatCardCompact title="Utilization" value={licensing.summary.utilization_pct + '%'} accent={licensing.summary.utilization_pct >= 80 ? '#34d399' : licensing.summary.utilization_pct >= 60 ? '#f5a623' : '#f06565'} />
                <StatCardCompact title="Est. Monthly Cost" value={'$' + licensing.summary.estimated_monthly_cost.toLocaleString()} accent="#5b9ef4" />
              </div>
              <div className="posture-table-wrap" style={{ marginTop: 14 }}>
                <table className="posture-table">
                  <thead><tr><SortTh field="name" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>License</SortTh><SortTh field="purchased" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Purchased</SortTh><SortTh field="assigned" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Assigned</SortTh><SortTh field="available" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Available</SortTh><SortTh field="cost" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Cost / License</SortTh><SortTh field="total" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Monthly Total</SortTh><SortTh field="util" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Utilization</SortTh></tr></thead>
                  <tbody>
                    {sortRows(licensing.licenses.filter(function(l) { return !l.isFree; }), licSort.sortField, licSort.sortDir, { name: function(l) { return l.name; }, purchased: function(l) { return l.purchased; }, assigned: function(l) { return l.assigned; }, available: function(l) { return l.purchased - l.assigned; }, cost: function(l) { return l.costPerLicense || 0; }, total: function(l) { return l.monthlyTotal || 0; }, util: function(l) { return l.purchased > 0 ? l.assigned / l.purchased : 0; } }).map(function(l) {
                      var utilPct = l.purchased > 0 ? Math.round((l.assigned / l.purchased) * 100) : 0;
                      var utilColor = utilPct >= 80 ? '#34d399' : utilPct >= 50 ? '#f5a623' : '#f06565';
                      return (
                        <tr key={l.skuPartNumber}>
                          <td className="posture-device-name">{l.name}</td>
                          <td>{l.purchased}</td><td>{l.assigned}</td>
                          <td style={{ color: l.available > 0 ? '#f5a623' : '#34d399' }}>{l.available}</td>
                          <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{l.costPerLicense > 0 ? '$' + l.costPerLicense.toFixed(2) : '—'}</td>
                          <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{l.monthlyCost > 0 ? '$' + l.monthlyCost.toLocaleString() : '—'}</td>
                          <td><span style={{ color: utilColor, fontWeight: 600 }}>{utilPct}%</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          )}
        </>
      ) : (function() {
        var searchFields = [function(d) { return d.name; }, function(d) { return d.lastUser; }];
        var devAccessors = { name: function(d) { return d.name; }, user: function(d) { return d.lastUser || ''; }, type: function(d) { return d.nodeClass || ''; }, status: function(d) { return d.offline ? 'Offline' : 'Online'; }, av: function(d) { return d.av ? d.av.product : ''; }, lastContact: function(d) { return d.lastContact || 0; } };
        // Device type groups (for filter options)
        var typeGroups = {};
        filteredDevices.forEach(function(d) {
          var t = (d.nodeClass || '').indexOf('LAPTOP') >= 0 ? 'Laptop' : (d.nodeClass || '').indexOf('MAC') >= 0 ? 'Mac' : (d.nodeClass || '').indexOf('SERVER') >= 0 ? 'Server' : 'Workstation';
          typeGroups[t] = (typeGroups[t] || 0) + 1;
        });
        var filterAccessors = {
          status: function(d) { return d.offline ? 'Offline' : 'Online'; },
          type: function(d) { var nc = d.nodeClass || ''; return nc.indexOf('LAPTOP') >= 0 ? 'Laptop' : nc.indexOf('MAC') >= 0 ? 'Mac' : nc.indexOf('SERVER') >= 0 ? 'Server' : 'Workstation'; },
          avstate: function(d) { return d.av && d.av.state === 'ON' ? 'Protected' : d.av && d.av.state === 'OFF' ? 'Disabled' : 'Missing'; }
        };
        var searchedDevs = filterRows(filteredDevices, devSearch, searchFields);
        var tableFilteredDevs = applyFilters(searchedDevs, devFilters, filterAccessors);
        var sortedDevs = sortRows(tableFilteredDevs, devSort.sortField, devSort.sortDir, devAccessors);
        var pageSize = 25;
        var totalPages = Math.max(1, Math.ceil(sortedDevs.length / pageSize));
        var safePage = Math.min(devPage, totalPages - 1);
        var pagedDevs = sortedDevs.slice(safePage * pageSize, (safePage + 1) * pageSize);
        return (
        <>
          <div className="section-title">
            {filter ? filterLabels[filter] : 'All Devices'} ({filteredDevices.length}{filter ? ' of ' + posture.devices.length : ''})
            {filter && <button className="insights-btn" style={{ marginLeft: 8, verticalAlign: 'middle' }} onClick={function() { setFilter(null); }}>Clear Filter</button>}
            <button className="insights-btn" style={{ marginLeft: 8, verticalAlign: 'middle' }} onClick={function() { setView('summary'); setFilter(null); }}>Back to Summary</button>
          </div>
          <TableSearch value={devSearch} onChange={function(v) { setDevSearch(v); setDevPage(0); }} count={sortedDevs.length} total={filteredDevices.length} noun="devices" onExport={function() { exportTableCSV(['Device','User','Type','Status','Antivirus','AV State','Last Contact'], sortedDevs.map(function(d) { return [d.name, d.lastUser || '', (d.nodeClass || '').replace('WINDOWS_','').replace('_',' '), d.offline ? 'Offline' : 'Online', d.av ? d.av.product : 'None', d.av ? d.av.state : '', d.lastContact ? new Date(d.lastContact * 1000).toLocaleDateString() : '']; }), 'posture-devices'); }} />
          <TableFilters filters={[
            { key: 'status', label: 'Status', options: [
              { value: 'Online', label: 'Online', color: '#22c55e', count: filteredDevices.filter(function(d) { return !d.offline; }).length },
              { value: 'Offline', label: 'Offline', color: '#64748b', count: filteredDevices.filter(function(d) { return d.offline; }).length }
            ]},
            { key: 'type', label: 'Type', options: Object.entries(typeGroups).sort(function(a, b) { return b[1] - a[1]; }).map(function(pair) { return { value: pair[0], label: pair[0], count: pair[1] }; }) },
            { key: 'avstate', label: 'AV', options: [
              { value: 'Protected', label: 'Protected', color: '#22c55e', count: filteredDevices.filter(function(d) { return d.av && d.av.state === 'ON'; }).length },
              { value: 'Disabled', label: 'Disabled', color: '#f59e0b', count: filteredDevices.filter(function(d) { return d.av && d.av.state === 'OFF'; }).length },
              { value: 'Missing', label: 'Missing', color: '#ef4444', count: filteredDevices.filter(function(d) { return !d.av; }).length }
            ]}
          ]} active={devFilters} onChange={function(f) { setDevFilters(f); setDevPage(0); }} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="name" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>Device</SortTh><SortTh field="user" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>User</SortTh><SortTh field="type" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>Type</SortTh><SortTh field="status" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>Status</SortTh><SortTh field="av" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>Antivirus</SortTh><th>AV State</th><SortTh field="lastContact" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>Last Contact</SortTh></tr></thead>
              <tbody>
                {pagedDevs.length === 0 && <tr><td colSpan="7" className="table-no-results">No devices match your search</td></tr>}
                {pagedDevs.map(function(d) {
                  var lastContact = d.lastContact ? new Date(d.lastContact * 1000).toLocaleDateString() : '—';
                  return (
                    <tr key={d.id} className={d.offline ? 'posture-row-offline' : ''}>
                      <td className="posture-device-name" title={d.name}><span className={'status-dot ' + (d.offline ? 'status-dot-muted' : 'status-dot-good')} />{d.name}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.lastUser || '—'}</td>
                      <td>{(d.nodeClass || '').replace('WINDOWS_', '').replace('_', ' ')}</td>
                      <td><span className={'posture-status ' + (d.offline ? 'posture-offline' : 'posture-online')}>{d.offline ? 'Offline' : 'Online'}</span></td>
                      <td>{(d.av ? d.av.product : 'None')}{d.defenderDisabled && <span className="posture-warning"> (Defender OFF)</span>}</td>
                      <td><span className={'posture-av-state posture-av-' + ((d.av ? d.av.state : '—') === 'ON' ? 'on' : (d.av ? d.av.state : '—') === 'OFF' ? 'off' : 'unknown')}>{d.av ? d.av.state : '—'}</span></td>
                      <td className="posture-date">{lastContact}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={safePage} totalPages={totalPages} onPageChange={setDevPage} totalItems={sortedDevs.length} startIndex={safePage * pageSize} endIndex={Math.min((safePage + 1) * pageSize, sortedDevs.length)} />
        </>
        );
      })()}
    </div>
  );
}
// ── Security Tab (Enriched Command Center) ────────────────────────
function SecurityTab({ clientId, c, live, onNavigate }) {
  const [siemData, setSiemData] = useState(null);
  const [postureData, setPostureData] = useState(null);
  const [identityData, setIdentityData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(function() {
    setLoading(true);
    setSiemData(null);
    setPostureData(null);
    setIdentityData(null);
    Promise.all([
      authFetch(API + '/clients/' + clientId + '/siem').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/posture').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/entra').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) {
      setSiemData(results[0]);
      setPostureData(results[1]);
      setIdentityData(results[2]);
      setLoading(false);
    });
  }, [clientId]);

  var siemSummary = siemData && siemData.enabled ? siemData.summary : null;
  var postureSummary = postureData && postureData.summary ? postureData.summary : null;
  var identitySummary = identityData && identityData.summary ? identityData.summary : null;

  var avPct = postureSummary && postureSummary.total > 0 ? Math.round((postureSummary.avProtected / postureSummary.total) * 100) : null;
  var compliantPct = identitySummary && identitySummary.devices.total > 0 ? Math.round((identitySummary.devices.compliant / identitySummary.devices.total) * 100) : null;
  var encryptedPct = identitySummary && identitySummary.intune.total > 0 ? Math.round((identitySummary.intune.encrypted / identitySummary.intune.total) * 100) : null;
  var edrCoverage = c.huntress_agents && c.endpoints.total > 0 ? Math.round((c.huntress_agents.total / c.endpoints.total) * 100) : null;

  function timeAgo(dateStr) {
    if (!dateStr) return '';
    var diff = Date.now() - new Date(dateStr).getTime();
    var hours = Math.floor(diff / 3600000);
    if (hours < 24) return hours + 'h ago';
    return Math.floor(hours / 24) + 'd ago';
  }

  var priorityColors = { 1: '#ef4444', 2: '#f59e0b', 3: '#60a5fa' };
  var priorityLabels = { 1: 'P1', 2: 'P2', 3: 'P3' };

  // Determine which security domains have ACTUAL data feeding them. A domain
  // without a configured integration shouldn't drag the verdict to "Critical"
  // — it's just an absence of data, not evidence of a problem. We score
  // ONLY the configured domains and flag the rest as informational context.
  var securityDomains = [
    { key: 'endpoint',   label: 'Endpoint health',   integration: 'NinjaOne', configured: !!(postureData && postureData.summary && postureData.summary.total > 0), score: c.scores.endpoint },
    { key: 'threat',     label: 'Threat detection',  integration: 'Huntress', configured: !!(c.huntress_agents && c.huntress_agents.total > 0),                  score: c.scores.threat },
    { key: 'siem',       label: 'SIEM findings',     integration: 'Blumira',  configured: !!(siemData && siemData.enabled),                                       score: c.scores.siem },
    { key: 'credential', label: 'Identity controls', integration: 'Entra',    configured: !!(identityData && identityData.summary),                                score: c.scores.credential || null }
  ];
  var configuredDomains = securityDomains.filter(function(d) { return d.configured; });
  var unconfiguredDomains = securityDomains.filter(function(d) { return !d.configured; });
  var minScore = configuredDomains.length > 0 ? Math.min.apply(null, configuredDomains.map(function(d) { return d.score || 0; })) : null;
  var verdictContext;
  if (configuredDomains.length === 0) {
    verdictContext = 'No security integrations are wired for this client yet. Configure NinjaOne, Huntress, Entra, or Blumira to populate this view.';
  } else if (minScore >= 90) {
    verdictContext = 'All ' + configuredDomains.length + ' configured security domain' + (configuredDomains.length === 1 ? ' is' : 's are') + ' performing well.' +
      (unconfiguredDomains.length > 0 ? ' (' + unconfiguredDomains.map(function(d) { return d.integration; }).join(', ') + ' not yet configured.)' : '');
  } else if (minScore >= 75) {
    verdictContext = 'Most security areas are healthy. Focus on the lowest-scoring domain for improvement priority.' +
      (unconfiguredDomains.length > 0 ? ' (' + unconfiguredDomains.map(function(d) { return d.integration; }).join(', ') + ' not yet configured.)' : '');
  } else if (minScore >= 50) {
    var weakest = configuredDomains.reduce(function(w, d) { return (d.score || 0) < (w.score || 0) ? d : w; });
    verdictContext = weakest.label + ' needs attention (' + (weakest.score || 0) + '%). Review the score breakdown.';
  } else {
    var weakest2 = configuredDomains.reduce(function(w, d) { return (d.score || 0) < (w.score || 0) ? d : w; });
    verdictContext = 'Critical gap in ' + weakest2.label + ' (' + (weakest2.score || 0) + '%). Immediate review recommended.';
  }

  return (
    <div className="tab-content">
      <TabDescription text="Unified security posture combining endpoint health, threat detection (Huntress EDR), SIEM findings (Blumira), and identity access controls (Microsoft Entra)." />
      <DataFreshness live={true} />
      <HealthVerdict score={minScore} context={verdictContext} />
      {/* Security Score Rings — pass null for unconfigured domains so they
          render a neutral 'Not configured' state instead of a red 0% ring. */}
      <div className="scores-row">
        <ScoreRing score={securityDomains[0].configured ? securityDomains[0].score : null} label="Endpoint" />
        <ScoreRing score={securityDomains[1].configured ? securityDomains[1].score : null} label="Threat" />
        <ScoreRing score={securityDomains[2].configured ? securityDomains[2].score : null} label="SIEM" />
        <ScoreRing score={securityDomains[3].configured ? securityDomains[3].score : null} label="Credential" />
      </div>

      {/* Endpoint Fleet — Segment Bar + Quick Stats */}
      <ContentBand title="Endpoint Fleet" subtitle={c.endpoints.total + ' managed devices'} rightContent={<>{live.ninjaone && <span className="live-badge">LIVE</span>}<button className="tab-drill-link" onClick={function() { sessionStorage.setItem('aurelius_tab_entry', 'posture:devices'); onNavigate('posture'); }}>View All Devices ›</button></>}>
        <SegmentBar segments={[
          { label: 'Online', value: c.endpoints.online || 0, color: '#22c55e' },
          { label: 'Off-Hours', value: c.endpoints.offHours || 0, color: '#64748b' },
          { label: 'Needs Attention', value: c.endpoints.stale || c.endpoints.critical || 0, color: '#ef4444', onClick: (c.endpoints.stale || c.endpoints.critical || 0) > 0 ? function() { onNavigate('posture'); } : undefined },
        ]} />
        <QuickStats items={[
          avPct !== null ? { value: avPct + '%', label: 'AV Coverage', color: avPct >= 90 ? '#22c55e' : avPct >= 70 ? '#f59e0b' : '#ef4444' } : null,
          postureSummary && postureSummary.defenderDisabled > 0 ? { value: postureSummary.defenderDisabled, label: 'Defender Off', color: '#f59e0b' } : null,
          edrCoverage !== null ? { value: edrCoverage + '%', label: 'EDR Coverage', color: edrCoverage >= 90 ? '#22c55e' : '#f59e0b' } : null,
        ].filter(Boolean)} />
      </ContentBand>

      {/* Threat Detection — Metric List */}
      <ContentBand title="Threat Detection" subtitle="Huntress EDR activity" rightContent={live.huntress ? <span className="live-badge">LIVE</span> : null}>
        <MetricList>
          <MetricListItem icon="⊘" iconColor="#22c55e" label="Threats Mitigated" sublabel="Resolved by Huntress SOC" value={c.threats.mitigated} valueColor="#22c55e" />
          <MetricListItem icon="◎" iconColor={c.threats.investigating > 0 ? '#f59e0b' : '#22c55e'} label="Under Investigation" sublabel="Active SOC review" value={c.threats.investigating} valueColor={c.threats.investigating > 0 ? '#f59e0b' : 'var(--text-primary)'} />
          {c.threats.active > 0 && <MetricListItem icon="⚠" iconColor="#ef4444" label="Active Threats" sublabel="Requires immediate attention" value={c.threats.active} valueColor="#ef4444" onClick={function() { onNavigate('posture'); }} />}
          {c.huntress_agents && <MetricListItem icon="◈" iconColor="#a78bfa" label="EDR Agents Deployed" sublabel={c.huntress_agents.online + ' active in last 24h'} value={c.huntress_agents.total} />}
        </MetricList>
      </ContentBand>

      {/* SIEM Highlights — Segment Bar + Metric List */}
      <ContentBand title="SIEM — Blumira" subtitle={siemData && siemData.enabled ? 'Security event monitoring' : null} rightContent={<>{siemData && siemData.enabled && <span className="live-badge">LIVE</span>}{siemData && siemData.enabled && <button className="tab-drill-link" onClick={function() { onNavigate('siem'); }}>View All Findings ›</button>}</>}>
        {siemData && siemData.enabled && siemSummary ? (
          <>
            <SegmentBar segments={[
              { label: 'Threats', value: siemSummary.threats, color: '#ef4444' },
              { label: 'Suspects', value: siemSummary.suspects, color: '#f59e0b' },
              { label: 'P1 Critical', value: siemSummary.priority_1, color: '#ef4444' },
              { label: 'P2 High', value: siemSummary.priority_2, color: '#f59e0b' },
              { label: 'Other Open', value: Math.max(0, siemSummary.total_open - siemSummary.threats - siemSummary.suspects), color: '#60a5fa' },
            ].filter(function(s) { return s.value > 0; })} />
            {siemData.findings && siemData.findings.length > 0 && (
              <div className="security-mini-table" style={{ marginTop: 12 }}>
                <div className="mini-table-header">Recent Findings</div>
                {siemData.findings.slice(0, 5).map(function(f, idx) {
                  return (
                    <div key={f.finding_id || idx} className="mini-table-row">
                      <span className={'status-dot ' + (f.priority === 1 ? 'status-dot-critical' : f.priority === 2 ? 'status-dot-warning' : 'status-dot-info')} />
                      <span className="mini-badge" style={{ background: (priorityColors[f.priority] || '#64748b') + '20', color: priorityColors[f.priority] || '#64748b' }}>{priorityLabels[f.priority] || 'P' + f.priority}</span>
                      <span className="mini-table-name">{f.name}</span>
                      <span className="mini-table-time">{timeAgo(f.created)}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {siemSummary.total_open === 0 && <div style={{ padding: '12px 0', fontSize: 13, color: '#22c55e', fontWeight: 600 }}>✓ No open findings — SIEM is clear</div>}
          </>
        ) : (
          <div className="empty-state" style={{ padding: '24px 16px' }}>
            {loading ? 'Loading SIEM data...' : 'Blumira SIEM is not configured for this client.'}
          </div>
        )}
      </ContentBand>

      {/* Identity & Access — Metric List */}
      <ContentBand title="Identity & Access — Microsoft Entra" rightContent={<>{identityData && identityData.summary && <span className="live-badge">LIVE</span>}{identityData && identityData.summary && <button className="tab-drill-link" onClick={function() { sessionStorage.setItem('aurelius_tab_entry', 'identity:devices'); onNavigate('identity'); }}>View Details ›</button>}</>}>
        {identitySummary ? (
          <MetricList>
            <MetricListItem icon="◉" iconColor="#22c55e" label="Directory Users" sublabel={identitySummary.users.enabled + ' enabled · ' + (identitySummary.users.guests || 0) + ' guests'} value={identitySummary.users.total} />
            <MetricListItem icon="◎" iconColor={compliantPct !== null && compliantPct >= 80 ? '#22c55e' : '#f59e0b'} label="Device Compliance" sublabel={identitySummary.devices.total + ' enrolled devices'} value={<MicroBar value={compliantPct || 0} max={100} color={compliantPct !== null ? (compliantPct >= 80 ? '#22c55e' : compliantPct >= 50 ? '#f59e0b' : '#ef4444') : '#64748b'} label={compliantPct !== null ? compliantPct + '%' : '—'} />} onClick={function() { sessionStorage.setItem('aurelius_tab_entry', 'identity:devices'); onNavigate('identity'); }} />
            <MetricListItem icon="◈" iconColor={encryptedPct !== null && encryptedPct >= 90 ? '#22c55e' : '#f59e0b'} label="Disk Encryption" sublabel="BitLocker / FileVault coverage" value={<MicroBar value={encryptedPct || 0} max={100} color={encryptedPct !== null ? (encryptedPct >= 90 ? '#22c55e' : encryptedPct >= 70 ? '#f59e0b' : '#ef4444') : '#64748b'} label={encryptedPct !== null ? encryptedPct + '%' : '—'} />} />
            {identitySummary.intune.nonCompliant > 0 && <MetricListItem icon="⚠" iconColor="#ef4444" label="Non-Compliant Devices" sublabel="Require policy remediation" value={identitySummary.intune.nonCompliant} valueColor="#ef4444" onClick={function() { sessionStorage.setItem('aurelius_tab_entry', 'identity:intune'); onNavigate('identity'); }} />}
          </MetricList>
        ) : (
          <div className="empty-state" style={{ padding: '24px 16px' }}>
            {loading ? 'Loading identity data...' : 'Microsoft Entra is not configured for this client.'}
          </div>
        )}
      </ContentBand>

    </div>
  );
}

// ── Keeper Credential Vault Section ───────────────────────────────
function KeeperVaultSection({ clientId }) {
  var [keeper, setKeeper] = useState(null);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    setLoading(true); setKeeper(null);
    authFetch(API + '/clients/' + clientId + '/keeper')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setKeeper(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) {
    return (
      <ContentBand title="Credential Management — Keeper">
        <div className="empty-state" style={{ padding: '24px 16px' }}>Loading vault data…</div>
      </ContentBand>
    );
  }

  if (!keeper || !keeper.enabled) {
    return (
      <ContentBand title="Credential Management — Keeper" rightContent={<span className="coming-soon">Coming Soon</span>}>
        <div className="empty-state" style={{ padding: '24px 16px' }}>Keeper integration pending MSP API access. Managed companies are configured.</div>
      </ContentBand>
    );
  }

  var s = keeper.summary;
  var activityIcons = { share: '⤴', rotate: '↻', policy: '◆', create: '+', access: '◉', invite: '◐', audit: '✓' };
  var activityColors = { share: '#60a5fa', rotate: '#22c55e', policy: '#a78bfa', create: '#22c55e', access: '#64748b', invite: '#f59e0b', audit: '#22c55e' };

  return (
    <>
      <ContentBand title="Credential Management — Keeper" subtitle={s.total_records + ' credentials under management · last sync ' + Math.round((Date.now() - new Date(s.last_sync).getTime()) / 60000) + 'm ago'} rightContent={<span className="live-badge">LIVE</span>}>
        <div className="metric-tiles">
          <div className={'metric-tile ' + (s.enrollment_pct === 100 ? 'metric-tile-good' : '')}>
            <div className="metric-tile-label">Vault Enrollment</div>
            <div className="metric-tile-value">{s.enrollment_pct}<span className="metric-tile-unit">%</span></div>
            <div className="metric-tile-context">{s.enrolled_users} users enrolled</div>
          </div>
          <div className={'metric-tile ' + (s.mfa_adoption_pct >= 90 ? 'metric-tile-good' : s.mfa_adoption_pct < 70 ? 'metric-tile-alert' : '')}>
            <div className="metric-tile-label">MFA Adoption</div>
            <div className="metric-tile-value">{s.mfa_adoption_pct}<span className="metric-tile-unit">%</span></div>
            <div className="metric-tile-context">Enforced on all privileged access</div>
          </div>
          <div className={'metric-tile' + (s.weak_passwords > 0 || s.reused_passwords > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
            <div className="metric-tile-label">Password Health</div>
            <div className="metric-tile-value">{s.avg_password_strength}<span className="metric-tile-unit">/100</span></div>
            <div className="metric-tile-context">{s.weak_passwords} weak · {s.reused_passwords} reused · {s.breached_passwords} breached</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">Shared Folders</div>
            <div className="metric-tile-value">{s.shared_folders}</div>
            <div className="metric-tile-context">Team credential sharing</div>
          </div>
        </div>
      </ContentBand>

      {keeper.security_insights && keeper.security_insights.length > 0 && (
        <ContentBand title="Vault Security Insights" subtitle="Continuous analysis of credential hygiene">
          <div className="insights-list">
            {keeper.security_insights.map(function(ins, i) {
              if (ins.level === 'good') return (
                <div key={i} className="insight-clear">
                  <div className="insight-clear-icon">&#10003;</div>
                  <div>
                    <div className="insight-clear-label">All Clear</div>
                    <div className="insight-clear-text">{ins.text}</div>
                  </div>
                </div>
              );
              return (
                <div key={i} className={'insight-card insight-card-' + ins.level}>
                  <div className={'insight-level-badge insight-level-' + ins.level}>{insightLevelLabels[ins.level] || ins.level}</div>
                  <div className="insight-card-text">{ins.text}</div>
                </div>
              );
            })}
          </div>
        </ContentBand>
      )}

      <ContentBand title="Shared Folders" subtitle="Team credential repositories with least-privilege access">
        <div className="keeper-folders-grid">
          {keeper.shared_folders.map(function(f, i) {
            var ageHours = Math.round((Date.now() - new Date(f.last_modified).getTime()) / 3600000);
            var ageLabel = ageHours < 1 ? 'just now' : ageHours < 24 ? ageHours + 'h ago' : Math.round(ageHours / 24) + 'd ago';
            return (
              <div key={i} className="keeper-folder">
                <div className="keeper-folder-icon">◈</div>
                <div className="keeper-folder-body">
                  <div className="keeper-folder-name">{f.name}</div>
                  <div className="keeper-folder-meta">
                    <span>{f.member_count} members</span>
                    <span>{f.record_count} records</span>
                    <span className="keeper-folder-time">{ageLabel}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      <ContentBand title="Enforcement Policies" subtitle="Active organization-wide security policies">
        <MetricList>
          {keeper.policies.map(function(p, i) {
            return <MetricListItem key={i} icon="◆" iconColor={p.status === 'enforced' ? '#22c55e' : '#f59e0b'} label={p.name} sublabel={p.value} badge={p.status.toUpperCase()} badgeColor={p.status === 'enforced' ? '#22c55e' : '#f59e0b'} />;
          })}
        </MetricList>
      </ContentBand>

      <ContentBand title="Recent Activity" subtitle="Vault access and modification events">
        <div className="keeper-activity">
          {keeper.recent_activity.map(function(a, i) {
            var ageMin = Math.round((Date.now() - new Date(a.time).getTime()) / 60000);
            var ageLabel = ageMin < 60 ? ageMin + 'm ago' : ageMin < 1440 ? Math.round(ageMin / 60) + 'h ago' : Math.round(ageMin / 1440) + 'd ago';
            return (
              <div key={i} className="keeper-activity-item">
                <div className="keeper-activity-icon" style={{ background: activityColors[a.type] + '20', color: activityColors[a.type] }}>{activityIcons[a.type] || '●'}</div>
                <div className="keeper-activity-body">
                  <div className="keeper-activity-main"><strong>{a.user}</strong> {a.action.toLowerCase()} <span className="keeper-activity-target">{a.target}</span></div>
                  <div className="keeper-activity-time">{ageLabel}</div>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>
    </>
  );
}

// ── Network Tab (UniFi) ───────────────────────────────────────────
// Pill-style segmented control for vendor switching on the Network tab.
// Only renders when a client has BOTH UniFi + Meraki configured. Single-
// vendor clients see no extra UI noise — the tab is identical to before.
function NetworkVendorSwitcher({ value, onChange, vendors, counts }) {
  var options = [{ key: 'all', label: 'All', count: counts.all }];
  if (vendors.includes('unifi'))  options.push({ key: 'unifi',  label: 'UniFi',  count: counts.unifi });
  if (vendors.includes('meraki')) options.push({ key: 'meraki', label: 'Meraki', count: counts.meraki });
  return (
    <div className="net-vendor-switcher" role="tablist" aria-label="Network vendor filter">
      {options.map(function(o) {
        var active = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={active}
            className={'net-vendor-btn' + (active ? ' active' : '') + ' net-vendor-btn-' + o.key}
            onClick={function() { onChange(o.key); }}
          >
            {o.key !== 'all' && <span className={'net-vendor-dot net-vendor-dot-' + o.key} />}
            <span>{o.label}</span>
            <span className="net-vendor-count">{o.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function NetworkTab({ clientId }) {
  const [netData, setNetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('summary');
  var netSort = useTableSort('name', 'asc');
  var [netSearch, setNetSearch] = useState('');
  const [filter, setFilter] = useState(null);
  var [netFilters, setNetFilters] = useState({});
  // Vendor filter — only meaningful when client has both UniFi + Meraki.
  // 'all' = combined view; 'unifi' / 'meraki' filter to that vendor's
  // devices + recompute the KPI summary client-side.
  var [vendorFilter, setVendorFilter] = useState('all');

  useEffect(function() {
    setLoading(true); setNetData(null); setView('summary'); setFilter(null); setVendorFilter('all');
    authFetch(API + '/clients/' + clientId + '/network')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setNetData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!netData || !netData.enabled) return <div className="tab-content"><EmptyStateAction icon="📡" title="Network Monitoring Not Connected" text="Network integration (UniFi or Cisco Meraki) provides real-time visibility into gateways, switches, access points, and cameras. Site-level health and firmware status will appear here once configured." cta="Contact CloudByte Group to enable" /></div>;

  var configuredVendors = netData.configuredVendors || (netData.vendors || []);
  var multiVendor = configuredVendors.length > 1;

  // Apply vendor filter: when 'all' use everything; otherwise filter to that vendor's devices and recompute summary client-side
  var rawDevices = netData.devices || [];
  var rawSites = netData.sites || [];
  var devices = vendorFilter === 'all' ? rawDevices : rawDevices.filter(function(d) { return d.vendor === vendorFilter; });
  var sites   = vendorFilter === 'all' ? rawSites   : rawSites.filter(function(site) { return site.vendor === vendorFilter; });

  // When filtering to a vendor, recompute summary from the filtered slice; otherwise use server-computed
  var s;
  if (vendorFilter === 'all') {
    s = netData.summary;
  } else {
    var fwCurrent = devices.filter(function(d) { return d.firmwareStatus === 'upToDate'; }).length;
    var fwOutdated = devices.filter(function(d) { return d.firmwareStatus && d.firmwareStatus !== 'upToDate'; }).length;
    s = {
      total: devices.length,
      online: devices.filter(function(d) { return d.status === 'online'; }).length,
      offline: devices.filter(function(d) { return d.status !== 'online'; }).length,
      sites: sites.length,
      gateways: devices.filter(function(d) { return d.vendor === 'meraki' ? d.bucket === 'gateway' : (d.productLine === 'network' && d.isConsole); }).length,
      access_points: devices.filter(function(d) { return d.vendor === 'meraki' ? d.bucket === 'access_point' : (d.productLine === 'network' && !d.isConsole && (d.shortname || '').match(/^(U6|U7|UAP|G7)/i)); }).length,
      switches: devices.filter(function(d) { return d.vendor === 'meraki' ? d.bucket === 'switch' : (d.productLine === 'network' && (d.shortname || '').match(/^(US|USL|USW)/i)); }).length,
      cameras: devices.filter(function(d) { return d.vendor === 'meraki' ? d.bucket === 'camera' : false; }).length,
      sensors: devices.filter(function(d) { return d.vendor === 'meraki' ? d.bucket === 'sensor' : false; }).length,
      protect: devices.filter(function(d) { return d.vendor === 'unifi' && d.productLine === 'protect'; }).length,
      access: devices.filter(function(d) { return d.vendor === 'unifi' && d.productLine === 'access'; }).length,
      firmware_current: fwCurrent,
      firmware_outdated: fwOutdated
    };
  }

  function uptimeStr(startupTime) {
    if (!startupTime) return '—';
    var diff = Date.now() - new Date(startupTime).getTime();
    var days = Math.floor(diff / 86400000);
    var hours = Math.floor((diff % 86400000) / 3600000);
    return days > 0 ? days + 'd ' + hours + 'h' : hours + 'h';
  }

  var filterLabels = { 'online': 'Online Devices', 'offline': 'Offline Devices', 'network': 'Network Devices', 'protect': 'Protect Devices', 'access': 'Access Control', 'outdated': 'Firmware Outdated' };
  var filteredDevices = devices;
  if (filter === 'online') filteredDevices = devices.filter(function(d) { return d.status === 'online'; });
  else if (filter === 'offline') filteredDevices = devices.filter(function(d) { return d.status !== 'online'; });
  else if (filter === 'network') filteredDevices = devices.filter(function(d) { return d.productLine === 'network'; });
  else if (filter === 'protect') filteredDevices = devices.filter(function(d) { return d.productLine === 'protect'; });
  else if (filter === 'access') filteredDevices = devices.filter(function(d) { return d.productLine === 'access'; });
  else if (filter === 'outdated') filteredDevices = devices.filter(function(d) { return d.firmwareStatus !== 'upToDate'; });

  var onlinePct = s.total > 0 ? Math.round((s.online / s.total) * 100) : 0;
  var fwPct = s.total > 0 ? Math.round((s.firmware_current / s.total) * 100) : 0;

  return (
    <div className="tab-content">
      {view === 'summary' ? (
        <>
          <TabDescription text={
            'Network infrastructure managed through ' + (configuredVendors.length === 0 ? 'your provider' : configuredVendors.map(function(v) { return v === 'meraki' ? 'Cisco Meraki' : 'UniFi'; }).join(' and ')) + '. Tracks device uptime, firmware compliance, and site-level health across gateways, switches, access points, and cameras.'
          } />
          <DataFreshness live={true} />
          {multiVendor && <NetworkVendorSwitcher value={vendorFilter} onChange={setVendorFilter} vendors={configuredVendors} counts={{
            all: rawDevices.length,
            unifi: rawDevices.filter(function(d) { return d.vendor === 'unifi'; }).length,
            meraki: rawDevices.filter(function(d) { return d.vendor === 'meraki'; }).length
          }} />}
          <HealthVerdict score={fwPct} context={
            fwPct >= 90 ? 'Network infrastructure healthy — ' + fwPct + '% firmware current.' + (s.offline > 0 ? ' ' + s.offline + ' device(s) currently offline.' : '') :
            fwPct >= 70 ? (s.total - s.firmware_current) + ' device(s) running outdated firmware. Schedule a maintenance window for updates.' :
            'Network maintenance needed — ' + (s.total - s.firmware_current) + ' device(s) need firmware updates.'
          } />
          <ContentBand title="Network Status" subtitle={s.total + ' managed devices across ' + s.sites + ' site(s)'} rightContent={<><span className="live-badge">LIVE</span><button className="tab-drill-link" onClick={function() { setFilter(null); setView('devices'); }}>View All Devices ›</button></>}>
            <SegmentBar size="lg" segments={[
              { label: 'Online', value: s.online, color: '#22c55e', onClick: s.online > 0 ? function() { setFilter('online'); setView('devices'); } : undefined },
              { label: 'Offline', value: s.offline, color: '#64748b', onClick: s.offline > 0 ? function() { setFilter('offline'); setView('devices'); } : undefined },
            ]} />
            <div className="metric-tiles">
              <div className={'metric-tile ' + (fwPct >= 90 ? 'metric-tile-good' : fwPct < 70 ? 'metric-tile-alert' : '')}>
                <div className="metric-tile-label">Firmware Current</div>
                <div className="metric-tile-value">{fwPct}<span className="metric-tile-unit">%</span></div>
                <div className="metric-tile-context">{s.firmware_current} of {s.total} devices current</div>
              </div>
              <div
                className={'metric-tile' + ((s.total - s.firmware_current) > 0 ? ' metric-tile-alert' : ' metric-tile-good') + ((s.total - s.firmware_current) > 0 ? ' metric-tile-clickable' : '')}
                onClick={(s.total - s.firmware_current) > 0 ? function() { setFilter('outdated'); setView('devices'); } : undefined}
              >
                <div className="metric-tile-label">Outdated Firmware</div>
                <div className="metric-tile-value">{s.total - s.firmware_current}</div>
                <div className="metric-tile-context">{(s.total - s.firmware_current) > 0 ? 'Schedule maintenance window' : 'All firmware current'}</div>
              </div>
              <div className="metric-tile">
                <div className="metric-tile-label">Total Devices</div>
                <div className="metric-tile-value">{s.total}</div>
                <div className="metric-tile-context">Across {s.sites} site{s.sites === 1 ? '' : 's'}</div>
              </div>
              <div className="metric-tile">
                <div className="metric-tile-label">Online</div>
                <div className="metric-tile-value">{onlinePct}<span className="metric-tile-unit">%</span></div>
                <div className="metric-tile-context">{s.online} currently reachable</div>
              </div>
            </div>
          </ContentBand>
          <CollapsibleSection title="Device Inventory" defaultOpen={true}>
            <div className="stats-grid-compact">
              {s.gateways > 0 && <StatCardCompact title="Gateways" value={s.gateways} />}
              {s.access_points > 0 && <StatCardCompact title="Access Points" value={s.access_points} />}
              {s.switches > 0 && <StatCardCompact title="Switches" value={s.switches} />}
              {s.protect > 0 && <StatCardCompact title="Cameras" value={s.protect} />}
              {s.access > 0 && <StatCardCompact title="Access Control" value={s.access} />}
            </div>
          </CollapsibleSection>
          {sites.length > 1 && (
            <ContentBand title="Site Health">
              <MetricList>
                {sites.map(function(site) {
                  return <MetricListItem key={site.label} icon="◉" iconColor={site.total === site.online ? '#22c55e' : '#64748b'} label={site.label} sublabel={site.offline > 0 ? site.offline + ' currently off' : 'All active'} value={site.online + ' / ' + site.total} />;
                })}
              </MetricList>
            </ContentBand>
          )}
        </>
      ) : (
        (function() {
          // Unified bucket derivation — works for both UniFi (productLine + isConsole + shortname)
          // and Meraki (uses the .bucket field already set by the backend).
          var bucketOf = function(d) {
            if (d.vendor === 'meraki') return d.bucket || 'other';
            if (d.productLine === 'network') {
              if (d.isConsole) return 'gateway';
              if ((d.shortname || '').match(/^(U6|U7|UAP|G7)/i)) return 'access_point';
              if ((d.shortname || '').match(/^(US|USL|USW)/i)) return 'switch';
              return 'network';
            }
            if (d.productLine === 'protect') return 'camera';
            if (d.productLine === 'access')  return 'access_control';
            return 'other';
          };
          var bucketLabels = { gateway: 'Gateway', switch: 'Switch', access_point: 'Access Point', network: 'Network', camera: 'Camera', sensor: 'Sensor', access_control: 'Access', other: 'Other' };
          var bucketColors = { gateway: '#5b9ef4', switch: '#60a5fa', access_point: '#2dd4bf', network: '#5b9ef4', camera: '#a78bfa', sensor: '#22c55e', access_control: '#f5a623', other: '#94a3b8' };

          var netSearchFields = [function(d) { return d.name; }, function(d) { return d.model; }, function(d) { return d.ip; }];
          var netFilterAccessors = {
            status: function(d) { return d.status === 'online' ? 'Online' : 'Offline'; },
            type: function(d) { return bucketLabels[bucketOf(d)] || 'Other'; },
            firmware: function(d) { return d.firmwareStatus === 'upToDate' ? 'Current' : 'Outdated'; },
            site: function(d) { return d.site || '—'; }
          };
          var netAccessors = { name: function(d) { return d.name; }, model: function(d) { return d.model; }, site: function(d) { return d.site || ''; }, type: function(d) { return bucketLabels[bucketOf(d)] || ''; }, status: function(d) { return d.status; }, ip: function(d) { return d.ip || ''; } };
          var searched = filterRows(filteredDevices, netSearch, netSearchFields);
          var tableFiltered = applyFilters(searched, netFilters, netFilterAccessors);
          var sortedNet = sortRows(tableFiltered, netSort.sortField, netSort.sortDir, netAccessors);
          var netSiteGroups = {};
          filteredDevices.forEach(function(d) { netSiteGroups[d.site || '—'] = (netSiteGroups[d.site || '—'] || 0) + 1; });
          return (
        <>
          <div className="section-title">
            {filter ? filterLabels[filter] : 'All Devices'} ({sortedNet.length}{filter || Object.keys(netFilters).length > 0 ? ' of ' + devices.length : ''})
            {filter && <button className="insights-btn" style={{ marginLeft: 8, verticalAlign: 'middle' }} onClick={function() { setFilter(null); }}>Clear Filter</button>}
            <button className="insights-btn" style={{ marginLeft: 8, verticalAlign: 'middle' }} onClick={function() { setView('summary'); setFilter(null); setNetFilters({}); }}>Back to Summary</button>
          </div>
          {multiVendor && <NetworkVendorSwitcher value={vendorFilter} onChange={setVendorFilter} vendors={configuredVendors} counts={{
            all: rawDevices.length,
            unifi: rawDevices.filter(function(d) { return d.vendor === 'unifi'; }).length,
            meraki: rawDevices.filter(function(d) { return d.vendor === 'meraki'; }).length
          }} />}
          <TableSearch value={netSearch} onChange={setNetSearch} count={sortedNet.length} total={filteredDevices.length} noun="devices" onExport={function() { exportTableCSV(['Device','Model','Site','Type','Status','IP','Firmware','Uptime'], sortedNet.map(function(d) { return [d.name, d.model, d.site || '', d.productLine || '', d.status, d.ip || '', d.firmwareStatus || '', d.startupTime || '']; }), 'network-devices'); }} />
          <TableFilters filters={[
            { key: 'status', label: 'Status', options: [
              { value: 'Online', label: 'Online', color: '#22c55e', count: filteredDevices.filter(function(d) { return d.status === 'online'; }).length },
              { value: 'Offline', label: 'Offline', color: '#64748b', count: filteredDevices.filter(function(d) { return d.status !== 'online'; }).length }
            ]},
            { key: 'type', label: 'Type', options: (function() {
              // Build the type filter from whatever buckets are actually
              // present in the filtered device list, vendor-agnostic.
              var counts = {};
              filteredDevices.forEach(function(d) { var b = bucketOf(d); counts[b] = (counts[b] || 0) + 1; });
              return Object.keys(counts).map(function(b) {
                return { value: bucketLabels[b] || 'Other', label: bucketLabels[b] || 'Other', color: bucketColors[b] || '#94a3b8', count: counts[b] };
              }).filter(function(o) { return o.count > 0; }).sort(function(a, b) { return b.count - a.count; });
            })() },
            { key: 'firmware', label: 'Firmware', options: [
              { value: 'Current', label: 'Current', color: '#22c55e', count: filteredDevices.filter(function(d) { return d.firmwareStatus === 'upToDate'; }).length },
              { value: 'Outdated', label: 'Outdated', color: '#ef4444', count: filteredDevices.filter(function(d) { return d.firmwareStatus !== 'upToDate'; }).length }
            ]},
            { key: 'site', label: 'Site', options: Object.entries(netSiteGroups).sort(function(a, b) { return b[1] - a[1]; }).map(function(p) { return { value: p[0], label: p[0], count: p[1] }; }) }
          ]} active={netFilters} onChange={setNetFilters} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="name" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>Device</SortTh><SortTh field="model" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>Model</SortTh><SortTh field="site" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>Site</SortTh><SortTh field="type" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>Type</SortTh><SortTh field="status" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>Status</SortTh><SortTh field="ip" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>IP</SortTh><th>Firmware</th><th>Uptime</th></tr></thead>
              <tbody>
                {sortedNet.length === 0 && <tr><td colSpan="8" className="table-no-results">No devices match your filters</td></tr>}
                {sortedNet.map(function(d) {
                  // Compute a unified bucket per device so UniFi and Meraki get
                  // consistent type labels + colors. Meraki devices already
                  // carry a bucket field from the backend; UniFi is derived
                  // from productLine + isConsole + shortname.
                  var bucket;
                  if (d.vendor === 'meraki') {
                    bucket = d.bucket || 'other';
                  } else if (d.productLine === 'network') {
                    bucket = d.isConsole ? 'gateway' : ((d.shortname || '').match(/^(U6|U7|UAP|G7)/i) ? 'access_point' : (d.shortname || '').match(/^(US|USL|USW)/i) ? 'switch' : 'network');
                  } else if (d.productLine === 'protect') bucket = 'camera';
                  else if (d.productLine === 'access')   bucket = 'access_control';
                  else bucket = 'other';
                  var bucketStyle = {
                    gateway:        { label: 'Gateway',      bg: 'rgba(91,158,244,0.14)',  fg: '#5b9ef4' },
                    switch:         { label: 'Switch',       bg: 'rgba(96,165,250,0.12)',  fg: '#60a5fa' },
                    access_point:   { label: 'Access Point', bg: 'rgba(45,212,191,0.12)',  fg: '#2dd4bf' },
                    network:        { label: 'Network',      bg: 'rgba(91,158,244,0.12)',  fg: '#5b9ef4' },
                    camera:         { label: 'Camera',       bg: 'rgba(167,139,250,0.12)', fg: '#a78bfa' },
                    sensor:         { label: 'Sensor',       bg: 'rgba(34,197,94,0.12)',   fg: '#22c55e' },
                    access_control: { label: 'Access',       bg: 'rgba(245,166,35,0.12)',  fg: '#f5a623' },
                    other:          { label: d.productLine || 'Other', bg: 'rgba(148,163,184,0.14)', fg: '#94a3b8' }
                  }[bucket];
                  var typeLabel = bucketStyle.label;
                  return (
                    <tr key={d.id} className={d.status !== 'online' ? 'posture-row-offline' : ''}>
                      <td className="posture-device-name">
                        <span className={'status-dot ' + (d.status === 'online' ? 'status-dot-good' : 'status-dot-muted')} />
                        {d.name}
                        {multiVendor && <span className={'net-vendor-chip net-vendor-chip-' + d.vendor} title={d.vendor === 'meraki' ? 'Cisco Meraki' : 'Ubiquiti UniFi'}>{d.vendor === 'meraki' ? 'Meraki' : 'UniFi'}</span>}
                      </td>
                      <td>{d.model}</td><td>{d.site}</td>
                      <td><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: bucketStyle.bg, color: bucketStyle.fg, fontWeight: 600 }}>{typeLabel}</span></td>
                      <td><span className={'posture-status ' + (d.status === 'online' ? 'posture-online' : 'posture-offline')}>{d.status === 'online' ? 'Online' : 'Offline'}</span></td>
                      <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{d.ip || '—'}</td>
                      <td><span className={'posture-av-state ' + (d.firmwareStatus === 'upToDate' ? 'posture-av-on' : 'posture-av-off')}>{d.firmwareStatus === 'upToDate' ? 'Current' : (d.firmwareStatus || '—')}</span></td>
                      <td className="posture-date">{uptimeStr(d.startupTime)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
          );
        })()
      )}
    </div>
  );
}
// ── SIEM Tab (Blumira) ────────────────────────────────────────────
// ── SIEM Components ───────────────────────────────────────────────
function SIEMMetricTiles({ summary, findings }) {
  var s = summary;
  var now = Date.now();
  var oldestMs = null;
  (findings || []).forEach(function(f) {
    if (!f.created) return;
    var t = new Date(f.created).getTime();
    if (t > 0 && (oldestMs === null || t < oldestMs)) oldestMs = t;
  });
  var oldestAgeMin = oldestMs !== null ? (now - oldestMs) / 60000 : null;
  var oldestAlert = oldestAgeMin !== null && oldestAgeMin > 7 * 24 * 60;
  return (
    <div className="metric-tiles">
      <div className="metric-tile">
        <div className="metric-tile-label">Total Open</div>
        <div className="metric-tile-value">{s.total_open || 0}</div>
        <div className="metric-tile-context">Active findings in queue</div>
      </div>
      <div className={'metric-tile' + (s.threats > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
        <div className="metric-tile-label">Confirmed Threats</div>
        <div className="metric-tile-value">{s.threats || 0}</div>
        <div className="metric-tile-context">{s.threats > 0 ? 'Immediate response required' : 'No active threats'}</div>
      </div>
      <div className={'metric-tile' + (s.priority_1 > 0 ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">P1 Critical</div>
        <div className="metric-tile-value">{s.priority_1 || 0}</div>
        <div className="metric-tile-context">{s.priority_1 > 0 ? 'Highest severity open' : 'No P1 findings'}</div>
      </div>
      <div className={'metric-tile' + (oldestAlert ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">Oldest Open</div>
        <div className="metric-tile-value">{formatDuration(oldestAgeMin)}</div>
        <div className="metric-tile-context">{oldestAgeMin !== null ? 'Age of longest-standing finding' : 'No open findings'}</div>
      </div>
    </div>
  );
}

function SIEMTypePriorityHeatmap({ findings }) {
  if (!findings || findings.length === 0) return <div className="priority-heatmap-empty">No open findings to analyze.</div>;

  var types = [
    { key: 30, label: 'Threat', color: '#ef4444' },
    { key: 20, label: 'Suspect', color: '#f59e0b' },
    { key: 15, label: 'Risk', color: '#a78bfa' },
    { key: 10, label: 'Info', color: '#64748b' }
  ];
  var priorities = [
    { key: 1, label: 'P1 Critical' },
    { key: 2, label: 'P2 High' },
    { key: 3, label: 'P3 Medium' }
  ];

  var matrix = {};
  types.forEach(function(t) { matrix[t.key] = {}; priorities.forEach(function(p) { matrix[t.key][p.key] = 0; }); });
  findings.forEach(function(f) { if (matrix[f.type] && matrix[f.type][f.priority] !== undefined) matrix[f.type][f.priority]++; });

  var activeTypes = types.filter(function(t) { return priorities.some(function(p) { return matrix[t.key][p.key] > 0; }); });
  if (activeTypes.length === 0) return <div className="priority-heatmap-empty">No findings to analyze.</div>;

  var max = 0;
  activeTypes.forEach(function(t) { priorities.forEach(function(p) { if (matrix[t.key][p.key] > max) max = matrix[t.key][p.key]; }); });

  var isHotspot = function(typeKey, priorityKey) {
    if (typeKey === 30 && matrix[typeKey][priorityKey] > 0) return true; // any Threat
    if (typeKey === 20 && priorityKey === 1 && matrix[typeKey][priorityKey] > 0) return true; // P1 Suspect
    return false;
  };

  return (
    <div className="priority-heatmap-wrap">
      <div className="priority-heatmap" style={{ gridTemplateColumns: '120px repeat(' + priorities.length + ', 1fr)' }}>
        <div className="priority-heatmap-corner"></div>
        {priorities.map(function(p) { return <div key={p.key} className="priority-heatmap-col-label">{p.label}</div>; })}
        {activeTypes.map(function(t) {
          return (
            <React.Fragment key={t.key}>
              <div className="priority-heatmap-row-label">
                <span className="priority-heatmap-row-dot" style={{ background: t.color }} />
                {t.label}
              </div>
              {priorities.map(function(p) {
                var count = matrix[t.key][p.key];
                var hotspot = isHotspot(t.key, p.key);
                var intensity = max > 0 ? count / max : 0;
                var bg = 'transparent';
                if (count > 0) {
                  if (hotspot) bg = 'rgba(239, 68, 68, ' + (0.18 + intensity * 0.32) + ')';
                  else bg = 'rgba(91, 158, 244, ' + (0.1 + intensity * 0.3) + ')';
                }
                return (
                  <div key={p.key} className={'priority-heatmap-cell' + (count === 0 ? ' cell-empty' : '') + (hotspot ? ' cell-hotspot' : '')} style={{ background: bg }}>
                    {count > 0 ? count : <span className="cell-dash">—</span>}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      <div className="priority-heatmap-legend">
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-hotspot" /> Hotspot — immediate attention</span>
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-active" /> Open finding</span>
      </div>
    </div>
  );
}

function SIEMTab({ clientId, c }) {
  const [siemData, setSiemData] = useState(null);
  const [loading, setLoading] = useState(true);
  var siemSort = useTableSort('created', 'desc');
  const [siemSearch, setSiemSearch] = useState('');
  const [siemFilters, setSiemFilters] = useState({});

  useEffect(function() {
    setLoading(true); setSiemData(null);
    authFetch(API + '/clients/' + clientId + '/siem')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setSiemData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  // Tier-aware empty state: CMMC clients use Microsoft Sentinel (wired
  // through Atomus approval), commercial clients use Blumira. Don't tell
  // a CMMC client we'd deploy Blumira — that's not the actual play.
  if (!siemData || !siemData.enabled) {
    var isCmmc = c && c.cmmc;
    return (
      <div className="tab-content">
        <EmptyStateAction
          icon="🛡"
          title="SIEM Monitoring Not Connected"
          text={isCmmc
            ? 'Microsoft Sentinel integration is the SIEM tier for CMMC clients. Coordinated through Atomus to align with the compliance program. Once wired, this view will show security event correlation, prioritized findings, and audit-ready logs across the M365 GCC tenant.'
            : 'Blumira SIEM integration provides security event monitoring, threat detection, and prioritized findings. Open threats and investigation status will appear here once configured.'
          }
          cta={isCmmc ? 'Contact CloudByte Group + Atomus to enable Sentinel' : 'Contact CloudByte Group to enable'}
        />
      </div>
    );
  }

  var s = siemData.summary;
  var findings = siemData.findings || [];

  function timeAgo(dateStr) {
    if (!dateStr) return '—';
    var diff = Date.now() - new Date(dateStr).getTime();
    var mins = Math.floor(diff / 60000);
    if (mins < 60) return mins + 'm ago';
    var hours = Math.floor(mins / 60);
    if (hours < 24) return hours + 'h ago';
    var days = Math.floor(hours / 24);
    return days < 30 ? days + 'd ago' : Math.floor(days / 30) + 'mo ago';
  }

  var priorityColors = { 1: '#ef4444', 2: '#f59e0b', 3: '#60a5fa' };
  var priorityLabels = { 1: 'P1', 2: 'P2', 3: 'P3' };
  var typeColors = { 30: '#ef4444', 20: '#f59e0b', 15: '#a78bfa', 10: '#64748b' };

  var typeLabels = { 30: 'Threat', 20: 'Suspect', 15: 'Risk', 10: 'Info' };
  var filteredFindings = applyFilters(
    filterRows(findings, siemSearch, [function(f) { return f.name; }, function(f) { return f.category_name; }, function(f) { return f.summary; }]),
    siemFilters,
    { priority: function(f) { return f.priority; }, type: function(f) { return f.type; } }
  );
  var sortedFindings = sortRows(filteredFindings, siemSort.sortField, siemSort.sortDir, {
    priority: function(f) { return f.priority; }, type: function(f) { return f.type; },
    name: function(f) { return f.name; }, category_name: function(f) { return f.category_name; },
    created: function(f) { return f.created; }
  });

  return (
    <div className="tab-content">
      <TabDescription text="Security event monitoring via Blumira SIEM. Findings are prioritized by severity — threats require immediate action, suspects need investigation, and risks track ongoing exposure." />
      <DataFreshness live={true} />
      <ContentBand title="SIEM Overview — Blumira" rightContent={<span className="live-badge">LIVE</span>}>
        <SIEMMetricTiles summary={s} findings={findings} />
      </ContentBand>
      <ContentBand title="Type × Priority Matrix" subtitle="Hotspots highlight threats and P1 suspects — critical findings surface instantly">
        <SIEMTypePriorityHeatmap findings={findings} />
      </ContentBand>
      {Object.keys(s.categories).length > 0 && (
        <ContentBand title="Finding Categories">
          <QuickStats items={Object.entries(s.categories).map(function([cat, count]) { return { label: cat, value: count }; })} />
        </ContentBand>
      )}
      {findings.length > 0 && (
        <>
          <div className="section-title">Open Findings ({findings.length}{s.total_open > findings.length ? ' of ' + s.total_open : ''})</div>
          <TableSearch value={siemSearch} onChange={setSiemSearch} count={sortedFindings.length} total={findings.length} noun="findings" onExport={function() { exportTableCSV(['Priority','Type','Finding','Category','Detected','URL'], sortedFindings.map(function(f) { return [priorityLabels[f.priority] || 'P'+f.priority, f.type_name||'', f.name, f.category_name||'', f.created ? new Date(f.created).toLocaleDateString() : '', f.url||'']; }), 'siem-findings'); }} />
          <TableFilters filters={[
            { key: 'priority', label: 'Priority', options: [
              { value: '1', label: 'P1 Critical', color: '#ef4444', count: findings.filter(function(f){return f.priority===1;}).length },
              { value: '2', label: 'P2 High', color: '#f59e0b', count: findings.filter(function(f){return f.priority===2;}).length },
              { value: '3', label: 'P3 Medium', color: '#60a5fa', count: findings.filter(function(f){return f.priority===3;}).length },
            ]},
            { key: 'type', label: 'Type', options: [
              { value: '30', label: 'Threat', color: '#ef4444', count: findings.filter(function(f){return f.type===30;}).length },
              { value: '20', label: 'Suspect', color: '#f59e0b', count: findings.filter(function(f){return f.type===20;}).length },
              { value: '15', label: 'Risk', color: '#a78bfa', count: findings.filter(function(f){return f.type===15;}).length },
            ]},
          ]} active={siemFilters} onChange={setSiemFilters} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead>
                <tr>
                  <SortTh field="priority" current={siemSort.sortField} dir={siemSort.sortDir} onSort={siemSort.toggleSort}>Priority</SortTh>
                  <SortTh field="type" current={siemSort.sortField} dir={siemSort.sortDir} onSort={siemSort.toggleSort}>Type</SortTh>
                  <SortTh field="name" current={siemSort.sortField} dir={siemSort.sortDir} onSort={siemSort.toggleSort}>Finding</SortTh>
                  <SortTh field="category_name" current={siemSort.sortField} dir={siemSort.sortDir} onSort={siemSort.toggleSort}>Category</SortTh>
                  <SortTh field="created" current={siemSort.sortField} dir={siemSort.sortDir} onSort={siemSort.toggleSort}>Detected</SortTh>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {sortedFindings.map(function(f, idx) {
                  return (
                    <tr key={f.finding_id || f.short_id || idx}>
                      <td><span className={'status-dot ' + (f.priority === 1 ? 'status-dot-critical' : f.priority === 2 ? 'status-dot-warning' : 'status-dot-info')} /><span className="inline-badge" style={{ backgroundColor: priorityColors[f.priority] || '#64748b' }}>{priorityLabels[f.priority] || 'P' + f.priority}</span></td>
                      <td><span className="inline-badge" style={{ backgroundColor: typeColors[f.type] || '#64748b' }}>{f.type_name || 'Unknown'}</span></td>
                      <td className="posture-device-name" style={{ maxWidth: 350 }}>
                        <div>{f.name}</div>
                        {f.summary && <div className="table-sub">{f.summary.length > 120 ? f.summary.substring(0, 120) + '...' : f.summary}</div>}
                      </td>
                      <td>{f.category_name || '—'}</td>
                      <td className="posture-date">{timeAgo(f.created)}</td>
                      <td>{f.url ? <a href={f.url} target="_blank" rel="noopener noreferrer" className="table-link">View ↗</a> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Ticket Command Center Components ──────────────────────────────
function TicketFlowPipeline({ tickets, metrics }) {
  var stages = [
    { key: 'new', label: 'New', sublabel: 'Awaiting CloudByte', count: metrics.waitingForSupport || 0, statusParent: 1000, threshold: 24 },
    { key: 'inprogress', label: 'In Progress', sublabel: 'Actively working', count: metrics.inProgress || 0, statusParent: 2000, threshold: 72 },
    { key: 'waiting', label: 'Waiting', sublabel: 'On client response', count: metrics.waitingForUser || 0, statusParent: 3000, threshold: 120 },
    { key: 'pending', label: 'Pending', sublabel: 'Scheduled / blocked', count: metrics.pending || 0, statusParent: 4000, threshold: 72 },
    { key: 'closed', label: 'Closed', sublabel: 'Resolved', count: metrics.closed || 0, statusParent: null }
  ];
  return (
    <div className="ticket-flow">
      {stages.map(function(stage, i) {
        var aged = 0;
        if (stage.threshold && stage.count > 0 && stage.statusParent) {
          var thresholdMs = stage.threshold * 3600 * 1000;
          aged = tickets.filter(function(t) {
            return t.statusParent === stage.statusParent && t.created && (Date.now() - new Date(t.created).getTime()) > thresholdMs;
          }).length;
        }
        var isEmpty = stage.count === 0;
        return (
          <React.Fragment key={stage.key}>
            <div className={'ticket-flow-stage ticket-flow-stage-' + stage.key + (aged > 0 ? ' ticket-flow-stage-aging' : '') + (isEmpty ? ' ticket-flow-stage-empty' : '')}>
              <div className="ticket-flow-stage-label">{stage.label}</div>
              <div className="ticket-flow-stage-count">{stage.count}</div>
              <div className="ticket-flow-stage-sub">{stage.sublabel}</div>
              {aged > 0 && <div className="ticket-flow-stage-aging-badge">{aged} over {stage.threshold >= 48 ? (stage.threshold / 24) + 'd' : stage.threshold + 'h'}</div>}
            </div>
            {i < stages.length - 1 && <div className="ticket-flow-connector"><span className="ticket-flow-arrow">›</span></div>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function PriorityStatusHeatmap({ tickets }) {
  var priorities = ['HIGH', 'MEDIUM', 'LOW', 'NONE'];
  var statuses = [
    { key: 'new', label: 'New', parents: [1000] },
    { key: 'inprog', label: 'In Progress', parents: [2000] },
    { key: 'waiting', label: 'Waiting', parents: [3000] },
    { key: 'pending', label: 'Pending', parents: [4000] },
    { key: 'closed', label: 'Closed', parents: [5000, 6000] }
  ];
  var priorityLabels = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low', NONE: 'Unset' };
  var priorityColors = { HIGH: '#ef4444', MEDIUM: '#f59e0b', LOW: '#22c55e', NONE: '#94a3b8' };

  var matrix = {};
  priorities.forEach(function(p) { matrix[p] = {}; statuses.forEach(function(s) { matrix[p][s.key] = 0; }); });
  var activeMax = 0;
  tickets.forEach(function(t) {
    var priority = priorities.indexOf(t.priority) >= 0 ? t.priority : 'NONE';
    var statusKey = null;
    statuses.forEach(function(s) { if (s.parents.indexOf(t.statusParent) >= 0) statusKey = s.key; });
    if (statusKey) {
      matrix[priority][statusKey]++;
      if (statusKey !== 'closed' && matrix[priority][statusKey] > activeMax) activeMax = matrix[priority][statusKey];
    }
  });

  var isHotspot = function(priority, statusKey) {
    if (statusKey === 'closed') return false;
    if (priority === 'HIGH' && matrix[priority][statusKey] > 0) return true;
    if (priority === 'MEDIUM' && (statusKey === 'new' || statusKey === 'waiting' || statusKey === 'pending') && matrix[priority][statusKey] > 2) return true;
    return false;
  };

  return (
    <div className="priority-heatmap-wrap">
      <div className="priority-heatmap">
        <div className="priority-heatmap-corner"></div>
        {statuses.map(function(s) { return <div key={s.key} className="priority-heatmap-col-label">{s.label}</div>; })}
        {priorities.map(function(p) {
          return (
            <React.Fragment key={p}>
              <div className="priority-heatmap-row-label">
                <span className="priority-heatmap-row-dot" style={{ background: priorityColors[p] }} />
                {priorityLabels[p]}
              </div>
              {statuses.map(function(s) {
                var count = matrix[p][s.key];
                var intensity = activeMax > 0 && s.key !== 'closed' ? count / activeMax : 0;
                var hotspot = isHotspot(p, s.key);
                var bg = 'transparent';
                if (count > 0) {
                  if (hotspot) bg = 'rgba(239, 68, 68, ' + (0.18 + intensity * 0.32) + ')';
                  else if (s.key === 'closed') bg = 'rgba(34, 197, 94, 0.08)';
                  else bg = 'rgba(91, 158, 244, ' + (0.1 + intensity * 0.3) + ')';
                }
                return (
                  <div key={s.key} className={'priority-heatmap-cell' + (count === 0 ? ' cell-empty' : '') + (hotspot ? ' cell-hotspot' : '')} style={{ background: bg }}>
                    {count > 0 ? count : <span className="cell-dash">—</span>}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      <div className="priority-heatmap-legend">
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-hotspot" /> Hotspot — needs attention</span>
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-active" /> Active</span>
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-closed" /> Resolved</span>
      </div>
    </div>
  );
}

function SourceChannelCards({ bySource }) {
  var sourceMeta = {
    END_USER: { label: 'End User Portal', icon: '\u25F0', color: '#5b9ef4', desc: 'Self-service submissions' },
    EMAIL: { label: 'Email', icon: '\u2709', color: '#a78bfa', desc: 'Direct email channel' },
    TECHNICIAN: { label: 'Technician', icon: '\u2699', color: '#22c55e', desc: 'Created by CloudByte' }
  };
  var entries = Object.entries(bySource);
  var total = entries.reduce(function(s, e) { return s + e[1]; }, 0);
  return (
    <div className="source-cards">
      {entries.map(function(pair) {
        var key = pair[0]; var count = pair[1];
        var meta = sourceMeta[key] || { label: key, icon: '\u25CF', color: '#64748b', desc: '' };
        var pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div key={key} className="source-card">
            <div className="source-card-icon" style={{ background: meta.color + '18', color: meta.color }}>{meta.icon}</div>
            <div className="source-card-body">
              <div className="source-card-name">{meta.label}</div>
              <div className="source-card-desc">{meta.desc}</div>
              <div className="source-card-stats">
                <span className="source-card-count">{count}</span>
                <span className="source-card-pct">{pct}% of total</span>
              </div>
              <div className="source-card-bar"><div className="source-card-bar-fill" style={{ width: pct + '%', background: meta.color }} /></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatDuration(minutes) {
  if (minutes == null || isNaN(minutes)) return '—';
  if (minutes < 60) return Math.max(0, Math.round(minutes)) + 'm';
  if (minutes < 48 * 60) return Math.round(minutes / 60) + 'h';
  var days = minutes / (60 * 24);
  return (days >= 10 ? Math.round(days) : days.toFixed(1)) + 'd';
}

function SmartMetricTiles({ metrics, tickets }) {
  var m = metrics;
  var active = (m.open || 0);
  var stalled = m.waitingForSupport || 0;
  var now = Date.now();

  // Time to Acknowledge — avg age of tickets currently in "New" stage (parentId 1000)
  var newTickets = (tickets || []).filter(function(t) { return t.statusParent === 1000 && t.created; });
  var tta = null;
  if (newTickets.length > 0) {
    var totalAgeMin = newTickets.reduce(function(sum, t) { return sum + (now - new Date(t.created).getTime()) / 60000; }, 0);
    tta = totalAgeMin / newTickets.length;
  }
  var ttaAlert = tta !== null && tta > 24 * 60; // alert if avg > 24h

  // MTTR — avg time from creation to lastUpdate for closed tickets
  var closedWithTimes = (tickets || []).filter(function(t) {
    return (t.statusParent === 5000 || t.statusParent === 6000) && t.created && t.lastUpdate;
  });
  var mttr = null;
  if (closedWithTimes.length > 0) {
    var totalResMin = closedWithTimes.reduce(function(sum, t) {
      return sum + (new Date(t.lastUpdate).getTime() - new Date(t.created).getTime()) / 60000;
    }, 0);
    mttr = totalResMin / closedWithTimes.length;
  }

  return (
    <div className="metric-tiles">
      <div className="metric-tile">
        <div className="metric-tile-label">Active Queue</div>
        <div className="metric-tile-value">{active}</div>
        <div className="metric-tile-context">{m.closed || 0} resolved all-time</div>
      </div>
      <div className={'metric-tile' + (stalled > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
        <div className="metric-tile-label">Awaiting CloudByte</div>
        <div className="metric-tile-value">{stalled}</div>
        <div className="metric-tile-context">{stalled > 0 ? 'Response required' : 'Queue is clear'}</div>
      </div>
      <div className={'metric-tile' + (ttaAlert ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">Time to Acknowledge</div>
        <div className="metric-tile-value">{formatDuration(tta)}</div>
        <div className="metric-tile-context">{newTickets.length > 0 ? 'Avg wait for first CloudByte response' : 'No new tickets in queue'}</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Mean Time to Resolution</div>
        <div className="metric-tile-value">{formatDuration(mttr)}</div>
        <div className="metric-tile-context">{mttr !== null ? 'Avg open → close wall-clock' : 'Awaiting resolution data'}</div>
      </div>
    </div>
  );
}

// ── Support Tab (NinjaOne FedRAMP Tickets) ────────────────────────
function SupportTab({ clientId, live }) {
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('summary');
  var tixSort = useTableSort('id', 'desc');
  const [tixSearch, setTixSearch] = useState('');
  const [tixFilters, setTixFilters] = useState({});

  useEffect(function() {
    setLoading(true); setTicketData(null); setView('summary');
    authFetch(API + '/clients/' + clientId + '/tickets')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setTicketData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!ticketData || !ticketData.metrics) return <EmptyStateAction icon="🎫" title="No Ticket Data" text="Support tickets from NinjaOne will appear here once the ticketing integration is active. You'll see open items, response times, and priority distribution." cta="Contact CloudByte Group to enable" />;

  var m = ticketData.metrics;
  var statusColor = function(parentId) {
    if (parentId === 1000) return '#f59e0b'; if (parentId === 2000) return '#60a5fa';
    if (parentId === 3000) return '#a78bfa'; if (parentId === 4000) return '#64748b';
    if (parentId === 5000 || parentId === 6000) return '#22c55e'; return '#94a3b8';
  };

  return (
    <div className="tab-content">
      {view === 'summary' ? (
        <>
          <TabDescription text="Active and historical support tickets from NinjaOne's FedRAMP-authorized ticketing system. Tracks open items, resolution times, and request sources for SLA monitoring." />
          <DataFreshness live={true} />
          {((m.waitingForSupport || 0) + (m.pending || 0)) > 0 && (function() {
            var stalledTix = ticketData.tickets.filter(function(t) { return t.statusParent === 1000 || t.statusParent === 4000; });
            var bodyLines = ['Hi CloudByte Support,', '', 'The following ticket(s) appear stalled and may need attention:', ''];
            stalledTix.forEach(function(t) { bodyLines.push('• Ticket #' + t.id + ' — ' + t.summary); bodyLines.push('  Status: ' + t.status + ' | Priority: ' + (t.priority === 'NONE' ? 'Unset' : t.priority) + (t.assignee ? ' | Assigned: ' + t.assignee : ' | Unassigned')); bodyLines.push(''); });
            bodyLines.push('Could you provide an update on these items?', '', 'Thank you');
            return (
            <a href={'mailto:support@cloudbytegroup.com?subject=' + encodeURIComponent('Aurelius — ' + stalledTix.length + ' stalled support ticket(s) for review') + '&body=' + encodeURIComponent(bodyLines.join('\n'))} className="verdict-mailto" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, marginBottom: 4, padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, color: '#60a5fa', background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.25)', textDecoration: 'none', cursor: 'pointer' }}>✉ Email CloudByte Support about stalled tickets</a>
            );
          })()}
          <ContentBand title="Ticket Flow — NinjaOne FedRAMP" subtitle="Live pipeline across all ticket stages" rightContent={<>{!IS_DEMO_VIEW && <a href="https://fed.ninjarmm.com/auth/#/" target="_blank" rel="noopener noreferrer" className="tab-drill-link" style={{ textDecoration: 'none' }}>Open NinjaOne ↗</a>}<span className="live-badge">LIVE</span><button className="tab-drill-link" onClick={function() { setView('tickets'); }}>View All Tickets ›</button></>}>
            <TicketFlowPipeline tickets={ticketData.tickets} metrics={m} />
            <SmartMetricTiles metrics={m} tickets={ticketData.tickets} />
          </ContentBand>
          <ContentBand title="Priority × Status Matrix" subtitle="Hotspots highlight tickets needing attention by priority and stage">
            <PriorityStatusHeatmap tickets={ticketData.tickets} />
          </ContentBand>
          <ContentBand title="Ticket Channels" subtitle="Where tickets originate">
            <SourceChannelCards bySource={m.bySource} />
          </ContentBand>
        </>
      ) : (
        (function() {
          var tixFiltered = applyFilters(
            filterRows(ticketData.tickets, tixSearch, [function(t){return t.summary;}, function(t){return t.requester;}, function(t){return t.assignee;}, function(t){return '#'+t.id;}]),
            tixFilters,
            { status: function(t) { return t.status; }, priority: function(t) { return t.priority; } }
          );
          var tixSorted = sortRows(tixFiltered, tixSort.sortField, tixSort.sortDir, {
            id: function(t){return t.id;}, summary: function(t){return t.summary;}, status: function(t){return t.status;},
            priority: function(t){return t.priority;}, requester: function(t){return t.requester;},
            assignee: function(t){return t.assignee;}, timeTrackedMinutes: function(t){return t.timeTrackedMinutes;},
            created: function(t){return new Date(t.created).getTime();}
          });
          var statuses = {}; var priorities = {};
          ticketData.tickets.forEach(function(t) { statuses[t.status] = (statuses[t.status]||0)+1; priorities[t.priority] = (priorities[t.priority]||0)+1; });
          return (
        <>
          <div className="section-title">
            All Tickets ({ticketData.tickets.length})
            <button className="insights-btn" style={{ marginLeft: 12, verticalAlign: 'middle' }} onClick={function() { setView('summary'); }}>Back to Summary</button>
          </div>
          <TableSearch value={tixSearch} onChange={setTixSearch} count={tixSorted.length} total={ticketData.tickets.length} noun="tickets" onExport={function() { exportTableCSV(['ID','Summary','Status','Priority','Requester','Assignee','Time (min)','Created'], tixSorted.map(function(t) { return [t.id, t.summary, t.status, t.priority === 'NONE' ? '' : t.priority, t.requester || '', t.assignee || '', t.timeTrackedMinutes || '', t.created ? new Date(t.created).toLocaleDateString() : '']; }), 'support-tickets'); }} />
          <TableFilters filters={[
            { key: 'status', label: 'Status', options: Object.entries(statuses).map(function([s,c]) { return { value: s, label: s, count: c }; }) },
            { key: 'priority', label: 'Priority', options: Object.entries(priorities).filter(function([p]) { return p !== 'NONE'; }).map(function([p,c]) {
              var color = p === 'HIGH' || p === 'URGENT' ? '#ef4444' : p === 'MEDIUM' ? '#f59e0b' : p === 'LOW' ? '#22c55e' : '#64748b';
              return { value: p, label: p, color: color, count: c };
            }) },
          ]} active={tixFilters} onChange={setTixFilters} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead>
                <tr>
                  <SortTh field="id" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}>ID</SortTh>
                  <SortTh field="summary" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}>Summary</SortTh>
                  <SortTh field="status" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}>Status</SortTh>
                  <SortTh field="priority" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}>Priority</SortTh>
                  <SortTh field="requester" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}>Requester</SortTh>
                  <SortTh field="assignee" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}>Assignee</SortTh>
                  <SortTh field="timeTrackedMinutes" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}>Time</SortTh>
                  <SortTh field="created" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}>Created</SortTh>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tixSorted.length === 0 && <tr><td colSpan="9" className="table-no-results">No tickets match your search</td></tr>}
                {tixSorted.map(function(t) {
                  var isClosed = t.statusParent === 5000 || t.statusParent === 6000;
                  return (
                    <tr key={t.id} className={isClosed ? 'posture-row-offline' : ''}>
                      <td>#{t.id}</td>
                      <td className="posture-device-name" style={{ maxWidth: 300 }}><span className={'status-dot ' + (t.statusParent === 1000 ? 'status-dot-warning' : isClosed ? 'status-dot-muted' : 'status-dot-info')} />{t.summary}</td>
                      <td><span className="posture-status" style={{ color: statusColor(t.statusParent) }}>{t.status}</span></td>
                      <td>{t.priority === 'NONE' ? '—' : t.priority}</td>
                      <td>{t.requester || '—'}</td><td>{t.assignee || 'Unassigned'}</td>
                      <td>{t.timeTrackedMinutes > 0 ? t.timeTrackedMinutes + 'm' : '—'}</td>
                      <td className="posture-date">{t.created ? new Date(t.created).toLocaleDateString() : '—'}</td>
                      <td><a href={'mailto:support@cloudbytegroup.com?subject=' + encodeURIComponent('Aurelius Ticket #' + t.id + ' — ' + t.summary) + '&body=' + encodeURIComponent('Hi CloudByte Support,\n\nI\'d like to follow up on this ticket:\n\n• Ticket #' + t.id + ' — ' + t.summary + '\n  Status: ' + t.status + '\n  Priority: ' + (t.priority === 'NONE' ? 'Unset' : t.priority) + '\n  Assignee: ' + (t.assignee || 'Unassigned') + '\n  Created: ' + (t.created ? new Date(t.created).toLocaleDateString() : 'Unknown') + '\n\nCould you provide an update?\n\nThank you')} className="table-link" title="Email CloudByte Support about this ticket">✉</a></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
          ); })()
      )}
    </div>
  );
}

// ── Projects Tab (Asana) ──────────────────────────────────────────
// ── Projects v2 helper components ────────────────────────────────────
// Goal: replace the bare-name / bare-date / disconnected-pill renderings
// with narrative chips a client can read at a glance. Every avatar and
// date carries semantic meaning rather than asking the reader to infer.

// Deterministic color from email (or name fallback) so the same person
// always gets the same circle. Avoids reseeding on each render.
function _projInitials(name) {
  if (!name) return '?';
  var parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function _projColorPair(seed) {
  // Hash → hue. Pair: vivid foreground tone + matching translucent bg so
  // we can tint dark + light themes consistently with one variable.
  var s = seed || 'unknown';
  var h = 0;
  for (var i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  var hue = Math.abs(h) % 360;
  return { hue: hue, fg: 'hsl(' + hue + ', 65%, 65%)', bg: 'hsla(' + hue + ', 60%, 50%, 0.18)', border: 'hsla(' + hue + ', 60%, 55%, 0.35)' };
}
function ProjAvatar({ name, email, size, title }) {
  var sz = size || 26;
  if (!name) {
    return <span className="proj-avatar proj-avatar-empty" style={{ width: sz, height: sz, fontSize: Math.round(sz * 0.42) }} title={title || 'Unassigned'}>?</span>;
  }
  var c = _projColorPair(email || name);
  return (
    <span
      className="proj-avatar"
      style={{ width: sz, height: sz, fontSize: Math.round(sz * 0.42), color: c.fg, background: c.bg, borderColor: c.border }}
      title={title || (email ? name + ' · ' + email : name)}
    >{_projInitials(name)}</span>
  );
}

// Narrative date chip — never renders a bare timestamp. Always says what
// the date *means* so a client can scan the row and understand state.
function ProjDueChip({ task }) {
  if (!task) return null;
  if (task.dueState === 'done') {
    var copy = task.completedAgo == null
      ? 'Done'
      : task.completedAgo === 0 ? 'Done today' : 'Done ' + task.completedAgo + 'd ago';
    return <span className="proj-chip proj-chip-done" title={task.completedAt ? new Date(task.completedAt).toLocaleString() : ''}><span className="proj-chip-dot" />{copy}</span>;
  }
  if (task.dueState === 'overdue') {
    var od = task.daysOverdue || 0;
    return <span className="proj-chip proj-chip-overdue" title={task.due ? 'Was due ' + new Date(task.due).toLocaleDateString() : ''}><span className="proj-chip-dot" />Overdue {od}d</span>;
  }
  if (task.dueState === 'due-soon') {
    var u = task.daysUntilDue;
    var label = u === 0 ? 'Due today' : u === 1 ? 'Due tomorrow' : 'Due in ' + u + 'd';
    return <span className="proj-chip proj-chip-due-soon" title={task.due ? new Date(task.due).toLocaleDateString() : ''}><span className="proj-chip-dot" />{label}</span>;
  }
  if (task.dueState === 'on-track') {
    return <span className="proj-chip proj-chip-on-track" title={task.due ? new Date(task.due).toLocaleDateString() : ''}><span className="proj-chip-dot" />Due in {task.daysUntilDue}d</span>;
  }
  return <span className="proj-chip proj-chip-no-due"><span className="proj-chip-dot" />No due date</span>;
}

// Single task row — avatar + name + metadata row beneath, optional inset
// subtask checklist. Replaces the flat field-dump that was the core
// readability problem.
function ProjTaskCard({ task, depth }) {
  var [openSubs, setOpenSubs] = useState(false);
  var d = depth || 0;
  var hasSubs = (task.subtaskCount || 0) > 0;
  var subDoneCount = task.subtasks ? task.subtasks.filter(function(s) { return s.completed; }).length : null;
  var subTotal = task.subtaskCount || (task.subtasks ? task.subtasks.length : 0);

  var rowCls = 'proj-task'
    + (task.dueState === 'overdue' ? ' proj-task-overdue' : '')
    + (task.dueState === 'due-soon' ? ' proj-task-due-soon' : '')
    + (task.completed ? ' proj-task-done' : '')
    + (d > 0 ? ' proj-task-sub' : '');

  return (
    <div className={rowCls}>
      <div className="proj-task-row">
        <div className="proj-task-left">
          <ProjAvatar name={task.assignee && task.assignee.name} email={task.assignee && task.assignee.email} size={d > 0 ? 22 : 28} title={task.assignee ? (task.assignee.name + (task.assignee.email ? ' · ' + task.assignee.email : '')) : 'Unassigned'} />
          <div className="proj-task-text">
            <div className="proj-task-title">
              {task.completed && <span className="proj-task-check" aria-hidden="true">✓</span>}
              <span className={task.completed ? 'proj-task-name strike' : 'proj-task-name'}>{task.name}</span>
              {task.permalink && <a className="proj-task-link" href={task.permalink} target="_blank" rel="noopener noreferrer" title="Open in Asana">↗</a>}
            </div>
            <div className="proj-task-meta">
              {task.assignee && task.assignee.name
                ? <span className="proj-task-meta-piece">{task.assignee.name}</span>
                : <span className="proj-task-meta-piece muted">Unassigned</span>
              }
              <ProjDueChip task={task} />
              {task.customFields && task.customFields.map(function(cf, ci) {
                return <span key={ci} className="proj-task-meta-cf"><span className="proj-task-meta-cf-name">{cf.name}</span>{cf.value}</span>;
              })}
              {hasSubs && (
                <button
                  type="button"
                  className="proj-task-subs-toggle"
                  onClick={function() { setOpenSubs(!openSubs); }}
                  title={task.subtasks ? 'Toggle subtasks' : 'Open in Asana to view subtasks'}
                >
                  <span className="proj-task-subs-icon">{openSubs ? '▾' : '▸'}</span>
                  {subDoneCount != null
                    ? subDoneCount + ' / ' + subTotal + ' subtasks'
                    : subTotal + ' subtask' + (subTotal === 1 ? '' : 's')}
                </button>
              )}
            </div>
            {task.description && (
              <div className="proj-task-desc">{task.description}</div>
            )}
          </div>
        </div>
      </div>

      {hasSubs && openSubs && task.subtasks && task.subtasks.length > 0 && (
        <div className="proj-subtask-list">
          {task.subtasks.map(function(st) {
            return (
              <div key={st.gid} className={'proj-subtask' + (st.completed ? ' done' : '') + (st.dueState === 'overdue' ? ' overdue' : '')}>
                <span className="proj-subtask-check" aria-hidden="true">{st.completed ? '✓' : '○'}</span>
                <span className={st.completed ? 'proj-subtask-name strike' : 'proj-subtask-name'}>{st.name}</span>
                {st.assignee && st.assignee.name && <ProjAvatar name={st.assignee.name} email={st.assignee.email} size={18} />}
                <ProjDueChip task={st} />
                {st.permalink && <a className="proj-task-link" href={st.permalink} target="_blank" rel="noopener noreferrer" title="Open in Asana">↗</a>}
              </div>
            );
          })}
        </div>
      )}
      {hasSubs && openSubs && (!task.subtasks || task.subtasks.length === 0) && (
        <div className="proj-subtask-empty">Subtasks not loaded — open in Asana for the full list.</div>
      )}
    </div>
  );
}

// 3-stat health ribbon at top of expansion. Translates the project's
// numeric state into "what should I look at next" callouts.
function ProjHealthRibbon({ project }) {
  var nextDeliverable = project.nextMilestone;
  var risks = project.overdueTasks || 0;
  var dueSoon = project.dueSoonTasks || 0;

  // "Active focus" — phase currently in flight (from the lifecycle spine)
  var activePhase = (project.phases || []).find(function(p) { return p.state === 'active'; });

  return (
    <div className="proj-ribbon">
      <div className={'proj-ribbon-card' + (nextDeliverable ? '' : ' empty')}>
        <div className="proj-ribbon-label">Next milestone</div>
        {nextDeliverable
          ? (
            <>
              <div className="proj-ribbon-value">{nextDeliverable.name}</div>
              <div className="proj-ribbon-context">
                {nextDeliverable.dueState === 'overdue'
                  ? <span className="proj-ribbon-warn">{nextDeliverable.daysOverdue}d overdue</span>
                  : nextDeliverable.due
                    ? (nextDeliverable.daysUntilDue === 0 ? 'Today' : nextDeliverable.daysUntilDue === 1 ? 'Tomorrow' : 'In ' + nextDeliverable.daysUntilDue + ' days')
                    : 'No date set'
                }
                {nextDeliverable.assignee && nextDeliverable.assignee.name && ' · ' + nextDeliverable.assignee.name}
              </div>
            </>
          )
          : <div className="proj-ribbon-value muted">No milestones set</div>
        }
      </div>

      <div className={'proj-ribbon-card' + (activePhase ? '' : ' empty')}>
        <div className="proj-ribbon-label">Active focus</div>
        {activePhase
          ? (
            <>
              <div className="proj-ribbon-value">{activePhase.name}</div>
              <div className="proj-ribbon-context">
                {activePhase.tasks.length} open
                {activePhase.dueSoon > 0 && ' · ' + activePhase.dueSoon + ' due soon'}
                {activePhase.overdue > 0 && <> · <span className="proj-ribbon-warn">{activePhase.overdue} overdue</span></>}
              </div>
            </>
          )
          : <div className="proj-ribbon-value muted">All phases complete</div>
        }
      </div>

      <div className={'proj-ribbon-card' + (risks > 0 ? ' alert' : dueSoon > 0 ? ' caution' : '')}>
        <div className="proj-ribbon-label">{risks > 0 ? 'Risks' : 'Outlook'}</div>
        {risks > 0
          ? (
            <>
              <div className="proj-ribbon-value">{risks} overdue</div>
              <div className="proj-ribbon-context">{dueSoon > 0 ? dueSoon + ' more due within 7 days' : 'No upcoming due dates'}</div>
            </>
          )
          : dueSoon > 0
            ? (
              <>
                <div className="proj-ribbon-value">{dueSoon} due soon</div>
                <div className="proj-ribbon-context">Within 7 days · no overdue items</div>
              </>
            )
            : (
              <>
                <div className="proj-ribbon-value">On track</div>
                <div className="proj-ribbon-context">No overdue or due-soon items</div>
              </>
            )
        }
      </div>
    </div>
  );
}

// Lifecycle spine — phase progression with embedded counts + state pill.
function ProjLifecycle({ phases }) {
  if (!phases || phases.length === 0) return null;
  return (
    <div className="proj-lifecycle">
      {phases.map(function(phase, idx) {
        var pct = phase.total > 0 ? Math.round((phase.completed / phase.total) * 100) : 0;
        var stateLabel = phase.state === 'complete' ? 'Complete' : phase.state === 'active' ? 'In progress' : 'Upcoming';
        return (
          <div key={phase.gid} className={'proj-phase proj-phase-' + phase.state}>
            <div className="proj-phase-head">
              <div className="proj-phase-marker">
                <span className="proj-phase-dot" />
                {idx < phases.length - 1 && <span className="proj-phase-line" />}
              </div>
              <div className="proj-phase-headline">
                <div className="proj-phase-name">
                  {phase.name}
                  <span className="proj-phase-state">{stateLabel}</span>
                </div>
                <div className="proj-phase-summary">
                  <span className="proj-phase-progress">{phase.completed} of {phase.total} done</span>
                  {phase.overdue > 0 && <span className="proj-phase-overdue">{phase.overdue} overdue</span>}
                  {phase.dueSoon > 0 && <span className="proj-phase-duesoon">{phase.dueSoon} due soon</span>}
                  <span className="proj-phase-pct">{pct}%</span>
                </div>
                <div className="proj-phase-track"><div className="proj-phase-track-fill" style={{ width: pct + '%' }} /></div>
              </div>
            </div>
            {(phase.tasks.length > 0 || (phase.state === 'complete' && phase.recentlyCompleted.length > 0)) && (
              <div className="proj-phase-body">
                {phase.tasks.map(function(t) { return <ProjTaskCard key={t.gid} task={t} />; })}
                {phase.state === 'complete' && phase.recentlyCompleted.length > 0 && (
                  <div className="proj-phase-recent">
                    <div className="proj-phase-recent-label">Recently completed</div>
                    {phase.recentlyCompleted.slice(0, 5).map(function(t) { return <ProjTaskCard key={t.gid} task={t} />; })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Milestones strip — distinct visual treatment from regular tasks.
function ProjMilestonesStrip({ milestones }) {
  if (!milestones || milestones.length === 0) return null;
  return (
    <div className="proj-section">
      <div className="proj-section-head">Milestones</div>
      <div className="proj-milestones">
        {milestones.map(function(m) {
          var stateCls = m.completed ? 'done' : m.dueState === 'overdue' ? 'overdue' : m.dueState === 'due-soon' ? 'due-soon' : 'upcoming';
          var dateCopy = m.completed
            ? (m.completedAgo == null ? 'Completed' : m.completedAgo === 0 ? 'Completed today' : 'Completed ' + m.completedAgo + 'd ago')
            : m.dueState === 'overdue' ? m.daysOverdue + 'd overdue'
            : m.dueState === 'due-soon' ? (m.daysUntilDue === 0 ? 'Today' : 'In ' + m.daysUntilDue + 'd')
            : m.due ? 'In ' + (m.daysUntilDue == null ? '?' : m.daysUntilDue) + 'd'
            : 'No date';
          return (
            <div key={m.gid} className={'proj-milestone ' + stateCls}>
              <span className="proj-milestone-icon" aria-hidden="true">{m.completed ? '◆' : '◇'}</span>
              <div className="proj-milestone-text">
                <div className="proj-milestone-name">{m.name}</div>
                <div className="proj-milestone-meta">
                  <span>{dateCopy}</span>
                  {m.assignee && m.assignee.name && <><span className="proj-meta-sep">·</span><ProjAvatar name={m.assignee.name} email={m.assignee.email} size={18} /><span>{m.assignee.name}</span></>}
                </div>
              </div>
              {m.permalink && <a className="proj-task-link" href={m.permalink} target="_blank" rel="noopener noreferrer" title="Open in Asana">↗</a>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Stakeholder pile — avatars + open-task counts for each contributor.
function ProjStakeholders({ stakeholders, owner }) {
  if ((!stakeholders || stakeholders.length === 0) && !owner) return null;
  // Limit to first 8 stakeholders so the pile doesn't overflow on huge projects
  var shown = (stakeholders || []).slice(0, 8);
  var hidden = Math.max(0, (stakeholders || []).length - shown.length);
  return (
    <div className="proj-section">
      <div className="proj-section-head">Stakeholders</div>
      <div className="proj-stakeholders">
        {owner && owner.name && (
          <div className="proj-stake proj-stake-owner" title={owner.email ? owner.name + ' · ' + owner.email : owner.name}>
            <ProjAvatar name={owner.name} email={owner.email} size={32} />
            <div className="proj-stake-text">
              <div className="proj-stake-name">{owner.name}</div>
              <div className="proj-stake-role">Project owner</div>
            </div>
          </div>
        )}
        {shown.map(function(s) {
          // Skip duplicate of owner — owner is shown separately above
          if (owner && (s.email && owner.email ? s.email === owner.email : s.name === owner.name) && s.openCount === 0) return null;
          return (
            <div key={s.email || s.name} className="proj-stake" title={s.email ? s.name + ' · ' + s.email : s.name}>
              <ProjAvatar name={s.name} email={s.email} size={32} />
              <div className="proj-stake-text">
                <div className="proj-stake-name">{s.name}</div>
                <div className="proj-stake-role">
                  {s.openCount === 0 ? 'No open tasks' : s.openCount + ' open'}
                  {s.overdueCount > 0 && <span className="proj-stake-warn"> · {s.overdueCount} overdue</span>}
                </div>
              </div>
            </div>
          );
        })}
        {hidden > 0 && <div className="proj-stake-more">+{hidden} more</div>}
      </div>
    </div>
  );
}

// Activity feed — flattened comments + status changes from the project's
// most recently touched tasks. Each row is "Author · verb · task · time".
function ProjActivityFeed({ activity }) {
  if (!activity || activity.length === 0) return null;
  return (
    <div className="proj-section">
      <div className="proj-section-head">Recent activity <span className="proj-section-sub">last {activity.length} events across active tasks</span></div>
      <div className="proj-activity">
        {activity.map(function(ev) {
          var author = ev.author && ev.author.name;
          var ago = ev.ago == null ? null : ev.ago === 0 ? 'today' : ev.ago === 1 ? 'yesterday' : ev.ago + 'd ago';
          return (
            <div key={ev.gid} className={'proj-activity-row proj-activity-' + (ev.verb || 'event').replace(/\s+/g, '-')}>
              <ProjAvatar name={author} email={ev.author && ev.author.email} size={26} />
              <div className="proj-activity-text">
                <div className="proj-activity-headline">
                  <strong>{author || 'Someone'}</strong>
                  <span className="proj-activity-verb">{ev.verb || 'updated'}</span>
                  <span className="proj-activity-task">"{ev.taskName}"</span>
                </div>
                {ev.text && <div className="proj-activity-comment">{ev.text}</div>}
                {ago && <div className="proj-activity-time">{ago}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Status update block — formatted like a project briefing snippet
// rather than truncated freeform text. Quotes the status with author + age.
function ProjStatusBlock({ statusUpdate }) {
  if (!statusUpdate || !statusUpdate.text) return null;
  // Strip URLs + collapse runs of dashes/whitespace, keep first ~360 chars
  var clean = statusUpdate.text.replace(/https?:\/\/\S+/g, '').replace(/-{3,}/g, '').replace(/\s{2,}/g, ' ').trim();
  if (!clean) return null;
  var short = clean.length > 360 ? clean.slice(0, 360).replace(/\s+\S*$/, '') + '…' : clean;
  var ageCopy = statusUpdate.createdAgo == null
    ? null
    : statusUpdate.createdAgo === 0 ? 'today'
    : statusUpdate.createdAgo === 1 ? 'yesterday'
    : statusUpdate.createdAgo + ' days ago';
  var colorClass = statusUpdate.color === 'green' ? 'good' : statusUpdate.color === 'yellow' ? 'caution' : statusUpdate.color === 'red' ? 'alert' : 'info';
  return (
    <div className={'proj-status-block ' + colorClass}>
      <div className="proj-status-head">
        Latest update
        {(statusUpdate.author || ageCopy) && (
          <span className="proj-status-byline">
            {statusUpdate.author && <>by <strong>{statusUpdate.author}</strong></>}
            {statusUpdate.author && ageCopy && ' · '}
            {ageCopy}
          </span>
        )}
      </div>
      <div className="proj-status-text">"{short}"</div>
    </div>
  );
}

function ProjectsTab({ clientId }) {
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(function() {
    setLoading(true); setProjectData(null); setExpanded(null);
    authFetch(API + '/clients/' + clientId + '/projects')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setProjectData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!projectData || !projectData.projects || projectData.projects.length === 0) return <div className="tab-content"><EmptyStateAction icon="📋" title="No Active Projects" text="Asana project tracking will show active engagements, task progress, and overdue items here once projects are linked to this client." cta="Contact CloudByte Group to link projects" /></div>;

  var projects = projectData.projects;
  var totalTasks = projects.reduce(function(s, p) { return s + p.totalTasks; }, 0);
  var totalCompleted = projects.reduce(function(s, p) { return s + p.completedTasks; }, 0);
  var totalOverdue = projects.reduce(function(s, p) { return s + p.overdueTasks; }, 0);
  var overallProgress = totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;

  return (
    <div className="tab-content">
      <TabDescription text="Project tracking from Asana. Shows active engagements, task completion progress, and overdue items. Click any project card to expand milestones and individual tasks." />
      <DataFreshness live={true} />
      <HealthVerdict score={totalOverdue > 3 ? 50 : totalOverdue > 0 ? 70 : overallProgress >= 75 ? 90 : overallProgress >= 50 ? 78 : 60} context={(function() {
        if (totalOverdue > 3) return totalOverdue + ' tasks are overdue across ' + projects.length + ' project(s). Review timeline and reassign blocked items.';
        if (totalOverdue > 0) return totalOverdue + ' overdue task(s). ' + totalCompleted + ' of ' + totalTasks + ' tasks completed (' + overallProgress + '%).';
        if (overallProgress >= 75) return 'Projects are on track — ' + overallProgress + '% complete with no overdue items.';
        if (overallProgress >= 50) return 'Projects progressing — ' + overallProgress + '% complete, ' + (totalTasks - totalCompleted) + ' tasks remaining.';
        return 'Projects are in early stages — ' + overallProgress + '% complete, ' + (totalTasks - totalCompleted) + ' tasks remaining.';
      })()} />
      {totalOverdue > 0 && (function() {
        var overdueTasks = [];
        projects.forEach(function(p) { p.incompleteTasks.filter(function(t) { return t.overdue; }).forEach(function(t) { overdueTasks.push({ project: p.name, name: t.name, assignee: t.assignee, due: t.due }); }); });
        var bodyLines = ['Hi CloudByte Support,', '', 'The following project task(s) are past their due date and may need attention:', ''];
        overdueTasks.forEach(function(t) {
          var daysOver = Math.ceil((Date.now() - new Date(t.due).getTime()) / 86400000);
          bodyLines.push('• ' + t.name); bodyLines.push('  Project: ' + t.project + ' | Due: ' + new Date(t.due).toLocaleDateString() + ' (' + daysOver + ' day' + (daysOver !== 1 ? 's' : '') + ' overdue)' + (t.assignee ? ' | Assigned: ' + t.assignee : ' | Unassigned')); bodyLines.push('');
        });
        bodyLines.push('Could you provide an update on the timeline for these items?', '', 'Thank you');
        return (
          <a href={'mailto:support@cloudbytegroup.com?subject=' + encodeURIComponent('Aurelius — ' + overdueTasks.length + ' overdue project task(s) for review') + '&body=' + encodeURIComponent(bodyLines.join('\n'))} className="verdict-mailto" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, marginBottom: 4, padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, color: '#60a5fa', background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.25)', textDecoration: 'none', cursor: 'pointer' }}>✉ Email CloudByte Support about overdue tasks</a>
        );
      })()}
      <ContentBand title="Project Portfolio — Asana" subtitle="Task-level breakdown across all active engagements" rightContent={<>{!IS_DEMO_VIEW && <a href="https://app.asana.com" target="_blank" rel="noopener noreferrer" className="tab-drill-link" style={{ textDecoration: 'none' }}>Open Asana ↗</a>}<span className="live-badge">LIVE</span></>}>
        <SegmentBar segments={[
          { label: 'Completed', value: totalCompleted || 0, color: '#22c55e' },
          { label: 'Remaining', value: (totalTasks - totalCompleted) || 0, color: '#60a5fa' },
          { label: 'Overdue', value: totalOverdue || 0, color: '#ef4444' },
        ]} />
        {(function() {
          var onTrack = projects.filter(function(p) { return p.statusColor === 'green'; }).length;
          var atRisk = projects.filter(function(p) { return p.statusColor === 'yellow' || p.statusColor === 'red'; }).length;
          var avgProgress = projects.length > 0 ? Math.round(projects.reduce(function(s, p) { return s + (p.progress || 0); }, 0) / projects.length) : 0;
          return (
            <div className="metric-tiles">
              <div className="metric-tile">
                <div className="metric-tile-label">Active Projects</div>
                <div className="metric-tile-value">{projects.length}</div>
                <div className="metric-tile-context">In flight across portfolio</div>
              </div>
              <div className="metric-tile">
                <div className="metric-tile-label">Avg Progress</div>
                <div className="metric-tile-value">{avgProgress}<span className="metric-tile-unit">%</span></div>
                <div className="metric-tile-context">Mean completion across projects</div>
              </div>
              <div className={'metric-tile' + (onTrack === projects.length && projects.length > 0 ? ' metric-tile-good' : '')}>
                <div className="metric-tile-label">On Track</div>
                <div className="metric-tile-value">{onTrack}</div>
                <div className="metric-tile-context">Status reported green</div>
              </div>
              <div className={'metric-tile' + (atRisk > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
                <div className="metric-tile-label">At Risk</div>
                <div className="metric-tile-value">{atRisk}</div>
                <div className="metric-tile-context">{atRisk > 0 ? 'Yellow or red status — review timeline' : 'No projects flagged'}</div>
              </div>
            </div>
          );
        })()}
      </ContentBand>
      {projects.map(function(p, idx) {
        var isExpanded = expanded === idx;
        var progressColor = p.progress >= 75 ? '#22c55e' : p.progress >= 50 ? '#f59e0b' : '#ef4444';
        var statusClass = p.statusColor === 'green' ? 'project-status-green' : p.statusColor === 'yellow' ? 'project-status-yellow' : p.statusColor === 'red' ? 'project-status-red' : 'project-status-blue';
        var statusDotColor = p.statusColor === 'green' ? '#22c55e' : p.statusColor === 'yellow' ? '#f59e0b' : p.statusColor === 'red' ? '#ef4444' : '#60a5fa';
        return (
          <div key={p.gid} className="project-card">
            <div className="project-card-header" onClick={function() { setExpanded(isExpanded ? null : idx); }}>
              <div className="project-card-left">
                <div className="project-card-name">{p.color && <span className="project-color-dot" style={{ background: p.color === 'light-green' ? '#22c55e' : p.color === 'light-blue' ? '#60a5fa' : p.color === 'light-red' ? '#ef4444' : p.color === 'light-orange' ? '#f59e0b' : p.color === 'light-purple' ? '#a78bfa' : p.color === 'light-pink' ? '#f472b6' : p.color === 'light-teal' ? '#2dd4bf' : '#94a3b8' }} />}{p.name}</div>
                <div className="project-card-meta">
                  {p.statusColor && <span className={'project-status-badge ' + statusClass}><span className="project-status-dot" style={{ background: statusDotColor }} />{p.statusColor === 'green' ? 'On Track' : p.statusColor === 'yellow' ? 'At Risk' : p.statusColor === 'red' ? 'Off Track' : 'Update Available'}</span>}
                  {p.owner && p.owner.name && <span className="project-owner">{p.owner.name}</span>}
                  {(p.startDate || p.dueDate) && <span className="project-date-range">{p.startDate ? new Date(p.startDate).toLocaleDateString() : '?'} → {p.dueDate ? new Date(p.dueDate).toLocaleDateString() : 'No end date'}</span>}
                  {p.overdueTasks > 0 && <span className="project-overdue-badge">{p.overdueTasks} overdue</span>}
                </div>
              </div>
              <div className="project-card-right">
                <span className="project-progress-number" style={{ color: progressColor }}>{p.progress}%</span>
                <div className="project-task-count">{p.completedTasks}/{p.totalTasks} tasks</div>
                <span className="project-expand">{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>
            <div className="project-progress-bar"><div className="project-progress-fill" style={{ width: p.progress + '%', backgroundColor: progressColor }} /></div>
            {isExpanded && (function() {
              // Defensive: detect whether this expansion has anything meaningful
              // to show. If every section would be empty/null, render a clear
              // empty-state message instead of a silent blank panel.
              var hasPhases = p.phases && p.phases.length > 0;
              var hasUnphased = p.unphasedTasks && p.unphasedTasks.length > 0;
              var hasMilestones = p.milestones && p.milestones.length > 0;
              var hasStakeholders = (p.stakeholders && p.stakeholders.length > 0) || (p.owner && p.owner.name);
              var hasStatusUpdate = p.statusUpdate && p.statusUpdate.text;
              var hasAnything = hasPhases || hasUnphased || hasMilestones || hasStakeholders || hasStatusUpdate;
              return (
              <div className="project-card-body proj-v2">
                {/* Always-visible summary header — guarantees the expansion never appears blank,
                    and makes the data clearly visible even if a downstream component fails to render. */}
                <div className="proj-summary-line">
                  <span className="proj-summary-piece"><strong>{p.totalTasks || 0}</strong> task{(p.totalTasks || 0) === 1 ? '' : 's'}</span>
                  {hasPhases && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece"><strong>{p.phases.length}</strong> phase{p.phases.length === 1 ? '' : 's'}</span></>}
                  {(p.completedTasks || 0) > 0 && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece"><strong>{p.completedTasks}</strong> done</span></>}
                  {(p.overdueTasks || 0) > 0 && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece overdue"><strong>{p.overdueTasks}</strong> overdue</span></>}
                  {(p.dueSoonTasks || 0) > 0 && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece due-soon"><strong>{p.dueSoonTasks}</strong> due this week</span></>}
                  {p.owner && p.owner.name && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece">Owner: <strong>{p.owner.name}</strong></span></>}
                  {p.lastModifiedAgo != null && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece muted">Activity {p.lastModifiedAgo === 0 ? 'today' : p.lastModifiedAgo + 'd ago'}</span></>}
                </div>

                {hasAnything ? (
                  <>
                    <ProjHealthRibbon project={p} />
                    <ProjStatusBlock statusUpdate={p.statusUpdate} />
                    <ProjMilestonesStrip milestones={p.milestones} />
                    {hasPhases && (
                      <div className="proj-section">
                        <div className="proj-section-head">Lifecycle</div>
                        <ProjLifecycle phases={p.phases} />
                      </div>
                    )}
                    {hasUnphased && (
                      <div className="proj-section">
                        <div className="proj-section-head">Unphased work <span className="proj-section-sub">({p.unphasedTasks.length}) — not yet assigned to a phase</span></div>
                        <div className="proj-task-list">
                          {p.unphasedTasks.map(function(t) { return <ProjTaskCard key={t.gid} task={t} />; })}
                        </div>
                      </div>
                    )}
                    <ProjStakeholders stakeholders={p.stakeholders} owner={p.owner} />
                    <ProjActivityFeed activity={p.recentActivity} />
                  </>
                ) : (
                  <div className="proj-empty-state">
                    <div className="proj-empty-state-icon">○</div>
                    <div className="proj-empty-state-title">No detailed data yet</div>
                    <div className="proj-empty-state-text">
                      This project hasn't been broken into sections, milestones, or assigned tasks in Asana.
                      Once the engineering team adds structure, it'll show up here.
                    </div>
                  </div>
                )}

                {p.permalink && (
                  <div className="proj-card-footer">
                    <a className="proj-deeplink" href={p.permalink} target="_blank" rel="noopener noreferrer">Open project in Asana ↗</a>
                    {p.lastModifiedAgo != null && <span className="proj-card-footer-meta">Last activity {p.lastModifiedAgo === 0 ? 'today' : p.lastModifiedAgo + 'd ago'}</span>}
                  </div>
                )}
              </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}

// ── DevOps Tab (Jira) — MODIFIED: billing moved to Budget tab ─────
// ── DevOps Components ─────────────────────────────────────────────
function DevOpsMetricTiles({ jiraData }) {
  var backlog = jiraData.totalOpen || 0;
  var overdue = jiraData.totalOverdue || 0;
  var velocity = jiraData.totalDone30d || 0;

  // Count In Progress from matrix
  var inProgress = 0;
  if (jiraData.priorityStatusMatrix) {
    Object.values(jiraData.priorityStatusMatrix).forEach(function(row) {
      inProgress += (row['In Progress'] || 0);
    });
  }

  // Throughput: velocity vs backlog
  var throughput = backlog > 0 ? Math.round((velocity / (velocity + backlog)) * 100) : null;

  return (
    <div className="metric-tiles">
      <div className="metric-tile">
        <div className="metric-tile-label">Open Backlog</div>
        <div className="metric-tile-value">{backlog}</div>
        <div className="metric-tile-context">Across {jiraData.projectCount || 0} project{jiraData.projectCount === 1 ? '' : 's'}</div>
      </div>
      <div className={'metric-tile' + (overdue > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
        <div className="metric-tile-label">Overdue</div>
        <div className="metric-tile-value">{overdue}</div>
        <div className="metric-tile-context">{overdue > 0 ? 'Past due date — needs triage' : 'All issues on track'}</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">In Progress</div>
        <div className="metric-tile-value">{inProgress}</div>
        <div className="metric-tile-context">Actively being worked</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Velocity (30d)</div>
        <div className="metric-tile-value">{velocity}</div>
        <div className="metric-tile-context">{throughput !== null ? throughput + '% throughput rate' : 'Completed last 30 days'}</div>
      </div>
    </div>
  );
}

function JiraPriorityStatusHeatmap({ matrix }) {
  if (!matrix || Object.keys(matrix).length === 0) return <div className="priority-heatmap-empty">No issue data available.</div>;

  var priorityOrder = ['Highest', 'High', 'Medium', 'Low', 'Lowest', 'None'];
  var priorityColors = { Highest: '#ef4444', High: '#f59e0b', Medium: '#60a5fa', Low: '#22c55e', Lowest: '#94a3b8', None: '#64748b' };
  var statuses = [
    { key: 'To Do', label: 'To Do' },
    { key: 'In Progress', label: 'In Progress' },
    { key: 'Done', label: 'Done (30d)' }
  ];

  // Only show priorities that have data
  var activePriorities = priorityOrder.filter(function(p) {
    if (!matrix[p]) return false;
    return statuses.some(function(s) { return (matrix[p][s.key] || 0) > 0; });
  });
  if (activePriorities.length === 0) return <div className="priority-heatmap-empty">No issue data available.</div>;

  // Max for intensity scaling (excluding Done column)
  var activeMax = 0;
  activePriorities.forEach(function(p) {
    statuses.forEach(function(s) {
      if (s.key === 'Done') return;
      var count = (matrix[p] && matrix[p][s.key]) || 0;
      if (count > activeMax) activeMax = count;
    });
  });

  var isHotspot = function(priority, statusKey) {
    if (statusKey === 'Done') return false;
    var count = (matrix[priority] && matrix[priority][statusKey]) || 0;
    if (count === 0) return false;
    if (priority === 'Highest') return true;
    if (priority === 'High' && count > 2) return true;
    return false;
  };

  return (
    <div className="priority-heatmap-wrap">
      <div className="priority-heatmap" style={{ gridTemplateColumns: '120px repeat(' + statuses.length + ', 1fr)' }}>
        <div className="priority-heatmap-corner"></div>
        {statuses.map(function(s) { return <div key={s.key} className="priority-heatmap-col-label">{s.label}</div>; })}
        {activePriorities.map(function(p) {
          return (
            <React.Fragment key={p}>
              <div className="priority-heatmap-row-label">
                <span className="priority-heatmap-row-dot" style={{ background: priorityColors[p], color: priorityColors[p] }} />
                {p}
              </div>
              {statuses.map(function(s) {
                var count = (matrix[p] && matrix[p][s.key]) || 0;
                var intensity = activeMax > 0 && s.key !== 'Done' ? count / activeMax : 0;
                var hotspot = isHotspot(p, s.key);
                var bg = 'transparent';
                if (count > 0) {
                  if (hotspot) bg = 'rgba(239, 68, 68, ' + (0.18 + intensity * 0.32) + ')';
                  else if (s.key === 'Done') bg = 'rgba(34, 197, 94, 0.08)';
                  else bg = 'rgba(91, 158, 244, ' + (0.1 + intensity * 0.3) + ')';
                }
                return (
                  <div key={s.key} className={'priority-heatmap-cell' + (count === 0 ? ' cell-empty' : '') + (hotspot ? ' cell-hotspot' : '')} style={{ background: bg }}>
                    {count > 0 ? count : <span className="cell-dash">—</span>}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      <div className="priority-heatmap-legend">
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-hotspot" /> Hotspot — high priority still active</span>
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-active" /> Active work</span>
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-closed" /> Done (30d)</span>
      </div>
    </div>
  );
}

function DevOpsTab({ clientId, onNavigate }) {
  const [jiraData, setJiraData] = useState(null);
  const [loading, setLoading] = useState(true);
  var jiraSort = useTableSort('updated', 'desc');
  const [jiraSearch, setJiraSearch] = useState('');
  const [jiraFilters, setJiraFilters] = useState({});
  const [expanded, setExpanded] = useState(null);

  useEffect(function() {
    setLoading(true); setJiraData(null); setExpanded(null);
    authFetch(API + '/clients/' + clientId + '/jira')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setJiraData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!jiraData || !jiraData.enabled) return <div className="tab-content"><EmptyStateAction icon="⚙" title="Jira Not Connected" text="Jira integration provides DevOps visibility — issue tracking, sprint health, priority distribution, and team workload across all projects." cta="Contact CloudByte Group to enable" /></div>;

  var priorityColors = { Highest: '#ef4444', High: '#f59e0b', Medium: '#60a5fa', Low: '#22c55e', Lowest: '#64748b' };
  var statusCatColors = { 'To Do': '#64748b', 'In Progress': '#60a5fa', 'Done': '#22c55e' };

  function timeAgo(dateStr) {
    if (!dateStr) return '—';
    var diff = Date.now() - new Date(dateStr).getTime();
    var mins = Math.floor(diff / 60000);
    if (mins < 60) return mins + 'm ago';
    var hours = Math.floor(mins / 60);
    if (hours < 24) return hours + 'h ago';
    var days = Math.floor(hours / 24);
    return days < 30 ? days + 'd ago' : Math.floor(days / 30) + 'mo ago';
  }

  var jiraAll = jiraData.recentIssues || [];
  var jiraFiltered = applyFilters(
    filterRows(jiraAll, jiraSearch, [function(i){return i.key;}, function(i){return i.summary;}, function(i){return i.assignee;}, function(i){return i.project;}]),
    jiraFilters,
    { priority: function(i){return i.priority;}, statusCategory: function(i){return i.statusCategory;} }
  );
  var sortedIssues = sortRows(jiraFiltered, jiraSort.sortField, jiraSort.sortDir, {
    key: function(i){return i.key;}, summary: function(i){return i.summary;}, status: function(i){return i.status;},
    priority: function(i){return i.priority;}, assignee: function(i){return i.assignee;}, updated: function(i){return i.updated;}
  });

  return (
    <div className="tab-content">
      <TabDescription text="Development and IT operations tracked in Jira. Shows issue velocity, priority distribution, and team workload across all active projects." />
      <DataFreshness live={true} />
      <ContentBand title="DevOps Overview — Jira" rightContent={<span className="live-badge">LIVE</span>}>
        <DevOpsMetricTiles jiraData={jiraData} />
      </ContentBand>
      {jiraData.billing && (
        <ContentBand title="Atlassian Billing" rightContent={onNavigate ? <button className="tab-drill-link" onClick={function() { onNavigate('budget'); }}>View in Budget ›</button> : null}>
          <MetricList>
            <MetricListItem icon="$" iconColor="#60a5fa" label="Monthly Cost" value={'$' + jiraData.billing.totalMonthlyCost.toLocaleString() + '/mo'} valueColor="#60a5fa" trail={jiraData.billing.totalLicensedUsers + ' users'} />
          </MetricList>
        </ContentBand>
      )}
      {jiraData.priorityStatusMatrix && Object.keys(jiraData.priorityStatusMatrix).length > 0 && (
        <ContentBand title="Priority × Status Matrix" subtitle="Hotspots highlight high-priority issues still active — find bottlenecks instantly">
          <JiraPriorityStatusHeatmap matrix={jiraData.priorityStatusMatrix} />
        </ContentBand>
      )}
      {jiraData.projects && jiraData.projects.length > 0 && (
        <>
          <div className="section-title">Projects</div>
          {jiraData.projects.map(function(p, idx) {
            var isExpanded = expanded === idx;
            return (
              <div key={p.key} className="project-card">
                <div className="project-card-header" onClick={function() { setExpanded(isExpanded ? null : idx); }}>
                  <div className="project-card-left">
                    <div className="project-card-name">{p.name || p.key} <span style={{ color: '#64748b', fontSize: 12, fontWeight: 400 }}>({p.key})</span></div>
                    <div className="project-card-meta">
                      <span style={{ color: '#60a5fa' }}>{p.open} open</span>
                      <span style={{ color: '#22c55e', marginLeft: 12 }}>{p.done30d} done (30d)</span>
                      {p.overdue > 0 && <span className="project-overdue-badge">{p.overdue} overdue</span>}
                    </div>
                  </div>
                  <div className="project-card-right"><span className="project-expand">{isExpanded ? '▲' : '▼'}</span></div>
                </div>
                {isExpanded && (
                  <div className="project-card-body">
                    {Object.keys(p.statusBreakdown).length > 0 && (<div className="project-sections"><div className="project-section-title">Status</div><div className="project-section-list">{Object.entries(p.statusBreakdown).map(function([status, count]) { return <span key={status} className="project-section-tag">{status}: {count}</span>; })}</div></div>)}
                    {Object.keys(p.assigneeBreakdown).length > 0 && (<div className="project-sections"><div className="project-section-title">Team Workload</div><div className="project-section-list">{Object.entries(p.assigneeBreakdown).sort(function(a, b) { return b[1] - a[1]; }).map(function([name, count]) { return <span key={name} className="project-section-tag">{name}: {count}</span>; })}</div></div>)}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
      {Object.keys(jiraData.assigneeBreakdown).length > 0 && (
        <ContentBand title="Team Workload (All Projects)">
          <MetricList>
            {Object.entries(jiraData.assigneeBreakdown).sort(function(a, b) { return b[1] - a[1]; }).map(function([name, count]) { var maxIssues = Math.max.apply(null, Object.values(jiraData.assigneeBreakdown)); return <MetricListItem key={name} icon="◉" iconColor="#a78bfa" label={name} value={<MicroBar value={count} max={maxIssues} color="#a78bfa" label={count} />} />; })}
          </MetricList>
        </ContentBand>
      )}
      {jiraAll.length > 0 && (
        <>
          <div className="section-title">Recent Issues ({jiraAll.length})</div>
          <TableSearch value={jiraSearch} onChange={setJiraSearch} count={sortedIssues.length} total={jiraAll.length} noun="issues" onExport={function() { exportTableCSV(['Key','Summary','Status','Priority','Assignee','Updated','URL'], sortedIssues.map(function(i) { return [i.key, i.summary, i.status, i.priority||'', i.assignee||'', i.updated ? new Date(i.updated).toLocaleDateString() : '', i.url||'']; }), 'jira-issues'); }} />
          <TableFilters filters={[
            { key: 'priority', label: 'Priority', options: (function() { var p={}; jiraAll.forEach(function(i){if(i.priority)p[i.priority]=(p[i.priority]||0)+1;}); return Object.entries(p).map(function([k,c]) { return { value: k, label: k, color: priorityColors[k]||'#64748b', count: c }; }); })() },
            { key: 'statusCategory', label: 'Status', options: (function() { var s={}; jiraAll.forEach(function(i){if(i.statusCategory)s[i.statusCategory]=(s[i.statusCategory]||0)+1;}); return Object.entries(s).map(function([k,c]) { return { value: k, label: k, color: statusCatColors[k]||'#94a3b8', count: c }; }); })() },
          ]} active={jiraFilters} onChange={setJiraFilters} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead>
                <tr>
                  <SortTh field="key" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Key</SortTh>
                  <SortTh field="summary" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Summary</SortTh>
                  <SortTh field="status" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Status</SortTh>
                  <SortTh field="priority" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Priority</SortTh>
                  <SortTh field="assignee" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Assignee</SortTh>
                  <SortTh field="updated" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Updated</SortTh>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {sortedIssues.map(function(issue) {
                  var isOverdue = issue.duedate && new Date(issue.duedate) < new Date();
                  return (
                    <tr key={issue.key} className={isOverdue ? 'posture-row-offline' : ''}>
                      <td className="table-mono">{issue.key}</td>
                      <td className="posture-device-name" style={{ maxWidth: 300 }}>
                        <div>{issue.summary}</div>
                        {issue.issueType && <div className="table-sub">{issue.issueType} · {issue.project}</div>}
                      </td>
                      <td><span className="inline-badge" style={{ backgroundColor: issue.statusCategory === 'In Progress' ? 'rgba(96,165,250,0.15)' : issue.statusCategory === 'Done' ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.15)', color: statusCatColors[issue.statusCategory] || '#94a3b8' }}>{issue.status}</span></td>
                      <td><span style={{ color: priorityColors[issue.priority] || '#94a3b8', fontWeight: 600, fontSize: 12 }}>{issue.priority || '—'}</span></td>
                      <td>{issue.assignee}</td>
                      <td className="posture-date">{timeAgo(issue.updated)}{isOverdue && <span className="project-overdue-badge">OVERDUE</span>}</td>
                      <td>{issue.url ? <a href={issue.url} target="_blank" rel="noopener noreferrer" className="table-link">View ↗</a> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
// ── Changes Tab (Change Management) ───────────────────────────────
// ── Changes Workflow Components ───────────────────────────────────
function ChangesFlowPipeline({ changes, summary, onStageClick, statusFilter }) {
  var now = Date.now();
  var stages = [
    { key: 'submitted', label: 'Submitted', sublabel: 'Awaiting triage', count: summary.submitted || 0, thresholdDays: 3 },
    { key: 'under_review', label: 'Under Review', sublabel: 'Being evaluated', count: summary.under_review || 0, thresholdDays: 7 },
    { key: 'approved', label: 'Approved', sublabel: 'Ready to start', count: summary.approved || 0, thresholdDays: 14 },
    { key: 'in_progress', label: 'In Progress', sublabel: 'Being implemented', count: summary.in_progress || 0, thresholdDays: 30 },
    { key: 'completed', label: 'Completed', sublabel: 'Resolved & closed', count: summary.completed || 0, thresholdDays: null }
  ];
  return (
    <div className="ticket-flow">
      {stages.map(function(stage, i) {
        var aged = 0;
        if (stage.thresholdDays && stage.count > 0) {
          var thresholdMs = stage.thresholdDays * 86400 * 1000;
          aged = (changes || []).filter(function(c) {
            return c.status === stage.key && c.submittedAt && (now - new Date(c.submittedAt).getTime()) > thresholdMs;
          }).length;
        }
        var isEmpty = stage.count === 0;
        var isActive = statusFilter === stage.key;
        var isClickable = stage.count > 0 && onStageClick;
        var classes = 'ticket-flow-stage ticket-flow-stage-' + stage.key +
          (aged > 0 ? ' ticket-flow-stage-aging' : '') +
          (isEmpty ? ' ticket-flow-stage-empty' : '') +
          (isActive ? ' ticket-flow-stage-active' : '') +
          (isClickable ? ' ticket-flow-stage-clickable' : '');
        return (
          <React.Fragment key={stage.key}>
            <div className={classes} onClick={isClickable ? function() { onStageClick(isActive ? null : stage.key); } : undefined}>
              <div className="ticket-flow-stage-label">{stage.label}</div>
              <div className="ticket-flow-stage-count">{stage.count}</div>
              <div className="ticket-flow-stage-sub">{stage.sublabel}</div>
              {aged > 0 && <div className="ticket-flow-stage-aging-badge">{aged} over {stage.thresholdDays}d</div>}
            </div>
            {i < stages.length - 1 && <div className="ticket-flow-connector"><span className="ticket-flow-arrow">›</span></div>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ChangesTerminalRow({ summary, onStageClick, statusFilter }) {
  var denied = summary.denied || 0;
  var cancelled = summary.cancelled || 0;
  if (denied === 0 && cancelled === 0) return null;
  return (
    <div className="changes-terminal-row">
      <span className="changes-terminal-label">Terminal states</span>
      {denied > 0 && (
        <button className={'changes-terminal-item changes-terminal-denied' + (statusFilter === 'denied' ? ' active' : '')} onClick={function() { onStageClick(statusFilter === 'denied' ? null : 'denied'); }}>
          <span className="changes-terminal-dot"></span>
          Denied
          <span className="changes-terminal-count">{denied}</span>
        </button>
      )}
      {cancelled > 0 && (
        <button className={'changes-terminal-item changes-terminal-cancelled' + (statusFilter === 'cancelled' ? ' active' : '')} onClick={function() { onStageClick(statusFilter === 'cancelled' ? null : 'cancelled'); }}>
          <span className="changes-terminal-dot"></span>
          Cancelled
          <span className="changes-terminal-count">{cancelled}</span>
        </button>
      )}
    </div>
  );
}

function ChangesMetricTiles({ changes, summary }) {
  var awaiting = (summary.submitted || 0) + (summary.under_review || 0);
  var activeWork = (summary.approved || 0) + (summary.in_progress || 0);

  var decided = (summary.approved || 0) + (summary.in_progress || 0) + (summary.completed || 0) + (summary.denied || 0);
  var approvedTotal = decided - (summary.denied || 0);
  var approvalRate = decided > 0 ? Math.round((approvedTotal / decided) * 100) : null;

  var completedChanges = (changes || []).filter(function(c) { return c.status === 'completed' && c.submittedAt && c.completedAt; });
  var avgCompletion = null;
  if (completedChanges.length > 0) {
    var totalMin = completedChanges.reduce(function(sum, c) {
      return sum + (new Date(c.completedAt).getTime() - new Date(c.submittedAt).getTime()) / 60000;
    }, 0);
    avgCompletion = totalMin / completedChanges.length;
  }

  return (
    <div className="metric-tiles">
      <div className={'metric-tile' + (awaiting > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
        <div className="metric-tile-label">Awaiting Review</div>
        <div className="metric-tile-value">{awaiting}</div>
        <div className="metric-tile-context">{awaiting > 0 ? 'Admin action required' : 'Review queue clear'}</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Active Work</div>
        <div className="metric-tile-value">{activeWork}</div>
        <div className="metric-tile-context">Approved or implementing</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Avg Time to Close</div>
        <div className="metric-tile-value">{formatDuration(avgCompletion)}</div>
        <div className="metric-tile-context">{avgCompletion !== null ? 'Submission to completion' : 'No completions yet'}</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Approval Rate</div>
        <div className="metric-tile-value">{approvalRate !== null ? approvalRate : '—'}{approvalRate !== null && <span className="metric-tile-unit">%</span>}</div>
        <div className="metric-tile-context">{approvalRate !== null ? 'Of all decided requests' : 'No decisions yet'}</div>
      </div>
    </div>
  );
}

function ChangesTab({ clientId, isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedChange, setSelectedChange] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', type: 'IT Change', category: 'Other', priority: 'Medium', name: '', email: '' });
  var chgSort = useTableSort('submitted', 'desc');
  var [chgSearch, setChgSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentName, setCommentName] = useState('');
  const [reviewName, setReviewName] = useState('');
  const [reviewEmail, setReviewEmail] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');

  function fetchChanges() {
    setLoading(true);
    authFetch(API + '/clients/' + clientId + '/changes').then(function(r) { return r.ok ? r.json() : null; }).then(function(d) { setData(d); setLoading(false); }).catch(function() { setLoading(false); });
  }
  useEffect(function() { setView('list'); setSelectedChange(null); setShowForm(false); setStatusFilter(null); fetchChanges(); }, [clientId]);

  // Check for pre-fill from Red Team or other tabs
  useEffect(function() {
    var prefillRaw = sessionStorage.getItem('aurelius_change_prefill');
    if (!prefillRaw) return;
    try {
      var prefill = JSON.parse(prefillRaw);
      setFormData(function(prev) { return Object.assign({}, prev, {
        title: prefill.title || '',
        description: prefill.description || '',
        type: prefill.type || 'IT Change',
        category: prefill.category || 'Other',
        priority: prefill.priority || 'Medium',
        reference: prefill.reference || '',
        _sourceContext: prefill
      }); });
      setShowForm(true);
      sessionStorage.removeItem('aurelius_change_prefill');
    } catch (e) { /* ignore */ }
  }, []);

  function handleSubmit() {
    if (!formData.title || !formData.description || !formData.name || !formData.email) return;
    setSubmitting(true);
    authFetch(API + '/clients/' + clientId + '/changes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: formData.title, description: formData.description, type: formData.type, category: formData.category, priority: formData.priority, submittedBy: { name: formData.name, email: formData.email }, reference: formData.reference || null }) })
      .then(function(r) { return r.json(); }).then(function(d) { if (d.success) { setShowForm(false); setFormData({ title: '', description: '', type: 'IT Change', category: 'Other', priority: 'Medium', name: formData.name, email: formData.email }); fetchChanges(); } setSubmitting(false); }).catch(function() { setSubmitting(false); });
  }
  function handleAction(changeId, action, extraBody) {
    setActionLoading(true);
    authFetch(API + '/clients/' + clientId + '/changes/' + changeId, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(Object.assign({ action: action }, extraBody || {})) })
      .then(function(r) { return r.json(); }).then(function(d) { if (d.success) { setSelectedChange(d.change); fetchChanges(); } setActionLoading(false); }).catch(function() { setActionLoading(false); });
  }

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  var summary = data ? data.summary : { total: 0, submitted: 0, under_review: 0, approved: 0, denied: 0, in_progress: 0, completed: 0, cancelled: 0 };
  var changes = data ? data.changes : [];
  if (statusFilter) changes = changes.filter(function(c) { return c.status === statusFilter; });

  var statusColors = { submitted: '#60a5fa', under_review: '#f59e0b', approved: '#22c55e', denied: '#ef4444', in_progress: '#a78bfa', completed: '#22c55e', cancelled: '#64748b' };
  var statusLabels = { submitted: 'Submitted', under_review: 'Under Review', approved: 'Approved', denied: 'Denied', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };
  var priorityColors = { Low: '#22c55e', Medium: '#60a5fa', High: '#f59e0b', Critical: '#ef4444' };
  var typeColors = { 'IT Change': '#60a5fa', 'Security Change': '#a78bfa' };

  function timeAgo(dateStr) {
    if (!dateStr) return '—';
    var diff = Date.now() - new Date(dateStr).getTime();
    var mins = Math.floor(diff / 60000);
    if (mins < 60) return mins + 'm ago';
    var hours = Math.floor(mins / 60);
    if (hours < 24) return hours + 'h ago';
    return Math.floor(hours / 24) + 'd ago';
  }
  function badgeStyle(color) { return { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: color, background: color + '18', border: '1px solid ' + color + '30' }; }
  var inputStyle = { background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-body)', width: '100%', outline: 'none' };
  var selectStyle = Object.assign({}, inputStyle, { appearance: 'none', cursor: 'pointer' });
  var textareaStyle = Object.assign({}, inputStyle, { minHeight: 80, resize: 'vertical' });
  var labelStyle = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 4, display: 'block' };
  var fieldStyle = { marginBottom: 14 };

  if (selectedChange) {
    var ch = selectedChange;
    return (
      <div className="tab-content">
        <Breadcrumb items={[{ label: 'Change Management', onClick: function() { setSelectedChange(null); } }, { label: 'Request ' + ch.id }]} />
        <div className="section-title">Change Request: {ch.id}</div>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{ch.title}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={badgeStyle(statusColors[ch.status] || '#64748b')}>{statusLabels[ch.status]}</span>
                <span style={badgeStyle(priorityColors[ch.priority] || '#64748b')}>{ch.priority}</span>
                <span style={badgeStyle(typeColors[ch.type] || '#64748b')}>{ch.type}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', padding: '3px 10px' }}>{ch.category}</span>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}><div>Submitted {timeAgo(ch.submittedAt)}</div><div>{new Date(ch.submittedAt).toLocaleString()}</div></div>
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 16, whiteSpace: 'pre-wrap' }}>{ch.description}</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)' }}>
            <div><strong style={{ color: 'var(--text-secondary)' }}>Submitted by:</strong> {ch.submittedBy.name} ({ch.submittedBy.email})</div>
            {ch.reviewedBy && <div><strong style={{ color: 'var(--text-secondary)' }}>Reviewed by:</strong> {ch.reviewedBy.name}</div>}
            {ch.implementedBy && <div><strong style={{ color: 'var(--text-secondary)' }}>Implemented by:</strong> {ch.implementedBy}</div>}
            {ch.completedAt && <div><strong style={{ color: 'var(--text-secondary)' }}>Completed:</strong> {new Date(ch.completedAt).toLocaleString()}</div>}
          </div>
          {ch.reference && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}><strong>Reference: </strong>{ch.reference.match(/^https?:\/\//) ? <a href={ch.reference} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}>{ch.reference} ↗</a> : ch.reference}</div>}
          {ch.reviewNotes && <div style={{ marginTop: 12, padding: 12, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--text-secondary)' }}><strong>Review Notes:</strong> {ch.reviewNotes}</div>}
          {ch.completionNotes && <div style={{ marginTop: 8, padding: 12, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--text-secondary)' }}><strong>Completion Notes:</strong> {ch.completionNotes}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {ch.status === 'submitted' && isAdmin && <button className="insights-btn" disabled={actionLoading} onClick={function() { handleAction(ch.id, 'review'); }}>Mark Under Review</button>}
          {(ch.status === 'submitted' || ch.status === 'under_review') && isAdmin && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input style={Object.assign({}, inputStyle, { width: 160 })} placeholder="Reviewer name" value={reviewName} onChange={function(e) { setReviewName(e.target.value); }} />
              <input style={Object.assign({}, inputStyle, { width: 200 })} placeholder="Reviewer email (optional)" value={reviewEmail} onChange={function(e) { setReviewEmail(e.target.value); }} />
              <input style={Object.assign({}, inputStyle, { width: 220 })} placeholder="Notes (optional)" value={reviewNotes} onChange={function(e) { setReviewNotes(e.target.value); }} />
              <button className="insights-btn" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.3)' }} disabled={actionLoading || !reviewName} onClick={function() { handleAction(ch.id, 'approve', { reviewedBy: { name: reviewName, email: reviewEmail }, reviewNotes: reviewNotes }); setReviewName(''); setReviewEmail(''); setReviewNotes(''); }}>Approve</button>
              <button className="insights-btn" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }} disabled={actionLoading || !reviewName} onClick={function() { handleAction(ch.id, 'deny', { reviewedBy: { name: reviewName, email: reviewEmail }, reviewNotes: reviewNotes || 'Denied' }); setReviewName(''); setReviewEmail(''); setReviewNotes(''); }}>Deny</button>
            </div>
          )}
          {ch.status === 'approved' && isAdmin && <button className="insights-btn" style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa', borderColor: 'rgba(167,139,250,0.3)' }} disabled={actionLoading} onClick={function() { handleAction(ch.id, 'start', { implementedBy: 'CloudByte Group' }); }}>Start Implementation</button>}
          {ch.status === 'in_progress' && isAdmin && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input style={Object.assign({}, inputStyle, { width: 300 })} placeholder="Completion notes (optional)" value={completionNotes} onChange={function(e) { setCompletionNotes(e.target.value); }} />
              <button className="insights-btn" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.3)' }} disabled={actionLoading} onClick={function() { handleAction(ch.id, 'complete', { completionNotes: completionNotes }); setCompletionNotes(''); }}>Mark Completed</button>
            </div>
          )}
          {!['completed', 'denied', 'cancelled'].includes(ch.status) && isAdmin && <button className="insights-btn" style={{ background: 'rgba(100,116,139,0.15)', color: '#64748b', borderColor: 'rgba(100,116,139,0.3)' }} disabled={actionLoading} onClick={function() { handleAction(ch.id, 'cancel'); }}>Cancel Request</button>}
        </div>
        <div className="section-title">Comments ({ch.comments.length})</div>
        {ch.comments.length > 0 && <div style={{ marginBottom: 16 }}>{ch.comments.map(function(c, i) { return (<div key={i} style={{ padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', marginBottom: 6 }}><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-blue)' }}>{c.by}</span><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{timeAgo(c.at)}</span></div><div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{c.text}</div></div>); })}</div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <input style={Object.assign({}, inputStyle, { width: 160 })} placeholder="Your name" value={commentName} onChange={function(e) { setCommentName(e.target.value); }} />
          <input style={Object.assign({}, inputStyle, { flex: 1 })} placeholder="Add a comment..." value={commentText} onChange={function(e) { setCommentText(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter' && commentName && commentText) { handleAction(ch.id, 'comment', { comment: { by: commentName, text: commentText } }); setCommentText(''); } }} />
          <button className="insights-btn" disabled={!commentName || !commentText || actionLoading} onClick={function() { handleAction(ch.id, 'comment', { comment: { by: commentName, text: commentText } }); setCommentText(''); }}>Add</button>
        </div>
      </div>
    );
  }

  if (showForm) {
    var sourceCtx = formData._sourceContext;
    return (
      <div className="tab-content">
        <Breadcrumb items={[{ label: 'Change Management', onClick: function() { setShowForm(false); } }, { label: 'New Request' }]} />
        <div className="section-title">Submit Change Request</div>
        {sourceCtx && sourceCtx.source === 'redteam' && (
          <div className="change-prefill-banner">
            <span className="change-prefill-badge">IMPORTED</span>
            <div className="change-prefill-body">
              <div className="change-prefill-title">Pre-filled from Aurelius Red Team</div>
              <div className="change-prefill-sub">Source: {sourceCtx.source_id} · Impact analysis auto-populated from observed simulation data</div>
            </div>
            {sourceCtx.impact_summary && (
              <div className="change-prefill-impact">
                <span><strong>{sourceCtx.impact_summary.users_affected}</strong> users</span>
                {sourceCtx.impact_summary.devices_affected > 0 && <span><strong>{sourceCtx.impact_summary.devices_affected}</strong> devices</span>}
                <span><strong>{sourceCtx.impact_summary.cost_estimate}</strong></span>
                <span><strong>{sourceCtx.impact_summary.timeline}</strong></span>
              </div>
            )}
          </div>
        )}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 28, maxWidth: 700 }}>
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={Object.assign({}, fieldStyle, { flex: 1 })}><label style={labelStyle}>Your Name *</label><input style={inputStyle} value={formData.name} onChange={function(e) { setFormData(Object.assign({}, formData, { name: e.target.value })); }} placeholder="John Smith" /></div>
            <div style={Object.assign({}, fieldStyle, { flex: 1 })}><label style={labelStyle}>Your Email *</label><input style={inputStyle} value={formData.email} onChange={function(e) { setFormData(Object.assign({}, formData, { email: e.target.value })); }} placeholder="john@company.com" /></div>
          </div>
          <div style={fieldStyle}><label style={labelStyle}>Title *</label><input style={inputStyle} value={formData.title} onChange={function(e) { setFormData(Object.assign({}, formData, { title: e.target.value })); }} placeholder="Brief description of the change" /></div>
          <div style={fieldStyle}><label style={labelStyle}>Description *</label><textarea style={textareaStyle} value={formData.description} onChange={function(e) { setFormData(Object.assign({}, formData, { description: e.target.value })); }} placeholder="Detailed description..." /></div>
          <div style={fieldStyle}><label style={labelStyle}>Reference (optional)</label><input style={inputStyle} value={formData.reference || ''} onChange={function(e) { setFormData(Object.assign({}, formData, { reference: e.target.value })); }} placeholder="Jira issue, Asana link, or ticket number" /></div>
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={Object.assign({}, fieldStyle, { flex: 1 })}><label style={labelStyle}>Type</label><select style={selectStyle} value={formData.type} onChange={function(e) { setFormData(Object.assign({}, formData, { type: e.target.value })); }}><option value="IT Change">IT Change</option><option value="Security Change">Security Change</option></select></div>
            <div style={Object.assign({}, fieldStyle, { flex: 1 })}><label style={labelStyle}>Category</label><select style={selectStyle} value={formData.category} onChange={function(e) { setFormData(Object.assign({}, formData, { category: e.target.value })); }}><option value="New User">New User</option><option value="Offboarding">Offboarding</option><option value="Software Install">Software Install</option><option value="Software Removal">Software Removal</option><option value="Hardware Request">Hardware Request</option><option value="Firewall Rule">Firewall Rule</option><option value="Access Change">Access Change</option><option value="Policy Update">Policy Update</option><option value="Security Exception">Security Exception</option><option value="Configuration Change">Configuration Change</option><option value="Network Change">Network Change</option><option value="Other">Other</option></select></div>
            <div style={Object.assign({}, fieldStyle, { flex: 1 })}><label style={labelStyle}>Priority</label><select style={selectStyle} value={formData.priority} onChange={function(e) { setFormData(Object.assign({}, formData, { priority: e.target.value })); }}><option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option><option value="Critical">Critical</option></select></div>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button className="qbr-generate-btn" style={{ flex: 'none', padding: '12px 32px' }} disabled={submitting || !formData.title || !formData.description || !formData.name || !formData.email} onClick={handleSubmit}>{submitting ? 'Submitting...' : 'Submit Change Request'}</button>
            <button className="insights-btn" onClick={function() { setShowForm(false); }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  var openCount = summary.submitted + summary.under_review + summary.approved + summary.in_progress;
  return (
    <div className="tab-content">
      <TabDescription text="Formal change management workflow. All IT and security changes are submitted, reviewed, and tracked here for compliance audit readiness and operational accountability." />
      <ContentBand title="Change Management Flow" subtitle="Live pipeline across all request stages — click any stage to filter">
        <ChangesFlowPipeline changes={data ? data.changes : []} summary={summary} onStageClick={setStatusFilter} statusFilter={statusFilter} />
        <ChangesTerminalRow summary={summary} onStageClick={setStatusFilter} statusFilter={statusFilter} />
        <ChangesMetricTiles changes={data ? data.changes : []} summary={summary} />
      </ContentBand>
      <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="qbr-generate-btn" style={{ flex: 'none', padding: '10px 24px', fontSize: 12 }} onClick={function() { setShowForm(true); }}>+ New Change Request</button>
        {statusFilter && <button className="insights-btn" onClick={function() { setStatusFilter(null); }}>Clear Filter: {statusLabels[statusFilter]}</button>}
      </div>
      {changes.length > 0 ? (
        <>
          <div className="section-title" style={{ marginTop: 24 }}>{statusFilter ? statusLabels[statusFilter] + ' Requests' : 'All Requests'} ({changes.length})</div>
          <TableSearch value={chgSearch} onChange={setChgSearch} count={sortRows(filterRows(changes, chgSearch, [function(c) { return c.id; }, function(c) { return c.title; }, function(c) { return c.submittedBy.name; }]), chgSort.sortField, chgSort.sortDir, {}).length} total={changes.length} noun="requests" />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="id" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>ID</SortTh><SortTh field="title" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>Title</SortTh><SortTh field="type" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>Type</SortTh><SortTh field="priority" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>Priority</SortTh><SortTh field="status" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>Status</SortTh><th>Submitted By</th><SortTh field="submitted" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>Submitted</SortTh></tr></thead>
              <tbody>
                {sortRows(filterRows(changes, chgSearch, [function(c) { return c.id; }, function(c) { return c.title; }, function(c) { return c.submittedBy.name; }]), chgSort.sortField, chgSort.sortDir, { id: function(c) { return c.id; }, title: function(c) { return c.title; }, type: function(c) { return c.type; }, priority: function(c) { return c.priority; }, status: function(c) { return c.status; }, submitted: function(c) { return new Date(c.submittedAt).getTime(); } }).map(function(ch) {
                  return (
                    <tr key={ch.id} style={{ cursor: 'pointer' }} onClick={function() { setSelectedChange(ch); }}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600 }}>{ch.id}</td>
                      <td className="posture-device-name" style={{ maxWidth: 280 }}><span className={'status-dot ' + (ch.status === 'submitted' ? 'status-dot-info' : ch.status === 'approved' || ch.status === 'completed' ? 'status-dot-good' : ch.status === 'rejected' ? 'status-dot-critical' : 'status-dot-warning')} />{ch.title}</td>
                      <td><span style={badgeStyle(typeColors[ch.type] || '#64748b')}>{ch.type}</span></td>
                      <td><span style={{ color: priorityColors[ch.priority] || '#94a3b8', fontWeight: 600, fontSize: 12 }}>{ch.priority}</span></td>
                      <td><span style={badgeStyle(statusColors[ch.status] || '#64748b')}>{statusLabels[ch.status]}</span></td>
                      <td style={{ fontSize: 12 }}>{ch.submittedBy.name}</td>
                      <td className="posture-date">{timeAgo(ch.submittedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="empty-state" style={{ marginTop: 24 }}>{statusFilter ? 'No ' + statusLabels[statusFilter].toLowerCase() + ' change requests.' : 'No change requests yet.'}</div>
      )}
    </div>
  );
}
// ── Budget Tab ────────────────────────────────────────────────────
// Wrapper: Budget parent gets a sub-tab toggle (Spend / Cost Savings).
// The original content is now BudgetSpendTab; the new CostSavingsTab
// surfaces leak-detection signals (license waste, contract renewals,
// cyber-insurance opportunities, top vendor spend).
function BudgetTab({ clientId }) {
  var [subTab, setSubTab] = useState('spend');
  return (
    <div className="tab-content compliance-with-subtabs">
      <div className="compliance-subtabs">
        <button type="button" className={'compliance-subtab' + (subTab === 'spend' ? ' active' : '')} onClick={function() { setSubTab('spend'); }}>
          <span className="compliance-subtab-icon">$</span>
          <span>Spend</span>
        </button>
        <button type="button" className={'compliance-subtab' + (subTab === 'savings' ? ' active' : '')} onClick={function() { setSubTab('savings'); }}>
          <span className="compliance-subtab-icon">↓</span>
          <span>Cost Savings</span>
        </button>
      </div>
      {subTab === 'spend' && <BudgetSpendTab clientId={clientId} />}
      {subTab === 'savings' && <CostSavingsTab clientId={clientId} />}
    </div>
  );
}

// ── Cost Savings sub-tab ─────────────────────────────────────────────
// Single-pane "where can we cut costs / what's renewing?" view. Aggregates:
//   • Microsoft 365 license waste (live from Graph)
//   • Pax8 top spend (renegotiation candidates)
//   • Atlassian billing snapshot
//   • Cyber-insurance premium-impact estimate
//   • Manually tracked contracts (CRUD'd by admin) with renewal alerts
function CostSavingsTab({ clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [editing, setEditing] = useState(null);     // { id?, ... } — open editor
  var [toast, setToast] = useState(null);
  var isAdmin = !IS_CLIENT_VIEW;

  function showToast(m, t) { setToast({ msg: m, type: t || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  function fetchData() {
    setLoading(true);
    authFetch(API + '/clients/' + clientId + '/cost-savings')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'Cost savings calculation failed');
      })
      .catch(function() { setLoading(false); setError('Cost savings calculation failed'); });
  }

  useEffect(fetchData, [clientId]);

  function deleteContract(c) {
    if (!window.confirm('Delete contract for ' + c.vendor + '? This is logged in the audit trail and can be restored from the DB if needed.')) return;
    authFetch(API + '/admin/clients/' + clientId + '/contracts/' + c.id, { method: 'DELETE' })
      .then(function(r) { if (r.ok) { showToast('Contract deleted', 'ok'); fetchData(); } else { showToast('Delete failed', 'error'); } });
  }

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (error) return <div className="tab-content"><div className="empty-state" style={{ color: 'var(--accent-red)' }}>{error}</div></div>;
  if (!data) return <div className="tab-content"><div className="empty-state">No data.</div></div>;

  var s = data.signals || {};
  var fmt = function(n) { if (n == null) return '—'; return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }); };

  return (
    <div className="tab-content">
      <TabDescription text="Cost-savings opportunities surfaced from live data plus admin-tracked contracts. The opportunity number is the sum of unused license cost, projected cyber-insurance reduction (if controls allow), and renegotiation candidates ready for action." />

      {/* Hero — total annual opportunity */}
      <div className="savings-hero">
        <div className="savings-hero-main">
          <div className="savings-hero-eyebrow">Annual savings opportunity</div>
          <div className="savings-hero-value">{fmt(data.annualOpportunity)}<span className="savings-hero-unit">/year</span></div>
          <div className="savings-hero-context">
            Sum of license waste + projected cyber-insurance reduction.
            Pax8 / Atlassian / contract spend shown for renegotiation context.
          </div>
        </div>
        <div className="savings-hero-side">
          {s.licenses && s.licenses.annualWaste > 0 && (
            <div className="savings-hero-strip-item">
              <div className="savings-hero-strip-label">M365 unused licenses</div>
              <div className="savings-hero-strip-value">{fmt(s.licenses.annualWaste)}</div>
            </div>
          )}
          {s.cyberInsurance && s.cyberInsurance.estimatedAnnualSavings > 0 && (
            <div className="savings-hero-strip-item">
              <div className="savings-hero-strip-label">Cyber insurance reduction</div>
              <div className="savings-hero-strip-value">{fmt(s.cyberInsurance.estimatedAnnualSavings)}</div>
            </div>
          )}
        </div>
      </div>

      {/* M365 license waste detail */}
      {s.licenses && (
        <ContentBand title="Microsoft 365 License Waste" subtitle={s.licenses.error ? 'Error fetching' : (s.licenses.totalUnusedSeats || 0) + ' seats unused across ' + (s.licenses.rows ? s.licenses.rows.length : 0) + ' SKUs — ' + fmt(s.licenses.annualWaste) + '/year recoverable'} rightContent={<span className="live-badge">LIVE</span>}>
          {s.licenses.error
            ? <div className="empty-state" style={{ color: 'var(--accent-red)', padding: 24 }}>{s.licenses.error}</div>
            : !s.licenses.rows || s.licenses.rows.length === 0
              ? <div className="empty-state" style={{ padding: 24 }}>No license SKUs reported by this tenant.</div>
              : (
                <div className="savings-table">
                  <div className="savings-table-head">
                    <span>SKU</span>
                    <span style={{ textAlign: 'right' }}>Purchased</span>
                    <span style={{ textAlign: 'right' }}>Assigned</span>
                    <span style={{ textAlign: 'right' }}>Unused</span>
                    <span style={{ textAlign: 'right' }}>Utilization</span>
                    <span style={{ textAlign: 'right' }}>Annual waste</span>
                  </div>
                  {s.licenses.rows.map(function(r) {
                    var utilCls = r.utilizationPct >= 90 ? 'good' : r.utilizationPct >= 70 ? 'ok' : r.utilizationPct >= 50 ? 'warn' : 'bad';
                    return (
                      <div key={r.sku} className={'savings-table-row util-' + utilCls}>
                        <span><code>{r.sku}</code></span>
                        <span style={{ textAlign: 'right' }}>{r.purchased}</span>
                        <span style={{ textAlign: 'right' }}>{r.assigned}</span>
                        <span style={{ textAlign: 'right', fontWeight: r.unused > 0 ? 700 : 400, color: r.unused > 0 ? 'var(--accent-amber)' : 'inherit' }}>{r.unused}</span>
                        <span style={{ textAlign: 'right', color: utilCls === 'bad' ? '#ef4444' : utilCls === 'warn' ? '#f59e0b' : utilCls === 'good' ? '#22c55e' : 'inherit', fontWeight: 600 }}>{r.utilizationPct}%</span>
                        <span style={{ textAlign: 'right', fontWeight: 700, color: r.annualWaste > 0 ? 'var(--accent-amber)' : 'inherit' }}>{fmt(r.annualWaste)}</span>
                      </div>
                    );
                  })}
                </div>
              )
          }
        </ContentBand>
      )}

      {/* Cyber insurance summary card */}
      {s.cyberInsurance && (
        <ContentBand title="Cyber Insurance Renegotiation" subtitle={s.cyberInsurance.error ? s.cyberInsurance.error : (s.cyberInsurance.carrier || 'No carrier on file') + ' · current premium ' + fmt(s.cyberInsurance.currentPremium) + (s.cyberInsurance.renewalDate ? ' · renews ' + new Date(s.cyberInsurance.renewalDate).toLocaleDateString() : '')}>
          {!s.cyberInsurance.error && s.cyberInsurance.estimatedAnnualSavings > 0 ? (
            <div className="savings-cyber-callout">
              <div className="savings-cyber-savings">
                <div className="savings-cyber-label">Estimated savings at renewal</div>
                <div className="savings-cyber-value">{fmt(s.cyberInsurance.estimatedAnnualSavings)}<span className="savings-cyber-unit">/year</span></div>
                <div className="savings-cyber-pct">{s.cyberInsurance.impactPctLow}–{s.cyberInsurance.impactPctHigh}% range based on evidenced controls</div>
              </div>
              <div className="savings-cyber-cta">
                <div>Review the underwriting checklist and present evidence packs at renewal to capture this savings.</div>
                <div style={{ marginTop: 8, fontSize: 11, opacity: 0.7 }}>Open <strong>Compliance → Cyber Insurance</strong> for the full breakdown.</div>
              </div>
            </div>
          ) : (
            <div className="empty-state" style={{ padding: 20 }}>{s.cyberInsurance.error || 'No carrier or premium on file. Add it in Admin → Integrations → Edit.'}</div>
          )}
        </ContentBand>
      )}

      {/* Pax8 top spend */}
      {s.pax8 && !s.pax8.error && s.pax8.subscriptionCount > 0 && (
        <ContentBand title="Pax8 Top Spend (Renegotiation Candidates)" subtitle={s.pax8.subscriptionCount + ' active subscriptions · ' + fmt(s.pax8.totalAnnualCost) + '/year'} rightContent={<span className="live-badge">LIVE</span>}>
          <div className="savings-topspend">
            {s.pax8.topSpend.map(function(t, i) {
              return (
                <div key={t.id} className="savings-topspend-row">
                  <span className="savings-topspend-rank">#{i + 1}</span>
                  <span className="savings-topspend-product"><code>{t.productId}</code> × {t.quantity}</span>
                  <span className="savings-topspend-cost">{fmt(t.monthlyCost)}<span className="muted">/mo</span></span>
                </div>
              );
            })}
          </div>
        </ContentBand>
      )}

      {/* Contracts (manually tracked) */}
      <ContentBand
        title="Tracked Contracts"
        subtitle={s.contracts ? (s.contracts.count + ' contract' + (s.contracts.count === 1 ? '' : 's') + ' · ' + fmt(s.contracts.totalAnnualCost) + '/year' + (s.contracts.expiringSoonCount > 0 ? ' · ' + s.contracts.expiringSoonCount + ' renewing soon' : '')) : ''}
        rightContent={isAdmin && <button className="admin-btn primary sm" onClick={function() { setEditing({}); }}>+ New contract</button>}
      >
        {(!s.contracts || s.contracts.count === 0)
          ? <div className="empty-state" style={{ padding: 24 }}>{isAdmin ? 'No contracts tracked yet. Add ISP, hardware leases, professional services, or any non-Pax8 vendor agreement here so renewals show up in the Cost Savings view.' : 'No contracts on file yet. Your account team will populate this view.'}</div>
          : (
            <div className="savings-contracts">
              {s.contracts.contracts.map(function(c) {
                var statusCls = c.renewal_status;
                var statusLabel = statusCls === 'expired' ? Math.abs(c.days_to_expire) + 'd past expiry'
                  : statusCls === 'critical' ? 'Renews in ' + c.days_to_expire + 'd'
                  : statusCls === 'soon' ? 'Renews in ' + c.days_to_expire + 'd'
                  : statusCls === 'ok' ? c.days_to_expire + 'd to renewal'
                  : 'No expiry on file';
                return (
                  <div key={c.id} className={'savings-contract savings-contract-' + statusCls}>
                    <div className="savings-contract-main">
                      <div className="savings-contract-head">
                        <span className="savings-contract-vendor">{c.vendor}</span>
                        {c.product && <span className="savings-contract-product">— {c.product}</span>}
                        <span className={'savings-contract-type type-' + (c.contract_type || 'other')}>{c.contract_type || 'other'}</span>
                        {c.auto_renew && <span className="savings-contract-autorenew">Auto-renew</span>}
                      </div>
                      <div className="savings-contract-meta">
                        <span><strong>{fmt(c.monthly_cost)}</strong>/mo · <strong>{fmt(c.annual_cost)}</strong>/yr</span>
                        {c.expires_at && <span>Expires {new Date(c.expires_at).toLocaleDateString()}</span>}
                        {c.renewal_notice_days && <span>{c.renewal_notice_days}d notice required</span>}
                      </div>
                      {c.notes && <div className="savings-contract-notes">{c.notes}</div>}
                    </div>
                    <div className="savings-contract-side">
                      <span className={'savings-contract-status status-' + statusCls}>{statusLabel}</span>
                      {c.days_to_notice_deadline != null && c.days_to_notice_deadline >= 0 && c.days_to_notice_deadline < 30 && (
                        <span className="savings-contract-notice-warning">⚠ {c.days_to_notice_deadline}d to give cancel notice</span>
                      )}
                      {isAdmin && (
                        <div className="savings-contract-actions">
                          <button className="admin-btn sm" onClick={function() { setEditing(c); }}>Edit</button>
                          <button className="admin-btn sm" onClick={function() { deleteContract(c); }}>Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        }
      </ContentBand>

      {editing && isAdmin && (
        <ContractEditorModal
          contract={editing}
          clientId={clientId}
          onClose={function() { setEditing(null); }}
          onSaved={function() { setEditing(null); fetchData(); showToast('Contract saved', 'ok'); }}
          onError={function(m) { showToast(m || 'Save failed', 'error'); }}
        />
      )}
      {toast && <div className={'admin-toast ' + (toast.type || 'info')}>{toast.msg}</div>}

      <div className="cyber-disclaimer">
        Generated {new Date(data.generatedAt).toLocaleString()}. License-waste numbers reflect Microsoft Graph at the time of fetch; cyber-insurance projection is an estimated range based on industry-published underwriting factors. Treat as renewal-conversation guidance, not a binding quote.
      </div>
    </div>
  );
}

function ContractEditorModal({ contract, clientId, onClose, onSaved, onError }) {
  var isEdit = contract && contract.id;
  var [form, setForm] = useState({
    vendor: contract.vendor || '',
    product: contract.product || '',
    contract_type: contract.contract_type || 'saas',
    monthly_cost: contract.monthly_cost == null ? '' : String(contract.monthly_cost),
    starts_at: contract.starts_at ? String(contract.starts_at).slice(0, 10) : '',
    expires_at: contract.expires_at ? String(contract.expires_at).slice(0, 10) : '',
    auto_renew: !!contract.auto_renew,
    renewal_notice_days: contract.renewal_notice_days == null ? '' : String(contract.renewal_notice_days),
    notes: contract.notes || ''
  });
  var [saving, setSaving] = useState(false);

  function setField(name, value) { setForm(function(prev) { var next = Object.assign({}, prev); next[name] = value; return next; }); }

  function save() {
    if (!form.vendor.trim()) { onError('Vendor is required'); return; }
    setSaving(true);
    var url = isEdit
      ? API + '/admin/clients/' + clientId + '/contracts/' + contract.id
      : API + '/admin/clients/' + clientId + '/contracts';
    var method = isEdit ? 'PUT' : 'POST';
    authFetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({}, form, { monthly_cost: form.monthly_cost === '' ? 0 : Number(form.monthly_cost) }))
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setSaving(false); if (x.ok) onSaved(); else onError(x.d.error || 'Save failed'); })
      .catch(function() { setSaving(false); onError('Save failed'); });
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 560 }}>
        <div className="admin-modal-header">{isEdit ? 'Edit contract' : 'New contract'}</div>
        <div className="admin-modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          <div className="admin-form-grid two">
            <div className="admin-field"><label>Vendor</label>
              <input type="text" value={form.vendor} onChange={function(e) { setField('vendor', e.target.value); }} placeholder="e.g. Comcast Business" />
            </div>
            <div className="admin-field"><label>Product / line</label>
              <input type="text" value={form.product} onChange={function(e) { setField('product', e.target.value); }} placeholder="e.g. 500/500 fiber" />
            </div>
            <div className="admin-field"><label>Type</label>
              <select value={form.contract_type} onChange={function(e) { setField('contract_type', e.target.value); }}>
                <option value="isp">ISP / Connectivity</option>
                <option value="saas">SaaS / Software</option>
                <option value="hardware">Hardware / Lease</option>
                <option value="services">Professional Services</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="admin-field"><label>Monthly cost (USD)</label>
              <input type="text" inputMode="decimal" value={form.monthly_cost} onChange={function(e) { setField('monthly_cost', e.target.value); }} placeholder="500" />
            </div>
            <div className="admin-field"><label>Starts at</label>
              <input type="date" value={form.starts_at} onChange={function(e) { setField('starts_at', e.target.value); }} />
            </div>
            <div className="admin-field"><label>Expires at</label>
              <input type="date" value={form.expires_at} onChange={function(e) { setField('expires_at', e.target.value); }} />
            </div>
            <div className="admin-field"><label>Renewal notice (days)</label>
              <input type="text" inputMode="numeric" value={form.renewal_notice_days} onChange={function(e) { setField('renewal_notice_days', e.target.value); }} placeholder="60" />
              <div className="admin-field-hint">If client must give notice N days before expiry to cancel.</div>
            </div>
            <div className="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 22 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', textTransform: 'none', fontSize: 13, letterSpacing: 0, fontWeight: 400, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.auto_renew} onChange={function(e) { setField('auto_renew', e.target.checked); }} /> Auto-renews
              </label>
            </div>
            <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Notes</label>
              <textarea className="admin-textarea" rows={3} value={form.notes} onChange={function(e) { setField('notes', e.target.value); }} placeholder="Account contact, MSA reference, renegotiation history…" />
            </div>
          </div>
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="admin-btn primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function BudgetSpendTab({ clientId }) {
  const [pax8Data, setPax8Data] = useState(null);
  const [licensingData, setLicensingData] = useState(null);
  const [jiraData, setJiraData] = useState(null);
  const [vmData, setVmData] = useState(null);
  const [loading, setLoading] = useState(true);
  var paxSort = useTableSort('product', 'asc');
  var vmSort = useTableSort('name', 'asc');
  const [forecastDelta, setForecastDelta] = useState(0);
  const [costHistory, setCostHistory] = useState(null);

  useEffect(function() {
    setLoading(true); setPax8Data(null); setLicensingData(null); setJiraData(null); setVmData(null); setForecastDelta(0); setCostHistory(null);
    Promise.all([
      authFetch(API + '/clients/' + clientId + '/subscriptions').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/licensing').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/jira').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/azure-vms').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/billing-history').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) { setPax8Data(results[0]); setLicensingData(results[1]); setJiraData(results[2]); setVmData(results[3]); setCostHistory(results[4]); setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;

  var costSources = [];
  var totalMonthly = 0;
  var pax8Monthly = 0, pax8Subs = [], pax8PerUserCost = 0, pax8UserCount = 0;
  if (pax8Data && pax8Data.enabled) {
    pax8Monthly = pax8Data.summary.totalMonthlyCost;
    pax8Subs = pax8Data.subscriptions || [];
    totalMonthly += pax8Monthly;
    costSources.push({ name: 'Microsoft (via Pax8)', monthly: pax8Monthly, source: 'pax8', live: true });
    var userSubs = pax8Subs.filter(function(s) { return s.quantity > 0 && s.pricePerUnit > 0; });
    if (userSubs.length > 0) { pax8UserCount = Math.max.apply(null, userSubs.map(function(s) { return s.quantity; })); pax8PerUserCost = pax8Monthly / pax8UserCount; }
  }
  var atlassianMonthly = 0, atlassianProducts = [];
  if (jiraData && jiraData.billing) { atlassianMonthly = jiraData.billing.totalMonthlyCost; atlassianProducts = jiraData.billing.products || []; totalMonthly += atlassianMonthly; costSources.push({ name: 'Atlassian', monthly: atlassianMonthly, source: 'atlassian', live: false }); }
  var vmMonthly = 0, vms = [];
  if (vmData && vmData.enabled) { vmMonthly = vmData.costs ? vmData.costs.forecast : 0; vms = vmData.vms || []; totalMonthly += vmMonthly; costSources.push({ name: 'Azure Infrastructure', monthly: vmMonthly, source: 'azure', live: true }); }
  var totalAnnual = totalMonthly * 12;

  var forecastUsers = pax8UserCount + forecastDelta;
  if (forecastUsers < 1) forecastUsers = 1;
  var forecastPax8 = pax8PerUserCost * forecastUsers;
  var forecastAtlassian = atlassianMonthly;
  if (jiraData && jiraData.billing && jiraData.billing.totalLicensedUsers > 0) forecastAtlassian = (atlassianMonthly / jiraData.billing.totalLicensedUsers) * forecastUsers;
  var forecastTotal = forecastPax8 + forecastAtlassian + (vmMonthly);
  var forecastDiff = forecastTotal - totalMonthly;

  if (costSources.length === 0) return <div className="tab-content"><EmptyStateAction icon="💰" title="No Billing Data" text="Cost tracking requires Pax8, Atlassian, or Azure integration. Once connected, you'll see consolidated spend, per-user costs, and cost forecasting tools." cta="Contact CloudByte Group to enable" /></div>;

  var fInputStyle = { background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 14, fontFamily: 'var(--font-mono)', width: 80, textAlign: 'center', outline: 'none' };

  return (
    <div className="tab-content">
      <TabDescription text="Consolidated IT spend across Microsoft licensing (Pax8), Atlassian subscriptions, and Azure infrastructure. Use the forecasting tool below to model cost impact from headcount changes." />
      <DataFreshness live={true} />
      <ContentBand title="Cost Overview">
        <QuickStats items={[
          { label: 'Est. Monthly', value: '$' + Math.round(totalMonthly).toLocaleString() },
          { label: 'Est. Annual', value: '$' + Math.round(totalAnnual).toLocaleString() },
          { label: 'Cost Sources', value: costSources.length },
          pax8UserCount > 0 ? { label: 'Cost Per User', value: '$' + Math.round(pax8PerUserCost).toLocaleString() + '/mo' } : null,
        ].filter(Boolean)} />
      </ContentBand>
      {costHistory && costHistory.months && costHistory.months.length > 0 && (
        <ContentBand title="Monthly Cost Trend" subtitle={'Last ' + costHistory.months.length + ' month' + (costHistory.months.length > 1 ? 's' : '') + ' of billing snapshots'}>
          {(function() {
            var allMonths = costHistory.months.slice();
            var maxCost = Math.max.apply(null, allMonths.map(function(m) { return m.billing.totalMonthly || 0; }).concat([totalMonthly]));
            var monthLabels = { '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec' };
            var rows = allMonths.map(function(entry) {
              var monthNum = entry.month.split('-')[1];
              var label = monthLabels[monthNum] + ' ' + entry.month.split('-')[0];
              var pct = maxCost > 0 ? Math.round((entry.billing.totalMonthly / maxCost) * 100) : 0;
              return (
                <div key={entry.month} className="cost-trend-row">
                  <span className="cost-trend-label">{label}</span>
                  <div className="cost-trend-bar-wrap">
                    <div className="cost-trend-bar" style={{ width: pct + '%' }} />
                  </div>
                  <span className="cost-trend-value">{'$' + Math.round(entry.billing.totalMonthly).toLocaleString()}</span>
                  {entry.delta ? (
                    <span className={'cost-trend-delta' + (entry.delta.direction === 'up' ? ' cost-up' : entry.delta.direction === 'down' ? ' cost-down' : '')}>
                      {entry.delta.direction === 'up' ? '↑' : entry.delta.direction === 'down' ? '↓' : '→'} {entry.delta.direction !== 'flat' ? (entry.delta.amount >= 0 ? '+$' : '-$') + Math.abs(Math.round(entry.delta.amount)).toLocaleString() : '$0'}
                      {entry.delta.pct !== 0 && <span className="cost-trend-pct"> ({entry.delta.pct > 0 ? '+' : ''}{entry.delta.pct}%)</span>}
                    </span>
                  ) : <span className="cost-trend-delta cost-trend-delta-empty" />}
                </div>
              );
            });
            // "Current" row comparing live data vs last snapshot
            var currentPct = maxCost > 0 ? Math.round((totalMonthly / maxCost) * 100) : 100;
            var lastHistorical = allMonths[allMonths.length - 1].billing.totalMonthly;
            var diff = totalMonthly - lastHistorical;
            var pctDiff = lastHistorical > 0 ? Math.round((diff / lastHistorical) * 1000) / 10 : 0;
            rows.push(
              <div key="current" className="cost-trend-row cost-trend-current">
                <span className="cost-trend-label">Current</span>
                <div className="cost-trend-bar-wrap">
                  <div className="cost-trend-bar cost-trend-bar-current" style={{ width: currentPct + '%' }} />
                </div>
                <span className="cost-trend-value">{'$' + Math.round(totalMonthly).toLocaleString()}</span>
                {Math.abs(diff) >= 1 ? (
                  <span className={'cost-trend-delta' + (diff > 0 ? ' cost-up' : ' cost-down')}>
                    {diff > 0 ? '↑' : '↓'} {diff > 0 ? '+$' : '-$'}{Math.abs(Math.round(diff)).toLocaleString()}
                    {pctDiff !== 0 && <span className="cost-trend-pct"> ({pctDiff > 0 ? '+' : ''}{pctDiff}%)</span>}
                  </span>
                ) : <span className="cost-trend-delta cost-trend-delta-empty" />}
              </div>
            );
            return <div className="cost-trend-chart">{rows}</div>;
          })()}
          {costHistory.months.length < 2 && (
            <div className="cost-trend-note">Cost tracking started — monthly trends and deltas will appear as more snapshots are recorded.</div>
          )}
        </ContentBand>
      )}
      <ContentBand title="Spend by Vendor">
        <SegmentBar size="lg" segments={costSources.map(function(src) { return { label: src.name, value: src.monthly, color: src.source === 'pax8' ? '#5b9ef4' : src.source === 'atlassian' ? '#60a5fa' : '#a78bfa' }; })} />
        <MetricList>
          {costSources.map(function(src) {
            var pct = totalMonthly > 0 ? Math.round((src.monthly / totalMonthly) * 100) : 0;
            return <MetricListItem key={src.name} icon="$" iconColor={src.source === 'pax8' ? '#5b9ef4' : src.source === 'atlassian' ? '#60a5fa' : '#a78bfa'} label={src.name} sublabel={pct + '% of total'} value={'$' + Math.round(src.monthly).toLocaleString() + '/mo'} valueColor={src.source === 'pax8' ? '#5b9ef4' : '#60a5fa'} />;
          })}
        </MetricList>
      </ContentBand>
      {pax8Subs.length > 0 && (
        <CollapsibleSection title="Microsoft Subscriptions (Pax8)" defaultOpen={true} rightContent={<span className="live-badge">LIVE</span>}>
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="product" current={paxSort.sortField} dir={paxSort.sortDir} onSort={paxSort.toggleSort}>Product</SortTh><SortTh field="vendor" current={paxSort.sortField} dir={paxSort.sortDir} onSort={paxSort.toggleSort}>Vendor</SortTh><SortTh field="qty" current={paxSort.sortField} dir={paxSort.sortDir} onSort={paxSort.toggleSort}>Qty</SortTh><th>Unit Price</th><SortTh field="cost" current={paxSort.sortField} dir={paxSort.sortDir} onSort={paxSort.toggleSort}>Monthly Cost</SortTh><th>Term</th><th>Commitment</th></tr></thead>
              <tbody>
                {sortRows(pax8Subs, paxSort.sortField, paxSort.sortDir, { product: function(s) { return s.productName; }, vendor: function(s) { return s.vendorName; }, qty: function(s) { return s.quantity; }, cost: function(s) { return s.monthlyCost || 0; } }).map(function(s, i) { return (<tr key={s.id || i}><td className="posture-device-name" title={s.productName}>{s.productName}</td><td style={{ fontSize: 12 }}>{s.vendorName}</td><td>{s.quantity}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>${s.pricePerUnit ? s.pricePerUnit.toFixed(2) : '—'}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>${s.monthlyCost ? s.monthlyCost.toFixed(2) : '—'}</td><td style={{ fontSize: 11 }}>{s.billingTerm || '—'}</td><td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.commitmentTerm || '—'}{s.commitmentEnd ? ' (ends ' + new Date(s.commitmentEnd).toLocaleDateString() + ')' : ''}</td></tr>); })}
                <tr style={{ fontWeight: 700 }}><td colSpan="4" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>Total</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--accent-blue)' }}>${Math.round(pax8Monthly).toLocaleString()}</td><td colSpan="2"></td></tr>
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      )}
      {atlassianProducts.length > 0 && (
        <CollapsibleSection title="Atlassian Subscriptions" defaultOpen={true}>
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><th>Product</th><th>Plan</th><th>Users</th><th>Monthly Cost</th><th>Cost / User</th></tr></thead>
              <tbody>
                {atlassianProducts.map(function(p, i) { var perUser = p.users > 0 ? (p.monthlyCost / p.users) : 0; return (<tr key={i}><td className="posture-device-name">{p.name}</td><td><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, backgroundColor: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}>{p.plan}</span></td><td>{p.users || '—'}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.monthlyCost > 0 ? '$' + p.monthlyCost.toFixed(2) : '—'}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{perUser > 0 ? '$' + perUser.toFixed(2) : '—'}</td></tr>); })}
                <tr style={{ fontWeight: 700 }}><td colSpan="3" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>Total</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--accent-blue)' }}>${Math.round(atlassianMonthly).toLocaleString()}</td><td></td></tr>
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>Atlassian billing based on admin portal snapshots.</div>
        </CollapsibleSection>
      )}
      {vmData && vmData.enabled ? (
        <ContentBand title="Azure Virtual Machines" rightContent={<span className="live-badge">LIVE</span>}>
          <SegmentBar segments={[
            { label: 'Running', value: vmData.summary.running || 0, color: '#22c55e' },
            { label: 'Stopped', value: vmData.summary.stopped || 0, color: '#64748b' },
          ]} />
          <QuickStats items={[
            { label: 'Total VMs', value: vmData.summary.total },
            { label: 'Running', value: vmData.summary.running },
            { label: 'MTD Actual', value: '$' + Math.round(vmData.summary.actualMonthlyCost).toLocaleString() },
            { label: 'Forecast', value: '$' + Math.round(vmData.summary.forecastMonthlyCost).toLocaleString() },
          ]} />
          {vms.length > 0 && (
            <div className="posture-table-wrap" style={{ marginTop: 12 }}>
              <table className="posture-table">
                <thead><tr><SortTh field="name" current={vmSort.sortField} dir={vmSort.sortDir} onSort={vmSort.toggleSort}>VM Name</SortTh><SortTh field="size" current={vmSort.sortField} dir={vmSort.sortDir} onSort={vmSort.toggleSort}>Size</SortTh><SortTh field="os" current={vmSort.sortField} dir={vmSort.sortDir} onSort={vmSort.toggleSort}>OS</SortTh><SortTh field="status" current={vmSort.sortField} dir={vmSort.sortDir} onSort={vmSort.toggleSort}>Status</SortTh><SortTh field="region" current={vmSort.sortField} dir={vmSort.sortDir} onSort={vmSort.toggleSort}>Region</SortTh><th>Resource Group</th><th>RG Cost (MTD)</th></tr></thead>
                <tbody>{sortRows(vms, vmSort.sortField, vmSort.sortDir, { name: function(v) { return v.name; }, size: function(v) { return v.size; }, os: function(v) { return v.os; }, status: function(v) { return v.status; }, region: function(v) { return v.location; } }).map(function(vm, i) { var isRunning = vm.status === 'VM running'; return (<tr key={i} className={!isRunning ? 'posture-row-offline' : ''}><td className="posture-device-name">{vm.name}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{vm.size}</td><td>{vm.os}</td><td><span className={'posture-status ' + (isRunning ? 'posture-online' : 'posture-offline')}>{isRunning ? 'Running' : vm.status}</span></td><td style={{ fontSize: 12 }}>{vm.location}</td><td style={{ fontSize: 12 }}>{vm.resourceGroup}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{vmData.costs && vmData.costs.byResourceGroup[vm.resourceGroup] ? '$' + vmData.costs.byResourceGroup[vm.resourceGroup].toFixed(2) : '—'}</td></tr>); })}</tbody>
              </table>
            </div>
          )}
          {vmData.costs && Object.keys(vmData.costs.byService).length > 0 && (
            <ContentBand title="Cost Breakdown by Service (MTD)">
              <QuickStats items={Object.entries(vmData.costs.byService).sort(function(a, b) { return b[1] - a[1]; }).map(function(entry) { return { label: entry[0], value: '$' + entry[1].toFixed(2) }; })} />
            </ContentBand>
          )}
        </ContentBand>
      ) : (
        <><div className="section-title" style={{ marginTop: 28 }}>Azure Virtual Machines</div><div className="empty-state">Azure VM monitoring is not configured for this client.</div></>
      )}
      {pax8UserCount > 0 && (
        <>
          <div className="section-title" style={{ marginTop: 28 }}>Cost Forecasting</div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Adjust the user count to estimate how costs would change.</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Current Users</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)' }}>{pax8UserCount}</div>
              <div style={{ fontSize: 20, color: 'var(--text-muted)' }}>→</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Forecast Users</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button className="insights-btn" style={{ fontSize: 16, padding: '4px 12px', fontWeight: 700 }} onClick={function() { setForecastDelta(forecastDelta - 5); }}>-5</button>
                <button className="insights-btn" style={{ fontSize: 16, padding: '4px 12px', fontWeight: 700 }} onClick={function() { setForecastDelta(forecastDelta - 1); }}>-1</button>
                <input style={fInputStyle} value={forecastUsers} onChange={function(e) { var v = parseInt(e.target.value) || 1; setForecastDelta(v - pax8UserCount); }} />
                <button className="insights-btn" style={{ fontSize: 16, padding: '4px 12px', fontWeight: 700 }} onClick={function() { setForecastDelta(forecastDelta + 1); }}>+1</button>
                <button className="insights-btn" style={{ fontSize: 16, padding: '4px 12px', fontWeight: 700 }} onClick={function() { setForecastDelta(forecastDelta + 5); }}>+5</button>
              </div>
              {forecastDelta !== 0 && <button className="insights-btn" onClick={function() { setForecastDelta(0); }}>Reset</button>}
            </div>
            <div className="stats-grid">
              <StatCard title="Forecast Monthly" value={'$' + Math.round(forecastTotal).toLocaleString()} accent={forecastDiff > 0 ? '#f59e0b' : forecastDiff < 0 ? '#22c55e' : '#60a5fa'} />
              <StatCard title="Forecast Annual" value={'$' + Math.round(forecastTotal * 12).toLocaleString()} accent="#a78bfa" />
              <StatCard title="Monthly Change" value={(forecastDiff >= 0 ? '+$' : '-$') + Math.abs(Math.round(forecastDiff)).toLocaleString()} accent={forecastDiff > 0 ? '#f59e0b' : forecastDiff < 0 ? '#22c55e' : '#64748b'} subtitle={forecastDelta > 0 ? '+' + forecastDelta + ' users' : forecastDelta < 0 ? forecastDelta + ' users' : 'no change'} />
              <StatCard title="Annual Change" value={(forecastDiff >= 0 ? '+$' : '-$') + Math.abs(Math.round(forecastDiff * 12)).toLocaleString()} accent={forecastDiff > 0 ? '#f59e0b' : forecastDiff < 0 ? '#22c55e' : '#64748b'} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Cyber Insurance tab ──────────────────────────────────────────────
// Maps the live CMMC control state to a consolidated cyber underwriting
// checklist + estimates premium impact. Reuses control snapshot from the
// CMMC tab — opening this tab without first opening CMMC will show
// 'unknown' for every item until the snapshot populates.

function _fmtCurrency(n) {
  if (n == null || isNaN(n)) return '—';
  return '$' + Number(n).toLocaleString();
}
function _ciAnswerCls(a) { return a === 'yes' ? 'yes' : a === 'no' ? 'no' : a === 'partial' ? 'partial' : 'unknown'; }
function _ciAnswerLabel(a) { return a === 'yes' ? 'Yes' : a === 'no' ? 'No' : a === 'partial' ? 'Partial' : 'Verify'; }

function CyberInsuranceItemRow({ item }) {
  var [open, setOpen] = useState(false);
  var hasSourceData = item.sourceControls && item.sourceControls.length > 0;
  return (
    <div className={'cyber-item cyber-item-' + _ciAnswerCls(item.answer) + ' weight-' + item.weight}>
      <button type="button" className="cyber-item-head" onClick={function() { setOpen(!open); }}>
        <span className={'cyber-answer cyber-answer-' + _ciAnswerCls(item.answer)}>
          <span className="cyber-answer-dot" />
          {_ciAnswerLabel(item.answer)}
        </span>
        <span className="cyber-item-question">{item.question}</span>
        {item.weight === 'critical' && <span className="cyber-weight-badge critical">Critical</span>}
        <span className="cyber-item-impact">{item.impact_label}</span>
        <span className="cyber-item-toggle">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="cyber-item-body">
          <div className="cyber-item-detail">{item.detail}</div>
          {hasSourceData && (
            <div className="cyber-item-sources">
              <div className="cyber-item-sources-label">Source controls:</div>
              {item.sourceControls.map(function(sc) {
                return (
                  <div key={sc.id} className={'cyber-source-control ' + sc.state}>
                    <code>{sc.id}</code>
                    <span className={'cmmc-state ' + sc.state}><span className="cmmc-state-dot" />{sc.state === 'pass' ? 'Evidenced' : sc.state === 'fail' ? 'Gap' : 'Verify'}</span>
                    <span className="cyber-source-reason">{sc.reason || '—'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CyberInsuranceTab({ c, clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [filter, setFilter] = useState('all');     // all | gaps | critical

  useEffect(function() {
    setLoading(true);
    authFetch(API + '/clients/' + clientId + '/cyber-insurance')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'Cyber insurance evaluation failed');
      })
      .catch(function() { setLoading(false); setError('Cyber insurance evaluation failed'); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (error) return <div className="tab-content"><div className="empty-state" style={{ color: 'var(--accent-red)' }}>{error}</div></div>;
  if (!data) return <div className="tab-content"><div className="empty-state">No cyber insurance data.</div></div>;

  var p = data.policy || {};
  var items = data.items || [];
  var visibleItems = items.slice();
  if (filter === 'gaps') visibleItems = visibleItems.filter(function(i) { return i.answer === 'no' || i.answer === 'unknown' || i.answer === 'partial'; });
  if (filter === 'critical') visibleItems = visibleItems.filter(function(i) { return i.weight === 'critical'; });
  // Critical first, then by answer (no/unknown first), then alphabetical
  visibleItems.sort(function(a, b) {
    var w = { critical: 0, high: 1, medium: 2, low: 3 };
    if (w[a.weight] !== w[b.weight]) return w[a.weight] - w[b.weight];
    var r = { no: 0, unknown: 1, partial: 2, yes: 3 };
    if (r[a.answer] !== r[b.answer]) return r[a.answer] - r[b.answer];
    return 0;
  });

  var renewLabel = data.daysToRenewal == null ? null
    : data.daysToRenewal === 0 ? 'Renews today'
    : data.daysToRenewal > 0 ? data.daysToRenewal + ' days to renewal'
    : Math.abs(data.daysToRenewal) + ' days past renewal';
  var renewClass = data.daysToRenewal == null ? '' : data.daysToRenewal < 0 ? 'overdue' : data.daysToRenewal < 60 ? 'soon' : 'ok';

  return (
    <div className="tab-content">
      <TabDescription text="Cyber insurance underwriting readiness — every question carriers ask (Coalition, At-Bay, Beazley, Travelers, Chubb), answered automatically from the live control state in your CMMC evaluation. Yes answers stack into a premium-reduction estimate based on industry-published ranges." />

      {data.hint && <div className="cyber-hint-banner">{data.hint}</div>}

      {/* Hero — policy + impact estimate */}
      <div className="cyber-hero">
        <div className="cyber-hero-policy">
          <div className="cyber-hero-eyebrow">Cyber Insurance Policy</div>
          {p.carrier
            ? <>
                <div className="cyber-hero-carrier">{p.carrier}</div>
                <div className="cyber-hero-policy-meta">
                  {p.policyNumber && <span>Policy <code>{p.policyNumber}</code></span>}
                  {p.coverageLimit && <span>Coverage limit {_fmtCurrency(p.coverageLimit)}</span>}
                  {p.deductible && <span>Deductible {_fmtCurrency(p.deductible)}</span>}
                </div>
                <div className="cyber-hero-premium">
                  <span className="cyber-hero-premium-label">Annual premium</span>
                  <span className="cyber-hero-premium-value">{_fmtCurrency(p.premiumAnnual)}</span>
                </div>
                {renewLabel && <div className={'cyber-hero-renewal cyber-hero-renewal-' + renewClass}>{renewLabel}{p.renewalDate ? ' (' + new Date(p.renewalDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ')' : ''}</div>}
                {p.notes && <div className="cyber-hero-notes">{p.notes}</div>}
              </>
            : <div className="cyber-hero-empty">No carrier on file yet. Add carrier, policy number, premium, and renewal date in <strong>Admin → Integrations → Edit</strong> for this client to enable savings projections.</div>
          }
        </div>

        <div className="cyber-hero-impact">
          <div className="cyber-hero-eyebrow">Estimated premium impact</div>
          {data.impact && (data.impact.pctLow > 0 || data.impact.pctHigh > 0) ? (
            <>
              <div className="cyber-hero-impact-pct">
                <span>{data.impact.pctLow}<span className="muted">–</span>{data.impact.pctHigh}</span>
                <span className="cyber-hero-impact-unit">%</span>
              </div>
              <div className="cyber-hero-impact-label">reduction available based on your evidenced controls</div>
              {data.dollarSavings && (
                <div className="cyber-hero-impact-dollars">
                  ≈ {_fmtCurrency(data.dollarSavings.low)} – {_fmtCurrency(data.dollarSavings.high)} / year
                </div>
              )}
              {data.impact.cappedNote && <div className="cyber-hero-impact-note">{data.impact.cappedNote}</div>}
            </>
          ) : (
            <div className="cyber-hero-impact-empty">Open the CMMC tab to populate the underwriting checklist with live control state.</div>
          )}
          <div className="cyber-tally">
            <span className="cyber-tally-pill yes"><strong>{data.tally.yes || 0}</strong> Yes</span>
            <span className="cyber-tally-pill partial"><strong>{data.tally.partial || 0}</strong> Partial</span>
            <span className="cyber-tally-pill no"><strong>{data.tally.no || 0}</strong> No</span>
            <span className="cyber-tally-pill unknown"><strong>{data.tally.unknown || 0}</strong> Verify</span>
          </div>
        </div>
      </div>

      {/* Eligibility risk banner */}
      {data.eligibilityRisks && data.eligibilityRisks.length > 0 && (
        <div className="cyber-eligibility-risk">
          <div className="cyber-eligibility-risk-title">⚠ Eligibility risk — {data.eligibilityRisks.length} critical control{data.eligibilityRisks.length === 1 ? '' : 's'} unresolved</div>
          <div className="cyber-eligibility-risk-body">Carriers may decline or materially increase rates without these. Resolve before next renewal:</div>
          <ul>
            {data.eligibilityRisks.map(function(r) {
              return <li key={r.id}>{r.question}</li>;
            })}
          </ul>
        </div>
      )}

      {/* Filter */}
      <ContentBand
        title="Underwriting Checklist"
        subtitle="Consolidated questions across major carriers — auto-answered from live control state"
        rightContent={
          <div className="cyber-filter">
            <button className={'cyber-filter-btn' + (filter === 'all' ? ' active' : '')} onClick={function() { setFilter('all'); }}>All ({items.length})</button>
            <button className={'cyber-filter-btn' + (filter === 'gaps' ? ' active' : '')} onClick={function() { setFilter('gaps'); }}>Gaps ({(data.tally.no||0) + (data.tally.partial||0) + (data.tally.unknown||0)})</button>
            <button className={'cyber-filter-btn' + (filter === 'critical' ? ' active' : '')} onClick={function() { setFilter('critical'); }}>Critical ({items.filter(function(i) { return i.weight === 'critical'; }).length})</button>
          </div>
        }
      >
        <div className="cyber-item-list">
          {visibleItems.map(function(i) { return <CyberInsuranceItemRow key={i.id} item={i} />; })}
          {visibleItems.length === 0 && <div className="empty-state" style={{ padding: 24 }}>No items match the current filter.</div>}
        </div>
      </ContentBand>

      <div className="cyber-disclaimer">
        Premium-impact ranges are sourced from carrier-published reports (Coalition Active Insurance, Marsh State of the Cyber Market, At-Bay Tech-Risk Benchmark, 2023–2025). Actual savings depend on the specific carrier's appetite, your industry, and prior claims history. This dashboard is a guide for renewal conversations — not a binding quote.
      </div>
    </div>
  );
}

// ── CMMC live-evaluation tab ─────────────────────────────────────────
// Replaces the static demo CMMC bars. Pulls real control state from
// /api/clients/:id/cmmc which evaluates every L1/L2 control against
// Aurelius's data sources (NinjaOne Gov + Entra + KeeperGov). Atomus
// owns the SSP — this is the operations + service-delivery view.
var CMMC_STAGE_LABELS = {
  gap_assessment:  'Gap Assessment',
  remediation:     'Remediation',
  pre_assessment:  'Pre-Assessment',
  audit_scheduled: 'C3PAO Audit Scheduled',
  certified:       'Certified',
  maintenance:     'Maintenance'
};
var CMMC_STAGES_ORDER = ['gap_assessment','remediation','pre_assessment','audit_scheduled','certified','maintenance'];

var CMMC_FAMILY_NAMES = {
  AC: 'Access Control', AT: 'Awareness & Training', AU: 'Audit & Accountability',
  CM: 'Configuration Management', IA: 'Identification & Authentication',
  IR: 'Incident Response', MA: 'Maintenance', MP: 'Media Protection',
  PS: 'Personnel Security', PE: 'Physical Protection', RA: 'Risk Assessment',
  SA: 'Security Assessment', SC: 'System & Communications Protection',
  SI: 'System & Information Integrity'
};

var CMMC_SOURCE_LABELS = {
  entra:        'Microsoft Entra',
  ninjaone_gov: 'NinjaOne Gov',
  keepergov:    'KeeperGov',
  meraki:       'Cisco Meraki',
  aegis:        'Atomus Aegis',
  atomus:       'Atomus',
  manual:       'Manual'
};

function CMMCStateChip({ state }) {
  var cls = state === 'pass' ? 'cmmc-state pass' : state === 'fail' ? 'cmmc-state fail' : 'cmmc-state unknown';
  var label = state === 'pass' ? 'Evidenced' : state === 'fail' ? 'Gap' : 'Verify';
  return <span className={cls}><span className="cmmc-state-dot" />{label}</span>;
}

function CMMCStageStrip({ stage, daysToAssessment }) {
  var idx = CMMC_STAGES_ORDER.indexOf(stage);
  return (
    <div className="cmmc-stage-strip">
      {CMMC_STAGES_ORDER.map(function(s, i) {
        var state = i < idx ? 'complete' : i === idx ? 'active' : 'upcoming';
        return (
          <div key={s} className={'cmmc-stage cmmc-stage-' + state}>
            <div className="cmmc-stage-marker">
              <span className="cmmc-stage-dot" />
              {i < CMMC_STAGES_ORDER.length - 1 && <span className="cmmc-stage-line" />}
            </div>
            <div className="cmmc-stage-label">{CMMC_STAGE_LABELS[s]}</div>
            {state === 'active' && daysToAssessment != null && i === CMMC_STAGES_ORDER.indexOf('audit_scheduled') && (
              <div className="cmmc-stage-meta">{daysToAssessment > 0 ? 'In ' + daysToAssessment + 'd' : daysToAssessment === 0 ? 'Today' : Math.abs(daysToAssessment) + 'd ago'}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CMMCFamilyTile({ family, onClick, active }) {
  var pct = family.total > 0 ? Math.round((family.pass / family.total) * 100) : 0;
  var hasFail = family.fail > 0;
  var cls = 'cmmc-family-tile' + (active ? ' active' : '') + (hasFail ? ' has-gap' : '') + (family.unknown === family.total ? ' unverified' : '');
  return (
    <button type="button" className={cls} onClick={onClick}>
      <div className="cmmc-family-code">{family.code}</div>
      <div className="cmmc-family-name">{family.name}</div>
      <div className="cmmc-family-stats">
        <span className="cmmc-family-pct">{pct}%</span>
        <span className="cmmc-family-counts">
          <span className="ok">{family.pass}</span> /
          <span className="warn">{family.unknown}</span> /
          <span className="bad">{family.fail}</span>
        </span>
      </div>
      <div className="cmmc-family-bar"><div className="cmmc-family-bar-fill" style={{ width: pct + '%' }} /></div>
    </button>
  );
}

function CMMCControlRow({ ctl }) {
  var [open, setOpen] = useState(false);
  var src = CMMC_SOURCE_LABELS[ctl.evidence_source] || ctl.evidence_source;
  var lastChecked = ctl.lastChecked ? new Date(ctl.lastChecked) : null;
  var ago = lastChecked ? Math.round((Date.now() - lastChecked.getTime()) / 60000) : null;
  var agoLabel = ago == null ? '—' : ago < 1 ? 'just now' : ago < 60 ? ago + 'm ago' : ago < 1440 ? Math.round(ago/60) + 'h ago' : Math.round(ago/1440) + 'd ago';
  return (
    <div className={'cmmc-control cmmc-control-' + ctl.state}>
      <button type="button" className="cmmc-control-head" onClick={function() { setOpen(!open); }}>
        <span className="cmmc-control-id">{ctl.id}</span>
        <span className="cmmc-control-title">{ctl.title}</span>
        <CMMCStateChip state={ctl.state} />
        <span className="cmmc-control-source">{src}</span>
        <span className="cmmc-control-toggle">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="cmmc-control-body">
          <div className="cmmc-control-statement">{ctl.statement}</div>
          {ctl.reason && <div className="cmmc-control-reason"><strong>Status:</strong> {ctl.reason}</div>}
          {ctl.lineage && <div className="cmmc-control-lineage"><strong>Data lineage:</strong> <code>{ctl.lineage}</code></div>}
          {ctl.evidence && <div className="cmmc-control-evidence"><strong>Evidence:</strong> <pre>{JSON.stringify(ctl.evidence, null, 2)}</pre></div>}
          <div className="cmmc-control-foot">
            <span>Last checked {agoLabel}</span>
            {ctl.rev3_id && <span className="cmmc-control-rev3">Rev 3 ref: {ctl.rev3_id}</span>}
            {ctl.evidence_source === 'aegis' && <span className="cmmc-aegis-link">→ Verify in Atomus Aegis</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function CMMCTab({ c, clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [activeFamily, setActiveFamily] = useState(null);
  var [showDrift, setShowDrift] = useState(false);

  useEffect(function() {
    setLoading(true);
    setActiveFamily(null);
    authFetch(API + '/clients/' + clientId + '/cmmc')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'CMMC evaluation failed');
      })
      .catch(function() { setLoading(false); setError('CMMC evaluation failed'); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (error) return <div className="tab-content"><div className="empty-state" style={{ color: 'var(--accent-red)' }}>{error}</div></div>;
  if (!data || !data.enabled) return <div className="tab-content"><div className="empty-state">CMMC is not enabled for this client.</div></div>;

  var camp = data.campaign || {};
  var totals = data.totals || { total: 0, pass: 0, fail: 0, unknown: 0, coveragePercent: 0 };
  var families = data.families || [];
  var allControls = data.controls || [];
  var visibleControls = activeFamily ? allControls.filter(function(c) { return c.family === activeFamily; }) : allControls;

  // Sort controls within selected family: gaps first, then unknowns, then pass
  visibleControls = visibleControls.slice().sort(function(a, b) {
    var rank = { fail: 0, unknown: 1, pass: 2 };
    if (rank[a.state] !== rank[b.state]) return rank[a.state] - rank[b.state];
    return a.id.localeCompare(b.id, undefined, { numeric: true });
  });

  return (
    <div className="tab-content">
      <TabDescription text="Continuous CMMC compliance evidence — every control evaluated against live data from Microsoft Entra, NinjaOne Gov, and KeeperGov. Endpoint and policy controls are owned by Atomus Aegis; the SSP is built and submitted by Atomus. This tab is the operations + service-delivery view." />

      {/* Campaign hero — partnership-first framing */}
      <div className="cmmc-hero">
        <div className="cmmc-hero-main">
          <div className="cmmc-hero-eyebrow">CMMC Level {camp.targetLevel || '?'} Campaign</div>
          <div className="cmmc-hero-title">
            {totals.coveragePercent}<span className="cmmc-hero-pct">%</span>
            <span className="cmmc-hero-subtitle">controls evidenced from live data</span>
          </div>
          <div className="cmmc-hero-meta">
            <span><strong>{totals.pass}</strong> evidenced</span>
            <span className="bad"><strong>{totals.fail}</strong> gap{totals.fail === 1 ? '' : 's'}</span>
            <span className="warn"><strong>{totals.unknown}</strong> verify with Aegis / manual</span>
            <span className="muted">of {totals.total} L{camp.targetLevel || '?'} controls</span>
          </div>
        </div>
        <div className="cmmc-hero-side">
          <div className="cmmc-partner-badge">
            <div className="cmmc-partner-dot" />
            <div>
              <div className="cmmc-partner-label">In partnership with</div>
              <div className="cmmc-partner-name">Atomus {camp.aegisActive && <span className="cmmc-aegis-active">· Aegis Active</span>}</div>
            </div>
          </div>
          {camp.atomusDashboardUrl && (
            <a className="cmmc-partner-link" href={camp.atomusDashboardUrl} target="_blank" rel="noopener noreferrer">Open client Aegis dashboard ↗</a>
          )}
          <a className="cmmc-partner-link" href="https://app.atomuscyber.us/companies" target="_blank" rel="noopener noreferrer">Atomus Compliance Dashboard ↗</a>
          {camp.targetAssessmentDate && (
            <div className="cmmc-hero-target">
              <div className="cmmc-hero-target-label">Target Assessment</div>
              <div className="cmmc-hero-target-date">{new Date(camp.targetAssessmentDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</div>
              {camp.daysToAssessment != null && <div className="cmmc-hero-target-days">{camp.daysToAssessment > 0 ? camp.daysToAssessment + ' days remaining' : camp.daysToAssessment === 0 ? 'Today' : 'Passed'}</div>}
            </div>
          )}
          {camp.c3pao && <div className="cmmc-c3pao">C3PAO: <strong>{camp.c3pao}</strong></div>}
        </div>
      </div>

      {/* Stage progress strip */}
      <CMMCStageStrip stage={camp.stage} daysToAssessment={camp.daysToAssessment} />

      {/* Control family grid */}
      <ContentBand title="Control Families" subtitle="Click a family to drill into individual controls. Gap-first sorting surfaces what needs attention.">
        <div className="cmmc-family-grid">
          {families.map(function(f) {
            return <CMMCFamilyTile key={f.code} family={f} active={activeFamily === f.code} onClick={function() { setActiveFamily(activeFamily === f.code ? null : f.code); }} />;
          })}
        </div>
      </ContentBand>

      {/* Controls list (filtered) */}
      <ContentBand
        title={activeFamily ? CMMC_FAMILY_NAMES[activeFamily] + ' — Controls' : 'All Controls'}
        subtitle={activeFamily ? 'Showing ' + visibleControls.length + ' control(s) in ' + activeFamily : visibleControls.length + ' total controls — pick a family above to filter'}
        rightContent={activeFamily && <button className="admin-btn sm" onClick={function() { setActiveFamily(null); }}>Show all</button>}
      >
        <div className="cmmc-control-list">
          {visibleControls.map(function(ctl) { return <CMMCControlRow key={ctl.id} ctl={ctl} />; })}
        </div>
      </ContentBand>

      {/* Drift log */}
      {data.drift && data.drift.length > 0 && (
        <ContentBand
          title="Recent Drift (30 days)"
          subtitle={data.drift.length + ' control state change' + (data.drift.length === 1 ? '' : 's') + ' — append-only log for audit defense'}
          rightContent={<button className="admin-btn sm" onClick={function() { setShowDrift(!showDrift); }}>{showDrift ? 'Hide' : 'Show'}</button>}
        >
          {showDrift && (
            <div className="cmmc-drift-log">
              {data.drift.map(function(d) {
                var when = new Date(d.detected_at);
                var ago = Math.round((Date.now() - when.getTime()) / 86400000);
                return (
                  <div key={d.id} className={'cmmc-drift-row drift-' + d.new_state}>
                    <span className="cmmc-drift-when">{ago === 0 ? 'today' : ago + 'd ago'}</span>
                    <span className="cmmc-drift-control">{d.control_id}</span>
                    <span className="cmmc-drift-arrow"><CMMCStateChip state={d.prev_state} /> → <CMMCStateChip state={d.new_state} /></span>
                    {d.reason && <span className="cmmc-drift-reason">{d.reason}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </ContentBand>
      )}

      {/* Source health footer */}
      <div className="cmmc-source-footer">
        <span>Evidence sources: </span>
        <span className={'cmmc-source-pill ' + (data.sources.entra ? 'on' : 'off')}>Entra {data.sources.entra ? '✓' : '·'}</span>
        <span className={'cmmc-source-pill ' + (data.sources.ninjaGov ? 'on' : 'off')}>NinjaOne Gov {data.sources.ninjaGov ? '✓' : '·'}</span>
        <span className={'cmmc-source-pill ' + (data.sources.keepergov ? 'on' : 'off')}>KeeperGov {data.sources.keepergov ? '✓' : '·'}</span>
        <span className="cmmc-source-stamp">Bundle fetched {new Date(data.bundleFetchedAt).toLocaleTimeString()}</span>
      </div>
    </div>
  );
}

function ComplianceTab({ c, clientId }) {
  const [soc2, setSoc2] = useState(null);
  const [soc2Loading, setSoc2Loading] = useState(false);
  // Sub-tab toggle: CMMC clients can flip between CMMC and Cyber Insurance.
  // Non-CMMC clients see SOC 2 (legacy demo) — no sub-tabs.
  var [subTab, setSubTab] = useState('cmmc');

  useEffect(function() {
    if (c.cmmc) return;
    setSoc2Loading(true);
    authFetch(API + '/clients/' + clientId + '/compliance')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setSoc2(data); setSoc2Loading(false); })
      .catch(function() { setSoc2Loading(false); });
  }, [clientId, c.cmmc]);

  // CMMC path — live control evaluation against NinjaOne Gov + Entra + KeeperGov.
  // Atomus owns the SSP and certification path; Aurelius surfaces evidence.
  if (c.cmmc) {
    return (
      <div className="tab-content compliance-with-subtabs">
        <div className="compliance-subtabs">
          <button type="button" className={'compliance-subtab' + (subTab === 'cmmc' ? ' active' : '')} onClick={function() { setSubTab('cmmc'); }}>
            <span className="compliance-subtab-icon">◆</span>
            <span>CMMC Campaign</span>
          </button>
          <button type="button" className={'compliance-subtab' + (subTab === 'cyber' ? ' active' : '')} onClick={function() { setSubTab('cyber'); }}>
            <span className="compliance-subtab-icon">◈</span>
            <span>Cyber Insurance</span>
          </button>
        </div>
        {subTab === 'cmmc' && <CMMCTab c={c} clientId={clientId} />}
        {subTab === 'cyber' && <CyberInsuranceTab c={c} clientId={clientId} />}
      </div>
    );
  }

  // SOC 2 path (demo client or future non-CMMC compliance-tracked clients)
  if (soc2Loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!soc2) return <div className="tab-content"><div className="empty-state">Compliance tracking is not yet configured for this client.{c.industry && c.industry.indexOf('Healthcare') >= 0 && <p style={{ marginTop: 12, color: '#94a3b8' }}>HIPAA compliance module coming soon.</p>}</div></div>;

  var totalControls = soc2.criteria.reduce(function(s, cr) { return s + cr.controls_total; }, 0);
  var implementedControls = soc2.criteria.reduce(function(s, cr) { return s + cr.controls_implemented; }, 0);
  var totalEvidence = soc2.criteria.reduce(function(s, cr) { return s + cr.evidence_count; }, 0);
  var daysUntilAudit = soc2.target_audit_date ? Math.round((new Date(soc2.target_audit_date).getTime() - Date.now()) / 86400000) : null;

  return (
    <div className="tab-content">
      <TabDescription text={'Formal compliance readiness for ' + soc2.framework + '. Each Trust Services Criterion tracks implemented controls, in-progress work, and evidence collected for the audit period.'} />
      <DataFreshness live={true} />
      <ContentBand title={soc2.framework + ' Readiness'} subtitle={daysUntilAudit !== null ? (daysUntilAudit > 0 ? daysUntilAudit + ' days until target audit date' : 'Audit period active') : 'Active program'} rightContent={<span className="live-badge">LIVE</span>}>
        <div className="metric-tiles">
          <div className={'metric-tile ' + (soc2.readiness_pct >= 90 ? 'metric-tile-good' : soc2.readiness_pct < 70 ? 'metric-tile-alert' : '')}>
            <div className="metric-tile-label">Overall Readiness</div>
            <div className="metric-tile-value">{soc2.readiness_pct}<span className="metric-tile-unit">%</span></div>
            <div className="metric-tile-context">Weighted across all criteria</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">Controls Implemented</div>
            <div className="metric-tile-value">{implementedControls}<span className="metric-tile-unit"> / {totalControls}</span></div>
            <div className="metric-tile-context">{totalControls - implementedControls} remaining</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">Evidence Collected</div>
            <div className="metric-tile-value">{totalEvidence}</div>
            <div className="metric-tile-context">Audit-ready artifacts</div>
          </div>
          <div className={'metric-tile' + (soc2.upcoming_tasks && soc2.upcoming_tasks.length > 2 ? ' metric-tile-alert' : '')}>
            <div className="metric-tile-label">Open Tasks</div>
            <div className="metric-tile-value">{soc2.upcoming_tasks ? soc2.upcoming_tasks.length : 0}</div>
            <div className="metric-tile-context">Remediation and documentation</div>
          </div>
        </div>
      </ContentBand>

      <ContentBand title="Trust Services Criteria" subtitle="Each criterion tracked independently — readiness scores, controls, and evidence">
        <div className="soc2-criteria">
          {soc2.criteria.map(function(cr) {
            var barColor = cr.score >= 90 ? '#22c55e' : cr.score >= 75 ? '#60a5fa' : cr.score >= 60 ? '#f59e0b' : '#ef4444';
            return (
              <div key={cr.id} className="soc2-criterion">
                <div className="soc2-criterion-header">
                  <div className="soc2-criterion-id">{cr.id}</div>
                  <div className="soc2-criterion-name">{cr.name}</div>
                  <div className="soc2-criterion-score" style={{ color: barColor }}>{cr.score}%</div>
                </div>
                <div className="soc2-criterion-bar"><div className="soc2-criterion-bar-fill" style={{ width: cr.score + '%', background: barColor }} /></div>
                <div className="soc2-criterion-meta">
                  <span>{cr.controls_implemented}/{cr.controls_total} controls implemented</span>
                  {cr.controls_in_progress > 0 && <span className="soc2-criterion-pending">{cr.controls_in_progress} in progress</span>}
                  <span>{cr.evidence_count} evidence items</span>
                  <span>Last tested {Math.round((Date.now() - new Date(cr.last_tested).getTime()) / 86400000)}d ago</span>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      <ContentBand title="Recent Evidence Collection" subtitle="Most recent audit artifacts — automated and manual">
        <MetricList>
          {soc2.recent_evidence.map(function(e, i) {
            var ageHours = Math.round((Date.now() - new Date(e.collected).getTime()) / 3600000);
            var ageLabel = ageHours < 24 ? ageHours + 'h ago' : Math.round(ageHours / 24) + 'd ago';
            return <MetricListItem key={i} icon={e.type === 'Automated collection' ? '◆' : '◇'} iconColor={e.type === 'Automated collection' ? '#22c55e' : '#60a5fa'} label={e.control} sublabel={e.source} value={ageLabel} trail={e.type} />;
          })}
        </MetricList>
      </ContentBand>

      {soc2.upcoming_tasks && soc2.upcoming_tasks.length > 0 && (
        <ContentBand title="Upcoming Compliance Tasks" subtitle="Remediation and documentation items in flight">
          <MetricList>
            {soc2.upcoming_tasks.map(function(t, i) {
              var due = new Date(t.due);
              var daysUntil = Math.round((due.getTime() - Date.now()) / 86400000);
              var dueLabel = daysUntil < 0 ? Math.abs(daysUntil) + 'd overdue' : 'Due in ' + daysUntil + 'd';
              var priorityColor = t.priority === 'High' ? '#ef4444' : t.priority === 'Medium' ? '#f59e0b' : '#64748b';
              return <MetricListItem key={i} icon="◈" iconColor={priorityColor} label={t.task} sublabel={'Owner: ' + t.owner} badge={t.priority} badgeColor={priorityColor} value={dueLabel} valueColor={daysUntil < 0 ? '#ef4444' : 'var(--text-primary)'} />;
            })}
          </MetricList>
        </ContentBand>
      )}
    </div>
  );
}
// ── Reviews Tab (QBR Meeting Recaps) ─────────────────────────────
function ReviewsTab({ clientId, isAdmin }) {
  var [reviews, setReviews] = useState([]);
  var [loading, setLoading] = useState(true);
  var [view, setView] = useState('list');
  var [activeReview, setActiveReview] = useState(null);
  var [editing, setEditing] = useState(false);
  var [projectData, setProjectData] = useState(null);
  var [ticketData, setTicketData] = useState(null);
  var [form, setForm] = useState(null);

  function fetchReviews() {
    setLoading(true);
    authFetch(API + '/clients/' + clientId + '/reviews')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { if (data) setReviews(data.reviews || []); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  useEffect(function() {
    fetchReviews();
    setView('list'); setActiveReview(null); setEditing(false); setForm(null);
    // Fetch linkable projects and tickets for the form
    Promise.all([
      authFetch(API + '/clients/' + clientId + '/projects').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/tickets').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) {
      setProjectData(results[0] && results[0].projects ? results[0].projects : []);
      setTicketData(results[1] && results[1].tickets ? results[1].tickets : []);
    });
  }, [clientId]);

  function newForm() {
    return {
      date: new Date().toISOString().split('T')[0],
      title: '',
      attendees: '',
      summary: '',
      topics: [{ title: '', notes: '' }],
      actionItems: [{ text: '', owner: '', status: 'open', dueDate: '', linkedProject: '', linkedTicket: '' }],
      challenges: '',
      nextSteps: '',
      nextReviewDate: '',
    };
  }

  function openCreate() { setForm(newForm()); setEditing(false); setView('form'); }
  function openEdit(review) {
    setForm({
      date: review.date || '',
      title: review.title || '',
      attendees: review.attendees || '',
      summary: review.summary || '',
      topics: review.topics && review.topics.length > 0 ? review.topics : [{ title: '', notes: '' }],
      actionItems: review.actionItems && review.actionItems.length > 0 ? review.actionItems.map(function(a) { return { id: a.id, text: a.text || '', owner: a.owner || '', status: a.status || 'open', dueDate: a.dueDate || '', linkedProject: a.linkedProject || '', linkedTicket: a.linkedTicket || '' }; }) : [{ text: '', owner: '', status: 'open', dueDate: '', linkedProject: '', linkedTicket: '' }],
      challenges: review.challenges || '',
      nextSteps: review.nextSteps || '',
      nextReviewDate: review.nextReviewDate || '',
    });
    setActiveReview(review);
    setEditing(true);
    setView('form');
  }

  function saveReview() {
    var url = editing ? API + '/clients/' + clientId + '/reviews/' + activeReview.id : API + '/clients/' + clientId + '/reviews';
    var method = editing ? 'PUT' : 'POST';
    var body = Object.assign({}, form);
    body.topics = body.topics.filter(function(t) { return t.title.trim(); });
    body.actionItems = body.actionItems.filter(function(a) { return a.text.trim(); });
    authFetch(url, { method: method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.success) { fetchReviews(); setView('detail'); setActiveReview(data.review); setForm(null); }
      }).catch(function() {});
  }

  function deleteReview(reviewId) {
    if (!window.confirm('Delete this review? This cannot be undone.')) return;
    authFetch(API + '/clients/' + clientId + '/reviews/' + reviewId, { method: 'DELETE' })
      .then(function(r) { return r.json(); })
      .then(function(data) { if (data.success) { fetchReviews(); setView('list'); setActiveReview(null); } });
  }

  function updateActionStatus(reviewId, aiId, newStatus) {
    var review = reviews.find(function(r) { return r.id === reviewId; });
    if (!review) return;
    var updatedItems = review.actionItems.map(function(a) { return a.id === aiId ? Object.assign({}, a, { status: newStatus }) : a; });
    authFetch(API + '/clients/' + clientId + '/reviews/' + reviewId, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actionItems: updatedItems }) })
      .then(function(r) { return r.json(); })
      .then(function(data) { if (data.success) { fetchReviews(); if (activeReview && activeReview.id === reviewId) setActiveReview(data.review); } });
  }

  function setFormField(field, value) { setForm(Object.assign({}, form, { [field]: value })); }
  function setTopic(idx, field, value) { var t = form.topics.slice(); t[idx] = Object.assign({}, t[idx], { [field]: value }); setForm(Object.assign({}, form, { topics: t })); }
  function addTopic() { setForm(Object.assign({}, form, { topics: form.topics.concat([{ title: '', notes: '' }]) })); }
  function removeTopic(idx) { var t = form.topics.slice(); t.splice(idx, 1); setForm(Object.assign({}, form, { topics: t.length > 0 ? t : [{ title: '', notes: '' }] })); }
  function setAction(idx, field, value) { var a = form.actionItems.slice(); a[idx] = Object.assign({}, a[idx], { [field]: value }); setForm(Object.assign({}, form, { actionItems: a })); }
  function addAction() { setForm(Object.assign({}, form, { actionItems: form.actionItems.concat([{ text: '', owner: '', status: 'open', dueDate: '', linkedProject: '', linkedTicket: '' }]) })); }
  function removeAction(idx) { var a = form.actionItems.slice(); a.splice(idx, 1); setForm(Object.assign({}, form, { actionItems: a.length > 0 ? a : [{ text: '', owner: '', status: 'open', dueDate: '', linkedProject: '', linkedTicket: '' }] })); }

  // Helper to resolve linked names
  function getProjectName(gid) { if (!gid || !projectData) return null; var p = projectData.find(function(pr) { return pr.gid === gid; }); return p ? p.name : null; }
  function getTicketSummary(id) { if (!id || !ticketData) return null; var t = ticketData.find(function(tk) { return String(tk.id) === String(id); }); return t ? '#' + t.id + ' ' + t.summary : null; }

  var statusColors = { open: '#f59e0b', 'in-progress': '#60a5fa', completed: '#22c55e', cancelled: '#64748b' };
  var statusLabels = { open: 'Open', 'in-progress': 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;

  // ── LIST VIEW ──
  if (view === 'list') {
    var openActions = 0, totalActions = 0;
    reviews.forEach(function(r) { if (r.actionItems) { totalActions += r.actionItems.length; openActions += r.actionItems.filter(function(a) { return a.status === 'open' || a.status === 'in-progress'; }).length; } });

    return (
      <div className="tab-content">
        <TabDescription text="Meeting recaps from Quarterly Business Reviews and strategic check-ins. Track action items, review discussions, and monitor progress between meetings." />
        <DataFreshness live={false} />
        {reviews.length > 0 && <HealthVerdict score={totalActions > 0 ? Math.round(((totalActions - openActions) / totalActions) * 100) : 100} context={
          openActions === 0 ? 'All action items from previous reviews have been completed.' :
          openActions <= 3 ? openActions + ' action item(s) still open across all reviews. On track.' :
          openActions + ' open action items need attention. Review priorities with your account team.'
        } />}
        {reviews.length > 0 && (
          <KPIStrip items={[
            { label: 'Total Reviews', value: reviews.length },
            { label: 'Action Items', value: totalActions },
            { label: 'Open Items', value: openActions, accent: openActions > 0 ? '#f59e0b' : '#22c55e' },
            { label: 'Completed', value: totalActions - openActions, accent: '#22c55e' },
            { label: 'Next Review', value: reviews[0] && reviews[0].nextReviewDate ? new Date(reviews[0].nextReviewDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—' },
          ]} />
        )}
        {isAdmin && <div style={{ margin: '16px 0' }}><button className="insights-btn" onClick={openCreate}>+ New Review</button></div>}
        {reviews.length === 0 ? (
          <EmptyStateAction icon="📋" title="No Business Reviews Yet" text="Quarterly Business Review recaps will appear here with discussion topics, action items, and progress tracking. Your CloudByte account team will add meeting notes after each QBR." cta={isAdmin ? 'Create your first review above' : 'Your next QBR recap will appear here'} />
        ) : (
          <div className="reviews-list">
            {reviews.map(function(r) {
              var aiTotal = r.actionItems ? r.actionItems.length : 0;
              var aiDone = r.actionItems ? r.actionItems.filter(function(a) { return a.status === 'completed'; }).length : 0;
              var aiOpen = r.actionItems ? r.actionItems.filter(function(a) { return a.status === 'open' || a.status === 'in-progress'; }).length : 0;
              return (
                <div key={r.id} className="review-card" onClick={function() { setActiveReview(r); setView('detail'); }}>
                  <div className="review-card-header">
                    <div>
                      <div className="review-card-date">{new Date(r.date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                      <div className="review-card-title">{r.title}</div>
                    </div>
                    {aiTotal > 0 && (
                      <div className="review-card-progress">
                        <div className="review-card-progress-bar"><div className="review-card-progress-fill" style={{ width: Math.round((aiDone / aiTotal) * 100) + '%' }} /></div>
                        <span className="review-card-progress-text">{aiDone}/{aiTotal} items done</span>
                        {aiOpen > 0 && <span className="review-card-open-badge">{aiOpen} open</span>}
                      </div>
                    )}
                  </div>
                  {r.summary && <div className="review-card-summary">{r.summary.length > 180 ? r.summary.slice(0, 180) + '...' : r.summary}</div>}
                  {r.attendees && <div className="review-card-attendees">Attendees: {r.attendees}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── DETAIL VIEW ──
  if (view === 'detail' && activeReview) {
    var r = activeReview;
    return (
      <div className="tab-content">
        <Breadcrumb items={[
          { label: 'Reviews', onClick: function() { setView('list'); setActiveReview(null); } },
          { label: r.title }
        ]} />
        <div className="review-detail-header">
          <div>
            <div className="review-detail-date">{new Date(r.date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            <div className="review-detail-title">{r.title}</div>
            {r.attendees && <div className="review-detail-attendees">Attendees: {r.attendees}</div>}
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="insights-btn" onClick={function() { openEdit(r); }}>Edit</button>
              <button className="insights-btn" style={{ color: '#ef4444' }} onClick={function() { deleteReview(r.id); }}>Delete</button>
            </div>
          )}
        </div>

        {r.summary && (
          <CollapsibleSection title="Summary" defaultOpen={true}>
            <div className="review-detail-text">{r.summary}</div>
          </CollapsibleSection>
        )}

        {r.topics && r.topics.length > 0 && (
          <CollapsibleSection title={'Topics Discussed (' + r.topics.length + ')'} defaultOpen={true}>
            {r.topics.map(function(t, i) {
              return (
                <div key={i} className="review-topic">
                  <div className="review-topic-title">{t.title}</div>
                  {t.notes && <div className="review-topic-notes">{t.notes}</div>}
                </div>
              );
            })}
          </CollapsibleSection>
        )}

        {r.actionItems && r.actionItems.length > 0 && (
          <CollapsibleSection title={'Action Items (' + r.actionItems.length + ')'} defaultOpen={true} rightContent={
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {r.actionItems.filter(function(a) { return a.status === 'completed'; }).length + '/' + r.actionItems.length + ' completed'}
            </span>
          }>
            <div className="review-actions-list">
              {r.actionItems.map(function(a) {
                var projName = getProjectName(a.linkedProject);
                var ticketName = getTicketSummary(a.linkedTicket);
                return (
                  <div key={a.id} className={'review-action-item review-action-' + a.status}>
                    <div className="review-action-top">
                      <span className={'status-dot status-dot-' + (a.status === 'completed' ? 'good' : a.status === 'open' ? 'warning' : a.status === 'in-progress' ? 'info' : 'muted')} />
                      <span className="review-action-text">{a.text}</span>
                      {isAdmin && (
                        <select className="review-action-status-select" value={a.status} onClick={function(e) { e.stopPropagation(); }} onChange={function(e) { updateActionStatus(r.id, a.id, e.target.value); }}>
                          <option value="open">Open</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      )}
                      {!isAdmin && <span className="review-action-status-badge" style={{ color: statusColors[a.status], borderColor: statusColors[a.status] }}>{statusLabels[a.status]}</span>}
                    </div>
                    <div className="review-action-meta">
                      {a.owner && <span>Owner: {a.owner}</span>}
                      {a.dueDate && <span>Due: {new Date(a.dueDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                      {projName && <span className="review-action-link" title={'Linked to project: ' + projName}>📁 {projName}</span>}
                      {ticketName && <span className="review-action-link" title={'Linked to ticket'}>🎫 {ticketName}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {r.challenges && (
          <CollapsibleSection title="Challenges & Blockers" defaultOpen={true}>
            <div className="review-detail-text">{r.challenges}</div>
          </CollapsibleSection>
        )}

        {r.nextSteps && (
          <CollapsibleSection title="Next Steps" defaultOpen={true}>
            <div className="review-detail-text">{r.nextSteps}</div>
          </CollapsibleSection>
        )}

        {r.nextReviewDate && (
          <div className="review-next-date">
            Next review scheduled: <strong>{new Date(r.nextReviewDate + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
          </div>
        )}
      </div>
    );
  }

  // ── FORM VIEW (Create / Edit) ──
  if (view === 'form' && form) {
    return (
      <div className="tab-content">
        <Breadcrumb items={[
          { label: 'Reviews', onClick: function() { setView('list'); setForm(null); } },
          { label: editing ? 'Edit Review' : 'New Review' }
        ]} />
        <div className="review-form">
          <div className="review-form-row">
            <label className="review-form-label">Date *<input type="date" className="review-form-input" value={form.date} onChange={function(e) { setFormField('date', e.target.value); }} /></label>
            <label className="review-form-label">Title *<input type="text" className="review-form-input" placeholder="Q2 2026 Business Review" value={form.title} onChange={function(e) { setFormField('title', e.target.value); }} /></label>
          </div>
          <label className="review-form-label">Attendees<input type="text" className="review-form-input" placeholder="Alex (CloudByte), Jane (Client)" value={form.attendees} onChange={function(e) { setFormField('attendees', e.target.value); }} /></label>
          <label className="review-form-label">Summary<textarea className="review-form-textarea" rows={4} placeholder="High-level recap of the meeting..." value={form.summary} onChange={function(e) { setFormField('summary', e.target.value); }} /></label>

          <div className="review-form-section-title">Topics Discussed</div>
          {form.topics.map(function(t, i) {
            return (
              <div key={i} className="review-form-topic">
                <input type="text" className="review-form-input" placeholder="Topic title" value={t.title} onChange={function(e) { setTopic(i, 'title', e.target.value); }} />
                <textarea className="review-form-textarea" rows={2} placeholder="Notes / discussion points" value={t.notes} onChange={function(e) { setTopic(i, 'notes', e.target.value); }} />
                {form.topics.length > 1 && <button className="review-form-remove" onClick={function() { removeTopic(i); }}>✕</button>}
              </div>
            );
          })}
          <button className="review-form-add" onClick={addTopic}>+ Add Topic</button>

          <div className="review-form-section-title">Action Items</div>
          {form.actionItems.map(function(a, i) {
            return (
              <div key={i} className="review-form-action">
                <div className="review-form-row">
                  <label className="review-form-label" style={{ flex: 3 }}>Action *<input type="text" className="review-form-input" placeholder="What needs to be done" value={a.text} onChange={function(e) { setAction(i, 'text', e.target.value); }} /></label>
                  <label className="review-form-label" style={{ flex: 1 }}>Owner<input type="text" className="review-form-input" placeholder="CloudByte / Client" value={a.owner} onChange={function(e) { setAction(i, 'owner', e.target.value); }} /></label>
                  <label className="review-form-label" style={{ flex: 1 }}>Due Date<input type="date" className="review-form-input" value={a.dueDate} onChange={function(e) { setAction(i, 'dueDate', e.target.value); }} /></label>
                </div>
                <div className="review-form-row">
                  <label className="review-form-label" style={{ flex: 1 }}>Status
                    <select className="review-form-input" value={a.status} onChange={function(e) { setAction(i, 'status', e.target.value); }}>
                      <option value="open">Open</option><option value="in-progress">In Progress</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                    </select>
                  </label>
                  <label className="review-form-label" style={{ flex: 1 }}>Link to Project
                    <select className="review-form-input" value={a.linkedProject || ''} onChange={function(e) { setAction(i, 'linkedProject', e.target.value || null); }}>
                      <option value="">— None —</option>
                      {projectData && projectData.map(function(p) { return <option key={p.gid} value={p.gid}>{p.name}</option>; })}
                    </select>
                  </label>
                  <label className="review-form-label" style={{ flex: 1 }}>Link to Ticket
                    <select className="review-form-input" value={a.linkedTicket || ''} onChange={function(e) { setAction(i, 'linkedTicket', e.target.value || null); }}>
                      <option value="">— None —</option>
                      {ticketData && ticketData.filter(function(tk) { return tk.statusParent !== 5000 && tk.statusParent !== 6000; }).slice(0, 50).map(function(tk) { return <option key={tk.id} value={tk.id}>#{tk.id} {tk.summary && tk.summary.length > 50 ? tk.summary.slice(0, 50) + '...' : tk.summary}</option>; })}
                    </select>
                  </label>
                </div>
                {form.actionItems.length > 1 && <button className="review-form-remove" onClick={function() { removeAction(i); }}>✕</button>}
              </div>
            );
          })}
          <button className="review-form-add" onClick={addAction}>+ Add Action Item</button>

          <label className="review-form-label">Challenges & Blockers<textarea className="review-form-textarea" rows={3} placeholder="Any issues discussed..." value={form.challenges} onChange={function(e) { setFormField('challenges', e.target.value); }} /></label>
          <label className="review-form-label">Next Steps<textarea className="review-form-textarea" rows={3} placeholder="Agreed next steps..." value={form.nextSteps} onChange={function(e) { setFormField('nextSteps', e.target.value); }} /></label>
          <label className="review-form-label">Next Review Date<input type="date" className="review-form-input" style={{ maxWidth: 200 }} value={form.nextReviewDate} onChange={function(e) { setFormField('nextReviewDate', e.target.value); }} /></label>

          <div className="review-form-buttons">
            <button className="review-form-save" onClick={saveReview} disabled={!form.title.trim() || !form.date}>{editing ? 'Update Review' : 'Save Review'}</button>
            <button className="review-form-cancel" onClick={function() { setView(editing ? 'detail' : 'list'); setForm(null); }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ── QBR Report View ───────────────────────────────────────────────
var qbrLoadingStages = ['Collecting endpoint data...', 'Analyzing identity posture...', 'Reviewing threats...', 'Gathering budget data...', 'Generating report...'];
var gradeColors = { A: '#22c55e', B: '#5b9ef4', C: '#f59e0b', D: '#f97316', F: '#ef4444' };
var priorityLabels = { urgent: 'Needs Attention Now', soon: 'This Quarter', planned: 'On the Roadmap' };
var priorityColors = { urgent: 'var(--accent-red)', soon: 'var(--accent-amber)', planned: 'var(--accent-blue)' };

function QBRPanel({ isOpen, onClose, report, clientName, loading }) {
  var today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  var [stageIdx, setStageIdx] = useState(0);

  useEffect(function() {
    if (!isOpen) return;
    var handler = function(e) { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return function() { window.removeEventListener('keydown', handler); };
  }, [isOpen, onClose]);

  useEffect(function() {
    if (!loading) { setStageIdx(0); return; }
    var interval = setInterval(function() { setStageIdx(function(prev) { return prev < qbrLoadingStages.length - 1 ? prev + 1 : prev; }); }, 3000);
    return function() { clearInterval(interval); };
  }, [loading]);

  var handlePrint = function() {
    if (!report) return;
    var printWindow = window.open('', '_blank');
    var s = '';
    if (report.grade) s += '<div style="text-align:center;margin:20px 0 30px"><div style="display:inline-block;width:64px;height:64px;border-radius:50%;background:' + (gradeColors[report.grade] || '#5b9ef4') + ';color:#fff;font-size:32px;font-weight:800;line-height:64px">' + report.grade + '</div><div style="font-size:13px;color:#64748b;margin-top:6px">' + (report.grade_label || '') + '</div></div>';
    if (report.bottom_line) s += '<div style="background:#f0f4ff;border-left:4px solid #1e40af;padding:14px 18px;font-size:16px;font-weight:600;color:#1e293b;margin-bottom:28px;border-radius:0 8px 8px 0">' + report.bottom_line + '</div>';
    s += '<div class="qbr-print-section">Executive Summary</div><div class="qbr-print-text">' + (report.executive_summary || '') + '</div>';
    if (report.value_delivered && report.value_delivered.length > 0) { s += '<div class="qbr-print-section">Value Delivered</div><table style="width:100%;border-collapse:collapse;margin:12px 0"><tbody>'; report.value_delivered.forEach(function(v) { s += '<tr><td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;font-weight:700;white-space:nowrap;vertical-align:top;color:#1e40af;width:140px">' + v.metric + '</td><td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;font-size:14px">' + v.detail + '</td></tr>'; }); s += '</tbody></table>'; }
    if (report.metrics_snapshot) { s += '<div class="qbr-print-section">Key Metrics</div><table style="width:100%;border-collapse:collapse;margin:12px 0"><tbody>'; Object.entries(report.metrics_snapshot).forEach(function(pair) { s += '<tr><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-weight:600;color:#475569;width:180px">' + pair[0] + '</td><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:14px">' + pair[1] + '</td></tr>'; }); s += '</tbody></table>'; }
    if (report.attention_items && report.attention_items.length > 0) { s += '<div class="qbr-print-section">Items Needing Attention</div>'; report.attention_items.forEach(function(item) { var pc = item.priority === 'urgent' ? '#dc2626' : item.priority === 'soon' ? '#d97706' : '#2563eb'; s += '<div style="margin-bottom:14px"><span style="display:inline-block;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:' + pc + ';margin-bottom:3px">' + (priorityLabels[item.priority] || item.priority) + '</span><div style="font-weight:600;font-size:14px;margin-bottom:3px">' + item.title + '</div><div style="font-size:13px;color:#64748b">' + item.detail + '</div></div>'; }); }
    if (report.investments && report.investments.length > 0) { s += '<div class="qbr-print-section">Strategic Investments</div>'; report.investments.forEach(function(inv) { s += '<div style="margin-bottom:14px"><div style="font-weight:600;font-size:14px;margin-bottom:3px">' + inv.title + '</div><div style="font-size:13px;color:#64748b">' + inv.detail + '</div></div>'; }); }
    if (report.project_status) s += '<div class="qbr-print-section">Project Status</div><div class="qbr-print-text">' + report.project_status + '</div>';
    if (report.budget_analysis) s += '<div class="qbr-print-section">Budget & Licensing</div><div class="qbr-print-text">' + report.budget_analysis + '</div>';
    if (report.identity_posture) s += '<div class="qbr-print-section">Identity & Access</div><div class="qbr-print-text">' + report.identity_posture + '</div>';
    if (report.compliance_notes) s += '<div class="qbr-print-section">Compliance</div><div class="qbr-print-text">' + report.compliance_notes + '</div>';
    printWindow.document.write('<!DOCTYPE html><html><head><title>QBR - ' + clientName + '</title><style>body{font-family:"Segoe UI",sans-serif;color:#1e293b;padding:40px;max-width:800px;margin:0 auto;line-height:1.6}.qbr-print-header{border-bottom:3px solid #1e40af;padding-bottom:16px;margin-bottom:32px}.qbr-print-logo{font-size:24px;font-weight:700;letter-spacing:3px;color:#1e40af}.qbr-print-sub{font-size:12px;color:#64748b}.qbr-print-client{font-size:20px;font-weight:600;margin-top:8px}.qbr-print-date{font-size:13px;color:#64748b}.qbr-print-section{font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#1e40af;margin:28px 0 12px;border-bottom:1px solid #e2e8f0;padding-bottom:6px}.qbr-print-text{font-size:14px;margin-bottom:12px;white-space:pre-line}.qbr-print-footer{margin-top:40px;padding-top:16px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center}@media print{body{padding:20px}}</style></head><body><div class="qbr-print-header"><div class="qbr-print-logo">AURELIUS</div><div class="qbr-print-sub">by CloudByte Group</div><div class="qbr-print-client">' + clientName + '</div><div class="qbr-print-date">Quarterly Business Review — ' + today + '</div></div>' + s + '<div class="qbr-print-footer">Prepared by CloudByte Group using Aurelius — ' + today + '</div></body></html>');
    printWindow.document.close();
    setTimeout(function() { printWindow.print(); }, 500);
  };

  if (!isOpen) return null;

  var gradeColor = report && report.grade ? (gradeColors[report.grade] || '#5b9ef4') : '#5b9ef4';

  return (
    <>
      <div className="qbr-overlay" onClick={onClose} />
      <div className="qbr-panel">
        <div className="qbr-header">
          <div>
            <div className="qbr-title">Quarterly Business Review</div>
            <div className="qbr-client-name">{clientName}</div>
            <div className="qbr-date">{today}</div>
          </div>
          <div className="qbr-actions">
            {report && <button className="qbr-print-btn" onClick={handlePrint}>Export / Print</button>}
            <button className="qbr-close-btn" onClick={onClose}>&#10005;</button>
          </div>
        </div>
        <div className="qbr-body">
          {loading && (
            <div className="qbr-loading">
              <div className="qbr-loading-spinner" />
              <div className="qbr-loading-stage">{qbrLoadingStages[stageIdx]}</div>
              <div className="qbr-loading-sub">This may take 15-30 seconds</div>
            </div>
          )}
          {!loading && report && (
            <>
              {report.grade && (
                <div className="qbr-grade-banner">
                  <div className="qbr-grade-circle" style={{ background: gradeColor }}>{report.grade}</div>
                  <div className="qbr-grade-info">
                    {report.grade_label && <div className="qbr-grade-label">{report.grade_label}</div>}
                    {report.bottom_line && <div className="qbr-bottom-line">{report.bottom_line}</div>}
                  </div>
                </div>
              )}
              {!report.grade && report.bottom_line && <div className="qbr-bottom-line-standalone">{report.bottom_line}</div>}

              <div className="qbr-section-title">Executive Summary</div>
              <p className="qbr-text">{report.executive_summary}</p>

              {report.value_delivered && report.value_delivered.length > 0 && (
                <>
                  <div className="qbr-section-title">Value Delivered by CloudByte</div>
                  <div className="qbr-value-grid">
                    {report.value_delivered.map(function(v, i) { return (
                      <div key={i} className="qbr-value-item">
                        <div className="qbr-value-metric">{v.metric}</div>
                        <div className="qbr-value-detail">{v.detail}</div>
                      </div>
                    ); })}
                  </div>
                </>
              )}

              {report.metrics_snapshot && (
                <>
                  <div className="qbr-section-title">Key Metrics</div>
                  <div className="qbr-metrics-grid">
                    {Object.entries(report.metrics_snapshot).map(function(pair, i) { return (
                      <div key={i} className="qbr-metric-row">
                        <span className="qbr-metric-label">{pair[0]}</span>
                        <span className="qbr-metric-value">{pair[1]}</span>
                      </div>
                    ); })}
                  </div>
                </>
              )}

              {report.attention_items && report.attention_items.length > 0 && (
                <>
                  <div className="qbr-section-title">Needs Your Attention</div>
                  <div className="qbr-attention-list">
                    {report.attention_items.map(function(item, i) { return (
                      <div key={i} className={'qbr-attention-item qbr-attention-' + item.priority}>
                        <div className="qbr-attention-priority" style={{ color: priorityColors[item.priority] || 'var(--accent-blue)' }}>{priorityLabels[item.priority] || item.priority}</div>
                        <div className="qbr-attention-title">{item.title}</div>
                        <div className="qbr-attention-detail">{item.detail}</div>
                      </div>
                    ); })}
                  </div>
                </>
              )}

              {report.investments && report.investments.length > 0 && (
                <>
                  <div className="qbr-section-title">Strategic Investments</div>
                  <div className="qbr-investments-list">
                    {report.investments.map(function(inv, i) { return (
                      <div key={i} className="qbr-investment-item">
                        <div className="qbr-investment-title">{inv.title}</div>
                        <div className="qbr-investment-detail">{inv.detail}</div>
                      </div>
                    ); })}
                  </div>
                </>
              )}

              {report.project_status && (<><div className="qbr-section-title">Project Status</div><p className="qbr-text">{report.project_status}</p></>)}
              {report.budget_analysis && (<><div className="qbr-section-title">Budget & Licensing</div><p className="qbr-text">{report.budget_analysis}</p></>)}
              {report.identity_posture && (<><div className="qbr-section-title">Identity & Access</div><p className="qbr-text">{report.identity_posture}</p></>)}
              {report.compliance_notes && (<><div className="qbr-section-title">Compliance</div><p className="qbr-text">{report.compliance_notes}</p></>)}
            </>
          )}
          {!loading && !report && <div className="qbr-empty">No report generated yet.</div>}
        </div>
        <div className="qbr-footer">Prepared by CloudByte Group using Aurelius</div>
      </div>
    </>
  );
}

// ── AI Insights Panel ─────────────────────────────────────────────
var tabInsightLabels = {
  overview: 'Overview Insights', posture: 'Device Insights', identity: 'Identity Insights',
  siem: 'Threat Insights', support: 'Support Insights', projects: 'Project Insights',
  devops: 'Engineering Insights', changes: 'Change Insights', budget: 'Cost Insights',
  network: 'Network Insights', compliance: 'Compliance Insights', security: 'Security Insights'
};
var aiButtonLabels = {
  overview: 'Analyze Posture', posture: 'Analyze Devices', identity: 'Analyze Identity',
  siem: 'Analyze Threats', support: 'Analyze Support', projects: 'Analyze Projects',
  devops: 'Analyze Engineering', changes: 'Analyze Changes', budget: 'Analyze Costs',
  network: 'Analyze Network', compliance: 'Analyze Compliance', security: 'Analyze Security'
};
var tabEndpoints = {
  posture: '/posture', identity: '/entra', siem: '/siem',
  support: '/tickets', projects: '/projects', devops: '/jira',
  changes: '/changes', budget: '/subscriptions', network: '/network'
};

function getTabRuleInsights(tab, c, tabData) {
  var rules = [];

  if (tab === 'overview' || tab === 'security') {
    if (c.endpoints && c.endpoints.critical > 3) rules.push({ level: 'critical', text: c.endpoints.critical + ' devices offline — CloudByte tracking for reconnection.' });
    else if (c.endpoints && c.endpoints.critical > 0) rules.push({ level: 'high', text: c.endpoints.critical + ' device(s) offline — monitoring for auto-recovery.' });
    if (c.threats && c.threats.active > 0) rules.push({ level: 'critical', text: 'Huntress flagged ' + c.threats.active + ' active threat(s) — CloudByte investigating.' });
    if (c.endpoints && c.endpoints.total > 0) { var healthPct = Math.round((c.endpoints.healthy / c.endpoints.total) * 100); if (healthPct < 80) rules.push({ level: 'high', text: 'Fleet health at ' + healthPct + '% — ' + (100 - healthPct) + '% of devices need attention.' }); }
    if (c.huntress_agents && c.endpoints && c.endpoints.total > 0) { var cov = Math.round((c.huntress_agents.total / c.endpoints.total) * 100); if (cov < 90) rules.push({ level: 'medium', text: 'EDR coverage at ' + cov + '% — ' + (100 - cov) + '% of fleet pending agent deployment.' }); }
    if (c.siemSummary && c.siemSummary.threats > 0) rules.push({ level: 'high', text: c.siemSummary.threats + ' SIEM threat finding(s) — under active review by CloudByte.' });
  }

  if (tab === 'posture' && tabData && tabData.summary) {
    var s = tabData.summary;
    if (s.total > 0 && s.offline > 0) { var offPct = Math.round((s.offline / s.total) * 100); if (offPct > 10) rules.push({ level: 'high', text: offPct + '% of fleet offline (' + s.offline + ' of ' + s.total + ') — reconnection in progress.' }); else if (s.offline > 0) rules.push({ level: 'medium', text: s.offline + ' device(s) offline — monitoring for auto-recovery.' }); }
    if (s.avUnprotected > 0) rules.push({ level: 'high', text: s.avUnprotected + ' device(s) missing antivirus — remediation queued.' });
    if (s.defenderDisabled > 0) rules.push({ level: 'medium', text: 'Defender disabled on ' + s.defenderDisabled + ' device(s) — policy enforcement pending.' });
    if (s.total > 0) { var staleDevs = tabData.devices ? tabData.devices.filter(function(d) { return d.offline && d.lastContact && (Date.now() / 1000 - d.lastContact) > 30 * 86400; }).length : 0; if (staleDevs > 0) rules.push({ level: 'medium', text: staleDevs + ' device(s) inactive 30+ days — inventory cleanup recommended.' }); }
  }

  if (tab === 'identity' && tabData && tabData.summary) {
    var us = tabData.summary.users; var ds = tabData.summary.devices; var ins = tabData.summary.intune;
    if (us) {
      var disabledWithLicense = tabData.users ? tabData.users.filter(function(u) { return !u.enabled && u.licensed; }).length : 0;
      if (disabledWithLicense > 0) rules.push({ level: 'medium', text: disabledWithLicense + ' disabled account(s) still consuming licenses — reclaim to reduce spend.' });
      if (us.total > 0 && us.guests > 0) { var guestPct = Math.round((us.guests / us.total) * 100); if (guestPct > 20) rules.push({ level: 'medium', text: 'Guest accounts at ' + guestPct + '% of directory (' + us.guests + ') — review for access hygiene.' }); }
    }
    if (ds && ds.total > 0) { var nonCompliant = ds.total - ds.compliant; if (nonCompliant > 0) rules.push({ level: 'high', text: nonCompliant + ' Entra device(s) out of compliance — conditional access may block users.' }); }
    if (ins && ins.total > 0) { var unencrypted = ins.total - ins.encrypted; if (unencrypted > 0) rules.push({ level: 'high', text: unencrypted + ' device(s) missing disk encryption — data at rest exposed.' }); if (ins.nonCompliant > 0) rules.push({ level: 'medium', text: ins.nonCompliant + ' Intune device(s) non-compliant — policy remediation in progress.' }); }
  }

  if (tab === 'siem' && tabData) {
    var sm = tabData.summary || tabData;
    if (sm.priority_1 > 0) rules.push({ level: 'critical', text: sm.priority_1 + ' P1 finding(s) flagged — highest severity, CloudByte investigating.' });
    if (sm.threats > 0) rules.push({ level: 'high', text: sm.threats + ' confirmed threat finding(s) — active response underway.' });
    if (sm.suspects > 0) rules.push({ level: 'medium', text: sm.suspects + ' suspect finding(s) being triaged by CloudByte.' });
    if (sm.total_open > 20) rules.push({ level: 'medium', text: sm.total_open + ' open findings in queue — prioritized by severity.' });
  }

  if (tab === 'support' && tabData && tabData.metrics) {
    var m = tabData.metrics;
    if (m.open > 20) rules.push({ level: 'high', text: m.open + ' open tickets — elevated volume, triaging by priority.' });
    var stalled = (m.waitingForSupport || 0) + (m.pending || 0);
    if (stalled > 0) rules.push({ level: 'high', text: stalled + ' ticket(s) stalled — CloudByte escalation in progress.' });
    if (m.waitingForUser > 3) rules.push({ level: 'medium', text: m.waitingForUser + ' ticket(s) awaiting your team\'s response.' });
  }

  if (tab === 'projects' && tabData && tabData.projects) {
    var totalOverdue = 0, totalTasks = 0, totalCompleted = 0;
    tabData.projects.forEach(function(p) { totalOverdue += (p.overdueTasks || 0); totalTasks += (p.totalTasks || 0); totalCompleted += (p.completedTasks || 0); });
    if (totalOverdue > 0) rules.push({ level: 'high', text: totalOverdue + ' task(s) past due across ' + tabData.projects.length + ' project(s) — review timeline.' });
    tabData.projects.forEach(function(p) { if (p.dueDate && new Date(p.dueDate) < new Date() && !p.completed && p.progress < 90) rules.push({ level: 'high', text: p.name + ' past due at ' + p.progress + '% — may need scope adjustment.' }); });
    if (totalTasks > 0 && totalCompleted / totalTasks < 0.5 && tabData.projects.some(function(p) { return p.dueDate && new Date(p.dueDate) < new Date(Date.now() + 30 * 86400000); })) rules.push({ level: 'medium', text: 'Completion below 50% with deadlines within 30 days — escalation may be needed.' });
  }

  if (tab === 'devops' && tabData) {
    if (tabData.totalOverdue > 0) rules.push({ level: 'high', text: tabData.totalOverdue + ' overdue issue(s) — sprint velocity at risk.' });
    if (tabData.totalOpen > 50) rules.push({ level: 'medium', text: tabData.totalOpen + ' issues in backlog — consider grooming to maintain velocity.' });
    if (tabData.priorityBreakdown) { var highP = (tabData.priorityBreakdown['Highest'] || 0) + (tabData.priorityBreakdown['High'] || 0); if (highP > 5) rules.push({ level: 'high', text: highP + ' high-priority issue(s) should be in active sprints.' }); }
  }

  if (tab === 'changes' && tabData && tabData.summary) {
    var cs = tabData.summary;
    var pendingReview = (cs.submitted || 0) + (cs.under_review || 0);
    if (pendingReview > 3) rules.push({ level: 'medium', text: pendingReview + ' change request(s) awaiting approval — review to keep pipeline moving.' });
    if (cs.total > 0 && cs.denied > 0) { var denyRate = Math.round((cs.denied / cs.total) * 100); if (denyRate > 20) rules.push({ level: 'medium', text: 'Denial rate at ' + denyRate + '% — submissions may need better scoping.' }); }
  }

  if (tab === 'budget' && tabData) {
    var bs = tabData.summary || tabData;
    if (bs.totalSubscriptions !== undefined && bs.totalMonthlyCost) rules.push({ level: 'low', text: bs.totalSubscriptions + ' active subscriptions at $' + bs.totalMonthlyCost.toLocaleString() + '/mo managed by CloudByte.' });
  }

  if (tab === 'network' && tabData && tabData.summary) {
    var ns = tabData.summary;
    if (ns.offline > 0) rules.push({ level: 'high', text: ns.offline + ' network device(s) offline — investigating connectivity.' });
    if (ns.firmware_outdated > 0) rules.push({ level: 'medium', text: ns.firmware_outdated + ' device(s) on outdated firmware — update window being scheduled.' });
    if (ns.total > 0 && ns.online === ns.total) rules.push({ level: 'good', text: 'All ' + ns.total + ' network devices healthy and online.' });
  }

  if (tab === 'compliance') {
    if (c.scores && c.scores.overall > 0 && c.scores.overall < 80) rules.push({ level: 'high', text: 'Security score at ' + c.scores.overall + '/100 — below 80 target, remediation plan active.' });
    if (c.scores && c.scores.endpoint > 0 && c.scores.endpoint < 80) rules.push({ level: 'medium', text: 'Endpoint score at ' + c.scores.endpoint + '/100 — contributing to overall gap.' });
    if (c.scores && c.scores.threat > 0 && c.scores.threat < 80) rules.push({ level: 'medium', text: 'Threat score at ' + c.scores.threat + '/100 — detection coverage needs improvement.' });
  }

  if (rules.length === 0) rules.push({ level: 'good', text: 'All systems operational — CloudByte is actively monitoring your environment.' });
  return rules;
}

var insightLevelLabels = { critical: 'Action Required', high: 'Needs Attention', medium: 'Monitoring', low: 'Informational', good: 'All Clear' };

function InsightsPanel({ c, clientName, activeTab, clientId, onGenerateQBR }) {
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('rules');
  const [tabData, setTabData] = useState(null);
  const [tabDataLoading, setTabDataLoading] = useState(false);

  useEffect(function() {
    setAiInsights(null);
    setMode('rules');
    setTabData(null);
    var endpoint = tabEndpoints[activeTab];
    if (endpoint && clientId) {
      setTabDataLoading(true);
      authFetch(API + '/clients/' + clientId + endpoint)
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(data) { setTabData(data); setTabDataLoading(false); })
        .catch(function() { setTabDataLoading(false); });
    }
  }, [activeTab, clientId]);

  useEffect(function() { setAiInsights(null); setMode('rules'); setTabData(null); }, [clientName]);

  var ruleInsights = getTabRuleInsights(activeTab, c, tabData);

  var fetchAIInsights = async function() {
    setLoading(true);
    try {
      var resp = await authFetch(API + '/insights/' + clientId, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tab: activeTab }) });
      if (resp.ok) { var data = await resp.json(); setAiInsights(data.insights); setMode('ai'); }
    } catch (e) { console.warn('AI insights failed:', e); }
    setLoading(false);
  };

  var insights = mode === 'ai' && aiInsights ? aiInsights : ruleInsights;
  var tabLabel = tabInsightLabels[activeTab] || 'Insights';
  var aiLabel = aiButtonLabels[activeTab] || 'Analyze';

  return (
    <div className="insights-panel">
      <div className="insights-header">
        <div className="insights-header-left">
          {mode === 'ai' && <div className="insights-ai-badge">AI</div>}
          <div className="insights-tab-label">{mode === 'ai' ? 'Aurelius AI' : tabLabel}</div>
        </div>
        <div className="insights-toggle">
          {mode === 'ai' ? (
            <button className="insights-btn" onClick={function() { setMode('rules'); }}>Back to Rules</button>
          ) : (CAN_USE_ADMIN_FEATURES && (
            <button className="insights-btn insights-btn-ai" onClick={fetchAIInsights} disabled={loading}>
              {loading ? 'Analyzing...' : aiLabel}
            </button>
          ))}
        </div>
      </div>
      {loading && (
        <div className="insights-loading">
          <div className="insights-loading-pulse" />
          <div className="insights-loading-text">Aurelius is analyzing {clientName}...</div>
        </div>
      )}
      {!loading && tabDataLoading && <div className="insights-loading-subtle">Loading data...</div>}
      {!loading && (
        <div className="insights-list">
          {insights.map(function(ins, i) {
            if (ins.level === 'good') return (
              <div key={i} className="insight-clear">
                <div className="insight-clear-icon">&#10003;</div>
                <div>
                  <div className="insight-clear-label">All Clear</div>
                  <div className="insight-clear-text">{ins.text}</div>
                </div>
              </div>
            );
            return (
              <div key={i} className={'insight-card insight-card-' + ins.level} style={{ animationDelay: (i * 0.06) + 's' }}>
                <div className={'insight-level-badge insight-level-' + ins.level}>{insightLevelLabels[ins.level] || ins.level}</div>
                <div className="insight-card-text">{ins.text}</div>
              </div>
            );
          })}
        </div>
      )}
      {CAN_USE_ADMIN_FEATURES && (
        <button className="qbr-generate-btn" onClick={onGenerateQBR}>
          <span className="qbr-btn-text">Generate QBR Report</span>
          <span className="qbr-btn-sub">Full executive business review</span>
        </button>
      )}
    </div>
  );
}

function ClientNotFound({ clientId }) {
  return (
    <div className="app"><header className="header"><div className="header-left"><div className="logo">AURELIUS</div><span className="logo-sub">by CloudByte Group</span></div></header>
      <div className="client-not-found"><div className="not-found-icon">404</div><div className="not-found-title">Client Not Found</div><div className="not-found-text">The client "{clientId}" does not exist.</div></div>
    </div>
  );
}

// ── NavRail ─────────────────────────────────────────────────────────
// Vertical primary navigation. Replaces the horizontal tab strip so the
// catalog can grow past the width of the screen (Linear / Vercel /
// Bloomberg-terminal style). Collapses to a 64px icon rail; preference
// persists in localStorage. Groups are semantic, in priority order, with
// small-caps eyebrows. Active state is a left accent bar + tinted
// background + bolder weight. Red Team gets a marquee accent treatment.
//
// Hover flyouts (collapsed mode only) show section eyebrow + full label +
// any sub-items. The 'children' field is empty today but the markup is
// in place so the moment a tab grows sub-views we don't need to
// re-architect — just add { id, label } objects to the children array.

// Custom SVG icon set — 14 glyphs, single-stroke, monochrome, drawn for
// recognition at 18px. Inline so they tree-shake cleanly with React.
function NavIcon(props) {
  var name = props.name;
  var common = { width: 18, height: 18, viewBox: '0 0 18 18', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  switch (name) {
    case 'overview':   return <svg {...common}><rect x="2.5" y="2.5" width="5.5" height="5.5" rx="1"/><rect x="10" y="2.5" width="5.5" height="5.5" rx="1"/><rect x="2.5" y="10" width="5.5" height="5.5" rx="1"/><rect x="10" y="10" width="5.5" height="5.5" rx="1"/></svg>;
    case 'redteam':    return <svg {...common}><circle cx="9" cy="9" r="6"/><line x1="9" y1="2" x2="9" y2="4.5"/><line x1="9" y1="13.5" x2="9" y2="16"/><line x1="2" y1="9" x2="4.5" y2="9"/><line x1="13.5" y1="9" x2="16" y2="9"/><circle cx="9" cy="9" r="1.4" fill="currentColor" stroke="none"/></svg>;
    case 'security':   return <svg {...common}><path d="M9 2 L15 4.5 V8.5 C15 12 12.5 14.5 9 16 C5.5 14.5 3 12 3 8.5 V4.5 Z"/></svg>;
    case 'devices':    return <svg {...common}><rect x="3" y="4" width="12" height="8" rx="1"/><line x1="2" y1="14.5" x2="16" y2="14.5"/><line x1="7.5" y1="14.5" x2="10.5" y2="14.5" strokeWidth="2.4"/></svg>;
    case 'identity':   return <svg {...common}><circle cx="9" cy="6.5" r="2.7"/><path d="M3 15.5 C3 12.5 5.5 10.5 9 10.5 C12.5 10.5 15 12.5 15 15.5"/></svg>;
    case 'credentials':return <svg {...common}><circle cx="6" cy="9" r="2.8"/><line x1="8.8" y1="9" x2="16" y2="9"/><line x1="13" y1="9" x2="13" y2="11.2"/><line x1="15.2" y1="9" x2="15.2" y2="12"/></svg>;
    case 'threats':    return <svg {...common}><path d="M2 9 C4 5.5 6 4.5 9 4.5 C12 4.5 14 5.5 16 9 C14 12.5 12 13.5 9 13.5 C6 13.5 4 12.5 2 9 Z"/><circle cx="9" cy="9" r="2"/></svg>;
    case 'network':    return <svg {...common}><circle cx="9" cy="9" r="6"/><line x1="3" y1="9" x2="15" y2="9"/><ellipse cx="9" cy="9" rx="2.5" ry="6"/></svg>;
    case 'engineering':return <svg {...common}><polyline points="6.5,3 3,9 6.5,15"/><polyline points="11.5,3 15,9 11.5,15"/></svg>;
    case 'support':    return <svg {...common}><path d="M3 11 V9 C3 5.7 5.7 3 9 3 C12.3 3 15 5.7 15 9 V11"/><rect x="2.5" y="10.5" width="3" height="4.5" rx="0.7"/><rect x="12.5" y="10.5" width="3" height="4.5" rx="0.7"/></svg>;
    case 'projects':   return <svg {...common}><rect x="2.5" y="3" width="3.4" height="12" rx="0.6"/><rect x="7.3" y="3" width="3.4" height="9" rx="0.6"/><rect x="12.1" y="3" width="3.4" height="6" rx="0.6"/></svg>;
    case 'changes':    return <svg {...common}><path d="M3 5 H12 L10 3"/><path d="M3 5 V8"/><path d="M15 13 H6 L8 15"/><path d="M15 13 V10"/></svg>;
    case 'reviews':    return <svg {...common}><circle cx="9" cy="9" r="6"/><path d="M5.8 9 L8 11.2 L12.2 7"/></svg>;
    case 'budget':     return <svg {...common}><circle cx="9" cy="9" r="6"/><path d="M9 5.2 V12.8"/><path d="M11 7 H8 C6.9 7 6.9 9 8 9 H10 C11.1 9 11.1 11 10 11 H7"/></svg>;
    case 'compliance': return <svg {...common}><rect x="3" y="3" width="12" height="12" rx="1.5"/><path d="M5.8 9 L8 11.2 L12.2 7"/></svg>;
    default:           return <svg {...common}><circle cx="9" cy="9" r="3"/></svg>;
  }
}

var NAV_RAIL_GROUPS = [
  { items: [{ id: 'overview', label: 'Overview', icon: 'overview', children: [] }] },
  { items: [{ id: 'redteam',  label: 'Red Team', icon: 'redteam',  accent: true, children: [] }] },
  { label: 'Security Posture', items: [
    { id: 'security', label: 'Security',          icon: 'security',    children: [] },
    { id: 'posture',  label: 'Devices',           icon: 'devices',     children: [] },
    { id: 'identity', label: 'Identity',          icon: 'identity',    children: [] },
    { id: 'keeper',   label: 'Credentials',       icon: 'credentials', children: [] },
    { id: 'siem',     label: 'Threat Monitoring', icon: 'threats',     children: [] }
  ]},
  { label: 'Operations', items: [
    { id: 'network', label: 'Network',     icon: 'network',     children: [] },
    { id: 'devops',  label: 'Engineering', icon: 'engineering', children: [] },
    { id: 'support', label: 'Support',     icon: 'support',     children: [] }
  ]},
  { label: 'Workstreams', items: [
    { id: 'projects', label: 'Projects', icon: 'projects', children: [] },
    { id: 'changes',  label: 'Changes',  icon: 'changes',  children: [] },
    { id: 'reviews',  label: 'Reviews',  icon: 'reviews',  children: [] }
  ]},
  { label: 'Business', items: [
    { id: 'budget',     label: 'Budget',     icon: 'budget',     children: [] },
    { id: 'compliance', label: 'Compliance', icon: 'compliance', children: [] }
  ]}
];

function NavRail({ activeTab, onTabChange, badgeData, collapsed, onToggleCollapse }) {
  // Flyouts use viewport-fixed positioning via a portal because the rail's
  // own scroll container (overflow-y: auto) would otherwise clip them.
  // CSS can't allow vertical scroll + horizontal overflow simultaneously
  // in the same element — browsers coerce overflow-x to auto when y is auto.
  // The portal escapes that constraint cleanly.
  var [flyoutState, setFlyoutState] = useState(null);  // { item, group, top, left }
  var leaveTimerRef = useRef(null);

  function showFlyout(item, group, e) {
    if (!collapsed) return;
    if (leaveTimerRef.current) { clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null; }
    var btn = e.currentTarget.closest('.nav-rail-item-wrap') || e.currentTarget;
    var rect = btn.getBoundingClientRect();
    setFlyoutState({
      item: item,
      group: group,
      top: rect.top - 6,
      left: rect.right + 8
    });
  }
  function scheduleHide() {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(function() { setFlyoutState(null); leaveTimerRef.current = null; }, 120);
  }
  function cancelHide() {
    if (leaveTimerRef.current) { clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null; }
  }

  // Hide flyouts when the rail expands (collapsed → expanded transition)
  useEffect(function() {
    if (!collapsed) setFlyoutState(null);
  }, [collapsed]);

  // Hide flyout when the active tab changes (clicking through it)
  useEffect(function() { setFlyoutState(null); }, [activeTab]);

  // Cleanup on unmount
  useEffect(function() {
    return function() { if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current); };
  }, []);

  return (
    <nav className={'nav-rail' + (collapsed ? ' nav-rail-collapsed' : '')} aria-label="Primary navigation">
      <div className="nav-rail-scroll">
        {NAV_RAIL_GROUPS.map(function(group, gi) {
          return (
            <div key={gi} className="nav-rail-group">
              {group.label && !collapsed && <div className="nav-rail-eyebrow">{group.label}</div>}
              {group.label && collapsed && gi > 0 && <div className="nav-rail-divider" />}
              {group.items.map(function(item) {
                var active = activeTab === item.id;
                var badge = badgeData && badgeData[item.id];
                var hasChildren = Array.isArray(item.children) && item.children.length > 0;
                return (
                  <div
                    key={item.id}
                    className="nav-rail-item-wrap"
                    onMouseEnter={function(e) { showFlyout(item, group, e); }}
                    onMouseLeave={scheduleHide}
                    onFocus={function(e) { showFlyout(item, group, e); }}
                    onBlur={scheduleHide}
                  >
                    <button
                      className={'nav-rail-item' + (active ? ' active' : '') + (item.accent ? ' accent' : '')}
                      onClick={function() { onTabChange(item.id); }}
                      aria-current={active ? 'page' : undefined}
                      aria-haspopup={hasChildren ? 'menu' : undefined}
                    >
                      <span className="nav-rail-icon"><NavIcon name={item.icon} /></span>
                      <span className="nav-rail-label">{item.label}</span>
                      {badge && badge.type === 'dot' && <span className="nav-rail-badge-dot" />}
                      {badge && badge.type === 'count' && badge.count > 0 && (
                        <span className="nav-rail-badge-count">{badge.count > 99 ? '99+' : badge.count}</span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <button className="nav-rail-collapse-btn" onClick={onToggleCollapse} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
        <span>{collapsed ? '›' : '‹'}</span>
        {!collapsed && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600 }}>Collapse</span>}
      </button>

      {/* Portal-rendered flyout — escapes the rail's overflow:auto so it overlays
          the dashboard content cleanly. Only renders in collapsed mode. */}
      {collapsed && flyoutState && createPortal(
        <NavRailFlyout
          item={flyoutState.item}
          group={flyoutState.group}
          top={flyoutState.top}
          left={flyoutState.left}
          activeTab={activeTab}
          onPick={onTabChange}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
        />,
        document.body
      )}
    </nav>
  );
}

function NavRailFlyout({ item, group, top, left, activeTab, onPick, onMouseEnter, onMouseLeave }) {
  var hasChildren = Array.isArray(item.children) && item.children.length > 0;
  var active = activeTab === item.id;
  return (
    <div
      className="nav-rail-flyout nav-rail-flyout-portal"
      role="menu"
      aria-label={item.label + ' menu'}
      style={{ top: top + 'px', left: left + 'px' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {group && group.label && <div className="nav-rail-flyout-eyebrow">{group.label}</div>}
      <button
        className={'nav-rail-flyout-title' + (active ? ' active' : '')}
        onClick={function() { onPick(item.id); }}
        role="menuitem"
      >
        {item.label}
      </button>
      {hasChildren && (
        <div className="nav-rail-flyout-children">
          {item.children.map(function(child) {
            return (
              <button
                key={child.id}
                className="nav-rail-flyout-child"
                onClick={function() { onPick(child.id || item.id); }}
                role="menuitem"
              >
                {child.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── InsightsDrawer + InsightsFab ────────────────────────────────────
// Replaces the persistent right-sidebar. Reclaims ~320px of content
// width while making the AI/insights surface still one click away.
// Same vocabulary as QBRPanel + ChangelogPanel — slide from right,
// backdrop, ESC closes.
function InsightsFab({ onClick, hasAi, hidden }) {
  if (hidden) return null;
  return (
    <button className="insights-fab" onClick={onClick} aria-label="Open insights">
      <span className="insights-fab-glyph">✦</span>
      <span className="insights-fab-text">Insights</span>
      {hasAi && <span className="insights-fab-ai">AI</span>}
    </button>
  );
}

function InsightsDrawer(props) {
  // ESC to close
  useEffect(function() {
    if (!props.isOpen) return;
    function onKey(e) { if (e.key === 'Escape') props.onClose(); }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [props.isOpen, props.onClose]);

  return (
    <>
      <div className={'insights-drawer-overlay' + (props.isOpen ? ' open' : '')} onClick={props.onClose} aria-hidden="true" />
      <aside
        className={'insights-drawer' + (props.isOpen ? ' open' : '')}
        role="dialog"
        aria-modal={props.isOpen ? 'true' : undefined}
        aria-label="Insights"
        aria-hidden={!props.isOpen}
      >
        <div className="insights-drawer-header">
          <div className="insights-drawer-title-block">
            <span className="insights-drawer-eyebrow">{props.clientName}</span>
            <span className="insights-drawer-title">Insights</span>
          </div>
          <button className="insights-drawer-close" onClick={props.onClose} aria-label="Close insights">×</button>
        </div>
        <div className="insights-drawer-body">
          {props.isOpen && (
            <InsightsPanel
              c={props.c}
              clientName={props.clientName}
              activeTab={props.activeTab}
              clientId={props.clientId}
              onGenerateQBR={props.onGenerateQBR}
            />
          )}
        </div>
      </aside>
    </>
  );
}

// ── Admin Panel ──────────────────────────────────────────────────────
// Admin-only surface for managing users, browsing the audit log, and
// revoking active sessions. Gated by role='admin' on backend + frontend.

function fmtDateTime(s) {
  if (!s) return '—';
  var d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function fmtDateShort(s) {
  if (!s) return '—';
  var d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}
function isUserLocked(u) {
  return u.locked_until && new Date(u.locked_until).getTime() > Date.now();
}

function AdminPanel({ clientList }) {
  var [activeTab, setActiveTab] = useState('users');
  var [stats, setStats] = useState(null);

  useEffect(function() {
    authFetch(API + '/admin/stats').then(function(r) { return r.ok ? r.json() : null; }).then(function(s) { setStats(s); }).catch(function() {});
  }, [activeTab]);

  return (
    <div className="admin-panel">
      <div className="admin-panel-header">
        <div>
          <div className="admin-panel-title">Administrator Panel</div>
          <div className="admin-panel-sub">Users · Audit · Sessions — actions here are logged permanently</div>
        </div>
        {stats && (
          <div className="admin-panel-stats">
            <div className="admin-stat"><div className="admin-stat-num">{stats.users_total}</div><div className="admin-stat-lbl">Users</div></div>
            <div className="admin-stat"><div className="admin-stat-num">{stats.users_enabled}</div><div className="admin-stat-lbl">Enabled</div></div>
            <div className="admin-stat"><div className="admin-stat-num">{stats.users_locked}</div><div className="admin-stat-lbl">Locked</div></div>
            <div className="admin-stat"><div className="admin-stat-num">{stats.audit_24h}</div><div className="admin-stat-lbl">Events / 24h</div></div>
            <div className="admin-stat"><div className="admin-stat-num">{stats.warn_24h}</div><div className="admin-stat-lbl">Warnings / 24h</div></div>
          </div>
        )}
      </div>
      <div className="admin-tabs">
        <button className={'admin-tab ' + (activeTab === 'users' ? 'active' : '')} onClick={function() { setActiveTab('users'); }}>Users</button>
        <button className={'admin-tab ' + (activeTab === 'integrations' ? 'active' : '')} onClick={function() { setActiveTab('integrations'); }}>Integrations</button>
        <button className={'admin-tab ' + (activeTab === 'assessments' ? 'active' : '')} onClick={function() { setActiveTab('assessments'); }}>Tenant Assessments</button>
        <button className={'admin-tab ' + (activeTab === 'automation' ? 'active' : '')} onClick={function() { setActiveTab('automation'); }}>Automation</button>
        <button className={'admin-tab ' + (activeTab === 'audit' ? 'active' : '')} onClick={function() { setActiveTab('audit'); }}>Audit Log</button>
        <button className={'admin-tab ' + (activeTab === 'sessions' ? 'active' : '')} onClick={function() { setActiveTab('sessions'); }}>Active Sessions</button>
      </div>
      <div className="admin-tab-content">
        {activeTab === 'users' && <AdminUsersTab clientList={clientList} />}
        {activeTab === 'integrations' && <AdminIntegrationsTab />}
        {activeTab === 'assessments' && <AdminAssessmentsTab />}
        {activeTab === 'automation' && <AdminAutomationTab clientList={clientList} />}
        {activeTab === 'audit' && <AdminAuditTab />}
        {activeTab === 'sessions' && <AdminSessionsTab />}
      </div>
    </div>
  );
}

function AdminUsersTab({ clientList }) {
  var [users, setUsers] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('');
  var [showDeleted, setShowDeleted] = useState(false);
  var [editingUser, setEditingUser] = useState(null);
  var [creatingUser, setCreatingUser] = useState(false);
  var [revealedPassword, setRevealedPassword] = useState(null);
  var [toast, setToast] = useState(null);

  function showToast(msg, type) {
    setToast({ msg: msg, type: type || 'info' });
    setTimeout(function() { setToast(null); }, 3500);
  }

  function fetchUsers() {
    setLoading(true);
    var qs = showDeleted ? '?includeDeleted=true' : '';
    authFetch(API + '/admin/users' + qs)
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(data) { setUsers(data); setLoading(false); })
      .catch(function() { setLoading(false); showToast('Failed to load users', 'error'); });
  }

  useEffect(fetchUsers, [showDeleted]);

  function onResetPassword(u) {
    if (!window.confirm('Reset password for ' + u.email + '?\n\nThis will immediately invalidate their current password.')) return;
    authFetch(API + '/admin/users/' + u.id + '/reset-password', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        if (!x.ok) { showToast(x.d.error || 'Reset failed', 'error'); return; }
        setRevealedPassword({ email: x.d.user.email, password: x.d.password, title: 'Password reset' });
        fetchUsers();
      })
      .catch(function() { showToast('Reset failed', 'error'); });
  }

  function onUnlock(u) {
    authFetch(API + '/admin/users/' + u.id + '/unlock', { method: 'POST' })
      .then(function(r) { if (r.ok) { showToast('Unlocked ' + u.email, 'ok'); fetchUsers(); } else { showToast('Unlock failed', 'error'); } })
      .catch(function() { showToast('Unlock failed', 'error'); });
  }

  function onToggleEnabled(u) {
    var verb = u.enabled ? 'disable' : 'enable';
    if (u.enabled && !window.confirm('Disable ' + u.email + '?\n\nThey will not be able to log in. Active sessions remain valid until they expire naturally.')) return;
    authFetch(API + '/admin/users/' + u.id + '/' + verb, { method: 'POST' })
      .then(function(r) { if (r.ok) { showToast((verb === 'disable' ? 'Disabled ' : 'Enabled ') + u.email, 'ok'); fetchUsers(); } else { showToast(verb + ' failed', 'error'); } })
      .catch(function() { showToast(verb + ' failed', 'error'); });
  }

  function onDelete(u) {
    if (!window.confirm('Delete ' + u.email + '?\n\nThe user will be soft-deleted — row preserved for audit integrity, all sessions revoked immediately, restorable from the "Show deleted" view. Hard delete requires DB admin.')) return;
    authFetch(API + '/admin/users/' + u.id + '/delete', { method: 'POST' })
      .then(function(r) { if (r.ok) { showToast('Deleted ' + u.email, 'ok'); fetchUsers(); } else { r.json().then(function(d) { showToast(d.error || 'Delete failed', 'error'); }); } })
      .catch(function() { showToast('Delete failed', 'error'); });
  }

  function onRestore(u) {
    authFetch(API + '/admin/users/' + u.id + '/restore', { method: 'POST' })
      .then(function(r) { if (r.ok) { showToast('Restored ' + u.email, 'ok'); fetchUsers(); } else { showToast('Restore failed', 'error'); } })
      .catch(function() { showToast('Restore failed', 'error'); });
  }

  var filtered = users.filter(function(u) {
    if (!filter) return true;
    var q = filter.toLowerCase();
    return (u.email || '').toLowerCase().includes(q)
        || (u.client_id || '').toLowerCase().includes(q)
        || (u.display_name || '').toLowerCase().includes(q)
        || (u.role || '').toLowerCase().includes(q)
        || (Array.isArray(u.tags) && u.tags.some(function(t) { return t.toLowerCase().includes(q); }));
  });

  return (
    <div className="admin-users-tab">
      <div className="admin-toolbar">
        <input className="admin-filter-input" placeholder="Filter by email, client, role, tag..." value={filter} onChange={function(e) { setFilter(e.target.value); }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: showDeleted ? '#f87171' : 'var(--text-muted)' }}>
          <input type="checkbox" checked={showDeleted} onChange={function(e) { setShowDeleted(e.target.checked); }} />
          Show deleted
        </label>
        <div style={{ flex: 1 }} />
        <button className="admin-btn primary" onClick={function() { setCreatingUser(true); }}>+ New User</button>
        <button className="admin-btn" onClick={fetchUsers} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Client</th>
              <th>Status</th>
              <th>Last Login</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading users...</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No users match "{filter}"</td></tr>}
            {!loading && filtered.map(function(u) {
              var locked = isUserLocked(u);
              var isDeleted = !!u.deleted_at;
              return (
                <tr key={u.id} className={(isDeleted ? 'deleted-row ' : '') + (!u.enabled && !isDeleted ? 'disabled-row' : '')}>
                  <td>
                    <div style={{ fontWeight: 600, textDecoration: isDeleted ? 'line-through' : 'none' }}>{u.email}</div>
                    {u.display_name && <div style={{ fontSize: 11, opacity: 0.6 }}>{u.display_name}</div>}
                    {Array.isArray(u.tags) && u.tags.length > 0 && (
                      <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {u.tags.map(function(t) { return <span key={t} className="admin-tag-chip">{t}</span>; })}
                      </div>
                    )}
                    {u.notes && <div style={{ marginTop: 4, fontSize: 10, opacity: 0.55, fontStyle: 'italic', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.notes}>{u.notes}</div>}
                  </td>
                  <td><span className={'admin-role-chip ' + u.role}>{u.role}</span></td>
                  <td>{u.client_id || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td>
                    {isDeleted
                      ? <span className="admin-chip bad">Deleted {fmtDateShort(u.deleted_at)}</span>
                      : u.enabled
                        ? <span className="admin-chip ok">Active</span>
                        : <span className="admin-chip bad">Disabled</span>}
                    {locked && !isDeleted && <span className="admin-chip warn" style={{ marginLeft: 6 }}>Locked</span>}
                    {u.must_change_password && !isDeleted && <span className="admin-chip info" style={{ marginLeft: 6 }}>Must change PW</span>}
                    {u.failed_login_count > 0 && !locked && !isDeleted && <span className="admin-chip warn-soft" style={{ marginLeft: 6 }}>{u.failed_login_count}/5 failed</span>}
                    {u.entra_object_id && !isDeleted && <span className="admin-chip info" style={{ marginLeft: 6 }} title={'Entra oid ' + u.entra_object_id.slice(0, 8) + '…'}>SSO</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {u.last_login_at ? fmtDateTime(u.last_login_at) : <span style={{ opacity: 0.4 }}>Never</span>}
                    {u.last_login_ip && <div style={{ fontSize: 10, opacity: 0.5 }}>from {u.last_login_ip}</div>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="admin-row-actions">
                      {isDeleted ? (
                        <button className="admin-btn sm ok" onClick={function() { onRestore(u); }}>Restore</button>
                      ) : (
                        <>
                          <button className="admin-btn sm" onClick={function() { setEditingUser(u); }}>Edit</button>
                          <button className="admin-btn sm" onClick={function() { onResetPassword(u); }}>Reset PW</button>
                          {locked && <button className="admin-btn sm warn" onClick={function() { onUnlock(u); }}>Unlock</button>}
                          <button className={'admin-btn sm ' + (u.enabled ? 'danger' : 'ok')} onClick={function() { onToggleEnabled(u); }}>{u.enabled ? 'Disable' : 'Enable'}</button>
                          <button className="admin-btn sm danger" onClick={function() { onDelete(u); }}>Delete</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(creatingUser || editingUser) && (
        <AdminUserModal
          user={editingUser}
          clientList={clientList}
          onClose={function() { setEditingUser(null); setCreatingUser(false); }}
          onSaved={function(result) {
            if (result.password) setRevealedPassword({ email: result.user.email, password: result.password, title: 'User created' });
            fetchUsers();
            setEditingUser(null);
            setCreatingUser(false);
          }}
          onError={function(msg) { showToast(msg, 'error'); }}
        />
      )}
      {revealedPassword && <AdminPasswordReveal data={revealedPassword} onClose={function() { setRevealedPassword(null); }} />}
      {toast && <AdminToast {...toast} />}
    </div>
  );
}

function AdminUserModal({ user, clientList, onClose, onSaved, onError }) {
  var isEdit = !!user;
  var [form, setForm] = useState({
    email: user ? user.email : '',
    role: user ? user.role : 'client',
    clientId: user ? (user.client_id || '') : '',
    displayName: user ? (user.display_name || '') : '',
    enabled: user ? user.enabled : true,
    notes: user ? (user.notes || '') : '',
    tags: user && Array.isArray(user.tags) ? user.tags.slice() : [],
  });
  var [tagInput, setTagInput] = useState('');
  var [emailInput, setEmailInput] = useState(user ? user.email : '');
  var [saving, setSaving] = useState(false);
  var [emailSaving, setEmailSaving] = useState(false);
  var [showAdvanced, setShowAdvanced] = useState(false);

  function addTagFromInput() {
    var v = tagInput.trim().toLowerCase().slice(0, 32);
    if (!v) return;
    if (form.tags.indexOf(v) >= 0) { setTagInput(''); return; }
    if (form.tags.length >= 12) { onError('Max 12 tags per user'); return; }
    setForm(Object.assign({}, form, { tags: form.tags.concat([v]) }));
    setTagInput('');
  }
  function removeTag(t) {
    setForm(Object.assign({}, form, { tags: form.tags.filter(function(x) { return x !== t; }) }));
  }

  function submit(e) {
    e.preventDefault();
    if (!form.email || !form.role) { onError('Email and role are required'); return; }
    if (form.role === 'client' && !form.clientId) { onError('clientId is required for role=client'); return; }
    setSaving(true);
    if (isEdit) {
      authFetch(API + '/admin/users/' + user.id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: form.role, clientId: form.clientId || null, displayName: form.displayName, enabled: form.enabled, notes: form.notes, tags: form.tags })
      })
        .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
        .then(function(x) { setSaving(false); if (x.ok) onSaved({ user: x.d }); else onError(x.d.error || 'Update failed'); })
        .catch(function() { setSaving(false); onError('Update failed'); });
    } else {
      authFetch(API + '/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, role: form.role, clientId: form.clientId || null, displayName: form.displayName, enabled: form.enabled })
      })
        .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
        .then(function(x) {
          setSaving(false);
          if (x.ok) {
            // After creating the user, send a follow-up PATCH if tags/notes are set (create endpoint doesn't accept them)
            if (x.d && x.d.user && (form.notes || form.tags.length > 0)) {
              authFetch(API + '/admin/users/' + x.d.user.id, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notes: form.notes, tags: form.tags })
              }).finally(function() { onSaved(x.d); });
            } else {
              onSaved(x.d);
            }
          } else onError(x.d.error || 'Create failed');
        })
        .catch(function() { setSaving(false); onError('Create failed'); });
    }
  }

  function saveEmail() {
    if (!emailInput || emailInput === user.email) return;
    if (!window.confirm('Change email from ' + user.email + ' to ' + emailInput + '?\n\nThis is audited as USER_EMAIL_CHANGED. The user will need to use the new email to log in next time.')) return;
    setEmailSaving(true);
    authFetch(API + '/admin/users/' + user.id + '/email', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailInput })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setEmailSaving(false); if (x.ok) onSaved({ user: x.d }); else onError(x.d.error || 'Email change failed'); })
      .catch(function() { setEmailSaving(false); onError('Email change failed'); });
  }

  function unlinkSso() {
    if (!window.confirm('Remove the Microsoft SSO link for ' + user.email + '?\n\nThey will need to sign in via SSO again (first sign-in re-links by email).')) return;
    authFetch(API + '/admin/users/' + user.id + '/unlink-sso', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { if (x.ok) onSaved({ user: x.d }); else onError(x.d.error || 'Unlink failed'); })
      .catch(function() { onError('Unlink failed'); });
  }

  function revokeSessions() {
    if (!window.confirm('Revoke ALL active sessions for ' + user.email + '?\n\nThey will be logged out of every device immediately. This is a security action.')) return;
    authFetch(API + '/admin/users/' + user.id + '/revoke-sessions', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { if (x.ok) onSaved({ user: Object.assign({}, user, { __revoked: x.d.revoked }) }); else onError(x.d.error || 'Revoke failed'); })
      .catch(function() { onError('Revoke failed'); });
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 520 }}>
        <form onSubmit={submit}>
          <div className="admin-modal-header">{isEdit ? 'Edit user' : 'New user'}</div>
          <div className="admin-modal-body">
            {/* Email — separate save flow on edit because it has its own audit event */}
            <div className="admin-field">
              <label>Email</label>
              {isEdit ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="email" style={{ flex: 1 }} value={emailInput} onChange={function(e) { setEmailInput(e.target.value); }} />
                  <button type="button" className="admin-btn sm" onClick={saveEmail} disabled={emailSaving || !emailInput || emailInput === user.email}>{emailSaving ? 'Saving...' : 'Update'}</button>
                </div>
              ) : (
                <input type="email" required value={form.email} onChange={function(e) { setForm(Object.assign({}, form, { email: e.target.value })); }} />
              )}
              {isEdit && <div className="admin-field-hint">Change is audited as USER_EMAIL_CHANGED. Case-insensitive, must be unique across active users.</div>}
            </div>

            <div className="admin-field">
              <label>Display name</label>
              <input type="text" value={form.displayName} onChange={function(e) { setForm(Object.assign({}, form, { displayName: e.target.value })); }} placeholder="Optional — shown in UI" />
            </div>

            <div className="admin-field">
              <label>Role</label>
              <select value={form.role} onChange={function(e) { setForm(Object.assign({}, form, { role: e.target.value, clientId: e.target.value === 'admin' ? '' : form.clientId })); }}>
                <option value="client">client</option>
                <option value="admin">admin</option>
              </select>
            </div>

            {form.role === 'client' && (
              <div className="admin-field">
                <label>Client</label>
                <select value={form.clientId} onChange={function(e) { setForm(Object.assign({}, form, { clientId: e.target.value })); }}>
                  <option value="">— Select a client —</option>
                  {(clientList || []).map(function(c) { return <option key={c.id} value={c.id}>{c.id} — {c.name}</option>; })}
                </select>
                <div className="admin-field-hint">Scopes all queries from this user to the selected client.</div>
              </div>
            )}

            <div className="admin-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.enabled} onChange={function(e) { setForm(Object.assign({}, form, { enabled: e.target.checked })); }} />
                <span>Enabled</span>
              </label>
            </div>

            <div className="admin-field">
              <label>Tags</label>
              <div className="admin-tag-editor">
                {form.tags.map(function(t) {
                  return (
                    <span key={t} className="admin-tag-chip editable">
                      {t}
                      <button type="button" onClick={function() { removeTag(t); }} title="Remove tag" aria-label="Remove tag">×</button>
                    </span>
                  );
                })}
                <input
                  type="text"
                  className="admin-tag-input"
                  placeholder={form.tags.length === 0 ? 'vip, on-leave, test-account…' : 'Add tag'}
                  value={tagInput}
                  onChange={function(e) { setTagInput(e.target.value); }}
                  onKeyDown={function(e) {
                    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTagFromInput(); }
                    else if (e.key === 'Backspace' && !tagInput && form.tags.length > 0) { removeTag(form.tags[form.tags.length - 1]); }
                  }}
                  onBlur={addTagFromInput}
                />
              </div>
              <div className="admin-field-hint">Press Enter or comma to add. Lowercased, deduped. Max 12 tags, 32 chars each.</div>
            </div>

            <div className="admin-field">
              <label>Notes</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={function(e) { setForm(Object.assign({}, form, { notes: e.target.value })); }}
                placeholder="Admin-only notes. Not visible to the user."
                style={{ padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', minHeight: 60 }}
              />
              <div className="admin-field-hint">Free-form. Shown in the user row as a tooltip on hover.</div>
            </div>

            {!isEdit && <div className="admin-modal-hint">A random 20-character password will be generated and shown once on save. The user will be required to change it on first login.</div>}

            {/* Advanced section — only in edit mode */}
            {isEdit && (
              <>
                <button type="button" className="admin-advanced-toggle" onClick={function() { setShowAdvanced(!showAdvanced); }}>
                  {showAdvanced ? '▴' : '▾'} Advanced actions
                </button>
                {showAdvanced && (
                  <div className="admin-advanced">
                    <div className="admin-advanced-row">
                      <div className="admin-advanced-label">
                        <div style={{ fontWeight: 600 }}>Force logout</div>
                        <div className="admin-field-hint">Revoke every active JWT for this user right now. Use for suspected compromise.</div>
                      </div>
                      <button type="button" className="admin-btn sm warn" onClick={revokeSessions}>Revoke all sessions</button>
                    </div>
                    <div className="admin-advanced-row">
                      <div className="admin-advanced-label">
                        <div style={{ fontWeight: 600 }}>Microsoft SSO</div>
                        <div className="admin-field-hint">
                          {user.entra_object_id
                            ? <>Linked to Entra oid <code>{user.entra_object_id.slice(0, 8)}…{user.entra_object_id.slice(-4)}</code></>
                            : 'Not linked. Will link automatically on first SSO sign-in that matches this email.'}
                        </div>
                      </div>
                      <button type="button" className="admin-btn sm danger" onClick={unlinkSso} disabled={!user.entra_object_id}>Unlink SSO</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn primary" disabled={saving}>{saving ? 'Saving...' : (isEdit ? 'Save changes' : 'Create user')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminPasswordReveal({ data, onClose }) {
  var [copied, setCopied] = useState(false);
  function copy() {
    try {
      navigator.clipboard.writeText(data.password).then(function() { setCopied(true); setTimeout(function() { setCopied(false); }, 2000); });
    } catch (e) { /* noop */ }
  }
  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" style={{ maxWidth: 520 }}>
        <div className="admin-modal-header" style={{ background: 'linear-gradient(135deg, rgba(91,158,244,0.15), rgba(120,80,255,0.15))' }}>{data.title}</div>
        <div className="admin-modal-body">
          <div style={{ marginBottom: 10, fontSize: 13 }}>The new password for <strong>{data.email}</strong> is shown once below. <strong>Save it now</strong> — it cannot be retrieved after closing this dialog.</div>
          <div className="admin-pw-display">
            <code>{data.password}</code>
            <button type="button" className="admin-btn sm primary" onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
          </div>
          <div className="admin-field-hint" style={{ marginTop: 12 }}>Distribute via 1Password, Keeper, or encrypted email. The user must change it on first login (must_change_password = TRUE).</div>
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn primary" onClick={onClose}>I've saved it — close</button>
        </div>
      </div>
    </div>
  );
}

function AdminAuditTab() {
  var [rows, setRows] = useState([]);
  var [total, setTotal] = useState(0);
  var [page, setPage] = useState(1);
  var [pageSize] = useState(50);
  var [loading, setLoading] = useState(true);
  var [meta, setMeta] = useState({ eventTypes: [], severities: [] });
  var [filters, setFilters] = useState({ email: '', eventType: '', severity: '', startDate: '', endDate: '', failuresOnly: false });
  var [expandedRow, setExpandedRow] = useState(null);

  function queryString(extras) {
    var params = Object.assign({}, filters, extras || {});
    var parts = [];
    for (var k in params) {
      if (params[k] === '' || params[k] === false || params[k] == null) continue;
      parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(params[k]));
    }
    return parts.join('&');
  }

  function fetchRows() {
    setLoading(true);
    var qs = queryString({ page: page, pageSize: pageSize });
    authFetch(API + '/admin/audit?' + qs)
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) {
        if (data) { setRows(data.rows); setTotal(data.total); }
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }

  useEffect(function() {
    authFetch(API + '/admin/audit/metadata').then(function(r) { return r.ok ? r.json() : null; }).then(function(m) { if (m) setMeta(m); }).catch(function() {});
  }, []);

  useEffect(fetchRows, [page, filters]);

  function updateFilter(k, v) { setFilters(Object.assign({}, filters, { [k]: v })); setPage(1); }

  function exportCsv() {
    var token = getToken();
    var qs = queryString({});
    // We can't easily stream with authFetch; use fetch with auth header and blob download
    fetch(API + '/admin/audit/export.csv?' + qs, { headers: { 'Authorization': 'Bearer ' + token } })
      .then(function(r) { return r.blob(); })
      .then(function(blob) {
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a'); a.href = url; a.download = 'aurelius-audit-' + new Date().toISOString().slice(0, 10) + '.csv';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  }

  var totalPages = Math.max(1, Math.ceil(total / pageSize));
  var severityClass = { INFO: 'info', WARN: 'warn', ERROR: 'bad' };

  return (
    <div className="admin-audit-tab">
      <div className="admin-toolbar">
        <input className="admin-filter-input" placeholder="Filter by email..." value={filters.email} onChange={function(e) { updateFilter('email', e.target.value); }} />
        <select className="admin-filter-select" value={filters.eventType} onChange={function(e) { updateFilter('eventType', e.target.value); }}>
          <option value="">All events</option>
          {meta.eventTypes.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
        </select>
        <select className="admin-filter-select" value={filters.severity} onChange={function(e) { updateFilter('severity', e.target.value); }}>
          <option value="">All severities</option>
          {meta.severities.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
        </select>
        <input type="date" className="admin-filter-select" value={filters.startDate} onChange={function(e) { updateFilter('startDate', e.target.value); }} title="From date" />
        <input type="date" className="admin-filter-select" value={filters.endDate} onChange={function(e) { updateFilter('endDate', e.target.value); }} title="To date" />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={filters.failuresOnly} onChange={function(e) { updateFilter('failuresOnly', e.target.checked); }} />
          Failures only
        </label>
        <button className="admin-btn" onClick={function() { setFilters({ email: '', eventType: '', severity: '', startDate: '', endDate: '', failuresOnly: false }); setPage(1); }}>Clear</button>
        <button className="admin-btn primary" onClick={exportCsv}>Export CSV</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table dense">
          <thead>
            <tr>
              <th style={{ width: 150 }}>When</th>
              <th>Event</th>
              <th style={{ width: 80 }}>Severity</th>
              <th style={{ width: 60 }}>OK</th>
              <th>User</th>
              <th style={{ width: 120 }}>Client</th>
              <th style={{ width: 140 }}>IP</th>
              <th>Metadata</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading audit events...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="8" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No events match these filters.</td></tr>}
            {!loading && rows.map(function(r) {
              var isExpanded = expandedRow === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className={'audit-row clickable' + (isExpanded ? ' expanded' : '')} onClick={function() { setExpandedRow(isExpanded ? null : r.id); }}>
                    <td style={{ fontSize: 11 }}>{fmtDateTime(r.created_at)}</td>
                    <td><strong>{r.event_type}</strong></td>
                    <td><span className={'admin-chip ' + (severityClass[r.severity] || 'info')}>{r.severity}</span></td>
                    <td>{r.success ? <span style={{ color: '#22c55e' }}>✓</span> : <span style={{ color: '#ef4444' }}>✗</span>}</td>
                    <td style={{ fontSize: 12 }}>{r.email || (r.user_id ? 'user#' + r.user_id : '—')}</td>
                    <td style={{ fontSize: 12 }}>{r.client_id || <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.ip || '—'}</td>
                    <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {r.metadata ? Object.keys(r.metadata).slice(0, 3).map(function(k) { return k + '=' + String(r.metadata[k]).slice(0, 25); }).join(' · ') : '—'}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="audit-row-expanded">
                      <td colSpan="8">
                        <div className="audit-expand-grid">
                          <div><div className="audit-expand-label">User Agent</div><div className="audit-expand-val">{r.user_agent || '—'}</div></div>
                          <div><div className="audit-expand-label">Metadata</div><pre className="audit-expand-val mono">{r.metadata ? JSON.stringify(r.metadata, null, 2) : '—'}</pre></div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="admin-pager">
        <span>{total.toLocaleString()} event(s) · page {page} of {totalPages}</span>
        <div style={{ flex: 1 }} />
        <button className="admin-btn sm" disabled={page <= 1} onClick={function() { setPage(1); }}>« First</button>
        <button className="admin-btn sm" disabled={page <= 1} onClick={function() { setPage(page - 1); }}>‹ Prev</button>
        <button className="admin-btn sm" disabled={page >= totalPages} onClick={function() { setPage(page + 1); }}>Next ›</button>
        <button className="admin-btn sm" disabled={page >= totalPages} onClick={function() { setPage(totalPages); }}>Last »</button>
      </div>
    </div>
  );
}

// Integration chip state:
//   configured + credentialPresent  → green   "Live"
//   configured + !credentialPresent → red     "Broken"  (wired but server can't call)
//   !configured                     → neutral "Not wired"
//   partial                         → yellow  "Partial"  (some config fields set, some missing)
function integrationStatus(it) {
  if (!it) return { cls: 'neutral', label: 'Unknown' };
  if (it.partial) return { cls: 'partial', label: 'Partial' };
  if (!it.configured) return { cls: 'neutral', label: 'Not wired' };
  if (it.credentialPresent === false) return { cls: 'broken', label: 'Broken' };
  return { cls: 'live', label: 'Live' };
}

var INTEGRATION_LABELS = {
  ninjaone_commercial: 'NinjaOne',
  ninjaone_fedramp:    'NinjaOne FedRAMP',
  huntress:            'Huntress',
  entra:               'Microsoft Entra',
  azure:               'Azure Resources',
  blumira:             'Blumira',
  asana:               'Asana',
  jira:                'Jira',
  unifi:               'UniFi',
  meraki:              'Meraki'
};

// ── Asana Discovery Modal ───────────────────────────────────────────
// Solves the "engineer creates new project, nobody updates client-config.js"
// problem. Lists every project visible to CBG's Asana token, joined with
// the per-client GID map, so unmapped projects are immediately visible
// alongside hints for which client to map them to.
function AsanaDiscoveryModal({ clients, onClose, onAssigned, onError }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [search, setSearch] = useState('');
  var [unmappedOnly, setUnmappedOnly] = useState(true);
  var [copiedGid, setCopiedGid] = useState(null);
  var [busyGid, setBusyGid] = useState(null);   // gid currently saving — disables that row's controls
  var clientList = clients || [];
  var clientNameById = {};
  clientList.forEach(function(c) { clientNameById[c.id] = c.name; });

  useEffect(function() {
    setLoading(true);
    authFetch(API + '/admin/asana/discover')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'Discovery failed');
      })
      .catch(function() { setLoading(false); setError('Discovery failed'); });
  }, []);

  function copyGid(gid) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(gid).then(function() {
        setCopiedGid(gid);
        setTimeout(function() { setCopiedGid(null); }, 1500);
      });
    }
  }

  // Optimistic assign — flip the local mappedTo[] immediately, then call the
  // server. On failure we revert and surface the error toast. This keeps
  // the UI snappy and makes batch-mapping a Asana workspace feel instant.
  function applyMappingChange(gid, clientId, action) {
    if (!gid || !clientId || (action !== 'add' && action !== 'remove')) return;
    setBusyGid(gid);
    setData(function(prev) {
      if (!prev) return prev;
      var clone = JSON.parse(JSON.stringify(prev));
      clone.workspaces.forEach(function(ws) {
        ws.projects.forEach(function(p) {
          if (p.gid !== gid) return;
          if (action === 'add' && p.mappedTo.indexOf(clientId) === -1) p.mappedTo.push(clientId);
          if (action === 'remove') p.mappedTo = p.mappedTo.filter(function(id) { return id !== clientId; });
        });
      });
      // Recompute summary counters so the header chips update
      var mapped = 0, unmapped = 0, total = 0;
      clone.workspaces.forEach(function(ws) {
        ws.projects.forEach(function(p) {
          total++;
          if (p.mappedTo.length > 0) mapped++; else unmapped++;
        });
      });
      clone.summary = Object.assign({}, clone.summary, { total: total, mapped: mapped, unmapped: unmapped });
      return clone;
    });
    authFetch(API + '/admin/asana/assign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gid: gid, clientId: clientId, action: action })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusyGid(null);
        if (x.ok) {
          if (onAssigned) {
            var name = clientNameById[clientId] || clientId;
            onAssigned(action === 'add' ? 'Assigned to ' + name : 'Removed from ' + name);
          }
        } else {
          // Revert on server-side error — flip the change back
          setData(function(prev) {
            if (!prev) return prev;
            var clone = JSON.parse(JSON.stringify(prev));
            clone.workspaces.forEach(function(ws) {
              ws.projects.forEach(function(p) {
                if (p.gid !== gid) return;
                if (action === 'add') p.mappedTo = p.mappedTo.filter(function(id) { return id !== clientId; });
                if (action === 'remove' && p.mappedTo.indexOf(clientId) === -1) p.mappedTo.push(clientId);
              });
            });
            return clone;
          });
          if (onError) onError(x.d.error || 'Assign failed');
        }
      })
      .catch(function() {
        setBusyGid(null);
        if (onError) onError('Network error — change was reverted');
      });
  }

  // Flatten + filter projects across workspaces
  var allProjects = data ? data.workspaces.flatMap(function(ws) {
    return ws.projects.map(function(p) { return Object.assign({}, p, { workspaceName: ws.name, workspaceGid: ws.gid }); });
  }) : [];
  var filtered = allProjects.filter(function(p) {
    if (unmappedOnly && p.mappedTo.length > 0) return false;
    if (search) {
      var q = search.toLowerCase();
      if (!(p.name || '').toLowerCase().includes(q)
        && !(p.gid || '').includes(search)
        && !(p.team || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 760 }}>
        <div className="admin-modal-header">Asana Project Discovery</div>
        <div className="admin-modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          <div className="admin-field-hint" style={{ marginBottom: 14 }}>
            Every project visible to CBG's Asana token, joined with the per-client mapping in the database. When an engineer creates a new project, it shows up here as <span className="admin-chip warn" style={{ fontSize: 10, padding: '1px 6px' }}>unmapped</span> — pick the client from the dropdown to assign it. The change persists immediately and the client's Projects tab reflects it on next load.
          </div>

          {loading && <div style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Calling Asana…</div>}
          {error && <div style={{ padding: 12, color: 'var(--accent-red)' }}>{error}</div>}

          {data && (
            <>
              <div className="admin-advanced" style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12 }}>
                  <span><strong style={{ fontSize: 18 }}>{data.summary.total}</strong> projects total</span>
                  <span><strong style={{ fontSize: 18, color: 'var(--accent-green)' }}>{data.summary.mapped}</strong> mapped</span>
                  <span><strong style={{ fontSize: 18, color: 'var(--accent-amber)' }}>{data.summary.unmapped}</strong> unmapped</span>
                  {data.summary.orphans > 0 && <span><strong style={{ fontSize: 18, color: 'var(--accent-red)' }}>{data.summary.orphans}</strong> orphan GIDs</span>}
                </div>
              </div>

              {data.orphans && data.orphans.length > 0 && (
                <div className="admin-advanced" style={{ marginBottom: 14, borderColor: 'rgba(240,101,101,0.3)' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--accent-red)' }}>Orphan GIDs in client-config.js</div>
                  <div className="admin-field-hint" style={{ marginBottom: 8 }}>
                    These GIDs are listed in client-config but Asana didn't return them — project deleted, archived, or out of token scope. Worth removing.
                  </div>
                  {data.orphans.map(function(o) {
                    return (
                      <div key={o.gid} style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', marginBottom: 4 }}>
                        <code>{o.gid}</code> mapped to <strong>{o.mappedTo.join(', ')}</strong>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="admin-toolbar" style={{ marginBottom: 12 }}>
                <input className="admin-filter-input" style={{ flex: 1 }} placeholder="Search by name, GID, or team…" value={search} onChange={function(e) { setSearch(e.target.value); }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={unmappedOnly} onChange={function(e) { setUnmappedOnly(e.target.checked); }} />
                  Unmapped only
                </label>
              </div>

              {filtered.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', opacity: 0.55 }}>
                  {unmappedOnly ? 'All projects are mapped — nothing to do here.' : 'No projects match.'}
                </div>
              )}

              {filtered.map(function(p) {
                var busy = busyGid === p.gid;
                return (
                  <div key={p.gid} style={{ padding: '12px 14px', marginBottom: 8, background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 8, position: 'relative', borderLeft: '3px solid ' + (p.mappedTo.length === 0 ? 'var(--accent-amber)' : 'var(--accent-green)'), opacity: busy ? 0.6 : 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name}</div>
                        <div style={{ fontSize: 11, opacity: 0.65, marginTop: 3 }}>
                          {p.workspaceName}
                          {p.team && ' · ' + p.team}
                          {p.owner && ' · owner ' + p.owner}
                        </div>
                        <div style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', marginTop: 4, opacity: 0.55 }}>
                          GID <code>{p.gid}</code>
                          <button
                            type="button"
                            onClick={function() { copyGid(p.gid); }}
                            style={{ marginLeft: 8, fontSize: 10, padding: '1px 8px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 4, color: 'var(--text-muted)', cursor: 'pointer' }}
                            title="Copy GID to clipboard"
                          >{copiedGid === p.gid ? 'copied ✓' : 'copy'}</button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 200 }}>
                        {p.mappedTo.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
                            {p.mappedTo.map(function(cid) {
                              var name = clientNameById[cid] || cid;
                              return (
                                <span key={cid} className="admin-chip ok" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title={'Mapped to ' + name}>
                                  {name}
                                  <button
                                    type="button"
                                    onClick={function() { applyMappingChange(p.gid, cid, 'remove'); }}
                                    disabled={busy}
                                    style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, marginLeft: 2, fontSize: 14, lineHeight: 1, opacity: 0.7 }}
                                    title={'Remove from ' + name}
                                    aria-label={'Remove from ' + name}
                                  >×</button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {p.mappedTo.length === 0 && <span className="admin-chip warn">unmapped</span>}
                        <select
                          value=""
                          disabled={busy || clientList.length === 0}
                          onChange={function(e) {
                            var val = e.target.value;
                            if (!val) return;
                            applyMappingChange(p.gid, val, 'add');
                            e.target.value = '';
                          }}
                          style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: busy ? 'wait' : 'pointer', minWidth: 180 }}
                          title="Assign this project to a client"
                        >
                          <option value="">{busy ? 'Saving…' : (p.mappedTo.length === 0 ? '+ Assign to client…' : '+ Also assign to…')}</option>
                          {clientList
                            .filter(function(c) { return p.mappedTo.indexOf(c.id) === -1; })
                            .map(function(c) {
                              return <option key={c.id} value={c.id}>{c.name}</option>;
                            })}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// Generic vendor discovery modal — handles NinjaOne (commercial+fedramp),
// Huntress, and UniFi. The vendor config object describes:
//   • title — modal heading
//   • discoverPath — GET endpoint
//   • assignPath — POST endpoint
//   • mode — 'single' (one value per client) or 'array' (many per client)
//   • payloadShape(action, resourceId, clientId, label?) — builds POST body
//   • sections (single-mode only) — for vendors like NinjaOne with two
//     instances (commercial vs fedramp), each with its own resource list
//   • renderResource(r) — optional extra info per row (e.g. UniFi device count)
//   • idLabel — what to call the ID in the UI (e.g. "Org ID", "Host ID")
function VendorDiscoveryModal({ vendor, clients, onClose, onAssigned, onError }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [search, setSearch] = useState('');
  var [unmappedOnly, setUnmappedOnly] = useState(true);
  var [busyId, setBusyId] = useState(null);
  var [labelDraft, setLabelDraft] = useState({});  // for UniFi: per-host label being edited
  var clientList = clients || [];
  var clientNameById = {};
  clientList.forEach(function(c) { clientNameById[c.id] = c.name; });

  useEffect(function() {
    setLoading(true);
    authFetch(API + vendor.discoverPath)
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'Discovery failed');
      })
      .catch(function() { setLoading(false); setError('Discovery failed'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor.discoverPath]);

  // For NinjaOne we render TWO sections (commercial + fedramp). For Huntress/UniFi
  // we render ONE section. Normalise to `sections[]` so the render path is uniform.
  function getSections() {
    if (!data) return [];
    if (vendor.sections) {
      // Multi-section vendor: data has shape { commercial: {...}, fedramp: {...} }
      return vendor.sections.map(function(s) { return Object.assign({ key: s.key, label: s.label, instance: s.instance }, data[s.key] || { resources: [], orphans: [], summary: { total: 0, mapped: 0, unmapped: 0, orphans: 0 } }); });
    }
    return [{ key: 'default', label: null, instance: vendor.instance, resources: data.resources || [], orphans: data.orphans || [], summary: data.summary || {} }];
  }

  function applyChange(section, resourceId, clientId, action, label) {
    var bid = section.key + ':' + resourceId;
    setBusyId(bid);
    // Optimistic update — flip mappedTo locally before the API call
    setData(function(prev) {
      if (!prev) return prev;
      var clone = JSON.parse(JSON.stringify(prev));
      var bucket = vendor.sections ? clone[section.key] : clone;
      if (!bucket) return clone;
      bucket.resources.forEach(function(r) {
        if (String(r.id) !== String(resourceId)) return;
        if (vendor.mode === 'array') {
          if (action === 'add' && r.mappedTo.indexOf(clientId) === -1) r.mappedTo.push(clientId);
          if (action === 'remove') r.mappedTo = r.mappedTo.filter(function(id) { return id !== clientId; });
        } else {
          // single-value: assigning to a client BOOTS any other client mapped to this resource (one-to-one)
          if (action === 'set') {
            // Remove this client from all other resources
            bucket.resources.forEach(function(r2) { r2.mappedTo = r2.mappedTo.filter(function(id) { return id !== clientId; }); });
            r.mappedTo = [clientId];
          } else if (action === 'clear') {
            r.mappedTo = r.mappedTo.filter(function(id) { return id !== clientId; });
          }
        }
      });
      // Recompute summary
      var mapped = bucket.resources.filter(function(r) { return r.mappedTo.length > 0; }).length;
      bucket.summary = Object.assign({}, bucket.summary, { mapped: mapped, unmapped: bucket.resources.length - mapped });
      return clone;
    });
    var body = vendor.payloadShape(action, resourceId, clientId, label, section.instance);
    authFetch(API + vendor.assignPath, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusyId(null);
        if (x.ok) {
          if (onAssigned) {
            var name = clientNameById[clientId] || clientId;
            onAssigned(action === 'add' || action === 'set' ? 'Assigned to ' + name : 'Removed from ' + name);
          }
        } else {
          // Best-effort revert: re-fetch from server (simpler than reverse-mutating)
          setError(x.d.error || 'Assign failed');
          if (onError) onError(x.d.error || 'Assign failed');
        }
      })
      .catch(function() { setBusyId(null); if (onError) onError('Network error'); });
  }

  function renderResourceRow(section, r) {
    var bid = section.key + ':' + r.id;
    var busy = busyId === bid;
    var stripeColor = r.mappedTo.length === 0 ? 'var(--accent-amber)' : 'var(--accent-green)';
    return (
      <div key={bid} style={{ padding: '12px 14px', marginBottom: 8, background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 8, borderLeft: '3px solid ' + stripeColor, opacity: busy ? 0.6 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{r.name}</div>
            {vendor.renderResource && (
              <div style={{ fontSize: 11, opacity: 0.65, marginTop: 3 }}>{vendor.renderResource(r)}</div>
            )}
            <div style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', marginTop: 4, opacity: 0.55 }}>
              {vendor.idLabel} <code>{r.id}</code>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 220 }}>
            {r.mappedTo.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
                {r.mappedTo.map(function(cid) {
                  var name = clientNameById[cid] || cid;
                  return (
                    <span key={cid} className="admin-chip ok" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title={'Mapped to ' + name}>
                      {name}
                      <button
                        type="button"
                        onClick={function() { applyChange(section, r.id, cid, vendor.mode === 'array' ? 'remove' : 'clear'); }}
                        disabled={busy}
                        style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, marginLeft: 2, fontSize: 14, lineHeight: 1, opacity: 0.7 }}
                        title={'Remove from ' + name} aria-label={'Remove from ' + name}
                      >×</button>
                    </span>
                  );
                })}
              </div>
            )}
            {r.mappedTo.length === 0 && <span className="admin-chip warn">unmapped</span>}
            {vendor.mode === 'array' && (
              <input
                type="text" placeholder="Site label (optional)"
                value={labelDraft[bid] || ''}
                onChange={function(e) { setLabelDraft(Object.assign({}, labelDraft, { [bid]: e.target.value })); }}
                style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-primary)', minWidth: 220 }}
              />
            )}
            <select
              value=""
              disabled={busy || clientList.length === 0}
              onChange={function(e) {
                var val = e.target.value;
                if (!val) return;
                var assignAction = vendor.mode === 'array' ? 'add' : 'set';
                applyChange(section, r.id, val, assignAction, labelDraft[bid]);
                e.target.value = '';
                setLabelDraft(Object.assign({}, labelDraft, { [bid]: '' }));
              }}
              style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: busy ? 'wait' : 'pointer', minWidth: 220 }}
              title="Assign this resource to a client"
            >
              <option value="">{busy ? 'Saving…' : (vendor.mode === 'single' && r.mappedTo.length > 0 ? '↻ Reassign to…' : '+ Assign to client…')}</option>
              {clientList
                .filter(function(c) { return vendor.mode === 'array' ? r.mappedTo.indexOf(c.id) === -1 : true; })
                .map(function(c) { return <option key={c.id} value={c.id}>{c.name}</option>; })}
            </select>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-modal-overlay" onClick={onClose}>
        <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }}>
          <div className="admin-modal-header">{vendor.title}</div>
          <div className="admin-modal-body" style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>Calling {vendor.title}…</div>
        </div>
      </div>
    );
  }

  var sections = getSections();
  // Apply search + unmapped filter to each section's resources
  var filteredSections = sections.map(function(s) {
    var filtered = s.resources.filter(function(r) {
      if (unmappedOnly && r.mappedTo.length > 0) return false;
      if (search) {
        var q = search.toLowerCase();
        if (!(r.name || '').toLowerCase().includes(q) && !String(r.id).includes(search)) return false;
      }
      return true;
    });
    return Object.assign({}, s, { filtered: filtered });
  });

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 760 }}>
        <div className="admin-modal-header">{vendor.title}</div>
        <div className="admin-modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          <div className="admin-field-hint" style={{ marginBottom: 14 }}>
            {vendor.helpText}
          </div>

          {error && <div style={{ padding: 12, color: 'var(--accent-red)' }}>{error}</div>}

          <div className="admin-toolbar" style={{ marginBottom: 12 }}>
            <input className="admin-filter-input" style={{ flex: 1 }} placeholder="Search by name or ID…" value={search} onChange={function(e) { setSearch(e.target.value); }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={unmappedOnly} onChange={function(e) { setUnmappedOnly(e.target.checked); }} />
              Unmapped only
            </label>
          </div>

          {filteredSections.map(function(s) {
            return (
              <div key={s.key} style={{ marginBottom: 18 }}>
                {s.label && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid var(--border-default)' }}>
                    <strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>{s.label}</strong>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>
                      {s.summary.total} total · <span style={{ color: 'var(--accent-green)' }}>{s.summary.mapped}</span> mapped · <span style={{ color: 'var(--accent-amber)' }}>{s.summary.unmapped}</span> unmapped
                      {s.summary.orphans > 0 && <> · <span style={{ color: 'var(--accent-red)' }}>{s.summary.orphans}</span> orphan</>}
                    </span>
                  </div>
                )}
                {s.orphans && s.orphans.length > 0 && (
                  <div className="admin-advanced" style={{ marginBottom: 12, borderColor: 'rgba(240,101,101,0.3)' }}>
                    <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--accent-red)', fontSize: 12 }}>Orphan IDs in client config</div>
                    <div className="admin-field-hint" style={{ marginBottom: 8 }}>
                      These IDs are mapped to a client but missing from the vendor's response. Probably deleted, archived, or out of token scope.
                    </div>
                    {s.orphans.map(function(o) {
                      return (
                        <div key={o.id} style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', marginBottom: 4 }}>
                          <code>{o.id}</code> mapped to <strong>{o.mappedTo.join(', ')}</strong>
                        </div>
                      );
                    })}
                  </div>
                )}
                {s.filtered.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', opacity: 0.55, fontSize: 12 }}>
                    {unmappedOnly ? 'All resources mapped — nothing to assign here.' : 'No resources match.'}
                  </div>
                )}
                {s.filtered.map(function(r) { return renderResourceRow(s, r); })}
              </div>
            );
          })}
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// Editor modal for a single client's integration mappings. Loads the raw
// DB record + recent change history, lets the operator update structured
// fields (string/number/array/object), then PUTs the diff back.
//
// Field rendering is intentionally hand-written per integration rather than
// a generic schema-driven form — each integration has different shape
// expectations (UniFi has paired host_id ↔ site_label entries; Asana is a
// flat GID list; Atlassian billing is an array of objects). Hand-written
// gives clearer affordances and validation hints.
function EditClientIntegrationsModal({ clientId, dbBacked, onClose, onSaved, onError }) {
  var [raw, setRaw] = useState(null);              // server's current state
  var [form, setForm] = useState(null);            // working copy
  var [history, setHistory] = useState([]);
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState(null);
  var [changeNote, setChangeNote] = useState('');
  var [showHistory, setShowHistory] = useState(false);
  var [advancedJson, setAdvancedJson] = useState({});  // per-field JSON editor unlock for atlassian_billing etc.

  useEffect(function() {
    setLoading(true);
    authFetch(API + '/admin/client-integrations/' + clientId)
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) {
          setRaw(x.d.data || {});
          setForm(JSON.parse(JSON.stringify(x.d.data || {})));
          setHistory(x.d.history || []);
        } else {
          setError(x.d.error || 'Failed to load');
        }
      })
      .catch(function() { setLoading(false); setError('Failed to load'); });
  }, [clientId]);

  function setField(name, value) {
    setForm(function(prev) { var next = Object.assign({}, prev); next[name] = value; return next; });
  }

  // Helper for array fields edited as newline-delimited text
  function arrToLines(arr) { return Array.isArray(arr) ? arr.join('\n') : ''; }
  function linesToArr(s) {
    return (s || '').split(/\r?\n/).map(function(x) { return x.trim(); }).filter(function(x) { return x.length > 0; });
  }

  // unifi_site_labels is an object of { hostId: label }. Edit it as paired rows.
  function getUnifiPairs() {
    var ids = (form && Array.isArray(form.unifi_host_ids)) ? form.unifi_host_ids : [];
    var labels = (form && form.unifi_site_labels) || {};
    return ids.map(function(id) { return { id: id, label: labels[id] || '' }; });
  }
  function updateUnifiPair(idx, key, value) {
    var pairs = getUnifiPairs();
    pairs[idx] = Object.assign({}, pairs[idx], (key === 'id' ? { id: value } : { label: value }));
    var newIds = pairs.map(function(p) { return p.id; });
    var newLabels = {};
    pairs.forEach(function(p) { if (p.id && p.label) newLabels[p.id] = p.label; });
    setForm(function(prev) { return Object.assign({}, prev, { unifi_host_ids: newIds, unifi_site_labels: newLabels }); });
  }
  function addUnifiPair() {
    setForm(function(prev) {
      var ids = Array.isArray(prev.unifi_host_ids) ? prev.unifi_host_ids.slice() : [];
      ids.push('');
      return Object.assign({}, prev, { unifi_host_ids: ids });
    });
  }
  function removeUnifiPair(idx) {
    var pairs = getUnifiPairs();
    pairs.splice(idx, 1);
    var newIds = pairs.map(function(p) { return p.id; });
    var newLabels = {};
    pairs.forEach(function(p) { if (p.id && p.label) newLabels[p.id] = p.label; });
    setForm(function(prev) { return Object.assign({}, prev, { unifi_host_ids: newIds, unifi_site_labels: newLabels }); });
  }

  // atlassian_billing — JSON-edit because the shape is rich and the only
  // current consumer (the Atlassian billing chart) just iterates the array.
  function getAtlassianJson() {
    if (advancedJson.atlassian_billing != null) return advancedJson.atlassian_billing;
    return form && Array.isArray(form.atlassian_billing) ? JSON.stringify(form.atlassian_billing, null, 2) : '[]';
  }
  function setAtlassianJson(s) {
    setAdvancedJson(function(prev) { return Object.assign({}, prev, { atlassian_billing: s }); });
    try {
      var parsed = JSON.parse(s);
      if (Array.isArray(parsed)) setField('atlassian_billing', parsed);
    } catch (e) { /* user is mid-typing — don't blow up */ }
  }

  // Validate before save — catches obvious typos that the server-side
  // sanitizer would otherwise silently coerce.
  function validate() {
    if (!form.name || !String(form.name).trim()) return 'Name is required.';
    if (form.ninjaone_org_id != null && form.ninjaone_org_id !== '' && !/^\d+$/.test(String(form.ninjaone_org_id))) {
      return 'NinjaOne org ID must be numeric.';
    }
    if (form.huntress_org_id != null && form.huntress_org_id !== '' && !/^\d+$/.test(String(form.huntress_org_id))) {
      return 'Huntress org ID must be numeric.';
    }
    if (advancedJson.atlassian_billing != null) {
      try { var v = JSON.parse(advancedJson.atlassian_billing); if (!Array.isArray(v)) return 'Atlassian billing must be a JSON array.'; }
      catch (e) { return 'Atlassian billing has invalid JSON: ' + e.message; }
    }
    return null;
  }

  function handleSave() {
    var err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setSaving(true);
    var payload = Object.assign({}, form, { changeNote: changeNote || null });
    authFetch(API + '/admin/client-integrations/' + clientId, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setSaving(false);
        if (x.ok) onSaved('Saved ' + (x.d.data && x.d.data.name ? x.d.data.name : clientId));
        else { setError(x.d.error || 'Save failed'); onError(x.d.error || 'Save failed'); }
      })
      .catch(function(e) { setSaving(false); setError('Save failed: ' + e.message); onError('Save failed'); });
  }

  if (loading) {
    return (
      <div className="admin-modal-overlay" onClick={onClose}>
        <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }}>
          <div className="admin-modal-header">Loading…</div>
          <div className="admin-modal-body" style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>Fetching {clientId}…</div>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="admin-modal-overlay" onClick={onClose}>
        <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }}>
          <div className="admin-modal-header">Failed to load</div>
          <div className="admin-modal-body" style={{ padding: 40, color: 'var(--accent-red)' }}>{error || 'Unknown error'}</div>
          <div className="admin-modal-footer"><button className="admin-btn" onClick={onClose}>Close</button></div>
        </div>
      </div>
    );
  }

  var unifiPairs = getUnifiPairs();

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 760 }}>
        <div className="admin-modal-header">
          Edit integration mappings — {raw && raw.name ? raw.name : clientId}
          <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.6, marginLeft: 8 }}>{clientId}</span>
        </div>
        <div className="admin-modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          {!dbBacked && (
            <div className="admin-banner warn" style={{ marginBottom: 14 }}>
              DB unreachable — saving will fail. Reload from DB or fix the connection first.
            </div>
          )}

          <div className="admin-form-section">
            <div className="admin-form-section-title">Identity</div>
            <div className="admin-form-grid two">
              <div className="admin-field"><label>Display name</label>
                <input type="text" value={form.name || ''} onChange={function(e) { setField('name', e.target.value); }} />
              </div>
              <div className="admin-field"><label>Type</label>
                <select value={form.type || ''} onChange={function(e) { setField('type', e.target.value); }}>
                  <option value="">—</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Regulated (DIB)">Regulated (DIB)</option>
                  <option value="Regulated (Healthcare)">Regulated (Healthcare)</option>
                  <option value="Regulated (Legal)">Regulated (Legal)</option>
                </select>
              </div>
              <div className="admin-field"><label>Industry</label>
                <input type="text" value={form.industry || ''} onChange={function(e) { setField('industry', e.target.value); }} />
              </div>
              <div className="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 18, marginTop: 22 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', textTransform: 'none', fontSize: 13, letterSpacing: 0, fontWeight: 400, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.cmmc} onChange={function(e) { setField('cmmc', e.target.checked); }} /> CMMC
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', textTransform: 'none', fontSize: 13, letterSpacing: 0, fontWeight: 400, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.isDemo} onChange={function(e) { setField('isDemo', e.target.checked); }} /> Demo
                </label>
              </div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">NinjaOne</div>
            <div className="admin-form-grid two">
              <div className="admin-field"><label>Commercial org ID (us2)</label>
                <input type="text" inputMode="numeric" value={form.ninjaone_org_id == null ? '' : form.ninjaone_org_id} onChange={function(e) { setField('ninjaone_org_id', e.target.value); }} placeholder="e.g. 7" />
              </div>
              <div className="admin-field"><label>FedRAMP org name (fed)</label>
                <input type="text" value={form.fedramp_org_name || ''} onChange={function(e) { setField('fedramp_org_name', e.target.value); }} />
                <div className="admin-field-hint">Org name on fed.ninjarmm.com (used for matching).</div>
              </div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Huntress</div>
            <div className="admin-field"><label>Huntress org ID</label>
              <input type="text" inputMode="numeric" value={form.huntress_org_id == null ? '' : form.huntress_org_id} onChange={function(e) { setField('huntress_org_id', e.target.value); }} placeholder="e.g. 506426" />
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Microsoft / Azure</div>
            <div className="admin-form-grid two">
              <div className="admin-field"><label>Entra tenant_id</label>
                <input type="text" value={form.tenant_id || ''} onChange={function(e) { setField('tenant_id', e.target.value); }} placeholder="GUID" />
              </div>
              <div className="admin-field"><label>Graph client_id (per-tenant app reg)</label>
                <input type="text" value={form.graph_client_id || ''} onChange={function(e) { setField('graph_client_id', e.target.value); }} placeholder="GUID" />
              </div>
              <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Azure subscription_id</label>
                <input type="text" value={form.azure_subscription_id || ''} onChange={function(e) { setField('azure_subscription_id', e.target.value); }} placeholder="GUID" />
              </div>
              <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Microsoft Sentinel workspace ID (CMMC tier)</label>
                <input type="text" value={form.sentinel_workspace_id || ''} onChange={function(e) { setField('sentinel_workspace_id', e.target.value); }} placeholder="Log Analytics workspace ID (GUID)" />
                <div className="admin-field-hint">Sentinel sits on the client's M365 GCC tenant. Wired through Atomus approval. Leave blank for commercial-tier clients (they use Blumira instead).</div>
              </div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Blumira (Commercial SIEM)</div>
            <div className="admin-field"><label>Account ID</label>
              <input type="text" value={form.blumira_account_id || ''} onChange={function(e) { setField('blumira_account_id', e.target.value); }} placeholder="GUID" />
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Asana</div>
            <div className="admin-field"><label>Project GIDs (one per line)</label>
              <textarea
                className="admin-textarea"
                rows={Math.max(3, (form.asana_project_gids || []).length + 1)}
                value={arrToLines(form.asana_project_gids)}
                onChange={function(e) { setField('asana_project_gids', linesToArr(e.target.value)); }}
                placeholder={'1209332459796027\n1210842111414768'}
              />
              <div className="admin-field-hint">
                {(form.asana_project_gids || []).length} GID{(form.asana_project_gids || []).length === 1 ? '' : 's'}.
                Use the "Discover Asana projects" button to find unmapped GIDs.
              </div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Jira</div>
            <div className="admin-form-grid two">
              <div className="admin-field"><label>Atlassian domain</label>
                <input type="text" value={form.jira_domain || ''} onChange={function(e) { setField('jira_domain', e.target.value); }} placeholder="acme.atlassian.net" />
              </div>
              <div className="admin-field"><label>Jira email (auth)</label>
                <input type="text" value={form.jira_email || ''} onChange={function(e) { setField('jira_email', e.target.value); }} placeholder="api@acme.com" />
              </div>
              <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Project keys (one per line)</label>
                <textarea
                  className="admin-textarea"
                  rows={3}
                  value={arrToLines(form.jira_project_keys)}
                  onChange={function(e) { setField('jira_project_keys', linesToArr(e.target.value)); }}
                  placeholder={'DOP\nHBT\nHED'}
                />
              </div>
              <div className="admin-field" style={{ gridColumn: '1 / -1' }}>
                <label>Atlassian billing (JSON array of {'{ name, plan, users, monthlyCost }'})</label>
                <textarea
                  className="admin-textarea mono"
                  rows={6}
                  value={getAtlassianJson()}
                  onChange={function(e) { setAtlassianJson(e.target.value); }}
                  placeholder='[{"name":"Jira Software","plan":"Standard","users":14,"monthlyCost":133.41}]'
                />
              </div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Cisco Meraki</div>
            <div className="admin-field"><label>Organization ID</label>
              <input type="text" value={form.meraki_org_id || ''} onChange={function(e) { setField('meraki_org_id', e.target.value); }} placeholder="e.g. 549236" />
              <div className="admin-field-hint">Numeric ID from the Meraki dashboard URL (or use the Meraki Discovery button on the Integrations tab to pick it visually).</div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">UniFi</div>
            <div className="admin-field-hint" style={{ marginBottom: 6 }}>
              Each row = one UniFi host_id paired with the site label that should appear in the UI.
            </div>
            {unifiPairs.length === 0 && <div style={{ fontSize: 11, opacity: 0.55, padding: '6px 0' }}>No UniFi hosts wired.</div>}
            {unifiPairs.map(function(p, idx) {
              return (
                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input type="text" style={{ flex: 2, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }} value={p.id} onChange={function(e) { updateUnifiPair(idx, 'id', e.target.value); }} placeholder="host_id" />
                  <input type="text" style={{ flex: 1 }} value={p.label} onChange={function(e) { updateUnifiPair(idx, 'label', e.target.value); }} placeholder="Site label" />
                  <button className="admin-btn sm" type="button" onClick={function() { removeUnifiPair(idx); }} title="Remove this row">✕</button>
                </div>
              );
            })}
            <button className="admin-btn sm" type="button" onClick={addUnifiPair}>+ Add UniFi host</button>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Pax8</div>
            <div className="admin-field"><label>Company ID</label>
              <input type="text" value={form.pax8_company_id || ''} onChange={function(e) { setField('pax8_company_id', e.target.value); }} />
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Cyber Insurance</div>
            <div className="admin-form-grid two">
              <div className="admin-field"><label>Carrier</label>
                <input type="text" value={form.cyber_insurance_carrier || ''} onChange={function(e) { setField('cyber_insurance_carrier', e.target.value); }} placeholder="e.g. Coalition, At-Bay, Beazley" />
              </div>
              <div className="admin-field"><label>Policy number</label>
                <input type="text" value={form.cyber_insurance_policy_number || ''} onChange={function(e) { setField('cyber_insurance_policy_number', e.target.value); }} />
              </div>
              <div className="admin-field"><label>Annual premium (USD)</label>
                <input type="text" inputMode="numeric" value={form.cyber_insurance_premium_annual == null ? '' : form.cyber_insurance_premium_annual} onChange={function(e) { setField('cyber_insurance_premium_annual', e.target.value); }} placeholder="12500" />
              </div>
              <div className="admin-field"><label>Renewal date</label>
                <input type="date" value={form.cyber_insurance_renewal_date || ''} onChange={function(e) { setField('cyber_insurance_renewal_date', e.target.value); }} />
              </div>
              <div className="admin-field"><label>Deductible (USD)</label>
                <input type="text" inputMode="numeric" value={form.cyber_insurance_deductible == null ? '' : form.cyber_insurance_deductible} onChange={function(e) { setField('cyber_insurance_deductible', e.target.value); }} placeholder="2500" />
              </div>
              <div className="admin-field"><label>Coverage limit (USD)</label>
                <input type="text" inputMode="numeric" value={form.cyber_insurance_coverage_limit == null ? '' : form.cyber_insurance_coverage_limit} onChange={function(e) { setField('cyber_insurance_coverage_limit', e.target.value); }} placeholder="1000000" />
              </div>
              <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Notes</label>
                <input type="text" value={form.cyber_insurance_notes || ''} onChange={function(e) { setField('cyber_insurance_notes', e.target.value); }} placeholder="Coverage exclusions, broker contact, etc." />
              </div>
            </div>
          </div>

          {/* CMMC campaign metadata — only relevant when client is flagged CMMC */}
          {form.cmmc && (
            <div className="admin-form-section">
              <div className="admin-form-section-title">CMMC Campaign (Atomus partnership)</div>
              <div className="admin-form-grid two">
                <div className="admin-field"><label>Target level</label>
                  <select value={form.cmmc_target_level == null ? '' : String(form.cmmc_target_level)} onChange={function(e) { setField('cmmc_target_level', e.target.value); }}>
                    <option value="">—</option>
                    <option value="1">Level 1</option>
                    <option value="2">Level 2</option>
                  </select>
                </div>
                <div className="admin-field"><label>Campaign stage</label>
                  <select value={form.cmmc_campaign_stage || ''} onChange={function(e) { setField('cmmc_campaign_stage', e.target.value); }}>
                    <option value="">—</option>
                    <option value="gap_assessment">Gap Assessment</option>
                    <option value="remediation">Remediation</option>
                    <option value="pre_assessment">Pre-Assessment</option>
                    <option value="audit_scheduled">C3PAO Audit Scheduled</option>
                    <option value="certified">Certified</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                <div className="admin-field"><label>Target assessment date</label>
                  <input type="date" value={form.cmmc_target_assessment_date || ''} onChange={function(e) { setField('cmmc_target_assessment_date', e.target.value); }} />
                </div>
                <div className="admin-field"><label>C3PAO (assessor)</label>
                  <input type="text" value={form.cmmc_c3pao || ''} onChange={function(e) { setField('cmmc_c3pao', e.target.value); }} placeholder="e.g. Apex Cybersecurity" />
                </div>
                <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Atomus Aegis client dashboard URL</label>
                  <input type="text" value={form.cmmc_atomus_dashboard_url || ''} onChange={function(e) { setField('cmmc_atomus_dashboard_url', e.target.value); }} placeholder="https://app.atomuscyber.us/companies/..." />
                  <div className="admin-field-hint">Per-client deep-link into Atomus's dashboard. Surfaced on the CMMC tab.</div>
                </div>
                <div className="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 22 }}>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', textTransform: 'none', fontSize: 13, letterSpacing: 0, fontWeight: 400, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!form.cmmc_aegis_active} onChange={function(e) { setField('cmmc_aegis_active', e.target.checked); }} /> Aegis agent active
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="admin-form-section">
            <div className="admin-form-section-title">Change note (optional)</div>
            <input type="text" className="admin-form-changenote" value={changeNote} onChange={function(e) { setChangeNote(e.target.value); }} placeholder="e.g. Added new BST Asana project — engineer onboarding" />
          </div>

          <div style={{ marginTop: 18 }}>
            <button className="admin-btn sm" type="button" onClick={function() { setShowHistory(!showHistory); }}>
              {showHistory ? 'Hide' : 'Show'} change history ({history.length})
            </button>
            {showHistory && (
              <div style={{ marginTop: 10, maxHeight: 220, overflow: 'auto', border: '1px solid var(--border-default)', borderRadius: 6, padding: 8, background: 'var(--bg-card)' }}>
                {history.length === 0 && <div style={{ opacity: 0.55, padding: 8 }}>No history yet — first edit will appear here.</div>}
                {history.map(function(h) {
                  return (
                    <div key={h.id} style={{ borderBottom: '1px solid var(--border-default)', padding: '8px 4px', fontSize: 11 }}>
                      <div style={{ fontWeight: 600 }}>{fmtDateTime(h.changed_at)} — {h.changed_by_email || 'system'}</div>
                      {h.change_note && <div style={{ fontStyle: 'italic', opacity: 0.7 }}>"{h.change_note}"</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {error && <div style={{ padding: 12, marginTop: 12, color: 'var(--accent-red)', fontSize: 12 }}>{error}</div>}
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="admin-btn primary" onClick={handleSave} disabled={saving || !dbBacked}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminIntegrationsTab() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('');
  var [expandedClient, setExpandedClient] = useState(null);
  var [asanaDiscoverOpen, setAsanaDiscoverOpen] = useState(false);
  var [vendorOpen, setVendorOpen] = useState(null);          // {key} for the vendor modal currently open
  var [editingClient, setEditingClient] = useState(null);    // clientId being edited, or null
  var [reloading, setReloading] = useState(false);
  var [toast, setToast] = useState(null);

  // Vendor configs for the parameterized VendorDiscoveryModal
  var VENDOR_CONFIGS = {
    ninjaone: {
      title: 'NinjaOne Discovery',
      discoverPath: '/admin/ninjaone/discover',
      assignPath: '/admin/ninjaone/assign',
      mode: 'single',
      idLabel: 'Org ID',
      sections: [
        { key: 'commercial', label: 'Commercial (us2.ninjarmm.com)', instance: 'commercial' },
        { key: 'fedramp', label: 'FedRAMP (fed.ninjarmm.com)', instance: 'fedramp' }
      ],
      payloadShape: function(action, id, clientId, label, instance) { return { clientId: clientId, instance: instance, value: id, action: action }; },
      helpText: 'Every NinjaOne organization visible across both instances. Pick a client from the dropdown to map an org. Each client has at most one commercial org and one FedRAMP org.'
    },
    huntress: {
      title: 'Huntress Discovery',
      discoverPath: '/admin/huntress/discover',
      assignPath: '/admin/huntress/assign',
      mode: 'single',
      idLabel: 'Org ID',
      payloadShape: function(action, id, clientId) { return { clientId: clientId, value: id, action: action }; },
      helpText: 'All Huntress organizations visible to CBG. Each client maps to exactly one Huntress org.'
    },
    unifi: {
      title: 'UniFi Discovery',
      discoverPath: '/admin/unifi/discover',
      assignPath: '/admin/unifi/assign',
      mode: 'array',
      idLabel: 'Host ID',
      renderResource: function(r) { return (r.deviceCount || 0) + ' device' + (r.deviceCount === 1 ? '' : 's') + (r.ip ? ' · ' + r.ip : ''); },
      payloadShape: function(action, id, clientId, label) { return { clientId: clientId, hostId: id, action: action, label: label || null }; },
      helpText: 'Every UniFi controller / host CBG can see. Clients can have multiple hosts (multi-site). Add a site label so it shows up nicely on the network tab.'
    },
    meraki: {
      title: 'Meraki Discovery',
      discoverPath: '/admin/meraki/discover',
      assignPath: '/admin/meraki/assign',
      mode: 'single',
      idLabel: 'Org ID',
      renderResource: function(r) { return r.url ? r.url.replace(/^https?:\/\//, '') : ''; },
      payloadShape: function(action, id, clientId) { return { clientId: clientId, value: id, action: action }; },
      helpText: 'Every Cisco Meraki organization visible to CBG. Each client maps to one Meraki org — switching organizations later is a single click.'
    }
  };

  function showToast(msg, type) { setToast({ msg: msg, type: type || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  function fetchData() {
    setLoading(true);
    authFetch(API + '/admin/integrations')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  useEffect(fetchData, []);

  function handleReload() {
    setReloading(true);
    authFetch(API + '/admin/client-integrations/reload', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setReloading(false);
        if (x.ok) { showToast('Reloaded ' + x.d.count + ' configs from ' + x.d.source.toUpperCase(), 'ok'); fetchData(); }
        else { showToast('Reload failed', 'error'); }
      })
      .catch(function() { setReloading(false); showToast('Reload failed', 'error'); });
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>Loading integration wiring...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--accent-red, #ef4444)' }}>Failed to load integrations.</div>;

  var filtered = data.clients.filter(function(c) {
    if (!filter) return true;
    var q = filter.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.clientId.toLowerCase().includes(q) || (c.industry || '').toLowerCase().includes(q);
  });

  var src = data.configSource || { source: 'file', count: data.clients.length };
  var dbBacked = src.source === 'db';

  return (
    <div className="admin-integrations-tab">
      <div className="admin-toolbar">
        <input className="admin-filter-input" placeholder="Filter clients..." value={filter} onChange={function(e) { setFilter(e.target.value); }} />
        <div style={{ flex: 1 }} />
        <button className="admin-btn sm" onClick={handleReload} disabled={reloading} title="Re-read client mappings from the database">
          {reloading ? 'Reloading…' : 'Reload from DB'}
        </button>
        <div className="integration-legend">
          <span className="int-legend-chip live">Live</span>
          <span className="int-legend-chip broken">Broken</span>
          <span className="int-legend-chip partial">Partial</span>
          <span className="int-legend-chip neutral">Not wired</span>
        </div>
      </div>

      <div className="integration-readonly-hint" style={{ borderColor: dbBacked ? 'rgba(78,201,176,0.35)' : 'rgba(245,166,35,0.35)' }}>
        {dbBacked
          ? <><strong style={{ color: 'var(--accent-green)' }}>DB-backed.</strong> Edits in this tab persist immediately to <code>aurelius.client_integrations</code> — no commit, no redeploy. The file (<code>backend/client-config.js</code>) is kept as a bootstrap seed and emergency fallback.</>
          : <><strong style={{ color: 'var(--accent-amber)' }}>File-backed (fallback mode).</strong> The DB-backed table is empty or unreachable. Edits in this tab will fail until the migration script runs (<code>node scripts/migrate-client-config-to-db.js</code>).</>
        }
      </div>

      <div className="integration-discover-bar">
        <span className="integration-discover-label">One-click vendor mapping:</span>
        <button className="integration-inline-action" onClick={function() { setAsanaDiscoverOpen(true); }}>Asana ›</button>
        <button className="integration-inline-action" onClick={function() { setVendorOpen('ninjaone'); }}>NinjaOne ›</button>
        <button className="integration-inline-action" onClick={function() { setVendorOpen('huntress'); }}>Huntress ›</button>
        <button className="integration-inline-action" onClick={function() { setVendorOpen('unifi'); }}>UniFi ›</button>
        <button className="integration-inline-action" onClick={function() { setVendorOpen('meraki'); }}>Meraki ›</button>
      </div>

      {asanaDiscoverOpen && (
        <AsanaDiscoveryModal
          clients={data.clients.map(function(c) { return { id: c.clientId, name: c.name }; })}
          onClose={function() { setAsanaDiscoverOpen(false); fetchData(); /* refresh wired counts after assignments */ }}
          onAssigned={function(msg) { showToast(msg, 'ok'); }}
          onError={function(msg) { showToast(msg, 'error'); }}
        />
      )}
      {vendorOpen && VENDOR_CONFIGS[vendorOpen] && (
        <VendorDiscoveryModal
          vendor={VENDOR_CONFIGS[vendorOpen]}
          clients={data.clients.map(function(c) { return { id: c.clientId, name: c.name }; })}
          onClose={function() { setVendorOpen(null); fetchData(); }}
          onAssigned={function(msg) { showToast(msg, 'ok'); }}
          onError={function(msg) { showToast(msg, 'error'); }}
        />
      )}
      {editingClient && (
        <EditClientIntegrationsModal
          clientId={editingClient}
          dbBacked={dbBacked}
          onClose={function() { setEditingClient(null); }}
          onSaved={function(msg) { showToast(msg || 'Saved', 'ok'); setEditingClient(null); fetchData(); }}
          onError={function(msg) { showToast(msg || 'Save failed', 'error'); }}
        />
      )}
      {toast && <div className={'admin-toast ' + (toast.type || 'info')}>{toast.msg}</div>}

      {/* Global integrations — not per-client */}
      <div className="integration-global-strip">
        <div className="integration-global-title">Global platforms</div>
        <div className="integration-global-chips">
          {Object.keys(data.globalIntegrations).map(function(k) {
            var g = data.globalIntegrations[k];
            var label = { pax8: 'Pax8', anthropic: 'Anthropic', keeper: 'Keeper' }[k] || k;
            return (
              <div key={k} className={'integration-global-chip ' + (g.credentialPresent ? 'live' : 'broken')}>
                <span className="int-global-dot" />
                <span>{label}</span>
                <span className="int-global-status">{g.credentialPresent ? 'credentials set' : 'credentials missing'}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="integration-cards-grid">
        {filtered.map(function(c) {
          var isOpen = expandedClient === c.clientId;
          return (
            <div key={c.clientId} className={'integration-card' + (isOpen ? ' expanded' : '')}>
              <div className="integration-card-header" onClick={function() { setExpandedClient(isOpen ? null : c.clientId); }}>
                <div className="integration-card-title-block">
                  <div className="integration-card-title">{c.name}</div>
                  <div className="integration-card-meta">
                    <span className="integration-card-key">{c.clientId}</span>
                    <span className="integration-card-sep">·</span>
                    <span>{c.type}</span>
                    <span className="integration-card-sep">·</span>
                    <span>{c.industry}</span>
                    {c.cmmc && <span className="admin-chip warn" style={{ marginLeft: 6 }}>CMMC</span>}
                    {c.isDemo && <span className="admin-chip info" style={{ marginLeft: 6 }}>Demo</span>}
                  </div>
                </div>
                <div className="integration-card-right">
                  <div className="integration-card-counts">
                    <span><strong>{c.summary.configured}</strong> wired</span>
                    {c.summary.broken > 0 && <span className="ic-broken"><strong>{c.summary.broken}</strong> broken</span>}
                    {c.summary.partial > 0 && <span className="ic-partial"><strong>{c.summary.partial}</strong> partial</span>}
                  </div>
                  <div className="integration-card-user">
                    {c.hasUser ? (
                      c.userEnabled
                        ? <span className="admin-chip ok" title={c.userEmail}>User active</span>
                        : <span className="admin-chip bad" title={c.userEmail}>User disabled</span>
                    ) : <span className="admin-chip warn">No DB user</span>}
                  </div>
                  <button
                    className="admin-btn sm"
                    onClick={function(e) { e.stopPropagation(); setEditingClient(c.clientId); }}
                    title={dbBacked ? 'Edit integration mappings (DB-backed)' : 'DB unreachable — fix the connection first'}
                    disabled={!dbBacked}
                  >Edit</button>
                  <span className="integration-expand-icon">{isOpen ? '▴' : '▾'}</span>
                </div>
              </div>

              <div className="integration-grid">
                {Object.keys(c.integrations).map(function(k) {
                  var it = c.integrations[k];
                  var status = integrationStatus(it);
                  return (
                    <div key={k} className={'int-chip ' + status.cls} title={status.label}>
                      <span className="int-chip-label">{INTEGRATION_LABELS[k] || k}</span>
                      <span className="int-chip-status">{status.label}</span>
                    </div>
                  );
                })}
              </div>

              {isOpen && (
                <div className="integration-detail">
                  {c.hasUser && (
                    <div className="integration-detail-section">
                      <div className="integration-detail-label">DB User</div>
                      <div className="integration-detail-value">
                        <code>{c.userEmail}</code>
                        {' · id=' + c.userId}
                        {' · ' + (c.userEnabled ? 'enabled' : 'disabled')}
                        {c.userLastLogin && ' · last login ' + fmtDateTime(c.userLastLogin)}
                      </div>
                    </div>
                  )}
                  {Object.keys(c.integrations).map(function(k) {
                    var it = c.integrations[k];
                    if (!it.configured && !it.partial) return null;
                    var status = integrationStatus(it);
                    return (
                      <div key={k} className="integration-detail-section">
                        <div className="integration-detail-label">
                          {INTEGRATION_LABELS[k] || k}
                          <span className={'int-inline-chip ' + status.cls}>{status.label}</span>
                        </div>
                        <div className="integration-detail-value mono">
                          {renderIntegrationDetail(k, it)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div style={{ padding: 40, textAlign: 'center', opacity: 0.5 }}>No clients match "{filter}"</div>}
      </div>
    </div>
  );
}

function renderIntegrationDetail(key, it) {
  if (!it.credentialPresent) {
    var envHint = {
      ninjaone_commercial: 'NINJAONE_CLIENT_ID / NINJAONE_CLIENT_SECRET',
      ninjaone_fedramp:    'NINJAONE_FED_CLIENT_ID / NINJAONE_FED_CLIENT_SECRET',
      huntress:            'HUNTRESS_API_KEY / HUNTRESS_API_SECRET',
      entra:               'MS_CLIENT_ID / MS_CLIENT_SECRET',
      azure:               'MS_CLIENT_ID / MS_CLIENT_SECRET',
      blumira:             'BLUMIRA_CLIENT_ID / BLUMIRA_CLIENT_SECRET',
      asana:               'ASANA_ACCESS_TOKEN',
      jira:                'JIRA_API_TOKEN',
      unifi:               'UNIFI_API_KEY',
      meraki:              'MERAKI_API_KEY'
    }[key] || '';
    return (
      <div>
        {integrationValueLine(key, it)}
        <div style={{ marginTop: 6, color: 'var(--accent-red, #ef4444)', fontSize: 11 }}>
          Server credentials missing — set {envHint} in App Service config to activate.
        </div>
      </div>
    );
  }
  return integrationValueLine(key, it);
}

function integrationValueLine(key, it) {
  if (it.value == null && !it.projectCount && !it.hostCount) return <span style={{ opacity: 0.5 }}>Configured but no value captured</span>;
  if (key === 'entra' && typeof it.value === 'object' && it.value) {
    return <div>tenant_id: {it.value.tenant}<br/>graph_client_id: {it.value.graph_client || <span style={{ color: 'var(--accent-amber, #f5a623)' }}>not set (partial)</span>}</div>;
  }
  if (key === 'asana') return <div>{it.projectCount} project{it.projectCount === 1 ? '' : 's'} wired</div>;
  if (key === 'jira')  return <div>domain: {it.value}<br/>{it.projectCount} project key{it.projectCount === 1 ? '' : 's'}</div>;
  if (key === 'unifi') return <div>{it.hostCount} host{it.hostCount === 1 ? '' : 's'} — sites: {it.siteLabels && it.siteLabels.length ? it.siteLabels.join(', ') : '(no labels)'}</div>;
  return <div>{String(it.value)}</div>;
}

function AdminSessionsTab() {
  var [sessions, setSessions] = useState([]);
  var [loading, setLoading] = useState(true);
  var [toast, setToast] = useState(null);

  function showToast(msg, type) { setToast({ msg: msg, type: type || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  function fetchSessions() {
    setLoading(true);
    authFetch(API + '/admin/sessions')
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(data) { setSessions(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  useEffect(fetchSessions, []);

  function revoke(s) {
    if (!window.confirm('Revoke session for ' + (s.email || ('user#' + s.user_id)) + '?\n\nThey will be logged out on their next API call.')) return;
    authFetch(API + '/admin/sessions/revoke', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jti: s.jti, userId: s.user_id, reason: 'admin-revoke' })
    })
      .then(function(r) { if (r.ok) { showToast('Session revoked', 'ok'); fetchSessions(); } else { showToast('Revoke failed', 'error'); } })
      .catch(function() { showToast('Revoke failed', 'error'); });
  }

  return (
    <div className="admin-sessions-tab">
      <div className="admin-toolbar">
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          Active sessions are inferred from the last 8 hours of LOGIN_SUCCESS events. A logged-out or expired session may still briefly appear here.
        </div>
        <div style={{ flex: 1 }} />
        <button className="admin-btn" onClick={fetchSessions} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Client</th>
              <th>Login time</th>
              <th>IP</th>
              <th>User agent</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading sessions...</td></tr>}
            {!loading && sessions.length === 0 && <tr><td colSpan="7" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No active sessions.</td></tr>}
            {!loading && sessions.map(function(s) {
              return (
                <tr key={s.jti}>
                  <td>{s.email || ('user#' + s.user_id)}</td>
                  <td><span className={'admin-role-chip ' + s.role}>{s.role}</span></td>
                  <td>{s.client_id || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(s.login_at)}</td>
                  <td style={{ fontSize: 11, fontFamily: 'monospace' }}>{s.ip || '—'}</td>
                  <td style={{ fontSize: 11, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.user_agent}>{s.user_agent || '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="admin-btn sm danger" onClick={function() { revoke(s); }}>Revoke</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {toast && <AdminToast {...toast} />}
    </div>
  );
}

function AdminToast({ msg, type }) {
  var bg = type === 'error' ? 'linear-gradient(135deg, rgba(239,68,68,0.95), rgba(220,38,38,0.95))'
         : type === 'ok'    ? 'linear-gradient(135deg, rgba(34,197,94,0.95), rgba(22,163,74,0.95))'
         :                    'linear-gradient(135deg, rgba(91,158,244,0.95), rgba(56,120,200,0.95))';
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, padding: '12px 20px', background: bg, color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
      {msg}
    </div>
  );
}

// ── Tenant Assessments admin tab ────────────────────────────────────
function AdminAssessmentsTab() {
  var [assessments, setAssessments] = useState([]);
  var [providers, setProviders] = useState([]);
  var [loading, setLoading] = useState(true);
  var [creating, setCreating] = useState(false);
  var [detail, setDetail] = useState(null);        // selected assessment id
  var [newConsent, setNewConsent] = useState(null); // { url, name, provider } — show after create
  var [toast, setToast] = useState(null);

  function showToast(msg, type) { setToast({ msg: msg, type: type || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  function fetchAll() {
    setLoading(true);
    Promise.all([
      authFetch(API + '/admin/assessments').then(function(r) { return r.ok ? r.json() : []; }),
      authFetch(API + '/admin/assessments/providers').then(function(r) { return r.ok ? r.json() : []; })
    ]).then(function(results) {
      setAssessments(results[0] || []);
      setProviders(results[1] || []);
      setLoading(false);
    }).catch(function() { setLoading(false); showToast('Failed to load assessments', 'error'); });
  }
  useEffect(fetchAll, []);

  function onDelete(a) {
    if (!window.confirm('Delete assessment for ' + a.prospect_name + '?\n\nSoft delete — row preserved in DB.')) return;
    authFetch(API + '/admin/assessments/' + a.id, { method: 'DELETE' })
      .then(function(r) { if (r.ok) { showToast('Deleted', 'ok'); fetchAll(); if (detail === a.id) setDetail(null); } else { showToast('Delete failed', 'error'); } });
  }

  function downloadPdf(a) {
    var token = getToken();
    fetch(API + '/admin/assessments/' + a.id + '/report.pdf', { headers: { 'Authorization': 'Bearer ' + token } })
      .then(function(r) { if (!r.ok) throw new Error(); return r.blob(); })
      .then(function(blob) {
        var url = URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url;
        link.download = 'aurelius-assessment-' + (a.tenant_domain || a.prospect_name).replace(/[^a-z0-9]/gi, '_') + '.pdf';
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch(function() { showToast('Report generation failed', 'error'); });
  }

  var statusClass = { pending_consent: 'warn', in_progress: 'info', completed: 'ok', failed: 'bad', expired: 'warn-soft' };
  var statusLabel = { pending_consent: 'Awaiting consent', in_progress: 'Collecting', completed: 'Complete', failed: 'Failed', expired: 'Expired' };

  return (
    <div className="admin-assessments-tab">
      <div className="admin-toolbar">
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Send a prospect a read-only tenant assessment link. They consent → we inventory → we generate a migration-readiness PDF.
        </div>
        <div style={{ flex: 1 }} />
        <button className="admin-btn primary" onClick={function() { setCreating(true); }}>+ New Assessment</button>
        <button className="admin-btn" onClick={fetchAll} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Prospect</th>
              <th>Provider</th>
              <th>Status</th>
              <th>Tenant</th>
              <th>Users</th>
              <th>Tier</th>
              <th>Created</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading assessments…</td></tr>}
            {!loading && assessments.length === 0 && (
              <tr><td colSpan="8" style={{ padding: 36, textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>No assessments yet</div>
                <div style={{ fontSize: 12, opacity: 0.65 }}>Create one to send a prospect a consent link.</div>
              </td></tr>
            )}
            {!loading && assessments.map(function(a) {
              var inv = a.inventory || {};
              var users = inv.users || {};
              return (
                <tr key={a.id} style={{ cursor: 'pointer' }} onClick={function() { setDetail(a.id); }}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{a.prospect_name}</div>
                    <div style={{ fontSize: 11, opacity: 0.6 }}>{a.prospect_email}</div>
                  </td>
                  <td><span className="admin-role-chip">{a.provider === 'microsoft' ? 'M365' : 'Google'}</span></td>
                  <td><span className={'admin-chip ' + (statusClass[a.status] || 'info')}>{statusLabel[a.status] || a.status}</span></td>
                  <td style={{ fontSize: 12 }}>{a.tenant_domain || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={{ fontSize: 12 }}>{users.total != null ? users.total.toLocaleString() : <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td>{inv.provider ? <AssessmentTierChip inventory={inv} /> : <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(a.created_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="admin-row-actions">
                      {a.status === 'completed' && <button className="admin-btn sm primary" onClick={function(e) { e.stopPropagation(); downloadPdf(a); }}>PDF</button>}
                      <button className="admin-btn sm danger" onClick={function(e) { e.stopPropagation(); onDelete(a); }}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <NewAssessmentModal
          providers={providers}
          onClose={function() { setCreating(false); }}
          onCreated={function(result) { setCreating(false); setNewConsent(result); fetchAll(); }}
          onError={function(msg) { showToast(msg, 'error'); }}
        />
      )}
      {newConsent && <ConsentLinkReveal data={newConsent} onClose={function() { setNewConsent(null); }} />}
      {detail && <AssessmentDetailDrawer id={detail} onClose={function() { setDetail(null); fetchAll(); }} onDownload={downloadPdf} />}
      {toast && <AdminToast {...toast} />}
    </div>
  );
}

function AssessmentTierChip({ inventory }) {
  // Score client-side from the stored inventory — matches backend exactly since
  // complexity.js's logic is deterministic on the inventory shape.
  var pts = 0;
  var u = inventory.users || {}, m = inventory.mailboxes || {}, f = inventory.fileStorage || {}, c = inventory.collab || {}, l = inventory.licenses || {};
  var TB = 1024*1024*1024*1024, GB = 1024*1024*1024;
  if (u.total > 100) pts += 1;
  if (u.total > 500) pts += 2;
  if (u.total > 2000) pts += 3;
  if ((m.totalBytes||0) > 1*TB) pts += 2;
  if ((m.totalBytes||0) > 10*TB) pts += 3;
  if ((m.largeOver50GB||0) > 5) pts += 1;
  if ((f.totalBytes||0) > 1*TB) pts += 1;
  if ((f.totalBytes||0) > 10*TB) pts += 2;
  if ((f.largeUsersOver100GB||0) > 10) pts += 1;
  if ((c.sites||0) > 100) pts += 1;
  if ((c.teamsCount||0) > 50) pts += 1;
  if (l.totalAvailable && (l.wastedSeats/l.totalAvailable) > 0.2) pts += 1;
  var tier = pts <= 2 ? 'S' : pts <= 5 ? 'M' : pts <= 8 ? 'L' : 'XL';
  var colors = { S: '#22c55e', M: '#f59e0b', L: '#f97316', XL: '#ef4444' };
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: colors[tier], color: '#fff', fontSize: 10, fontWeight: 800 }}>{tier}</span>;
}

function NewAssessmentModal({ providers, onClose, onCreated, onError }) {
  var [form, setForm] = useState({ provider: '', prospectName: '', prospectEmail: '', notes: '', expiryDays: 14 });
  var [saving, setSaving] = useState(false);

  // Auto-select first enabled provider on mount
  useEffect(function() {
    if (!form.provider) {
      var firstEnabled = providers.find(function(p) { return p.enabled; });
      if (firstEnabled) setForm(function(s) { return { ...s, provider: firstEnabled.name }; });
    }
  }, [providers]);

  function submit(e) {
    e.preventDefault();
    if (!form.provider || !form.prospectName || !form.prospectEmail) { onError('All fields required'); return; }
    setSaving(true);
    authFetch(API + '/admin/assessments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setSaving(false);
        if (x.ok) onCreated({ url: x.d.consentUrl, name: form.prospectName, provider: form.provider });
        else onError(x.d.error || 'Create failed');
      })
      .catch(function() { setSaving(false); onError('Create failed'); });
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 520 }}>
        <form onSubmit={submit}>
          <div className="admin-modal-header">New Tenant Assessment</div>
          <div className="admin-modal-body">
            <div className="admin-field">
              <label>Provider</label>
              <select value={form.provider} onChange={function(e) { setForm(Object.assign({}, form, { provider: e.target.value })); }}>
                {providers.map(function(p) {
                  return <option key={p.name} value={p.name} disabled={!p.enabled}>{p.displayName}{!p.enabled ? ' — ' + p.disabledReason : ''}</option>;
                })}
              </select>
            </div>
            <div className="admin-field">
              <label>Prospect name</label>
              <input type="text" value={form.prospectName} onChange={function(e) { setForm(Object.assign({}, form, { prospectName: e.target.value })); }} placeholder="Rodriguez Law Firm" required />
            </div>
            <div className="admin-field">
              <label>Prospect email (consent link recipient)</label>
              <input type="email" value={form.prospectEmail} onChange={function(e) { setForm(Object.assign({}, form, { prospectEmail: e.target.value })); }} placeholder="admin@rodriguezlaw.com" required />
              <div className="admin-field-hint">Pre-fills sign-in at the IdP. Must be a Global Admin / Super Admin to consent.</div>
            </div>
            <div className="admin-field">
              <label>Link expiry (days)</label>
              <input type="number" min="1" max="90" value={form.expiryDays} onChange={function(e) { setForm(Object.assign({}, form, { expiryDays: parseInt(e.target.value || '14', 10) })); }} />
            </div>
            <div className="admin-field">
              <label>Notes (internal)</label>
              <textarea rows={2} value={form.notes} onChange={function(e) { setForm(Object.assign({}, form, { notes: e.target.value })); }}
                placeholder="Context for the sales team"
                style={{ padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit' }} />
            </div>
            <div className="admin-modal-hint">On save, you'll get a consent link to send the prospect. Email delivery is deferred — copy & send via your preferred secure channel.</div>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn primary" disabled={saving || !form.provider}>{saving ? 'Creating…' : 'Create assessment'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConsentLinkReveal({ data, onClose }) {
  var [copied, setCopied] = useState(false);
  function copy() {
    try { navigator.clipboard.writeText(data.url).then(function() { setCopied(true); setTimeout(function() { setCopied(false); }, 2000); }); } catch (e) {}
  }
  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" style={{ maxWidth: 560 }}>
        <div className="admin-modal-header" style={{ background: 'linear-gradient(135deg, rgba(91,158,244,0.15), rgba(120,80,255,0.15))' }}>Consent link created</div>
        <div className="admin-modal-body">
          <div style={{ fontSize: 13 }}>Send this URL to <strong>{data.name}</strong> via your preferred channel. The link is token-authenticated and expires automatically.</div>
          <div className="admin-pw-display"><code>{data.url}</code><button type="button" className="admin-btn sm primary" onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button></div>
          <div className="admin-field-hint">Remind them: a <strong>{data.provider === 'microsoft' ? 'Global Admin' : 'Super Admin'}</strong> must be the one to click it. Standard user accounts cannot consent to the read-only scopes we request.</div>
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function AssessmentDetailDrawer({ id, onClose, onDownload }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  useEffect(function() {
    authFetch(API + '/admin/assessments/' + id)
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [id]);

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 720 }}>
        <div className="admin-modal-header">Assessment detail</div>
        <div className="admin-modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading…</div>}
          {!loading && data && (
            <>
              <div className="admin-advanced">
                <div><strong>{data.assessment.prospect_name}</strong> — {data.assessment.prospect_email}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {data.assessment.provider === 'microsoft' ? 'Microsoft 365' : 'Google Workspace'}
                  {data.assessment.tenant_domain ? ' · ' + data.assessment.tenant_domain : ''}
                  {' · status '}<strong>{data.assessment.status}</strong>
                </div>
                {data.assessment.notes && <div style={{ fontSize: 12, marginTop: 6, fontStyle: 'italic' }}>{data.assessment.notes}</div>}
                {data.assessment.error_message && <div style={{ fontSize: 12, marginTop: 6, color: '#f87171' }}>Error: {data.assessment.error_message}</div>}
              </div>

              {data.consentUrl && (
                <div className="admin-advanced" style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Consent link (pending)</div>
                  <div className="admin-pw-display"><code style={{ fontSize: 11 }}>{data.consentUrl}</code>
                    <button className="admin-btn sm primary" onClick={function() { navigator.clipboard && navigator.clipboard.writeText(data.consentUrl); }}>Copy</button>
                  </div>
                </div>
              )}

              {data.complexity && (
                <div className="admin-advanced" style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Complexity — {data.complexity.label} (Tier {data.complexity.tier})</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                    {data.complexity.points} points · est. {data.complexity.estimatedHours} hours · {data.complexity.flags.length} flag(s)
                  </div>
                  {data.complexity.flags.map(function(f, i) {
                    return (
                      <div key={i} style={{ fontSize: 12, marginBottom: 4 }}>
                        <span className="admin-chip warn-soft" style={{ marginRight: 6 }}>+{f.points}</span>
                        {f.blurb}
                      </div>
                    );
                  })}
                </div>
              )}

              {data.assessment.inventory && (
                <div className="admin-advanced" style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Inventory</div>
                  <pre style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'var(--bg-base)', padding: 10, borderRadius: 6, maxHeight: 260, overflow: 'auto' }}>
                    {JSON.stringify(data.assessment.inventory, null, 2)}
                  </pre>
                </div>
              )}

              <div className="admin-advanced" style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Timeline</div>
                {data.events.length === 0 && <div style={{ fontSize: 12, opacity: 0.5 }}>No events recorded.</div>}
                {data.events.map(function(ev) {
                  return (
                    <div key={ev.id} style={{ fontSize: 11, marginBottom: 4, display: 'flex', gap: 10 }}>
                      <span style={{ color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{fmtDateTime(ev.created_at)}</span>
                      <span style={{ fontWeight: 600 }}>{ev.event_type}</span>
                      {ev.metadata && <span style={{ opacity: 0.7 }}>{JSON.stringify(ev.metadata).slice(0, 80)}</span>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="admin-modal-footer">
          {data && data.assessment.status === 'completed' && <button className="admin-btn primary" onClick={function() { onDownload(data.assessment); }}>Download PDF</button>}
          <button className="admin-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Admin Automation tab ────────────────────────────────────────────
// Tier-1 helpdesk playbook execution against consented client tenants.
// All routes are admin-gated; destructive playbooks require step-up
// re-auth and a second admin's approval.

function AdminAutomationTab({ clientList }) {
  var [section, setSection] = useState('run');  // 'run' | 'consents' | 'executions' | 'approvals'
  var [toast, setToast] = useState(null);
  function showToast(msg, type) { setToast({ msg: msg, type: type || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  // Process consent-callback redirect query string once
  useEffect(function() {
    var p = new URLSearchParams(window.location.search);
    if (p.get('automation_consent') === 'success') {
      showToast('Consent granted for ' + (p.get('client') || 'client'), 'ok');
      window.history.replaceState({}, '', window.location.pathname);
      setSection('consents');
    } else if (p.get('automation_error')) {
      showToast('Consent failed: ' + p.get('automation_error'), 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <div className="admin-automation-tab">
      <div className="admin-toolbar">
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Tier-1 automation. Run predefined playbooks (offboarding, onboarding, etc.) against client tenants you've been consented to. Destructive actions require step-up re-auth and a second admin's approval.
        </div>
        <div style={{ flex: 1 }} />
        <div className="admin-tabs" style={{ borderBottom: 'none', margin: 0 }}>
          <button className={'admin-tab ' + (section === 'run' ? 'active' : '')} onClick={function() { setSection('run'); }}>Run a playbook</button>
          <button className={'admin-tab ' + (section === 'approvals' ? 'active' : '')} onClick={function() { setSection('approvals'); }}>Approvals</button>
          <button className={'admin-tab ' + (section === 'executions' ? 'active' : '')} onClick={function() { setSection('executions'); }}>Executions</button>
          <button className={'admin-tab ' + (section === 'consents' ? 'active' : '')} onClick={function() { setSection('consents'); }}>Consents</button>
        </div>
      </div>

      {section === 'run'        && <AutomationRunSection clientList={clientList} showToast={showToast} />}
      {section === 'consents'   && <AutomationConsentsSection clientList={clientList} showToast={showToast} />}
      {section === 'executions' && <AutomationExecutionsSection />}
      {section === 'approvals'  && <AutomationApprovalsSection showToast={showToast} />}
      {toast && <AdminToast {...toast} />}
    </div>
  );
}

function AutomationConsentsSection({ clientList, showToast }) {
  var [consents, setConsents] = useState([]);
  var [loading, setLoading] = useState(true);
  var [picking, setPicking] = useState(false);
  var [pickedClient, setPickedClient] = useState('');

  function fetchConsents() {
    setLoading(true);
    authFetch(API + '/admin/automation/consents')
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(d) { setConsents(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }
  useEffect(fetchConsents, []);

  function startConsent() {
    if (!pickedClient) { showToast('Pick a client first', 'error'); return; }
    authFetch(API + '/admin/automation/consents/start', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId: pickedClient })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        if (!x.ok) { showToast(x.d.error || 'Consent start failed', 'error'); return; }
        // Open Microsoft consent in a new window to preserve our admin session
        window.open(x.d.consentUrl, '_blank', 'noopener,noreferrer,width=720,height=820');
        setPicking(false); setPickedClient('');
      });
  }

  function setKillSwitch(c, on) {
    if (on && !window.confirm('Kill-switch ' + c.client_id + '? All automation against this tenant will be blocked until cleared.')) return;
    authFetch(API + '/admin/automation/consents/' + c.id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kill_switched: on })
    }).then(function(r) { if (r.ok) fetchConsents(); else showToast('Failed', 'error'); });
  }
  function setEnabled(c, on) {
    authFetch(API + '/admin/automation/consents/' + c.id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: on })
    }).then(function(r) { if (r.ok) fetchConsents(); else showToast('Failed', 'error'); });
  }
  function revoke(c) {
    if (!window.confirm('REVOKE consent for ' + c.client_id + '?\n\nThis drops all stored admin tokens for this client. Future automation requires re-consent. Proceed?')) return;
    authFetch(API + '/admin/automation/consents/' + c.id, { method: 'DELETE' })
      .then(function(r) { if (r.ok) { showToast('Revoked', 'ok'); fetchConsents(); } else showToast('Failed', 'error'); });
  }

  return (
    <div>
      <div className="admin-toolbar" style={{ marginTop: 14 }}>
        {!picking && <button className="admin-btn primary" onClick={function() { setPicking(true); }}>+ Request consent</button>}
        {picking && (
          <>
            <select value={pickedClient} onChange={function(e) { setPickedClient(e.target.value); }} className="admin-filter-select">
              <option value="">— Pick a client —</option>
              {(clientList || []).filter(function(c) { return c.id !== 'demo'; }).map(function(c) {
                return <option key={c.id} value={c.id}>{c.name}</option>;
              })}
            </select>
            <button className="admin-btn primary" onClick={startConsent}>Open Microsoft consent</button>
            <button className="admin-btn" onClick={function() { setPicking(false); setPickedClient(''); }}>Cancel</button>
          </>
        )}
        <div style={{ flex: 1 }} />
        <button className="admin-btn" onClick={fetchConsents} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Tenant</th>
              <th>Granted by</th>
              <th>State</th>
              <th>Consented</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading…</td></tr>}
            {!loading && consents.length === 0 && (
              <tr><td colSpan="6" style={{ padding: 36, textAlign: 'center' }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>No active consents</div>
                <div style={{ fontSize: 12, opacity: 0.65 }}>Request consent for a client to enable automation against their tenant.</div>
              </td></tr>
            )}
            {!loading && consents.map(function(c) {
              var state = c.revoked_at ? 'revoked' : c.kill_switched ? 'kill-switched' : !c.enabled ? 'disabled' : 'active';
              var stateColor = c.revoked_at ? 'bad' : c.kill_switched ? 'warn' : !c.enabled ? 'warn-soft' : 'ok';
              return (
                <tr key={c.id}>
                  <td><strong>{c.client_id}</strong></td>
                  <td style={{ fontSize: 12 }}>{c.entra_tenant_domain || c.entra_tenant_id}</td>
                  <td style={{ fontSize: 12 }}>{c.consenting_user || '—'}</td>
                  <td><span className={'admin-chip ' + stateColor}>{state}</span></td>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(c.consented_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="admin-row-actions">
                      {c.kill_switched
                        ? <button className="admin-btn sm ok" onClick={function() { setKillSwitch(c, false); }}>Clear kill-switch</button>
                        : <button className="admin-btn sm warn" onClick={function() { setKillSwitch(c, true); }}>Kill-switch</button>}
                      {c.enabled
                        ? <button className="admin-btn sm" onClick={function() { setEnabled(c, false); }}>Disable</button>
                        : <button className="admin-btn sm ok" onClick={function() { setEnabled(c, true); }}>Enable</button>}
                      <button className="admin-btn sm danger" onClick={function() { revoke(c); }}>Revoke</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AutomationRunSection({ clientList, showToast }) {
  var [playbooks, setPlaybooks] = useState([]);
  var [pickedPlaybook, setPickedPlaybook] = useState('');
  var [pickedClient, setPickedClient] = useState('');
  var [userQuery, setUserQuery] = useState('');
  var [matches, setMatches] = useState(null);
  var [target, setTarget] = useState(null);
  var [forwardTo, setForwardTo] = useState('');
  var [plan, setPlan] = useState(null);
  var [previewSteps, setPreviewSteps] = useState(null);   // step list for the checklist
  var [enabledSteps, setEnabledSteps] = useState({});      // { stepId: true }
  var [lookingUp, setLookingUp] = useState(false);
  var [planning, setPlanning] = useState(false);
  var [executing, setExecuting] = useState(false);
  var [stepUpFresh, setStepUpFresh] = useState(false);

  useEffect(function() {
    authFetch(API + '/admin/automation/playbooks')
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(d) { setPlaybooks(d); if (d.length && !pickedPlaybook) setPickedPlaybook(d[0].name); });
  }, []);

  // When the target changes, fetch a preview plan so the admin can
  // choose which non-essential steps to include before running anything.
  useEffect(function() {
    if (!target || !pickedPlaybook || !pickedClient) { setPreviewSteps(null); return; }
    authFetch(API + '/admin/automation/plan', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playbook: pickedPlaybook,
        clientId: pickedClient,
        target: { userId: target.id, email: target.userPrincipalName, displayName: target.displayName },
        params: forwardTo ? { forwardTo: forwardTo } : {}
      })
    })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (!d || !d.plan) return;
        setPreviewSteps(d.plan.plan);
        var initial = {};
        d.plan.plan.forEach(function(s) { initial[s.id] = true; }); // default: all enabled
        setEnabledSteps(initial);
      })
      .catch(function() {});
  }, [target, pickedPlaybook, pickedClient, forwardTo]);

  function toggleStep(id) {
    setEnabledSteps(function(prev) {
      var next = Object.assign({}, prev);
      next[id] = !next[id];
      return next;
    });
  }

  function lookupUser() {
    if (!pickedClient || !userQuery.trim()) return;
    setLookingUp(true);
    setMatches(null); setTarget(null);
    authFetch(API + '/admin/automation/lookup-user', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId: pickedClient, query: userQuery.trim() })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLookingUp(false);
        if (!x.ok) { showToast(x.d.error || x.d.message || 'Lookup failed', 'error'); return; }
        setMatches(x.d.matches || []);
      })
      .catch(function() { setLookingUp(false); showToast('Lookup failed', 'error'); });
  }

  function buildPlan(mode) {
    if (!target) { showToast('Select a user first', 'error'); return; }
    setPlanning(mode === 'plan');
    setExecuting(mode === 'execute');
    var enabledStepsList = previewSteps ? previewSteps.filter(function(s) { return enabledSteps[s.id]; }).map(function(s) { return s.id; }) : null;
    authFetch(API + '/admin/automation/executions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playbook: pickedPlaybook,
        clientId: pickedClient,
        target: { userId: target.id, email: target.userPrincipalName, displayName: target.displayName },
        params: forwardTo ? { forwardTo: forwardTo } : {},
        enabledSteps: enabledStepsList,
        mode: mode === 'execute' ? 'execute' : 'dry_run'
      })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, status: r.status, d: d }; }); })
      .then(function(x) {
        setPlanning(false); setExecuting(false);
        if (!x.ok) {
          if (x.d.error === 'STEP_UP_REQUIRED') {
            stepUp(function() { showToast('Step-up successful — try again', 'ok'); setStepUpFresh(true); });
            return;
          }
          showToast(x.d.error || 'Failed', 'error');
          return;
        }
        setPlan(x.d);
        var msg = mode === 'execute'
          ? (x.d.execution.status === 'awaiting_approval' ? 'Queued — awaiting second admin approval' : 'Execution started')
          : 'Dry run started — see Executions tab';
        showToast(msg, 'ok');
      })
      .catch(function() { setPlanning(false); setExecuting(false); showToast('Failed', 'error'); });
  }

  function stepUp(onSuccess) {
    if (!window.confirm('Re-authenticate to authorize this destructive action? Your step-up token is valid for 5 minutes.')) return;
    authFetch(API + '/admin/automation/stepup', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        if (x.ok) { setStepUpFresh(true); if (onSuccess) onSuccess(); }
        else showToast(x.d.error || 'Step-up failed', 'error');
      });
  }

  var pb = playbooks.find(function(p) { return p.name === pickedPlaybook; });

  return (
    <div style={{ marginTop: 14 }}>
      <div className="admin-advanced">
        <h3 style={{ marginTop: 0, fontSize: 13, color: 'var(--text-primary)' }}>1. Pick a playbook + client</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="admin-field">
            <label>Playbook</label>
            <select value={pickedPlaybook} onChange={function(e) { setPickedPlaybook(e.target.value); setPlan(null); }}>
              {playbooks.map(function(p) { return <option key={p.name} value={p.name}>{p.displayName}</option>; })}
            </select>
            {pb && <div className="admin-field-hint">{pb.description}</div>}
            {pb && pb.requiresApproval && <span className="admin-chip warn" style={{ marginTop: 6, alignSelf: 'flex-start' }}>Requires second-admin approval</span>}
          </div>
          <div className="admin-field">
            <label>Client</label>
            <select value={pickedClient} onChange={function(e) { setPickedClient(e.target.value); setTarget(null); setMatches(null); setPlan(null); }}>
              <option value="">— Pick a consented client —</option>
              {(clientList || []).filter(function(c) { return c.id !== 'demo'; }).map(function(c) {
                return <option key={c.id} value={c.id}>{c.name}</option>;
              })}
            </select>
            <div className="admin-field-hint">Client must have granted Automation consent (see Consents tab).</div>
          </div>
        </div>
      </div>

      {pickedPlaybook === 'offboard-user' && pickedClient && (
        <div className="admin-advanced" style={{ marginTop: 12 }}>
          <h3 style={{ marginTop: 0, fontSize: 13, color: 'var(--text-primary)' }}>2. Find the user</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="admin-filter-input" style={{ flex: 1 }}
              placeholder="email, UPN, or display name"
              value={userQuery}
              onChange={function(e) { setUserQuery(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Enter') lookupUser(); }}
            />
            <button className="admin-btn primary" onClick={lookupUser} disabled={!userQuery.trim() || lookingUp}>
              {lookingUp ? 'Looking up…' : 'Look up'}
            </button>
          </div>
          {matches && matches.length === 0 && <div className="admin-field-hint" style={{ marginTop: 8 }}>No match.</div>}
          {matches && matches.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {matches.map(function(u) {
                var selected = target && target.id === u.id;
                return (
                  <button key={u.id} className={'admin-btn ' + (selected ? 'primary' : '')} style={{ textAlign: 'left' }}
                    onClick={function() { setTarget(u); setPlan(null); }}>
                    <div style={{ fontWeight: 700 }}>{u.displayName}</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>{u.userPrincipalName}{u.accountEnabled === false ? ' · disabled' : ''}{u.assignedLicenses && u.assignedLicenses.length ? ' · ' + u.assignedLicenses.length + ' license(s)' : ''}</div>
                  </button>
                );
              })}
            </div>
          )}

          {target && (
            <div className="admin-field" style={{ marginTop: 14 }}>
              <label>Forward mail to (optional)</label>
              <input type="email" placeholder="manager@client.com" value={forwardTo} onChange={function(e) { setForwardTo(e.target.value); }} />
            </div>
          )}
        </div>
      )}

      {target && previewSteps && (
        <div className="admin-advanced" style={{ marginTop: 12 }}>
          <h3 style={{ marginTop: 0, fontSize: 13, color: 'var(--text-primary)' }}>3. Pick the steps to run</h3>
          <div className="admin-field-hint" style={{ marginBottom: 10 }}>
            Essential snapshot steps always run (forensic data). Everything else is opt-in: uncheck what you don't need for this run. Useful when you're temporarily suspending vs fully offboarding, or when a client wants license-strip-only.
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button type="button" className="admin-btn sm" onClick={function() {
              var all = {}; previewSteps.forEach(function(s) { all[s.id] = true; }); setEnabledSteps(all);
            }}>Select all</button>
            <button type="button" className="admin-btn sm" onClick={function() {
              var min = {}; previewSteps.forEach(function(s) { if (s.essential) min[s.id] = true; }); setEnabledSteps(min);
            }}>Essentials only</button>
          </div>
          {previewSteps.map(function(s, i) {
            var checked = !!enabledSteps[s.id];
            var locked = s.essential;
            return (
              <label key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderLeft: '3px solid ' + (s.essential ? '#16a34a' : s.destructive ? '#dc2626' : s.manual ? '#d97706' : '#5b9ef4'), marginBottom: 6, cursor: locked ? 'default' : 'pointer', opacity: locked || checked ? 1 : 0.55 }}>
                <input type="checkbox" checked={locked || checked} disabled={locked} onChange={function() { if (!locked) toggleStep(s.id); }} style={{ marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {i+1}. {s.label}
                    {s.essential && <span className="admin-chip ok" style={{ marginLeft: 8 }}>essential</span>}
                    {s.manual && <span className="admin-chip warn" style={{ marginLeft: 8 }}>manual</span>}
                    {s.destructive && <span className="admin-chip bad" style={{ marginLeft: 8 }}>destructive</span>}
                    {s.nonFatal && <span className="admin-chip warn-soft" style={{ marginLeft: 8 }}>non-fatal</span>}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.75, marginTop: 3 }}>{s.summary}</div>
                </div>
              </label>
            );
          })}
        </div>
      )}

      {target && (
        <div className="admin-advanced" style={{ marginTop: 12 }}>
          <h3 style={{ marginTop: 0, fontSize: 13, color: 'var(--text-primary)' }}>4. Run it</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="admin-btn primary" onClick={function() { buildPlan('plan'); }} disabled={planning}>
              {planning ? 'Running dry-run…' : 'Dry-run'}
            </button>
            {!stepUpFresh && pb && pb.category === 'destructive' && (
              <button className="admin-btn warn" onClick={function() { stepUp(); }}>Step-up re-auth</button>
            )}
            <button
              className="admin-btn danger"
              onClick={function() {
                var enabledNames = previewSteps
                  ? previewSteps.filter(function(s) { return enabledSteps[s.id] && !s.essential && !s.manual; }).map(function(s) { return s.label; })
                  : [];
                var summary = enabledNames.length === 0 ? 'snapshots only' : enabledNames.join(', ');
                if (!window.confirm('Execute "' + pb.displayName + '" against ' + target.displayName + '?\n\nWill run: ' + summary + '\n\nThis is a destructive action. ' + (pb.requiresApproval ? 'A second admin must approve before any Graph calls run.' : 'It will run immediately.'))) return;
                buildPlan('execute');
              }}
              disabled={executing || (pb && pb.category === 'destructive' && !stepUpFresh)}
              title={pb && pb.category === 'destructive' && !stepUpFresh ? 'Step-up re-auth required first' : ''}
            >
              {executing ? 'Submitting…' : 'Execute'}
            </button>
          </div>
          <div className="admin-field-hint" style={{ marginTop: 10 }}>
            Dry-run records the planned steps without making any Graph calls. Executions appear under the "Executions" tab.
            {pb && pb.requiresApproval && ' This playbook requires a second admin to approve before any actions run.'}
          </div>
        </div>
      )}
    </div>
  );
}

function AutomationExecutionsSection() {
  var [rows, setRows] = useState([]);
  var [loading, setLoading] = useState(true);
  var [detailId, setDetailId] = useState(null);
  function fetch_() {
    setLoading(true);
    authFetch(API + '/admin/automation/executions')
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(d) { setRows(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }
  useEffect(fetch_, []);
  var statusClass = { queued: 'info', awaiting_approval: 'warn', running: 'info', completed: 'ok', failed: 'bad', cancelled: 'warn-soft', rolled_back: 'warn' };

  return (
    <div style={{ marginTop: 14 }}>
      <div className="admin-toolbar">
        <div style={{ flex: 1 }} />
        <button className="admin-btn" onClick={fetch_} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Requested</th>
              <th>Playbook</th>
              <th>Client</th>
              <th>Mode</th>
              <th>Status</th>
              <th>Target</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No executions yet.</td></tr>}
            {!loading && rows.map(function(r) {
              var t = r.target || {};
              return (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={function() { setDetailId(r.id); }}>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(r.requested_at)}</td>
                  <td>{r.playbook}</td>
                  <td>{r.client_id}</td>
                  <td><span className="admin-chip info">{r.mode}</span></td>
                  <td><span className={'admin-chip ' + (statusClass[r.status] || 'info')}>{r.status.replace('_', ' ')}</span></td>
                  <td style={{ fontSize: 12 }}>{t.email || t.displayName || t.userId || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {detailId && <AutomationExecutionDetail id={detailId} onClose={function() { setDetailId(null); fetch_(); }} />}
    </div>
  );
}

function AutomationExecutionDetail({ id, onClose }) {
  var [data, setData] = useState(null);
  useEffect(function() {
    authFetch(API + '/admin/automation/executions/' + id)
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setData(d); });
  }, [id]);

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 760 }}>
        <div className="admin-modal-header">Execution detail</div>
        <div className="admin-modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {!data && <div style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading…</div>}
          {data && (
            <>
              <div className="admin-advanced">
                <div><strong>{data.execution.playbook}</strong> · {data.execution.mode}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{data.execution.client_id} · status <strong>{data.execution.status}</strong></div>
                <div style={{ fontSize: 11, marginTop: 6 }}><strong>Target:</strong> {JSON.stringify(data.execution.target)}</div>
                {data.execution.params && <div style={{ fontSize: 11 }}><strong>Params:</strong> {JSON.stringify(data.execution.params)}</div>}
                {data.execution.error_message && <div style={{ fontSize: 12, marginTop: 8, color: '#f87171' }}>Error: {data.execution.error_message}</div>}
              </div>
              <div className="admin-advanced" style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Steps</div>
                {data.steps.length === 0 && <div style={{ fontSize: 12, opacity: 0.5 }}>No step records yet (still queued).</div>}
                {data.steps.map(function(s) {
                  var sc = { running: '#5b9ef4', succeeded: '#16a34a', failed: '#dc2626', skipped: '#6b7280', pending: '#9ca3af' };
                  return (
                    <div key={s.id} style={{ padding: '8px 10px', marginBottom: 6, background: 'var(--bg-base)', borderRadius: 4, borderLeft: '3px solid ' + (sc[s.status] || '#5b9ef4') }}>
                      <div style={{ fontSize: 12 }}><strong>{s.seq}. {s.step_label}</strong> <span className={'admin-chip ' + (s.status === 'succeeded' ? 'ok' : s.status === 'failed' ? 'bad' : s.status === 'skipped' ? 'warn-soft' : 'info')}>{s.status}</span></div>
                      {s.error_message && <div style={{ fontSize: 11, marginTop: 4, color: '#f87171' }}>{s.error_message}</div>}
                      {s.after_state && <pre style={{ fontSize: 10, marginTop: 4, fontFamily: 'JetBrains Mono, monospace', maxHeight: 100, overflow: 'auto' }}>{JSON.stringify(s.after_state, null, 2)}</pre>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function AutomationApprovalsSection({ showToast }) {
  var [rows, setRows] = useState([]);
  var [loading, setLoading] = useState(true);
  var [stepUpFresh, setStepUpFresh] = useState(false);

  function fetch_() {
    setLoading(true);
    authFetch(API + '/admin/automation/executions?status=awaiting_approval')
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(d) { setRows(d); setLoading(false); });
  }
  useEffect(fetch_, []);

  function stepUp() {
    if (!window.confirm('Re-authenticate to approve a destructive execution?')) return;
    authFetch(API + '/admin/automation/stepup', { method: 'POST' })
      .then(function(r) { if (r.ok) { setStepUpFresh(true); showToast('Step-up successful', 'ok'); } else showToast('Step-up failed', 'error'); });
  }
  function approve(r) {
    if (!stepUpFresh) { stepUp(); return; }
    authFetch(API + '/admin/automation/executions/' + r.id + '/approve', { method: 'POST' })
      .then(function(resp) { return resp.json().then(function(d) { return { ok: resp.ok, d: d }; }); })
      .then(function(x) {
        if (!x.ok) {
          if (x.d.error === 'STEP_UP_REQUIRED') { setStepUpFresh(false); stepUp(); return; }
          showToast(x.d.error || 'Approve failed', 'error'); return;
        }
        showToast('Approved — execution running', 'ok');
        fetch_();
      });
  }
  function cancel(r) {
    if (!window.confirm('Cancel this pending execution?')) return;
    authFetch(API + '/admin/automation/executions/' + r.id + '/cancel', { method: 'POST' })
      .then(function(resp) { if (resp.ok) { showToast('Cancelled', 'ok'); fetch_(); } else showToast('Cancel failed', 'error'); });
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div className="admin-toolbar">
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Executions awaiting a second-admin approval. You cannot approve your own request.</div>
        <div style={{ flex: 1 }} />
        {!stepUpFresh && <button className="admin-btn warn" onClick={stepUp}>Step-up re-auth</button>}
        <button className="admin-btn" onClick={fetch_} disabled={loading}>{loading ? '…' : 'Refresh'}</button>
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Requested</th><th>Playbook</th><th>Client</th><th>Target</th><th>By</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No pending approvals.</td></tr>}
            {!loading && rows.map(function(r) {
              var t = r.target || {};
              return (
                <tr key={r.id}>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(r.requested_at)}</td>
                  <td>{r.playbook}</td>
                  <td>{r.client_id}</td>
                  <td style={{ fontSize: 12 }}>{t.email || t.displayName || t.userId}</td>
                  <td style={{ fontSize: 12 }}>admin#{r.requested_by}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="admin-row-actions">
                      <button className="admin-btn sm primary" onClick={function() { approve(r); }} disabled={!stepUpFresh}>Approve</button>
                      <button className="admin-btn sm" onClick={function() { cancel(r); }}>Cancel</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Prospect-facing tenant assessment landing page ──────────────────
// Rendered for /assess/<token> and /assess/error — no authentication
// required. Prospect clicks "Grant access" → backend redirects to their
// IdP → callback → they come back here with ?done=1 or ?error=...
function AssessmentLandingPage({ token }) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [params] = useState(new URLSearchParams(window.location.search));
  var done = params.get('done') === '1';
  var errorParam = params.get('error');

  useEffect(function() {
    if (!token) { setState({ loading: false, data: null, error: 'missing_token' }); return; }
    fetch(API + '/assess/by-token/' + encodeURIComponent(token))
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, status: r.status, d: d }; }); })
      .then(function(x) {
        if (x.ok) setState({ loading: false, data: x.d, error: null });
        else setState({ loading: false, data: null, error: x.d && x.d.error ? x.d.error : ('http_' + x.status) });
      })
      .catch(function() { setState({ loading: false, data: null, error: 'network' }); });
  }, [token]);

  // Polling: if in_progress, refresh status every 20s
  useEffect(function() {
    if (!state.data || state.data.status !== 'in_progress') return;
    var iv = setInterval(function() {
      fetch(API + '/assess/by-token/' + encodeURIComponent(token))
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(d) { if (d) setState(function(s) { return { ...s, data: d }; }); })
        .catch(function() {});
    }, 20000);
    return function() { clearInterval(iv); };
  }, [token, state.data && state.data.status]);

  useEffect(function() {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  // Error-page route (/assess/error or failed lookup)
  if (state.error || window.location.pathname === '/assess/error') {
    var reasonMap = {
      invalid: 'This link is invalid or has been revoked.',
      expired: 'This assessment link has expired.',
      provider_disabled: 'The assessment provider is temporarily unavailable.',
      state_invalid: 'Your session timed out during sign-in. Please open the link again.',
      unknown_provider: 'Unknown provider. Contact CloudByte Group.',
      consent_rejected: 'Consent was not granted.',
      start_failed: 'Something went wrong starting the flow. Please try again.',
      exchange_failed: 'Microsoft rejected the consent exchange. Please try again or contact CloudByte Group.',
      network: 'Could not reach the server. Please try again.',
      missing_token: 'No assessment token in the URL.',
      http_404: 'This link is not recognized.',
      http_410: 'This link has been revoked or expired.'
    };
    var reason = params.get('reason') || state.error || 'unknown';
    return (
      <AssessmentShell title="We hit a snag" variant="error">
        <p>{reasonMap[reason] || ('Error: ' + reason)}</p>
        <p className="assess-muted">If you believe this is a mistake, reach out to the CloudByte Group contact who sent you this link.</p>
      </AssessmentShell>
    );
  }

  if (state.loading) {
    return <AssessmentShell title="Loading…"><div className="assess-spinner" /></AssessmentShell>;
  }

  var d = state.data;

  // Success state — OAuth just completed
  if (done || d.status === 'in_progress' || d.status === 'completed') {
    var inProgress = d.status === 'in_progress' || done;
    return (
      <AssessmentShell title={inProgress ? 'Thank you — we\'re on it' : 'Assessment complete'} variant="success">
        <p>
          {inProgress
            ? 'CloudByte Group is now collecting read-only telemetry from your tenant. This typically takes 10–30 minutes depending on tenant size.'
            : 'The assessment has completed. CloudByte Group will reach out with the readiness report shortly.'}
        </p>
        <div className="assess-metadata">
          <div><span>Tenant</span><strong>{d.tenantDomain || d.prospectName}</strong></div>
          <div><span>Provider</span><strong>{d.providerDisplay}</strong></div>
          <div><span>Status</span><strong>{d.status.replace('_', ' ')}</strong></div>
        </div>
        <p className="assess-muted">You can close this tab. A CloudByte Group representative will be in touch.</p>
      </AssessmentShell>
    );
  }

  if (d.status === 'failed' || errorParam) {
    return (
      <AssessmentShell title="Assessment didn't complete" variant="error">
        <p>Something prevented the read-only inventory from completing. This usually happens when the signed-in account lacked the admin role required to read the tenant.</p>
        <div className="assess-metadata">
          <div><span>Tenant</span><strong>{d.prospectName}</strong></div>
          <div><span>Provider</span><strong>{d.providerDisplay}</strong></div>
        </div>
        <p className="assess-muted">Contact CloudByte Group to reissue a link. We can retry the flow from a Global Admin / Super Admin account.</p>
      </AssessmentShell>
    );
  }

  if (d.status === 'expired') {
    return (
      <AssessmentShell title="This link has expired" variant="error">
        <p>For security, assessment links expire after a fixed window. Reach out to your CloudByte Group contact for a fresh link.</p>
      </AssessmentShell>
    );
  }

  // pending_consent — the main consent UI
  var startUrl = API + '/assess/' + encodeURIComponent(token) + '/start';
  var scopesByProvider = {
    microsoft: [
      'Read-only user directory (names, accounts, license assignment, sign-in activity)',
      'Read-only group, Teams, and SharePoint site lists',
      'Read-only usage reports (mailbox, OneDrive, and site storage in aggregate)',
      'Read-only privileged role assignments'
    ],
    google: [
      'Read-only Workspace user directory',
      'Read-only group and shared drive lists',
      'Read-only usage reports (Gmail and Drive storage in aggregate)',
      'Read-only admin activity reports'
    ]
  };
  var scopes = scopesByProvider[d.provider] || [];

  return (
    <AssessmentShell title="Tenant Assessment for CloudByte Group" variant="default">
      <p>
        <strong>{d.prospectName}</strong>, CloudByte Group has prepared a read-only assessment of your{' '}
        <strong>{d.providerDisplay}</strong> tenant. The result is a migration-readiness report
        covering user counts, storage, licensing, groups, and complexity indicators — used to
        scope and price a potential migration engagement.
      </p>
      <div className="assess-section-heading">What we will read</div>
      <ul className="assess-scope-list">
        {scopes.map(function(s, i) { return <li key={i}>{s}</li>; })}
      </ul>
      <div className="assess-section-heading">What we will NOT read</div>
      <ul className="assess-scope-list negative">
        <li>Email message bodies, attachments, or subject lines</li>
        <li>File or document contents in OneDrive, SharePoint, or Drive</li>
        <li>Chat messages in Teams or Google Chat</li>
        <li>Any personally-identifying record beyond display name and work email</li>
      </ul>
      <div className="assess-callout">
        <strong>Admin required.</strong> You will need to sign in as a{' '}
        {d.provider === 'microsoft' ? 'Microsoft 365 Global Admin (or equivalent consent-capable role)' : 'Google Workspace Super Admin'}
        . A standard user account cannot grant these scopes.
      </div>
      <a className="assess-cta" href={startUrl}>
        Grant read-only access with {d.providerDisplay}
      </a>
      <p className="assess-muted">
        Link expires {d.expiresAt ? new Date(d.expiresAt).toLocaleDateString('en-US', { dateStyle: 'long' }) : 'in 14 days'}.
        Access token is short-lived and refresh tokens are not stored.
      </p>
    </AssessmentShell>
  );
}

function AssessmentShell({ title, children, variant }) {
  return (
    <div className={'assess-page' + (variant ? ' ' + variant : '')}>
      <div className="assess-card">
        <div className="assess-brand">
          <img src="/logo.png" alt="CloudByte Group" />
          <div>
            <div className="assess-brand-name">CLOUDBYTE GROUP</div>
            <div className="assess-brand-sub">Aurelius Tenant Assessment</div>
          </div>
        </div>
        <h1 className="assess-title">{title}</h1>
        <div className="assess-body">{children}</div>
        <div className="assess-footer">
          Questions? Reach your CloudByte Group contact, or email <a href="mailto:info@cloudbytegroup.com">info@cloudbytegroup.com</a>.
        </div>
      </div>
    </div>
  );
}

// ── User badge in header ─────────────────────────────────────────────
// Shows the currently signed-in user (avatar + display name + role chip).
// Click → dropdown with full email + Sign out. Replaces the bare Logout
// button so it's always obvious which account this session belongs to.
function UserBadge({ onLogout }) {
  var [open, setOpen] = useState(false);
  var role = sessionStorage.getItem('aurelius_role') || '';
  var clientId = sessionStorage.getItem('aurelius_clientId') || '';
  var email = sessionStorage.getItem('aurelius_email') || '';
  var displayName = sessionStorage.getItem('aurelius_displayName') || email || (clientId ? (clientId === 'demo' ? 'Demo' : clientId) : 'Signed in');

  // Compute initials from displayName or email
  var initials = (function() {
    var src = displayName || email;
    if (!src) return '?';
    var parts = src.split(/[\s.@-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return src.slice(0, 2).toUpperCase();
  })();

  var roleLabel = role === 'admin' ? 'Admin'
    : clientId === 'demo' ? 'Demo'
    : role === 'client' ? 'Client'
    : '';

  // Close dropdown on outside click
  useEffect(function() {
    if (!open) return;
    function onDoc(e) {
      if (!e.target.closest || !e.target.closest('.user-badge-wrap')) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return function() { document.removeEventListener('mousedown', onDoc); };
  }, [open]);

  return (
    <div className="user-badge-wrap" style={{ position: 'relative' }}>
      <button
        type="button"
        className="user-badge"
        onClick={function() { setOpen(!open); }}
        title={email + (roleLabel ? ' · ' + roleLabel : '')}
      >
        <span className="user-badge-avatar">{initials}</span>
        <span className="user-badge-text">
          <span className="user-badge-name">{displayName}</span>
          {roleLabel && <span className={'user-badge-role ' + role}>{roleLabel}</span>}
        </span>
        <span className="user-badge-caret">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="user-badge-menu" role="menu">
          <div className="user-badge-menu-header">
            <div className="user-badge-menu-name">{displayName}</div>
            {email && <div className="user-badge-menu-email">{email}</div>}
            <div className="user-badge-menu-meta">
              {roleLabel && <span className={'user-badge-role ' + role}>{roleLabel}</span>}
              {clientId && clientId !== 'demo' && <span className="user-badge-menu-client">· {clientId}</span>}
            </div>
          </div>
          <button type="button" className="user-badge-menu-item logout" onClick={function() { setOpen(false); onLogout(); }}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────
function App() {
  // Top-level router: /assess/* → prospect landing, everything else → MSP app
  if (IS_ASSESS_VIEW) return <AssessmentLandingPage token={URL_ASSESS_TOKEN} />;
  return <AureliusApp />;
}

function AureliusApp() {
  const [clientList, setClientList] = useState([]);
  const [activeClient, setActiveClient] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [clientNotFound, setClientNotFound] = useState(false);
  const [authenticated, setAuthenticated] = useState(!!getToken());
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [theme, setTheme] = useState(sessionStorage.getItem('aurelius_theme') || 'dark');
  const [badgeData, setBadgeData] = useState({});
  const [qbrOpen, setQbrOpen] = useState(false);
  const [qbrReport, setQbrReport] = useState(null);
  const [qbrLoading, setQbrLoading] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(function() {
    try { return localStorage.getItem('aurelius_nav_collapsed') === '1'; } catch (e) { return false; }
  });
  const [insightsOpen, setInsightsOpen] = useState(false);
  function toggleNavCollapsed() {
    setNavCollapsed(function(prev) {
      var next = !prev;
      try { localStorage.setItem('aurelius_nav_collapsed', next ? '1' : '0'); } catch (e) {}
      return next;
    });
  }
  const isAdmin = sessionStorage.getItem('aurelius_role') === 'admin';

  var generateQBR = async function() {
    if (!activeClient) return;
    setQbrOpen(true);
    setQbrLoading(true);
    setQbrReport(null);
    try {
      var resp = await authFetch(API + '/qbr', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ clientId: activeClient }) });
      if (resp.ok) { var data = await resp.json(); setQbrReport(data.report); }
    } catch (e) { console.warn('QBR generation failed:', e); }
    setQbrLoading(false);
  };

  useEffect(function() {
    document.documentElement.setAttribute('data-theme', theme);
    sessionStorage.setItem('aurelius_theme', theme);
  }, [theme]);

  // Idle timeout: auto-logout after 15 minutes with 60s warning (NIST 800-171 3.1.10)
  var [idleWarning, setIdleWarning] = useState(false);
  var [changelogOpen, setChangelogOpen] = useState(false);
  var [changelogNew, setChangelogNew] = useState(false);

  // Check if there are new changelog entries
  useEffect(function() {
    if (!authenticated) return;
    authFetch(API + '/changelog').then(function(r) { return r.json(); }).then(function(data) {
      if (data[0]) {
        var seen = sessionStorage.getItem('aurelius_changelog_seen');
        if (seen !== data[0].version) setChangelogNew(true);
      }
    }).catch(function() {});
  }, [authenticated]);
  useEffect(function() {
    if (!authenticated) return;
    var IDLE_TIMEOUT = 14 * 60 * 1000;
    var WARNING_DURATION = 60 * 1000;
    var idleTimer = null;
    var logoutTimer = null;
    function resetTimer() {
      if (idleTimer) clearTimeout(idleTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
      setIdleWarning(false);
      idleTimer = setTimeout(function() {
        setIdleWarning(true);
        logoutTimer = setTimeout(function() {
          var token = getToken();
          if (token) fetch(API + '/logout', { method: 'POST', headers: { 'Authorization': 'Bearer ' + token } }).catch(function() {});
          clearToken(); setAuthenticated(false); setClientData(null); setClientList([]);
        }, WARNING_DURATION);
      }, IDLE_TIMEOUT);
    }
    var events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(function(evt) { window.addEventListener(evt, resetTimer); });
    resetTimer();
    return function() { if (idleTimer) clearTimeout(idleTimer); if (logoutTimer) clearTimeout(logoutTimer); events.forEach(function(evt) { window.removeEventListener(evt, resetTimer); }); };
  }, [authenticated]);

  useEffect(function() {
    if (!authenticated) return;
    authFetch(API + '/clients')
      .then(function(r) { return r.json(); })
      .then(function(list) {
        setClientList(list);
        if (IS_CLIENT_VIEW) {
          var found = list.find(function(c) { return c.id === URL_CLIENT; });
          if (found) { setActiveClient(URL_CLIENT); } else { setClientNotFound(true); }
        } else if (list.length > 0) { setActiveClient(list[0].id); }
      })
      .catch(function(e) { console.error('Failed to load clients:', e); });
  }, [authenticated]);

  var fetchClientData = useCallback(async function() {
    if (!activeClient) return;
    setLoading(true);
    try {
      var resp = await authFetch(API + '/clients/' + activeClient + '/data');
      if (resp.ok) {
        var data = await resp.json();
        try {
          var siemResp = await authFetch(API + '/clients/' + activeClient + '/siem');
          if (siemResp.ok) { var siemData = await siemResp.json(); if (siemData.enabled) data.siemSummary = siemData.summary; }
        } catch (e) {}
        setClientData(data);
        setLastRefresh(new Date());
      }
    } catch (e) { console.error('Failed to fetch client data:', e); }
    setLoading(false);
  }, [activeClient]);

  useEffect(function() {
    fetchClientData();
    var interval = setInterval(fetchClientData, 60000);
    return function() { clearInterval(interval); };
  }, [fetchClientData]);

  // ── Badge data computation ──
  useEffect(function() {
    if (!activeClient || !clientData) return;
    var badges = {};

    // Security: red dot if active threats or SIEM threats
    if ((clientData.threats && clientData.threats.active > 0) || (clientData.siemSummary && clientData.siemSummary.threats > 0)) {
      badges.security = { type: 'dot' };
    }
    // SIEM: count of open findings
    if (clientData.siemSummary && clientData.siemSummary.total_open > 0) {
      badges.siem = { type: 'count', count: clientData.siemSummary.total_open };
    }

    setBadgeData(badges);

    // Fetch additional badge counts
    Promise.all([
      authFetch(API + '/clients/' + activeClient + '/changes').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + activeClient + '/tickets').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + activeClient + '/projects').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) {
      var newBadges = Object.assign({}, badges);
      if (results[0] && results[0].summary) {
        var s = results[0].summary;
        var openChanges = s.submitted + s.under_review + s.approved + s.in_progress;
        if (openChanges > 0) newBadges.changes = { type: 'count', count: openChanges };
      }
      if (results[1] && results[1].metrics && results[1].metrics.open > 0) {
        newBadges.support = { type: 'count', count: results[1].metrics.open };
      }
      if (results[2] && results[2].projects) {
        var totalOverdue = results[2].projects.reduce(function(s, p) { return s + p.overdueTasks; }, 0);
        if (totalOverdue > 0) newBadges.projects = { type: 'dot' };
      }
      setBadgeData(newBadges);
    });
  }, [activeClient, clientData]);

  // Keyboard navigation: Ctrl+← / Ctrl+→ to cycle tabs
  var allTabs = ['overview','redteam','security','posture','identity','keeper','siem','network','devops','support','projects','changes','reviews','budget','compliance'];
  useEffect(function() {
    function handleKeyNav(e) {
      if (!e.ctrlKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      e.preventDefault();
      var idx = allTabs.indexOf(activeTab);
      if (idx === -1) return;
      if (e.key === 'ArrowRight') setActiveTab(allTabs[(idx + 1) % allTabs.length]);
      else setActiveTab(allTabs[(idx - 1 + allTabs.length) % allTabs.length]);
    }
    window.addEventListener('keydown', handleKeyNav);
    return function() { window.removeEventListener('keydown', handleKeyNav); };
  }, [activeTab]);

  var handleLogout = function() {
    var token = getToken();
    if (token) {
      fetch(API + '/logout', { method: 'POST', headers: { 'Authorization': 'Bearer ' + token } }).catch(function() {});
    }
    clearToken(); setAuthenticated(false); setClientData(null); setClientList([]);
    sessionStorage.removeItem('aurelius_demo_role');
  };

  if (!authenticated) {
    if (IS_CLIENT_VIEW) return <LoginPage clientId={URL_CLIENT} onLogin={function() { setAuthenticated(true); }} />;
    return <LoginPage onLogin={function() { setAuthenticated(true); }} />;
  }
  if (clientNotFound) return <ClientNotFound clientId={URL_CLIENT} />;

  var clientInfo = clientList.find(function(c) { return c.id === activeClient; }) || {};
  var live = clientData ? clientData.live : { ninjaone: false, huntress: false };

  var c = {
    name: clientData ? clientData.name : (clientInfo.name || ''),
    type: clientData ? clientData.type : (clientInfo.type || ''),
    industry: clientData ? clientData.industry : (clientInfo.industry || ''),
    cmmc: clientData ? clientData.cmmc : (clientInfo.cmmc || false),
    endpoints: clientData ? (clientData.endpoints || { total: 0, healthy: 0, warning: 0, critical: 0 }) : { total: 0, healthy: 0, warning: 0, critical: 0 },
    threats: clientData ? (clientData.threats || { active: 0, mitigated: 0, investigating: 0 }) : { active: 0, mitigated: 0, investigating: 0 },
    huntress_agents: clientData ? clientData.huntress_agents : null,
    patches: placeholders.patches, credentials: placeholders.credentials, siem: placeholders.siem,
    tickets: placeholders.tickets, projects: placeholders.projects, cmmc_domains: placeholders.cmmc_domains,
    scores: { overall: 0, endpoint: 0, threat: 0, credential: 0, siem: 0 },
    live: live,
  };

  // Score calculation — only compute when live data is actually available
  if (clientData && clientData.endpoints && c.endpoints.total > 0) {
    c.scores.endpoint = Math.round((c.endpoints.healthy / c.endpoints.total) * 100);
  }
  if (clientData && clientData.threats) {
    c.scores.threat = c.threats.active === 0 ? 95 : c.threats.active <= 2 ? 70 : 45;
  }
  c.scores.credential = 0;
  if (clientData && clientData.siemSummary) {
    var st = clientData.siemSummary.threats || 0;
    var so = clientData.siemSummary.total_open || 0;
    c.scores.siem = so === 0 ? 95 : st === 0 && so <= 5 ? 85 : st === 0 ? 75 : st <= 2 ? 60 : 40;
  }
  var activeScores = [c.scores.endpoint, c.scores.threat, c.scores.siem].filter(function(s) { return s > 0; });
  c.scores.overall = activeScores.length > 0 ? Math.round(activeScores.reduce(function(a, b) { return a + b; }, 0) / activeScores.length) : 0;

  // Tab catalog now lives in NAV_RAIL_GROUPS at module scope; this used to
  // hold tabGroups / tabLabel for the horizontal tab strip but that's been
  // replaced by the vertical NavRail.

  if (clientList.length === 0 && !clientNotFound) {
    return (<div className="app"><header className="header"><div className="header-left"><div className="logo">AURELIUS</div><span className="logo-sub">by CloudByte Group</span></div></header><div className="tab-content"><SkeletonLoader /></div></div>);
  }

  return (
    <div className="app">
      {idleWarning && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, padding: '12px 20px', background: 'linear-gradient(135deg, rgba(240,101,101,0.95), rgba(220,60,60,0.95))', backdropFilter: 'blur(8px)', color: '#fff', fontSize: 14, fontWeight: 600, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
          <span>Session expiring in 60 seconds due to inactivity</span>
          <button onClick={function() { setIdleWarning(false); }} style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 6, color: '#fff', padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Stay Signed In</button>
        </div>
      )}
      <header className="header">
        <div className="header-left"><div className="logo">AURELIUS</div><span className="logo-sub">{adminMode ? 'Administrator Panel' : 'by CloudByte Group'}</span></div>
        <div className="header-right">
          {!IS_CLIENT_VIEW && !adminMode && (
            <select className="client-select" value={activeClient || ''} onChange={function(e) { setActiveClient(e.target.value); setActiveTab('overview'); setQbrOpen(false); setQbrReport(null); }}>
              {clientList.map(function(cl) { return <option key={cl.id} value={cl.id}>{cl.name}</option>; })}
            </select>
          )}
          {isAdmin && !IS_CLIENT_VIEW && (
            <button className={'admin-toggle-btn' + (adminMode ? ' active' : '')} onClick={function() { setAdminMode(!adminMode); }} title={adminMode ? 'Back to dashboard' : 'Administrator panel'}>
              {adminMode ? '← Dashboard' : 'Admin'}
            </button>
          )}
          <button className="theme-toggle" onClick={function() { setChangelogOpen(true); if (changelogNew) setChangelogNew(false); }} title="What's New" style={{ position: 'relative' }}>
            {'◈'}
            {changelogNew && <span style={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, borderRadius: '50%', background: '#5b9ef4', boxShadow: '0 0 6px rgba(91,158,244,0.6)' }} />}
          </button>
          <button className="theme-toggle" onClick={function() { setTheme(theme === 'dark' ? 'light' : 'dark'); }} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>{theme === 'dark' ? '☀' : '☾'}</button>
          <UserBadge onLogout={handleLogout} />
        </div>
      </header>

      {adminMode && <AdminPanel clientList={clientList} />}

      {!adminMode && (
        <div className="layout">
          <NavRail
            activeTab={activeTab}
            onTabChange={setActiveTab}
            badgeData={badgeData}
            collapsed={navCollapsed}
            onToggleCollapse={toggleNavCollapsed}
          />
          <main className="layout-content">
            <div className="client-banner">
              <div><span className="client-name">{c.name}</span><span className="client-type">{c.type} — {c.industry}</span></div>
              <div className="banner-right">
                <CoverageBadge c={c} live={live} onClick={function() { setCoverageOpen(true); }} />
                {lastRefresh && <span className="last-refresh">Updated {lastRefresh.toLocaleTimeString()}</span>}
                <button className="refresh-btn" onClick={fetchClientData}>Refresh</button>
              </div>
            </div>
            {coverageOpen && <CoverageDrawer c={c} live={live} onClose={function() { setCoverageOpen(false); }} />}
            <div className="layout-content-inner">
              {loading && !clientData ? (
                <div className="tab-content"><SkeletonLoader /></div>
              ) : (
                <>
                  {activeTab === 'overview' && <OverviewTab c={c} live={live} clientId={activeClient} onNavigate={setActiveTab} />}
                  {activeTab === 'security' && <SecurityTab clientId={activeClient} c={c} live={live} onNavigate={setActiveTab} />}
                  {activeTab === 'posture' && <PostureTab clientId={activeClient} live={live} />}
                  {activeTab === 'identity' && <IdentityTab clientId={activeClient} live={live} />}
                  {activeTab === 'keeper' && <KeeperTab clientId={activeClient} />}
                  {activeTab === 'redteam' && <RedTeamTab clientId={activeClient} onNavigate={setActiveTab} />}
                  {activeTab === 'siem' && <SIEMTab clientId={activeClient} c={c} />}
                  {activeTab === 'network' && <NetworkTab clientId={activeClient} />}
                  {activeTab === 'devops' && <DevOpsTab clientId={activeClient} onNavigate={setActiveTab} />}
                  {activeTab === 'support' && <SupportTab clientId={activeClient} live={live} />}
                  {activeTab === 'changes' && <ChangesTab clientId={activeClient} isAdmin={sessionStorage.getItem('aurelius_role') === 'admin'} />}
                  {activeTab === 'budget' && <BudgetTab clientId={activeClient} />}
                  {activeTab === 'projects' && <ProjectsTab clientId={activeClient} />}
                  {activeTab === 'reviews' && <ReviewsTab clientId={activeClient} isAdmin={sessionStorage.getItem('aurelius_role') === 'admin'} />}
                  {activeTab === 'compliance' && <ComplianceTab c={c} clientId={activeClient} />}
                </>
              )}
            </div>
          </main>
        </div>
      )}
      <InsightsFab
        hidden={adminMode}
        hasAi={CAN_USE_ADMIN_FEATURES}
        onClick={function() { setInsightsOpen(true); }}
      />
      <InsightsDrawer
        isOpen={insightsOpen}
        onClose={function() { setInsightsOpen(false); }}
        c={c}
        clientName={c.name}
        activeTab={activeTab}
        clientId={activeClient}
        onGenerateQBR={function() { setInsightsOpen(false); generateQBR(); }}
      />
      <QBRPanel isOpen={qbrOpen} onClose={function() { setQbrOpen(false); }} report={qbrReport} clientName={c ? c.name : ''} loading={qbrLoading} />
      <ChangelogPanel isOpen={changelogOpen} onClose={function() { setChangelogOpen(false); }} />
    </div>
  );
}

export default App;